
        /* =========================
          GLOBAL VARIABLES
        ========================== */
        let cropper = null;
        let activeImage = null;
        let activeCustomizer = null;
        let scaleX = 1;
        let scaleY = 1;
        let cropOpenToken = 0;
        let customizerHistoryLock = false;
        let customizerHistoryClosing = false;
        const customizerCropState = new WeakMap();
        const cropImagePreloadCache = new Map();

        /* =========================
          CUSTOMIZER STATE
        ========================== */
        const CM_PER_INCH = 2.54;
        const ARTWORK_OVERSIZE_SHORT_MAX = 32;
        const ARTWORK_OVERSIZE_LONG_MAX = 40;
        const ARTWORK_SIZE_MIN = 5;
        const ARTWORK_SIZE_MAX = 75;
        const ARTWORK_SIZE_STEP = 0.1;
        const DEFAULT_TOP_MAT_SIZE_INCHES = 4;
        const DEFAULT_BOTTOM_MAT_SIZE_INCHES = 0.25;

        /* =========================
          PRICING (May 2026 formulas)
          - Base product price shown on page is treated as "Retail Art Price"
          - Variable components are computed from glass size/perimeter + selected options
        ========================== */
        const PRICING_CONFIG = {
          // Canvas: flat universal fit charge (no glass / plexi per pricing guide).
          canvasFitChargeFlat: 1.00,
          // Optional: provide a per-liner costPerInch on liner objects (API or hardcoded).
          linerCostPerInchFallback: 0.70,
          // If you don’t have cost-per-inch in the frame API, add it in this lookup by frame id.
          // Values below match your Pricing Setup Guide examples; expand to your full catalog.
          frameCostPerInchById: {
            '104-100': 3.197,
            '104-200': 3.197,
            'b222': 1.345,
            'b520': 1.345,
            'n2531': 1.91,
            'n2610': 1.37,
            'p2607': 1.72,
            '83997': 1.69
          },
          // FIT — Underglass tiers by framed united inches. Glass stays until 84 UI → plexi.
          glassChargeTiers: [
            { maxUi: 32, charge: 2.30 },
            { maxUi: 41, charge: 2.90 },
            { maxUi: 45, charge: 3.50 },
            { maxUi: 53, charge: 4.50 },
            { maxUi: 65, charge: 6.25 },
            { maxUi: 71, charge: 7.05 },
            { maxUi: 83, charge: 8.30 },
            { maxUi: Infinity, charge: 8.30 }
          ],
          // FR/FAB — foam core (deckled artwork), by framed united inches.
          foamCoreFitTiers: [
            { maxUi: 32, charge: 1.00 },
            { maxUi: 41, charge: 1.00 },
            { maxUi: 45, charge: 2.00 },
            { maxUi: 53, charge: 2.00 },
            { maxUi: 65, charge: 3.00 },
            { maxUi: 71, charge: 3.00 },
            { maxUi: 83, charge: 3.00 },
            { maxUi: Infinity, charge: 4.00 }
          ],
          // HFM $ OR AB — hand finish / acrylic brushstroke only (not FR/FAB).
          // Glass-size / inside-frame united inches: 40–74 / 75–84 / 85–95 / 96+.
          handFinishFitTiers: [
            { maxUi: 39, charge: 0 },
            { maxUi: 74, charge: 3.00 },
            { maxUi: 84, charge: 4.00 },
            { maxUi: 95, charge: 5.00 },
            { maxUi: Infinity, charge: 6.00 }
          ],
          // Plexi replaces glass when the glass opening exceeds 40 × 48 (either orientation),
          // or when glass united inches reach this value.
          fitPlexiMaxShortInches: 40,
          fitPlexiMaxLongInches: 48,
          fitPlexiMinUnitedInches: 84,
          // Plexi (under glass): tiered by framed united inches.
          plexiChargeTiers: [
            { maxUi: 50, charge: 15.00 },
            { maxUi: 70, charge: 20.00 },
            { maxUi: Infinity, charge: 25.00 }
          ],
          // Artwork Float charge (FR/FI) — flat fee by UI tier (only when enabled).
          artworkFloatTiers: [
            { maxUi: 41, charge: 1.00 }, // 0–35 and 36–41
            { maxUi: Infinity, charge: 2.00 } // 42+ (includes all larger tiers)
          ],
          // Exclusive Giclee deckled float — universal $2 on customizer change (not original spec).
          exclusiveGicleeArtworkFloatCharge: 2.00,
          // Matched against product metafield custom.product_types (data-product-types).
          exclusiveGicleeProductType: 'Exclusive Giclee, Deckled and Floating on a Mat',
          // Flat fee when customer changes artwork size from the original specification.
          sizeChangeChargeFlat: 40.00,
          // Printed mat — (glass area × rate) − wholesale art (all printed mat cases).
          printedMatSpecificationRate: 0.07,
          // Non-size customization: 1 change (frame, mat, liner, substrate, glass/plexi, or acrylic brushstroke) = $20; 2+ = $40 (max).
          customizationFeeSingle: 20.00,
          customizationFeeMax: 40.00,
          // Mat sheet tiering (per mat) based on glass dimensions.
          matTiers: [
            // Quarter sheet: up to 16 × 20
            { name: 'Quarter Sheet', maxW: 16, maxH: 20, cost: 0.43 },
            // Half sheet: up to 20×32 OR 40×16 (orientation-aware)
            { name: 'Half Sheet', altMaxW: 20, altMaxH: 32, altMaxW2: 40, altMaxH2: 16, cost: 0.85 },
            // Full sheet: up to 32 × 40
            { name: 'Full Sheet', maxW: 32, maxH: 40, cost: 1.69 },
            // Oversize: up to 40 × 60 (fallback)
            { name: 'Oversize Sheet', maxW: 40, maxH: 60, cost: 4.50 }
          ]
        };

        /* =========================
          FORMULA PRICING (June 2026 — review / moulding formula)
          Separate from PRICING_CONFIG / calculatePricing — do not merge.
        ========================== */
        const FORMULA_PRICING_CONFIG = {
          artWorkCostRate: 0.01,
          artCostMultiplier: 2,
          // Retail art $/sq in by substrate (material rate table).
          retailArtRateDefault: 0.14,
          retailArtMaterialRates: {
            canvas: 0.20,
            highGlossPhotoPaper: 0.18,
            matPaper: 0.14
          },
          wholesaleArtDivisor: 2,
          importRate: 0.05,
          bomFtUnitedMultiplier: 2,
          bomFtFrameCornerMultiplier: 8,
          bomFtDivisor: 12,
          bomFtWasteFactor: 1.05,
          mdlgMarkup: 1.15,
          fitCanvasCharge: 1.00,
          // Canvas linear moulding default when liner has no basePrice (spreadsheet CL46).
          linearMldgCostDefault: 0.65,
          ttlMultiplier: 1.2,
          ttlDivisor: 0.2725,
          labor: 5.00,
          royaltyRateDefault: 0.015,
          // Manufacturing column (after Total).
          mfgMarginMultiplier: 0.55,
          mfgPlusMultiplier: 1.05,
          finalProductPriceDivisor: 0.33,
          canvasFinalProductPriceDivisor: 0.28
        };

        function roundFormulaMoney(n) {
          const v = Number(n);
          if (!isFinite(v)) return 0;
          return Math.round(v * 100) / 100;
        }

        function roundFormulaMoneyToDollar(n) {
          const v = Number(n);
          if (!isFinite(v)) return 0;
          return Math.round(v);
        }

        function roundFormulaMoneyCeilToDollar(n) {
          const v = Number(n);
          if (!isFinite(v) || v <= 0) return 0;
          return Math.ceil(v);
        }

        /** Stocking dealer price: Total Manufacturing ÷ substrate divisor, rounded up to a whole dollar. */
        function roundFinalStockingDealerPrice(totalManufacturing, divisor) {
          const totalMfg = Number(totalManufacturing);
          const d = Number(divisor);
          if (!isFinite(totalMfg) || totalMfg <= 0) return 0;
          const safeDivisor = isFinite(d) && d > 0
            ? d
            : FORMULA_PRICING_CONFIG.finalProductPriceDivisor;
          return roundFormulaMoneyCeilToDollar(totalMfg / safeDivisor);
        }

        function formatMoneyUSDDollars(n) {
          const safe = roundFormulaMoneyToDollar(n);
          return '$' + safe;
        }

        function getFrameMouldingPriceForFormula(frame) {
          if (!frame) return 0;
          const direct = parseFloat(frame.basePrice ?? frame.base_price);
          if (isFinite(direct) && direct > 0) return direct;
          const api = frame._api;
          if (api) {
            const fromApi = parseFloat(api.basePrice ?? api.base_price);
            if (isFinite(fromApi) && fromApi > 0) return fromApi;
          }
          return 0;
        }

        /** Linear / liner moulding price for formula (CL46); falls back to linearMldgCostDefault. */
        function getLinearMouldingPriceForFormula(liner) {
          const l = liner || customizerState.liner || null;
          if (!l) return FORMULA_PRICING_CONFIG.linearMldgCostDefault;
          const direct = parseFloat(l.basePrice ?? l.base_price);
          if (isFinite(direct) && direct >= 0) return direct;
          const api = l._api;
          if (api) {
            const fromApi = parseFloat(api.basePrice ?? api.base_price);
            if (isFinite(fromApi) && fromApi >= 0) return fromApi;
          }
          return FORMULA_PRICING_CONFIG.linearMldgCostDefault;
        }

        /** Liner moulding width for MLDG WIDTH 2 — API linearSize / linearWidth. */
        function getLinerMouldingWidthForFormula(liner) {
          const l = liner || customizerState.liner || null;
          if (!l) return 0;
          const fromSize = parseFloat(l.linearSize ?? l.linearWidth ?? l.sizeWidth);
          if (isFinite(fromSize) && fromSize > 0) return fromSize;
          const api = l._api;
          if (api) {
            const fromApi = getLinerWidthInchesFromApiRow(api);
            if (isFinite(fromApi) && fromApi > 0) return fromApi;
          }
          const fromFace = parseFloat(l.frameW);
          if (isFinite(fromFace) && fromFace > 0) return fromFace;
          return 0;
        }

        function getFrameMouldingWidthForFormula(frame, axis) {
          if (!frame) return 0;
          const inches = getFrameMoldingInches(frame, axis || 'width');
          if (isFinite(inches) && inches > 0) return inches;
          return getFrameFaceWidthInches(frame) || 0;
        }

        /** Material rate ($/sq in) for Retail Art from substrate selection. */
        function getFormulaRetailArtMaterial(substrate) {
          const cfg = FORMULA_PRICING_CONFIG;
          const rates = cfg.retailArtMaterialRates || {};
          const sub = String(substrate || customizerState.substrate || '').trim();
          const lower = sub.toLowerCase();

          if (/canvas/i.test(lower)) {
            return { rate: rates.canvas, material: 'Canvas' };
          }
          if (/high\s*gloss/i.test(lower) || lower === 'high gloss photo paper') {
            return { rate: rates.highGlossPhotoPaper, material: 'High Gloss Photo Paper' };
          }
          if (/matte\s*paper|mat\s*paper/i.test(lower) || /deckle/i.test(lower)) {
            return { rate: rates.matPaper, material: 'Mat Paper' };
          }
          return {
            rate: cfg.retailArtRateDefault != null ? cfg.retailArtRateDefault : rates.matPaper,
            material: 'Mat Paper'
          };
        }

        function getFormulaRetailArtRate(substrate) {
          const mat = getFormulaRetailArtMaterial(substrate);
          const rate = parseFloat(mat.rate);
          return isFinite(rate) && rate > 0 ? rate : FORMULA_PRICING_CONFIG.retailArtRateDefault;
        }

        function normalizePublisherName(name) {
          return String(name || '')
            .trim()
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/\s+/g, ' ');
        }

        function getProductPublisher(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          return String(root?.dataset?.publisher || '').trim();
        }

        /** Royalty rate from product.metafields.custom.publisher. */
        function getRoyaltyRateForPublisher(publisherRaw) {
          const name = normalizePublisherName(publisherRaw);
          if (!name) return FORMULA_PRICING_CONFIG.royaltyRateDefault;

          if (name.includes('harrison')) return 0.01;

          const artists = [
            'kate mancini',
            'nancy sharpe',
            'richard sherman',
            'martha spak'
          ];
          if (artists.some(function (artist) { return name.includes(artist); })) return 0.02;

          const vendors = [
            'pi creative',
            'wild apple',
            'world art group',
            'old world',
            'third and wall',
            'third wall'
          ];
          if (vendors.some(function (vendor) { return name.includes(vendor); })) return 0.015;

          return FORMULA_PRICING_CONFIG.royaltyRateDefault;
        }

        function getRoyaltyRateForCustomizer(customizerRoot) {
          return getRoyaltyRateForPublisher(getProductPublisher(customizerRoot));
        }

        function formatRoyaltyFormulaLabel(royaltyRate, publisherRaw) {
          const publisher = String(publisherRaw || '').trim();
          const rateLabel = royaltyRate;
          if (!publisher) return 'Width × Height × ' + rateLabel;
          return 'Width × Height × ' + rateLabel + ' (' + publisher + ')';
        }

        /** Glass size for formula united inches / BOM (dynamic MAT1 + MAT2 per side). */
        function computeFormulaGlassSizeInches(imageWidth, imageHeight, opts) {
          const isCanvas = opts && opts.isCanvas;
          const w = parseFloat(imageWidth) || 0;
          const h = parseFloat(imageHeight) || 0;
          if (isCanvas || w <= 0 || h <= 0) {
            return {
              width: roundSizeInches(w),
              height: roundSizeInches(h),
              mat1Inches: 0,
              mat2Inches: 0
            };
          }
          let mat1 = 0;
          let mat2 = 0;
          if (opts && opts.mat1Inches != null) {
            mat1 = parseFloat(opts.mat1Inches) || 0;
            mat2 = parseFloat(opts.mat2Inches) || 0;
          } else {
            const mats = getFormulaMatSelection();
            mat1 = mats.mat1Inches;
            mat2 = mats.mat2Inches;
          }
          const matBorder = (mat1 + mat2) * 2;
          return {
            width: roundSizeInches(w + matBorder),
            height: roundSizeInches(h + matBorder),
            mat1Inches: mat1,
            mat2Inches: mat2
          };
        }

        function formatFormulaPercent(n) {
          const v = Number(n);
          if (!isFinite(v)) return '—';
          return (v * 100).toFixed(1) + '%';
        }

        /** United-inches tier lookup (FIT chart rows). */
        function getFitTierCharge(ui, tiers) {
          const u = parseFloat(ui) || 0;
          if (u <= 0 || !tiers || !tiers.length) return 0;
          for (let i = 0; i < tiers.length; i++) {
            if (u <= tiers[i].maxUi) return tiers[i].charge;
          }
          return tiers[tiers.length - 1].charge;
        }

        /**
         * Auto-Plexi when glass will not fit a 40 × 48 sheet (short > 40 or long > 48),
         * e.g. 41.5 × 41.5. Also when glass united inches reach 84″.
         * Oversized mats (32 × 40) are a separate catalog rule.
         */
        function glassExceedsPlexiSheet(glassW, glassH) {
          const w = parseFloat(glassW) || 0;
          const h = parseFloat(glassH) || 0;
          if (w <= 0 || h <= 0) return false;
          const short = Math.min(w, h);
          const long = Math.max(w, h);
          const maxShort = PRICING_CONFIG.fitPlexiMaxShortInches != null
            ? PRICING_CONFIG.fitPlexiMaxShortInches
            : 40;
          const maxLong = PRICING_CONFIG.fitPlexiMaxLongInches != null
            ? PRICING_CONFIG.fitPlexiMaxLongInches
            : 48;
          return short > maxShort || long > maxLong;
        }

        function shouldUseFitPlexi(glassUi, glassW, glassH) {
          let w = parseFloat(glassW);
          let h = parseFloat(glassH);
          if (!(w > 0 && h > 0)) {
            const glass = computeGlassSizeInches();
            w = parseFloat(glass && glass.width) || 0;
            h = parseFloat(glass && glass.height) || 0;
          }
          if (glassExceedsPlexiSheet(w, h)) return true;
          const u = parseFloat(glassUi) || 0;
          const minUi = PRICING_CONFIG.fitPlexiMinUnitedInches != null
            ? PRICING_CONFIG.fitPlexiMinUnitedInches
            : 84;
          return u >= minUi;
        }

        function normalizeGlazingChoice(value) {
          const v = String(value || '').trim();
          if (/plexi/i.test(v)) return 'Plexi';
          if (/^none$/i.test(v)) return 'None';
          if (/glass/i.test(v)) return 'Glass';
          return '';
        }

        /** Original Glass/Plexi from product metafield (data-original-glazing-spec / data-default-glazing). */
        function readOriginalGlazingMetafield(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root || !root.dataset) return '';
          return normalizeGlazingChoice(root.dataset.originalGlazingSpec) ||
            normalizeGlazingChoice(root.dataset.defaultGlazing);
        }

        /**
         * Lock original Glass/Plexi once on first load so a later Plexi pick cannot
         * rewrite the baseline (which would make the $20 change look like “no change”).
         */
        function freezeOriginalGlazingChoice(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root || !root.dataset) return '';
          if (root.dataset.originalGlazingFrozen === '1') {
            return readOriginalGlazingMetafield(root) ||
              normalizeGlazingChoice(customizerState.defaultGlazing);
          }
          const fromMf = readOriginalGlazingMetafield(root);
          const calc = getCalculatedGlazingChoice(root);
          const orig = fromMf || (calc === 'None' || calc === 'Plexi' ? calc : 'Glass');
          if (orig) {
            root.dataset.originalGlazingSpec = orig;
            if (!customizerState.defaultGlazing) customizerState.defaultGlazing = orig;
          }
          root.dataset.originalGlazingFrozen = '1';
          return orig;
        }

        /**
         * Default dropdown value: original metafield first, then size-based calc.
         * Auto-Plexi (40×48 / 84″ UI) still wins. Canvas = None.
         */
        function resolveDefaultGlazingFromOriginalSpec(customizerRoot) {
          const frozen = freezeOriginalGlazingChoice(customizerRoot);
          const calc = getCalculatedGlazingChoice(customizerRoot);
          if (calc === 'None' || calc === 'Plexi') return calc;
          return frozen || readOriginalGlazingMetafield(customizerRoot) || calc || 'Glass';
        }

        /** Fit-chart default: Canvas = None; Plexi when glass exceeds 40×48 or 84″ UI. */
        function getCalculatedGlazingChoice(customizerRoot) {
          if (isCanvasSubstrate() || !isUnderGlass()) return 'None';
          const glass = computeGlassSizeInches();
          const ui = getUnitedInches(glass.width, glass.height);
          if (shouldUseFitPlexi(ui, glass.width, glass.height)) return 'Plexi';
          return 'Glass';
        }

        function getOriginalGlazingChoice(customizerRoot) {
          const frozen = freezeOriginalGlazingChoice(customizerRoot);
          if (frozen) return frozen;
          const fromMf = readOriginalGlazingMetafield(customizerRoot);
          if (fromMf) return fromMf;
          const stored = normalizeGlazingChoice(customizerState.defaultGlazing);
          if (stored) return stored;
          return getCalculatedGlazingChoice(customizerRoot);
        }

        function getCurrentGlazingChoice(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const select = root && root.querySelector('[id^="customizer-glazing-"]');
          const wrap = root && root.querySelector('[data-glazing-option]');
          const wrapHidden = !!(wrap && wrap.hidden);
          if (select && !select.disabled && !wrapHidden) {
            const fromSelect = normalizeGlazingChoice(select.value);
            if (fromSelect === 'Glass' || fromSelect === 'Plexi') return fromSelect;
          }
          return resolveEffectiveGlazing(root);
        }

        /** Customer pick, except the auto-Plexi size / UI rule always wins. */
        function resolveEffectiveGlazing(customizerRoot) {
          const calc = getCalculatedGlazingChoice(customizerRoot);
          if (calc === 'None') return 'None';
          if (calc === 'Plexi') return 'Plexi';
          const chosen = normalizeGlazingChoice(customizerState.glazing);
          if (chosen === 'Plexi') return 'Plexi';
          return 'Glass';
        }

        /** Charge plexi when the auto-Plexi rule is active, or the customer selected Plexi. */
        function shouldChargePlexi(glassUi, glassW, glassH) {
          if (isCanvasSubstrate() || !isUnderGlass()) return false;
          if (shouldUseFitPlexi(glassUi, glassW, glassH)) return true;
          return normalizeGlazingChoice(customizerState.glazing) === 'Plexi';
        }

        function hasGlazingChangeFromOriginal(customizerRoot) {
          const orig = getOriginalGlazingChoice(customizerRoot);
          const cur = getCurrentGlazingChoice(customizerRoot);
          if (orig === 'None' && cur === 'None') return false;
          if (!orig || !cur) return false;
          return orig !== cur;
        }

        let applyingGlazingRules = false;

        function applyGlazingRules(customizer) {
          if (applyingGlazingRules) return;
          const root = getActiveCustomizerRoot(customizer) ||
            document.querySelector('.customizer');
          if (!root) return;
          applyingGlazingRules = true;
          try {
            const select = root.querySelector('[id^="customizer-glazing-"]');
            const wrap = root.querySelector('[data-glazing-option]');
            const origRow = root.querySelector('[data-original-glazing-row]');
            const calc = getCalculatedGlazingChoice(root);
            const locked = !!customizerState.lockOriginalSubstrateMat;
            const plexiRequired = calc === 'Plexi';

            if (!customizerState.defaultGlazing) {
              customizerState.defaultGlazing = resolveDefaultGlazingFromOriginalSpec(root);
            }
            freezeOriginalGlazingChoice(root);

            if (calc === 'None') {
              customizerState.glazing = 'None';
              if (wrap) wrap.hidden = true;
              if (select) {
                select.disabled = true;
                if (select.value !== 'Glass') select.value = 'Glass';
              }
            } else {
              if (wrap) wrap.hidden = false;
              if (select && !locked && !plexiRequired) {
                const selected = normalizeGlazingChoice(select.value);
                const orig = getOriginalGlazingChoice(root);
                if ((selected === 'Glass' || selected === 'Plexi') && selected !== orig) {
                  customizerState.glazingManual = true;
                  customizerState.glazing = selected;
                }
              }
              if (plexiRequired) {
                customizerState.glazing = 'Plexi';
              } else if (locked) {
                customizerState.glazingManual = false;
                customizerState.glazing = getOriginalGlazingChoice(root) === 'Plexi'
                  ? 'Plexi'
                  : 'Glass';
              } else if (!customizerState.glazingManual) {
                customizerState.glazing = getOriginalGlazingChoice(root) === 'Plexi'
                  ? 'Plexi'
                  : 'Glass';
              } else {
                customizerState.glazing =
                  normalizeGlazingChoice(customizerState.glazing) === 'Plexi'
                    ? 'Plexi'
                    : 'Glass';
              }
              if (select) {
                const next = customizerState.glazing === 'Plexi' ? 'Plexi' : 'Glass';
                if (select.value !== next) select.value = next;
                select.disabled = locked;
                Array.from(select.options).forEach(function (opt) {
                  if (opt.value === 'Glass') opt.disabled = plexiRequired;
                });
              }
            }

            const origLabel = getOriginalGlazingChoice(root);
            root.querySelectorAll('[data-original-glazing]').forEach(function (el) {
              el.textContent = origLabel === 'None' ? 'None' : origLabel;
            });
            if (origRow) origRow.hidden = origLabel === 'None';
          } finally {
            applyingGlazingRules = false;
          }
        }

        function getFoamCoreFitCharge(ui) {
          if (!isDeckleSubstrate() || isCanvasSubstrate()) return 0;
          return getFitTierCharge(ui, PRICING_CONFIG.foamCoreFitTiers || []);
        }

        function getHandFinishFitCharge(ui) {
          if (!isCanvasSubstrate() || !customizerState.acrylicBrushstroke) return 0;
          const tiers = PRICING_CONFIG.handFinishFitTiers || PRICING_CONFIG.acrylicBrushstrokeTiers || [];
          return getFitTierCharge(ui, tiers);
        }

        /**
         * FIT (column CR) + FR/FAB (column CP) rules:
         * - FIT = underglass, OR plexi when glass exceeds 40 × 48 or 84″ UI, OR canvas.
         * - FR/FAB = foam core when deckled (plexi is charged in FIT, not double-counted).
         * - HFM $ OR AB = hand finish (acrylic brushstroke), tiered by glass-size
         *   united inches (glass W + H).
         * - TTL = Mat Charge + FIT + MLDG + MLDX + Liner Ost + FR/FAB + HFM $ OR AB.
         */
        function getFormulaFitCharges(glassW, glassH, unitedInches) {
          const ui = parseFloat(unitedInches) || 0;
          const frFabUi = ui;
          const isCanvas = isCanvasSubstrate();
          const deckled = isDeckleSubstrate();
          const hasHandFinish = isCanvas && !!customizerState.acrylicBrushstroke;
          const usePlexi = !isCanvas && shouldChargePlexi(frFabUi, glassW, glassH);

          let fit = 0;
          let fitFormula = '';
          let underglassCharge = 0;
          let plexiCharge = 0;
          let canvasCharge = 0;

          if (isCanvas) {
            canvasCharge = FORMULA_PRICING_CONFIG.fitCanvasCharge != null
              ? FORMULA_PRICING_CONFIG.fitCanvasCharge
              : PRICING_CONFIG.canvasFitChargeFlat;
            fit = roundFormulaMoney(canvasCharge);
            fitFormula = 'Canvas only — $' + fit.toFixed(2) + ' (glass UI ' + frFabUi + '″)';
          } else if (!isUnderGlass() || frFabUi <= 0) {
            fitFormula = 'N/A';
          } else if (usePlexi) {
            plexiCharge = roundFormulaMoney(getPlexiChargeFromUnitedInches(frFabUi));
            fit = plexiCharge;
            underglassCharge = 0;
            fitFormula = shouldUseFitPlexi(frFabUi, glassW, glassH)
              ? (glassExceedsPlexiSheet(glassW, glassH)
                ? 'Plexi — glass exceeds 40 × 48'
                : 'Plexi — glass united inches ' + frFabUi + '″ tier')
              : 'Plexi — customer selection, glass united inches ' + frFabUi + '″ tier';
          } else {
            underglassCharge = roundFormulaMoney(getGlassCharge(frFabUi));
            fit = underglassCharge;
            fitFormula = (deckled ? 'Deckle + underglass' : 'Underglass') +
              ' — glass united inches ' + frFabUi + '″ tier';
          }

          const foamCoreCharge = deckled && !isCanvas
            ? roundFormulaMoney(getFoamCoreFitCharge(frFabUi))
            : 0;
          const handFinishCharge = hasHandFinish
            ? roundFormulaMoney(getHandFinishFitCharge(frFabUi))
            : 0;
          const frFab = roundFormulaMoney(foamCoreCharge);

          const frFabParts = [];
          if (foamCoreCharge > 0) {
            frFabParts.push('foam core (deckled) ' + formatMoneyUSD(foamCoreCharge) +
              ' (glass united inches ' + frFabUi + '″)');
          }
          const frFabFormula = frFabParts.length
            ? frFabParts.join(' + ')
            : 'N/A';
          const handFinishFormula = handFinishCharge > 0
            ? 'hand finish ' + formatMoneyUSD(handFinishCharge) +
              ' (glass united inches ' + frFabUi + '″)'
            : 'N/A';

          return {
            unitedInches: ui,
            frFabCharge: frFab,
            frFabFormula: frFabFormula,
            handFinishFormula: handFinishFormula,
            underglassCharge: underglassCharge,
            canvasCharge: canvasCharge,
            glassFitCharge: isCanvas ? canvasCharge : underglassCharge,
            plexiCharge: plexiCharge,
            foamCoreCharge: foamCoreCharge,
            handFinishCharge: handFinishCharge,
            fitCharge: fit,
            fitFormula: fitFormula,
            usePlexi: usePlexi,
            deckled: deckled,
            hasHandFinish: hasHandFinish
          };
        }

        /**
         * Moulding / united-inches formula pricing (June 2026 spreadsheet).
         * Full calculation table: glass united inches, moulding BOM, mat charge, FIT, TTL, manufacturing.
         */
        function calculateFormulaPricing(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer') || document;
          ensureArtworkSizeForPricing(root);
          if (typeof syncCustomizerSelectionsFromDOM === 'function') {
            syncCustomizerSelectionsFromDOM(root);
          }
          const width = roundSizeInches(parseFloat(customizerState.width) || 0);
          const height = roundSizeInches(parseFloat(customizerState.height) || 0);
          const areaDims = getFormulaPricingAreaDimensions(root, width, height);
          const formulaWidth = areaDims.width;
          const formulaHeight = areaDims.height;
          const imageWidthFormula = areaDims.formula;
          const imageHeightFormula = areaDims.formula;
          const frame = customizerState.frame || null;
          const cfg = FORMULA_PRICING_CONFIG;
          const isCanvas = isCanvasSubstrate();
          const retailArtMaterial = getFormulaRetailArtMaterial(customizerState.substrate);
          const retailArtRate = getFormulaRetailArtRate(customizerState.substrate);

          const frameMouldingWidth = roundMouldingInches(getFrameMouldingWidthForFormula(frame, 'width'));
          const frameMouldingHeight = roundMouldingInches(getFrameMouldingWidthForFormula(frame, 'height'));
          const frameMouldingPrice = getFrameMouldingPriceForFormula(frame);
          const frameCode = frame ? String(frame.id || '').trim() : '—';
          const liner = customizerState.liner || null;

          const formulaMats = getFormulaMatSelection(root);
          const formulaGlass = computeFormulaGlassSizeInches(formulaWidth, formulaHeight, {
            isCanvas: isCanvas,
            mat1Inches: formulaMats.mat1Inches,
            mat2Inches: formulaMats.mat2Inches
          });
          const mldgWidth = frameMouldingWidth;
          const mldgWidth2 = liner
            ? roundMouldingInches(getLinerMouldingWidthForFormula(liner))
            : 0;
          const computedGlassW = formulaGlass.width;
          const computedGlassH = formulaGlass.height;
          const computedFramedW = (computedGlassW > 0)
            ? roundSizeInches((mldgWidth + mldgWidth2) * 2 + computedGlassW)
            : 0;
          const computedFramedH = (computedGlassH > 0)
            ? roundSizeInches((mldgWidth + mldgWidth2) * 2 + computedGlassH)
            : 0;
          const metafieldDims = applyProductMetafieldFormulaDimensions(
            root,
            computedGlassW,
            computedGlassH,
            computedFramedW,
            computedFramedH,
            mldgWidth,
            mldgWidth2
          );
          const glassWidth = metafieldDims.glassWidth;
          const glassHeight = metafieldDims.glassHeight;
          const framedWidth = metafieldDims.framedWidth;
          const framedHeight = metafieldDims.framedHeight;
          const usingMetafieldDimensions = metafieldDims.fromMetafield;
          const mat1Inches = formulaMats.mat1Inches;
          const mat2Inches = formulaMats.mat2Inches;
          const mat1Label = formulaMats.mat1Label;
          const mat2Label = formulaMats.mat2Label;

          const unitedInches = (glassWidth > 0 && glassHeight > 0)
            ? roundSizeInches(glassWidth + glassHeight)
            : 0;
          const glazingUnitedInches = unitedInches;
          const fitCharges = getFormulaFitCharges(
            glassWidth,
            glassHeight,
            unitedInches
          );

          const matCount = isCanvas ? 0 : getNumMatsSelected();
          const chargeableMatCount = isCanvas ? 0 : getChargeableMatCount(root);
          const matTier = chargeableMatCount > 0 ? getMatTierForGlass(glassWidth, glassHeight) : null;
          const matCost = matTier ? roundFormulaMoney(matTier.cost * chargeableMatCount) : 0;

          const area = formulaWidth * formulaHeight;

          const artWorkCost = roundFormulaMoney(area * cfg.artWorkCostRate);
          const artCost = roundFormulaMoney(artWorkCost * cfg.artCostMultiplier);
          const retailArt = roundFormulaMoney(area * retailArtRate);
          const applyPrintedMatCost = shouldApplyPrintedMatCost(root);
          const wholesaleArt = roundFormulaMoney(retailArt / cfg.wholesaleArtDivisor);
          const printedMatCost = roundFormulaMoney(getPrintedMatFee(root, {
            width: glassWidth,
            height: glassHeight
          }, wholesaleArt));
          const importRate = cfg.importRate;

          const mldgCost = roundFormulaMoney(frameMouldingPrice);
          const mldgVendor = mldgCost;
          const linerMldgCost = liner
            ? roundFormulaMoney(getLinearMouldingPriceForFormula(liner))
            : (isCanvas
              ? roundFormulaMoney(FORMULA_PRICING_CONFIG.linearMldgCostDefault)
              : 0);
          const mldgOst2 = linerMldgCost;
          const mldgVendor2 = liner ? linerMldgCost : 0;

          const bomFtRaw = unitedInches > 0
            ? ((unitedInches * cfg.bomFtUnitedMultiplier) +
                (mldgWidth * cfg.bomFtFrameCornerMultiplier)) / cfg.bomFtDivisor
            : 0;
          const bomFt = roundFormulaMoney(bomFtRaw);

          const fit = fitCharges.fitCharge;
          const frFab = fitCharges.frFabCharge;
          const handFinishCharge = fitCharges.handFinishCharge;
          const mdlg = roundFormulaMoney(
            roundFormulaMoney(bomFt * cfg.bomFtWasteFactor) * mldgVendor * cfg.mdlgMarkup
          );
          const mldx = roundFormulaMoney(mdlg * importRate);
          const linerOst = mldgOst2 > 0
            ? roundFormulaMoney(
                roundFormulaMoney(bomFt * cfg.bomFtWasteFactor) * mldgOst2 * cfg.mdlgMarkup
              )
            : 0;
          const ttl = roundFormulaMoney(matCost + fit + mdlg + mldx + linerOst + frFab + handFinishCharge);
          const x12 = roundFormulaMoney(ttl * cfg.ttlMultiplier);
          const divResult = roundFormulaMoney(x12 / cfg.ttlDivisor);
          const labor = roundFormulaMoney(cfg.labor);
          const publisher = getProductPublisher(root);
          const royaltyRate = getRoyaltyRateForPublisher(publisher);
          const royalty = roundFormulaMoney(area * royaltyRate);
          const finalWholesaleArt = roundFormulaMoney(wholesaleArt + printedMatCost);
          const totalPrice = roundFormulaMoney(divResult + labor + royalty + finalWholesaleArt);

          const framedUnitedInches = (framedWidth > 0 && framedHeight > 0)
            ? roundSizeInches(framedWidth + framedHeight)
            : 0;
          const dimLabel = (formulaWidth > 0 && formulaHeight > 0)
            ? (formulaWidth + '" × ' + formulaHeight + '"')
            : '—';
          const glassLabel = (glassWidth > 0 && glassHeight > 0)
            ? (glassWidth + '" × ' + glassHeight + '"')
            : '—';
          const framedLabel = (framedWidth > 0 && framedHeight > 0)
            ? (Math.round(framedWidth) + '" × ' + Math.round(framedHeight) + '"')
            : '—';
          const glassWidthFormula = usingMetafieldDimensions
            ? 'Product metafield — Current Glass Size'
            : (isCanvas ? 'Image width (canvas)' : 'Image width + (MAT1 + MAT2) × 2');
          const glassHeightFormula = usingMetafieldDimensions
            ? 'Product metafield — Current Glass Size'
            : (isCanvas ? 'Image height (canvas)' : 'Image height + (MAT1 + MAT2) × 2');
          const framedWidthFormula = usingMetafieldDimensions
            ? 'Product metafield — Current Outside Dimensions'
            : '(MLDG WIDTH + MLDG WIDTH 2) × 2 + Glass width';
          const framedHeightFormula = usingMetafieldDimensions
            ? 'Product metafield — Current Outside Dimensions'
            : '(MLDG WIDTH + MLDG WIDTH 2) × 2 + Glass height';
          const frameLabel = frame
            ? (frameCode + (frame.name ? ' — ' + frame.name : ''))
            : 'None selected';

          const matChargeFormula = chargeableMatCount > 0 && matTier
            ? chargeableMatCount + '× ' + matTier.name + ' (glass ' + glassLabel + ')'
            : (applyPrintedMatCost
              ? 'Printed mat — sheet mat charge not applied (printed mat cost separate)'
              : (matCount > 0 ? 'Mat tier (glass ' + glassLabel + ')' : 'No mat selected'));

          const steps = [
            { name: 'Image Width', formula: imageWidthFormula, value: formulaWidth, isDimension: true },
            { name: 'Image Height', formula: imageHeightFormula, value: formulaHeight, isDimension: true },
            { name: 'Art Work Cost', formula: 'Width × Height × ' + cfg.artWorkCostRate, value: artWorkCost },
            { name: 'Art Cost', formula: 'Art Work Cost × ' + cfg.artCostMultiplier, value: artCost },
            {
              name: 'Retail Art',
              formula: 'Width × Height × ' + retailArtRate + ' (' + retailArtMaterial.material + ' rate)',
              value: retailArt
            },
            {
              name: 'Wholesale Art',
              formula: 'Retail Art ÷ ' + cfg.wholesaleArtDivisor,
              value: wholesaleArt
            },
            {
              name: 'Printed Mat Cost',
              formula: applyPrintedMatCost && glassWidth > 0 && glassHeight > 0
                ? 'Glass width × Glass height × ' + PRICING_CONFIG.printedMatSpecificationRate + ' − Wholesale Art'
                : 'Currently not applied',
              value: printedMatCost
            },
            { name: 'MAT 1', formula: 'From customizer (top mat)', value: mat1Label, isText: true },
            {
              name: 'MAT1 Inches',
              formula: isCanvas
                ? 'N/A'
                : (hasPrintedMatSpecificationCharges(root) && !customizerState.topMat
                  ? 'From mat specification metafield'
                  : 'Top mat reveal (SIZE inches)'),
              value: isCanvas ? '—' : (mat1Inches > 0 ? mat1Inches : 'None'),
              isText: isCanvas || mat1Inches <= 0,
              isDimension: !isCanvas && mat1Inches > 0
            },
            { name: 'MAT 2', formula: 'From customizer (bottom mat)', value: mat2Label, isText: true },
            {
              name: 'MAT2 Inches',
              formula: isCanvas ? 'N/A' : 'Bottom mat reveal (double mat)',
              value: isCanvas ? '—' : (mat2Inches > 0 ? mat2Inches : 'None'),
              isText: isCanvas || mat2Inches <= 0,
              isDimension: !isCanvas && mat2Inches > 0
            },
            { name: 'Frame Code', formula: 'From customizer', value: frameCode, isText: true },
            {
              name: 'Glass Width',
              formula: glassWidthFormula,
              value: glassWidth,
              isDimension: true
            },
            {
              name: 'Glass Height',
              formula: glassHeightFormula,
              value: glassHeight,
              isDimension: true
            },
            {
              name: 'United Inches',
              formula: 'Glass width + Glass height',
              value: unitedInches,
              isDimension: true
            },
            {
              name: 'MLDG WIDTH',
              formula: 'Frame moulding width (API)',
              value: mldgWidth,
              isDimension: true
            },
            {
              name: 'MLDG WIDTH 2',
              formula: liner ? 'Liner moulding width (API linearSize)' : 'None',
              value: mldgWidth2,
              isDimension: true
            },
            {
              name: 'Framed Width',
              formula: framedWidthFormula,
              value: framedWidth,
              isDimension: true
            },
            {
              name: 'Framed Height',
              formula: framedHeightFormula,
              value: framedHeight,
              isDimension: true
            },
            {
              name: 'Framed United Inches',
              formula: 'Framed width + Framed height',
              value: framedUnitedInches,
              isDimension: true
            },
            { name: 'MLDG COST', formula: 'Frame moulding price (API)', value: mldgCost },
            {
              name: 'MLDG VENDOR',
              formula: 'MLDG COST',
              value: mldgVendor
            },
            {
              name: 'MLDG OST 2',
              formula: liner
                ? 'Liner moulding price (API basePrice)'
                : (isCanvas ? 'Canvas linear default' : 'None'),
              value: mldgOst2
            },
            {
              name: 'MLDG VENDOR 2',
              formula: liner ? 'MLDG OST 2' : '$0',
              value: mldgVendor2
            },
            { name: 'Import', formula: '$' + importRate.toFixed(2) + ' static', value: importRate },
            {
              name: 'BOM FT',
              formula: '(United Inches × ' + cfg.bomFtUnitedMultiplier + ' + MLDG WIDTH × ' +
                cfg.bomFtFrameCornerMultiplier + ') ÷ ' + cfg.bomFtDivisor,
              value: bomFt
            },
            { name: 'Mat Charge', formula: matChargeFormula, value: matCost }
          ];

          steps.push(
            {
              name: 'FIT',
              formula: fitCharges.fitFormula,
              value: fit,
              isFitTotal: true
            },
            {
              name: 'FR/FAB',
              formula: frFab > 0 ? fitCharges.frFabFormula : 'N/A',
              value: frFab
            },
            {
              name: 'HFM $ OR AB',
              formula: handFinishCharge > 0 ? fitCharges.handFinishFormula : 'N/A',
              value: handFinishCharge
            },
            {
              name: 'MLDG',
              formula: '(BOM FT × ' + cfg.bomFtWasteFactor + ') × MLDG VENDOR × ' + cfg.mdlgMarkup,
              value: mdlg
            },
            { name: 'MLDX', formula: 'MLDG × Import', value: mldx },
            {
              name: 'Liner Ost',
              formula: mldgOst2 > 0
                ? '(BOM FT × ' + cfg.bomFtWasteFactor + ') × MLDG OST 2 × ' + cfg.mdlgMarkup
                : 'None',
              value: linerOst
            },
            {
              name: 'TTL',
              formula: 'Mat Charge + FIT + MLDG + MLDX + Liner Ost + FR/FAB + HFM $ OR AB',
              value: ttl
            },
            { name: '×1.2', formula: 'TTL × ' + cfg.ttlMultiplier, value: x12 },
            { name: '÷ ' + cfg.ttlDivisor, formula: '×1.2 ÷ ' + cfg.ttlDivisor, value: divResult },
            { name: 'Labor', formula: '$' + cfg.labor.toFixed(2) + ' static', value: labor },
            {
              name: 'Royalty',
              formula: formatRoyaltyFormulaLabel(royaltyRate, publisher),
              value: royalty
            },
            {
              name: 'Wholesale Art (Total)',
              formula: 'Wholesale Art + Printed Mat Cost',
              value: finalWholesaleArt
            },
            {
              name: 'Total $',
              formula: '÷' + cfg.ttlDivisor + ' + Labor + Royalty + Wholesale Art (Total)',
              value: totalPrice,
              isTotal: true
            }
          );

          const ttlForTakeOut = matCost + fit + mdlg + mldx + linerOst + frFab + handFinishCharge;
          const takeOutPrecise = (ttlForTakeOut * cfg.ttlMultiplier / cfg.ttlDivisor) + cfg.labor;
          const takeOutArtFinalPrice = roundFormulaMoneyCeilToDollar(takeOutPrecise);
          const mfgPriceAtMargin = roundFormulaMoney(
            takeOutPrecise * cfg.mfgMarginMultiplier
          );
          const activeFinalProductPriceDivisor = isCanvas
            ? cfg.canvasFinalProductPriceDivisor
            : cfg.finalProductPriceDivisor;
          const manufacturingPlus5 = roundFormulaMoney(mfgPriceAtMargin * cfg.mfgPlusMultiplier);
          const totalManufacturing = roundFormulaMoney(
            manufacturingPlus5 + royalty + artWorkCost
          );
          const finalProductPrice = roundFinalStockingDealerPrice(
            totalManufacturing,
            activeFinalProductPriceDivisor
          );
          const mfgPercent = finalProductPrice > 0
            ? mfgPriceAtMargin / finalProductPrice
            : 0;
          const mfgPlusPercent = finalProductPrice > 0
            ? manufacturingPlus5 / finalProductPrice
            : 0;
          const totalMfgPercent = activeFinalProductPriceDivisor;

          steps.push({ isSectionHeader: true, name: 'Manufacturing' });
          steps.push({
            name: 'Take Out Art Final Price',
            formula: 'ROUNDUP(TTL × ' + cfg.ttlMultiplier + ' ÷ ' + cfg.ttlDivisor + ' + Labor)',
            value: takeOutArtFinalPrice,
            isWholeDollar: true
          });
          steps.push({
            name: 'Framer Cost',
            formula: '(TTL × ' + cfg.ttlMultiplier + ' ÷ ' + cfg.ttlDivisor + ' + Labor) × ' +
              cfg.mfgMarginMultiplier + ' — unrounded before ROUNDUP',
            value: mfgPriceAtMargin
          });
          steps.push({
            name: 'Percent',
            formula: 'Framer Cost ÷ Final Price Stocking Dealer',
            value: mfgPercent,
            isPercent: true
          });
          steps.push({
            name: 'Manufacturing Plus 5%',
            formula: 'Framer Cost × ' + cfg.mfgPlusMultiplier,
            value: manufacturingPlus5
          });
          steps.push({
            name: '%',
            formula: 'Manufacturing Plus 5% ÷ Final Price Stocking Dealer',
            value: mfgPlusPercent,
            isPercent: true
          });
          steps.push({
            name: 'Total Manufacturing',
            formula: 'Manufacturing Plus 5% + Royalty + Art Work Cost',
            value: totalManufacturing,
            isMfgTotal: true
          });
          steps.push({
            name: 'Total Manuf %',
            formula: isCanvas ? 'Canvas stocking divisor (28%)' : 'Other substrate stocking divisor (33%)',
            value: totalMfgPercent,
            isPercent: true
          });
          steps.push({
            name: 'Final Price Stocking Dealer',
            formula: 'ROUNDUP(Total Manufacturing ÷ ' + activeFinalProductPriceDivisor + ')',
            value: finalProductPrice,
            isFinalDealer: true
          });

          const addonFees = getFormulaAddonChangeFee(root);
          if (addonFees.sizeChangeCharge > 0) {
            steps.push({
              name: 'Size change charge',
              formula: 'Artwork size changed — $' + PRICING_CONFIG.sizeChangeChargeFlat.toFixed(2) + ' flat',
              value: addonFees.sizeChangeCharge
            });
          }
          if (addonFees.customizationChangeFee > 0) {
            steps.push({
              name: 'Customization change fee',
              formula: addonFees.formula,
              value: addonFees.customizationChangeFee
            });
          }
          const formulaFinalTotal = getFormulaPricingCustomerTotal(
            root,
            finalProductPrice,
            addonFees,
            printedMatCost
          );
          let finalTotalFormula = 'Final Price Stocking Dealer';
          if (printedMatCost !== 0) {
            finalTotalFormula += ' + Printed Mat Cost';
          }
          if (addonFees.sizeChangeCharge > 0) {
            finalTotalFormula += ' + Size change charge';
          } else if (addonFees.customizationChangeFee > 0) {
            finalTotalFormula += ' + Customization change fee';
          } else if (!hasAnyPricingAddOns(root)) {
            finalTotalFormula = 'Product base price';
          }
          steps.push({
            name: 'Final Total',
            formula: finalTotalFormula,
            value: formulaFinalTotal,
            isFormulaGrandTotal: true
          });

          return {
            width: formulaWidth,
            height: formulaHeight,
            artworkWidth: width,
            artworkHeight: height,
            usingMetafieldAreaDimensions: areaDims.fromMetafield,
            dimLabel,
            glassWidth,
            glassHeight,
            glassLabel,
            substrate: String(customizerState.substrate || '').trim(),
            retailArtRate,
            retailArtMaterial: retailArtMaterial.material,
            frame,
            frameCode,
            frameLabel,
            frameMouldingWidth,
            frameMouldingHeight,
            frameMouldingPrice,
            mldgWidth,
            mldgWidth2,
            mldgCost,
            mldgVendor,
            mldgOst2,
            mldgVendor2,
            linerOst,
            bomFt,
            unitedInches,
            glazingUnitedInches,
            glassFitCharge: fitCharges.glassFitCharge,
            plexiCharge: fitCharges.plexiCharge,
            glazing: getGlazingMaterialLabel(root),
            foamCoreFitCharge: fitCharges.foamCoreCharge,
            handFinishCharge: fitCharges.handFinishCharge,
            frFabCharge: fitCharges.frFabCharge,
            fitCharge: fit,
            matCount,
            matTier: matTier ? matTier.name : '',
            matCost,
            printedMatCost,
            framedWidth,
            framedHeight,
            framedLabel,
            framedUnitedInches,
            outsideWidth: framedWidth,
            outsideHeight: framedHeight,
            outsideLabel: framedLabel,
            glassLabel: glassLabel,
            usingMetafieldDimensions: usingMetafieldDimensions,
            steps,
            totalPrice,
            takeOutArtFinalPrice,
            mfgPriceAtMargin,
            mfgPercent,
            mfgPlusPercent,
            manufacturingPlus5,
            totalManufacturing,
            totalMfgPercent,
            finalProductPrice,
            finalProductPriceDivisor: activeFinalProductPriceDivisor,
            sizeChangeCharge: addonFees.total,
            sizeChangeChargeDetail: addonFees.sizeChangeCharge,
            customizationChangeFee: addonFees.customizationChangeFee,
            sizeChangeFormula: addonFees.formula,
            artworkFloatCharge: 0,
            formulaFinalTotal,
            royaltyRate,
            publisher,
            isCanvas
          };
        }

        function parseMoneyToNumber(raw) {
          if (raw == null) return 0;
          const s = String(raw);
          const cleaned = s.replace(/[^0-9.\-]/g, '');
          const n = parseFloat(cleaned);
          return isFinite(n) ? n : 0;
        }

        function formatMoneyUSD(n) {
          const v = Number(n);
          const safe = isFinite(v) ? v : 0;
          return '$' + safe.toFixed(2);
        }

        function getBaseRetailArtPrice(customizerRoot) {
          const root = customizerRoot || document;
          // If we've already captured the initial retail art price, always use it.
          const rootBase = parseFloat(root?.dataset?.baseRetailArtPrice);
          if (isFinite(rootBase) && rootBase >= 0) return rootBase;
          const el = root.querySelector('.customizer-price-value, [data-product-price]');
          if (!el) return 0;
          const elBase = parseFloat(el.getAttribute('data-base-retail-art-price'));
          if (isFinite(elBase) && elBase >= 0) return elBase;
          return parseMoneyToNumber(el.textContent || el.getAttribute('data-product-price') || '');
        }

        function captureBaseRetailArtPrice(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer') || document;
          if (!root || root.dataset.baseRetailArtPrice) return;
          const el = root.querySelector('.customizer-price-value, [data-product-price]');
          if (!el) return;
          const n = parseMoneyToNumber(el.textContent || el.getAttribute('data-product-price') || '');
          if (!isFinite(n) || n < 0) return;
          root.dataset.baseRetailArtPrice = String(n);
          try { el.setAttribute('data-base-retail-art-price', String(n)); } catch (e) {}
        }

        function getActiveCustomizerRoot(customizerRoot) {
          return customizerRoot || document.querySelector('.customizer') || document;
        }

        function isCustomerLoggedIn(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          return !!(root && root.dataset && root.dataset.customerLoggedIn === '1');
        }

        function getCustomerLoginUrl(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const url = root && root.dataset ? String(root.dataset.loginUrl || '').trim() : '';
          return url || '/account/login';
        }

        function customerHasModifiedCustomizer(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          return !!(root && root.dataset && root.dataset.userModifiedSelections === '1');
        }

        /** All “keep original” lock checkboxes checked (manual override). */
        function isKeepingAllOriginalSpecificationLocks() {
          return !!(
            customizerState.lockOriginalSubstrateMat &&
            customizerState.lockOriginalArtworkSize &&
            customizerState.lockOriginalMat &&
            customizerState.lockOriginalFrame &&
            customizerState.lockOriginalLiner
          );
        }

        function matchesOriginalSubstrate(customizer, spec) {
          if (!customizer) return false;
          const root = getActiveCustomizerRoot(customizer);
          const activeSpec = spec || readOriginalSpecFromCustomizer(root);
          if (!activeSpec && !root.dataset.originalSubstrate && !root.dataset.defaultSubstrate) return false;
          const currentSub = String(customizerState.substrate || '').trim();
          const wantSubRaw = (activeSpec && activeSpec.substrate) ||
            root.dataset.originalSubstrate ||
            root.dataset.defaultSubstrate;
          const wantSub = resolveSubstrateForSelect(root, wantSubRaw) ||
            parseDefaultAttr(wantSubRaw);
          if (!wantSub || /^none$/i.test(wantSub)) return true;
          return catalogIdsMatch(currentSub, wantSub) ||
            String(currentSub).toLowerCase() === String(wantSub).toLowerCase();
        }

        function matchesOriginalSubstrateAndTreatment(customizer, spec) {
          if (!spec || !customizer) return false;
          if (!matchesOriginalSubstrate(customizer, spec)) return false;

          const treatment = String(customizerState.treatment || '').trim();
          let wantTreatment = '';
          if (spec.bottomMatId && !/^none$/i.test(spec.bottomMatId)) wantTreatment = 'Double Mat';
          else if (spec.treatment && !/^none$/i.test(spec.treatment)) {
            wantTreatment = String(spec.treatment).trim();
          }
          const treatmentOk = treatmentsMatchOriginal(treatment, wantTreatment);
          return treatmentOk;
        }

        function treatmentsMatchOriginal(current, want) {
          if (!want) return true;
          const cur = String(current || '').trim();
          if (catalogIdsMatch(cur, want)) return true;
          if (want === 'Double Mat' && cur === 'Double Mat') return true;
          if (isNoMatTreatment(want) && isNoMatTreatment(cur)) return true;
          if (cur.toLowerCase() === String(want).trim().toLowerCase()) return true;
          const inferred = inferTreatmentFromMatSpecification(want);
          if (inferred && cur === inferred) return true;
          return false;
        }

        function matchesOriginalArtworkDimensions(spec, customizerRoot) {
          if (!spec) return true;
          const root = getActiveCustomizerRoot(customizerRoot);
          const dim = resolveProductArtworkDimensions(root) || parseMetafieldDimensions(spec.dimensions);
          if (!dim) return true;
          const curW = parseFloat(customizerState.width);
          const curH = parseFloat(customizerState.height);
          if (!isFinite(curW) || curW <= 0 || !isFinite(curH) || curH <= 0) return false;
          return roundSizeInches(curW) === roundSizeInches(dim.width) &&
            roundSizeInches(curH) === roundSizeInches(dim.height);
        }

        function matchesOriginalMatSizes(spec) {
          if (!spec) return true;
          if (spec.topMatId && customizerState.topMat) {
            const topSize = snapMatSizeInches(customizerState.topMatSizeInches, 'topMat');
            if (topSize !== spec.topMatSize) return false;
          }
          if (spec.bottomMatId && customizerState.bottomMat) {
            const bottomSize = snapMatSizeInches(customizerState.bottomMatSizeInches, 'bottomMat');
            if (bottomSize !== spec.bottomMatSize) return false;
          }
          return true;
        }

        /** Snapshot mat reveal sizes when original spec is active — used for fee deltas only. */
        function captureOriginalSpecMatBaselines(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          if (!root) return;
          syncAllMatSizesFromUI();
          if (customizerState.topMat) {
            root.dataset.baselineTopMatSize = String(
              snapMatSizeInches(customizerState.topMatSizeInches, 'topMat')
            );
          } else {
            delete root.dataset.baselineTopMatSize;
          }
          if (customizerState.bottomMat) {
            root.dataset.baselineBottomMatSize = String(
              snapMatSizeInches(customizerState.bottomMatSizeInches, 'bottomMat')
            );
          } else {
            delete root.dataset.baselineBottomMatSize;
          }
        }

        /** True only when the customer changed mat reveal size from the captured baseline. */
        function hasMatSizeCustomizationDelta(root) {
          const activeRoot = getActiveCustomizerRoot(root);
          if (customizerState.lockOriginalMat) return false;
          const baseTop = parseFloat(activeRoot.dataset.baselineTopMatSize);
          const baseBottom = parseFloat(activeRoot.dataset.baselineBottomMatSize);
          if (isFinite(baseTop) || isFinite(baseBottom)) {
            let changed = false;
            if (customizerState.topMat && isFinite(baseTop) && baseTop > 0) {
              const curTop = snapMatSizeInches(customizerState.topMatSizeInches, 'topMat');
              if (curTop !== baseTop) changed = true;
            }
            if (customizerState.bottomMat && isFinite(baseBottom) && baseBottom > 0) {
              const curBottom = snapMatSizeInches(customizerState.bottomMatSizeInches, 'bottomMat');
              if (curBottom !== baseBottom) changed = true;
            }
            return changed;
          }
          const spec = readOriginalSpecFromCustomizer(activeRoot);
          if (!spec || !(spec.topMatId || spec.bottomMatId)) return false;
          if (matchesOriginalMatSizes(spec)) return false;
          if (!isOriginalSpecPieceAppliedOrLocked(spec, 'topMat')) return false;
          if (spec.bottomMatId && !isOriginalSpecPieceAppliedOrLocked(spec, 'bottomMat')) return false;
          return true;
        }

        /** One mat bucket: top mat, bottom mat, or mat-size change from original spec. */
        function hasMatCustomizationDelta(spec, root) {
          if (customizerState.lockOriginalMat) return false;
          if (!spec) {
            return !!(customizerState.topMat || customizerState.bottomMat);
          }
          // Detect add / remove / swap — including when original had no mat id.
          if (isCatalogPieceChangedFromOriginal(spec, 'topMat', root)) return true;
          if (isCatalogPieceChangedFromOriginal(spec, 'bottomMat', root)) return true;
          if (hasMatSizeCustomizationDelta(root)) return true;
          return false;
        }

        /** True when canvas acrylic brushstroke differs from the product metafield default. */
        function hasAcrylicBrushstrokeChange(customizerRoot) {
          if (!isCanvasSubstrate()) return false;
          const root = getActiveCustomizerRoot(customizerRoot);
          const defaultOn = parseDatasetBoolean(
            root && root.dataset ? root.dataset.defaultAcrylicBrushstroke : false
          );
          return !!customizerState.acrylicBrushstroke !== !!defaultOn;
        }

        function hasOtherCustomizerChangesFromOriginal() {
          if (hasAcrylicBrushstrokeChange()) return true;
          // Side Mark / Additional Notes are free-text only — never a pricing change.
          if (customizerState.artworkColor) return true;
          if (isMonogramTypographyProduct() && customizerState.customTextEnabled &&
              String(customizerState.customerNotes || '').trim()) {
            return true;
          }
          if (String(customizerState.colorInstruction || '').trim()) return true;
          // Step "Notes / Special Instructions" are free-text only — never a pricing change.
          return false;
        }

        /** Free-text side mark / notes that should allow Add to Order without changing price. */
        function hasSideMarkOrAdditionalNotes() {
          return !!customizerState.sideMark;
        }

        /**
         * No customizer/art add-on charges when the customer keeps the product’s original
         * specification (auto-filled or restored). Charges apply only after they change
         * substrate, size, mats, frame, liner, or other options.
         */
        function isKeepingOriginalSpecifications(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!productHasOriginalSpec(root)) return false;
          syncSelectionsForOriginalSpecCheck(root);
          if (hasGlazingChangeFromOriginal(root)) return false;
          if (isKeepingAllOriginalSpecificationLocks()) return true;

          const spec = readOriginalSpecFromCustomizer(root);
          if (!spec) return false;

          if (!customizerState.lockOriginalSubstrateMat &&
              !matchesOriginalSubstrateAndTreatment(root, spec)) return false;
          if (!customizerState.lockOriginalArtworkSize &&
              !matchesOriginalArtworkDimensions(spec, root)) return false;
          if (!customizerState.lockOriginalMat &&
              !matchesOriginalMatSizes(spec)) return false;
          // Mat / frame / liner catalog changes (including adding a mat when original had none).
          if (!customizerState.lockOriginalMat && hasMatCustomizationDelta(spec, root)) return false;
          if (!customizerState.lockOriginalFrame &&
              isCatalogPieceChangedFromOriginal(spec, 'frame', root)) return false;
          if (!customizerState.lockOriginalLiner &&
              isCatalogPieceChangedFromOriginal(spec, 'liner', root)) return false;
          if (hasOtherCustomizerChangesFromOriginal()) return false;

          // While auto-fill runs, keep Shopify base price (avoid flash of add-on math).
          if (root.dataset.applyingOriginalSpec === '1') {
            return true;
          }

          // Only when live selections still match the product spec (not merely “was applied once”).
          return isOriginalSpecFullyApplied(root);
        }

        /** Recalculate glass/outside display, oversize rules, header price, and summary after a change. */
        function refreshCustomizerPricingAndDimensions(customizer, opts) {
          const root = customizer || document.querySelector('.customizer');
          if (!root) return;
          const skipSummary = !!(opts && opts.skipSummary);
          reconcileOriginalSpecAfterUserChange(root);
          captureBaseRetailArtPrice(root);
          syncAllMatSizesFromUI();
          updateCurrentSizesDisplay(root);
          enforceOversizeMatRules(root);
          if (!isKeepingOriginalSpecifications(root)) {
            restoreOriginalSpecMatsAfterSizeChange(root);
          }
          document.querySelectorAll('.customizer').forEach(function (c) {
            refreshStepPillStates(c);
            updateCustomizerMainPrice(c);
            if (!skipSummary && !isApplyingOriginalSpec(c)) {
              updateSummary(c);
            }
          });
          if (typeof window.__fsRedrawWithMats === 'function') {
            window.__fsRedrawWithMats();
          }
        }

        /** Product-configured glass / outside labels (not recalculated from frame+mats). */
        function getOriginalProductSizeDisplay(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          let glassText = String(root.dataset.originalGlassDisplay || '').trim();
          let overallText = String(root.dataset.originalOutsideDisplay || '').trim();
          const dimLabel = String(root.dataset.originalDimensions || '').trim();
          if ((!glassText || !overallText) && dimLabel && !/^none$/i.test(dimLabel)) {
            if (!glassText) glassText = dimLabel;
            if (!overallText) overallText = dimLabel;
          }
          return { glassText: glassText, overallText: overallText };
        }

        function captureOriginalProductSizeDisplay(customizer) {
          const root = customizer || document.querySelector('.customizer');
          if (!root || !productHasOriginalSpec(root)) return;
          if (root.dataset.originalSizeDisplayCaptured === '1') return;
          const display = getOriginalProductSizeDisplay(root);
          if (display.glassText) root.dataset.originalGlassDisplay = display.glassText;
          if (display.overallText) root.dataset.originalOutsideDisplay = display.overallText;
          root.dataset.originalSizeDisplayCaptured = '1';
          applyGlazingRules(root);
          if (!customizerState.defaultGlazing) {
            customizerState.defaultGlazing = resolveDefaultGlazingFromOriginalSpec(root);
          }
        }

        /** Parsed glass / outside / artwork sizes from product metafields. */
        function getProductMetafieldDimensionSets(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          captureOriginalProductSizeDisplay(root);
          const dimLabel = String(root.dataset.originalDimensions || '').trim();
          const glassRaw = String(root.dataset.originalGlassDisplay || '').trim();
          const outsideRaw = String(root.dataset.originalOutsideDisplay || '').trim();
          let glass = parseMetafieldDimensions(glassRaw);
          let outside = parseMetafieldDimensions(outsideRaw);
          const artwork = resolveProductArtworkDimensions(root) ||
            parseMetafieldDimensions(dimLabel);
          if (!glass && artwork && !/^none$/i.test(dimLabel)) glass = artwork;
          if (!outside && artwork && !/^none$/i.test(dimLabel)) outside = artwork;
          if (!outside && glass) outside = glass;
          return { glass: glass, outside: outside, artwork: artwork };
        }

        /** Keep product metafield glass/outside sizes until the customer changes artwork size. */
        function shouldUseProductMetafieldFormulaDimensions(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          // image_size drives artwork defaults — derive glass/outside from artwork + frame/mats.
          if (getProductImageSizeDimensions(root)) return false;
          if (!productHasOriginalSpec(root)) return false;
          if (isKeepingOriginalSpecifications(root)) return true;
          if (isKeepingAllOriginalSpecificationLocks()) return true;
          const dimSets = getProductMetafieldDimensionSets(root);
          if (!dimSets.outside && !dimSets.glass) return false;
          return !hasArtworkDimensionCustomization(root);
        }

        function getFormulaPricingAreaDimensions(root, stateWidth, stateHeight) {
          const w = roundSizeInches(parseFloat(stateWidth) || 0);
          const h = roundSizeInches(parseFloat(stateHeight) || 0);
          return {
            width: w,
            height: h,
            fromMetafield: !!getProductImageSizeDimensions(root),
            formula: getArtworkSizeFormulaLabel(root)
          };
        }

        function applyProductMetafieldFormulaDimensions(root, glassWidth, glassHeight, framedWidth, framedHeight, mldgWidth, mldgWidth2) {
          if (!shouldUseProductMetafieldFormulaDimensions(root)) {
            return {
              glassWidth: glassWidth,
              glassHeight: glassHeight,
              framedWidth: framedWidth,
              framedHeight: framedHeight,
              fromMetafield: false
            };
          }
          const dimSets = getProductMetafieldDimensionSets(root);
          let gw = glassWidth;
          let gh = glassHeight;
          let fw = framedWidth;
          let fh = framedHeight;
          const border = (mldgWidth + mldgWidth2) * 2;

          if (dimSets.outside) {
            fw = roundSizeInches(dimSets.outside.width);
            fh = roundSizeInches(dimSets.outside.height);
          }
          if (dimSets.glass) {
            gw = roundSizeInches(dimSets.glass.width);
            gh = roundSizeInches(dimSets.glass.height);
          } else if (dimSets.outside && fw > 0 && fh > 0 && border > 0) {
            gw = roundSizeInches(Math.max(fw - border, 0));
            gh = roundSizeInches(Math.max(fh - border, 0));
          }
          if (!dimSets.outside && gw > 0 && gh > 0 && border > 0) {
            fw = roundSizeInches(gw + border);
            fh = roundSizeInches(gh + border);
          }
          return {
            glassWidth: gw,
            glassHeight: gh,
            framedWidth: fw,
            framedHeight: fh,
            fromMetafield: !!(dimSets.outside || dimSets.glass)
          };
        }

        function hasStep1PricingAddOns(customizerRoot) {
          if (isKeepingOriginalSpecifications(customizerRoot)) return false;
          return hasArtworkSizeChangedFromDefault(customizerRoot) ||
            hasSubstrateChangeFromOriginal(customizerRoot) ||
            hasGlazingChangeFromOriginal(customizerRoot) ||
            hasAcrylicBrushstrokeChange(customizerRoot);
        }

        /** Any customization delta that should show live pricing (from step 1 onward). */
        function hasAnyPricingAddOns(customizerRoot) {
          if (isKeepingOriginalSpecifications(customizerRoot)) return false;
          if (hasStep1PricingAddOns(customizerRoot)) return true;
          const root = getActiveCustomizerRoot(customizerRoot);
          const flags = getOriginalSpecChangeFlags(root);
          const spec = readOriginalSpecFromCustomizer(root);
          return !!(
            hasMatCustomizationDelta(spec, root) ||
            flags.frameChanged ||
            flags.linerChanged
          );
        }

        /** Glass / fit / plexi / canvas fit follow united-inches (size), frame, or glazing pick. */
        function shouldApplyGlazingAndFitCharges(flags) {
          return !!(flags && (flags.sizeChanged || flags.frameChanged || flags.glazingChanged));
        }

        /** Count frame / mat / liner / substrate / glass-plexi / acrylic-brushstroke changes (size excluded). */
        function countNonSizeCustomizationChanges(root, step) {
          const flags = getOriginalSpecChangeFlags(root);
          const spec = readOriginalSpecFromCustomizer(root);
          let count = 0;

          if (step == null || step >= 1) {
            if (hasSubstrateChangeFromOriginal(root)) count++;
            if (hasGlazingChangeFromOriginal(root)) count++;
            if (hasAcrylicBrushstrokeChange(root)) count++;
          }
          if (!flags.hasSpec) {
            if (step == null || step >= 2) {
              if (customizerState.topMat || customizerState.bottomMat) count++;
            }
            if (step == null || step >= 3) {
              if (customizerState.frame) count++;
              if (customizerState.liner) count++;
            }
            return count;
          }
          if (step == null || step >= 2) {
            if (hasMatCustomizationDelta(spec, root)) count++;
          }
          if (step == null || step >= 3) {
            if (flags.frameChanged) count++;
            if (flags.linerChanged) count++;
          }
          return count;
        }

        function feeFromCustomizationChangeCount(changeCount) {
          if (changeCount <= 0) return 0;
          if (changeCount === 1) return PRICING_CONFIG.customizationFeeSingle;
          return PRICING_CONFIG.customizationFeeMax;
        }

        /** $20 or $40 for frame/mat/liner/substrate/glass-plexi/brushstroke changes; $0 when size changed (size fee applies instead). */
        function getCustomizationChangeFee(root, step) {
          const activeRoot = getActiveCustomizerRoot(root);
          const glazingChanged = hasGlazingChangeFromOriginal(activeRoot);
          // During initial load, original-spec pieces (frame/mats/liner) can apply asynchronously.
          // Avoid showing a customization change fee until the original spec is fully applied,
          // unless the user has actually modified selections or Glass/Plexi.
          if (productHasOriginalSpec(activeRoot) &&
              activeRoot &&
              activeRoot.dataset &&
              activeRoot.dataset.userModifiedSelections !== '1' &&
              activeRoot.dataset.originalSpecFullyApplied !== '1' &&
              !glazingChanged) {
            return 0;
          }
          if (!glazingChanged && isKeepingOriginalSpecifications(activeRoot)) return 0;
          if (hasArtworkDimensionCustomization(activeRoot)) return 0;
          return feeFromCustomizationChangeCount(countNonSizeCustomizationChanges(activeRoot, step));
        }

        function isCatalogPieceChangedFromOriginal(spec, piece, root) {
          if (!spec) return false;
          const wantId = piece === 'frame' ? spec.frameId
            : piece === 'topMat' ? spec.topMatId
              : piece === 'bottomMat' ? spec.bottomMatId
                : piece === 'liner' ? spec.linerId
                  : '';
          const currentId = getCurrentCatalogIdForSpecPiece(root, piece);

          // Original had none — selecting anything is a customer change.
          if (!wantId) return !!currentId;

          // Original had a piece but nothing is selected yet.
          // During async original-spec apply this is normal (not a change).
          // After the user has modified selections, a clear/removal counts.
          if (!currentId) {
            if (isApplyingOriginalSpec(root)) return false;
            if (root && root.dataset && root.dataset.userModifiedSelections === '1') return true;
            return false;
          }

          return !isOriginalSpecPieceAppliedOrLocked(spec, piece);
        }

        function getOriginalSpecChangeFlags(root) {
          syncSelectionsForOriginalSpecCheck(root);
          const spec = readOriginalSpecFromCustomizer(root);
          const hasSpec = productHasOriginalSpec(root);
          if (!hasSpec) {
            return {
              hasSpec: false,
              sizeChanged: hasArtworkSizeChangedFromDefault(root),
              topMatChanged: true,
              bottomMatChanged: true,
              frameChanged: true,
              linerChanged: true,
              matSizesChanged: false,
              glazingChanged: hasGlazingChangeFromOriginal(root)
            };
          }
          return {
            hasSpec: true,
            sizeChanged: hasArtworkSizeChangedFromDefault(root),
            topMatChanged: isCatalogPieceChangedFromOriginal(spec, 'topMat', root),
            bottomMatChanged: isCatalogPieceChangedFromOriginal(spec, 'bottomMat', root),
            frameChanged: isCatalogPieceChangedFromOriginal(spec, 'frame', root),
            linerChanged: isCatalogPieceChangedFromOriginal(spec, 'liner', root),
            matSizesChanged: hasMatSizeCustomizationDelta(root),
            glazingChanged: hasGlazingChangeFromOriginal(root)
          };
        }

        /** Clean display label for an original-spec piece raw dataset string. */
        function formatOriginalSpecPieceLabel(raw) {
          const s = String(raw || '').trim();
          if (!s || /^none$/i.test(s)) return 'None';
          const id = parseSpecCatalogId(s);
          const name = parseSpecMatName(s);
          if (id && name) return id + ' — ' + name;
          return id || name || s;
        }

        /** Crop details for order properties / summary: dimensions + reference (pre-crop) image. */
        function getCustomizerCropInfo(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          const info = { hasCrop: false, sizeText: '', pxText: '', referenceUrl: '' };
          if (!root || !hasCustomizerCrop(root)) return info;
          info.hasCrop = true;

          const w = roundSizeInches(customizerState.width);
          const h = roundSizeInches(customizerState.height);
          if (w > 0 && h > 0) info.sizeText = formatSizeInches(w, h).replace(' × ', ' x ');

          let cropData = (customizerCropState.get(root) || {}).cropData || null;
          const images = getCustomizerImages(root);
          if (!cropData) {
            for (let i = 0; i < images.length; i++) {
              const raw = images[i] && images[i].dataset ? images[i].dataset.cropData : '';
              if (raw) { try { cropData = JSON.parse(raw); } catch (e) { /* ignore */ } break; }
            }
          }
          if (cropData && cropData.width && cropData.height) {
            info.pxText = Math.round(cropData.width) + ' × ' + Math.round(cropData.height) + ' px';
          }

          let ref = '';
          for (let i = 0; i < images.length; i++) {
            const d = images[i] && images[i].dataset ? images[i].dataset.originalSrc : '';
            if (d) { ref = d; break; }
          }
          if (!ref) ref = window.__fsProductImg || '';
          info.referenceUrl = String(ref || '').trim();
          return info;
        }

        function hasSubstrateChangeFromOriginal(root) {
          const activeRoot = getActiveCustomizerRoot(root);
          if (isKeepingOriginalSpecifications(activeRoot)) return false;
          if (customizerState.lockOriginalSubstrateMat) return false;
          const spec = readOriginalSpecFromCustomizer(activeRoot);
          if (productHasOriginalSpec(activeRoot)) {
            return !matchesOriginalSubstrate(activeRoot, spec);
          }
          const defaultSub = parseDefaultAttr(activeRoot.dataset.defaultSubstrate);
          if (!defaultSub) return false;
          const current = String(customizerState.substrate || '').trim();
          const want = resolveSubstrateForSelect(activeRoot, defaultSub) || defaultSub;
          if (!current || !want) return false;
          return !catalogIdsMatch(current, want) &&
            String(current).toLowerCase() !== String(want).toLowerCase();
        }

        /** @deprecated Use hasSubstrateChangeFromOriginal — kept for any external callers. */
        function hasDeckleChargeFromChange(root) {
          return hasSubstrateChangeFromOriginal(root);
        }

        /** Charge only for selections that differ from the product original specification. */
        function applyOriginalSpecPricingDeltas(raw, root, ui) {
          const base = parseFloat(raw.baseRetailArt) || 0;

          if (shouldUseFullFormulaPricing(root)) {
            return Object.assign({}, raw, {
              sizeChangeCharge: 0,
              customizationChangeFee: 0,
              deckleFloatCharge: 0,
              matCost: 0,
              matCount: 0,
              matTier: '',
              frameCost: 0,
              glassCharge: 0,
              plexiCharge: 0,
              foamCoreFitCharge: 0,
              acrylicBrushstrokeCharge: 0,
              artworkFloatCharge: 0,
              linerCost: 0,
              canvasFitCharge: 0,
              printedMatFee: 0,
              retailTotal: roundSizeInches(base)
            });
          }

          if (!hasAnyPricingAddOns(root)) {
            return Object.assign({}, raw, { retailTotal: roundSizeInches(base) });
          }

          const addon = getFormulaAddonChangeFee(root);
          return Object.assign({}, raw, {
            sizeChangeCharge: roundSizeInches(addon.sizeChangeCharge),
            customizationChangeFee: roundSizeInches(addon.customizationChangeFee),
            deckleFloatCharge: 0,
            matCost: 0,
            matCount: 0,
            matTier: '',
            frameCost: 0,
            glassCharge: 0,
            plexiCharge: 0,
            foamCoreFitCharge: 0,
            acrylicBrushstrokeCharge: 0,
            artworkFloatCharge: 0,
            linerCost: 0,
            canvasFitCharge: 0,
            printedMatFee: 0,
            retailTotal: roundSizeInches(base + addon.total)
          });
        }

        function getActiveFrameTab(customizer) {
          const panel = customizer && customizer.querySelector('.step-panel.active[data-step="3"]');
          if (!panel) return 'frame';
          const tab = panel.querySelector('.cz-tab.cz-tab--active')?.dataset.czTab;
          return tab === 'liner' ? 'liner' : 'frame';
        }

        /**
         * All applicable charges are included on every step (no progressive hiding).
         * Customization fee alone may scope by which changes are visible at the current step.
         */
        function applyStepScopedPricing(pricing, root, ui, step, opts) {
          const summaryStep = getSummaryStep();
          if (isSummaryStepNum(step) || isArtworkStepNum(step) || step > summaryStep) {
            return pricing;
          }

          const base = parseFloat(pricing.baseRetailArt) || 0;
          const sizeChange = parseFloat(pricing.sizeChangeCharge) || 0;
          const customizationChangeFee = getCustomizationChangeFee(root, step);
          const matCost = parseFloat(pricing.matCost) || 0;
          const frameCost = parseFloat(pricing.frameCost) || 0;
          const linerCost = parseFloat(pricing.linerCost) || 0;
          const glassCharge = parseFloat(pricing.glassCharge) || 0;
          const plexiCharge = parseFloat(pricing.plexiCharge) || 0;
          const foamCoreFitCharge = parseFloat(pricing.foamCoreFitCharge) || 0;
          const canvasFitCharge = parseFloat(pricing.canvasFitCharge) || 0;
          const acrylicCharge = parseFloat(pricing.acrylicBrushstrokeCharge) || 0;
          const artworkFloat = parseFloat(pricing.artworkFloatCharge) || 0;
          const printedMatFee = parseFloat(pricing.printedMatFee) || 0;

          const retailTotal = base + sizeChange + customizationChangeFee + matCost + frameCost + linerCost +
            glassCharge + plexiCharge + foamCoreFitCharge + canvasFitCharge +
            acrylicCharge + artworkFloat + printedMatFee;

          return Object.assign({}, pricing, {
            customizationChangeFee: roundSizeInches(customizationChangeFee),
            retailTotal: roundSizeInches(retailTotal)
          });
        }

        /** Frame / mat / liner selections that add cost beyond base art price. */
        function hasCustomizationPricingSelections(customizerRoot) {
          if (isKeepingOriginalSpecifications(customizerRoot)) return false;
          return !!(
            customizerState.frame ||
            customizerState.topMat ||
            customizerState.bottomMat ||
            customizerState.liner
          );
        }

        /** Acrylic brushstroke is a canvas-only add-on (not under-glass / framed paper). */
        function syncCanvasOnlyPricingAddons() {
          if (!isCanvasSubstrate()) {
            customizerState.acrylicBrushstroke = false;
          }
        }

        /**
         * Whether the header shows the running configured total (vs base-only).
         * Any change from step 1 onward shows all applicable charges.
         */
        function shouldShowLivePricing(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer');
          if (!root) return false;
          if (!isCustomerLoggedIn(root)) return false;
          if (isKeepingOriginalSpecifications(root)) return false;
          return hasAnyPricingAddOns(root);
        }

        /** Show full charge breakdown whenever live pricing is active. */
        function shouldShowHeaderBreakdown(root, panel) {
          return shouldShowLivePricing(root);
        }

        /** Always show every applicable charge line on the active step. */
        function getPanelPricingBreakdownMode(panel) {
          if (!panel) return 'none';
          const root = panel.closest('.customizer') || document.querySelector('.customizer');
          if (isKeepingOriginalSpecifications(root)) return 'none';
          return hasAnyPricingAddOns(root) ? 'full' : 'none';
        }

        function buildHeaderPricingBreakdownHtml(pricing, mode) {
          if (!pricing || !mode || mode === 'none') return '';
          const rows = [];
          const push = function (label, amount, extraClass) {
            const n = parseFloat(amount) || 0;
            if (n <= 0) return;
            rows.push(
              '<div class="hpb-row' + (extraClass ? ' ' + extraClass : '') + '">' +
              '<span>' + label + '</span><span>' + formatMoneyUSD(n) + '</span></div>'
            );
          };

          const sizeChangeFee = parseFloat(pricing.sizeChangeCharge) || 0;
          const customizationFee = parseFloat(pricing.customizationChangeFee) || 0;
          if (sizeChangeFee > 0) {
            push('Size change charge', sizeChangeFee, 'hpb-addon');
          }
          if (customizationFee > 0) {
            push('Customization change fee', customizationFee, 'hpb-addon');
          }
          if (pricing.matCost > 0 && pricing.matCount > 0) {
            const chargeCount = pricing.matChargeCount != null ? pricing.matChargeCount : pricing.matCount;
            push(
              'Mat cost (' + chargeCount + '× ' + (pricing.matTier || 'Sheet') + ')',
              pricing.matCost,
              'hpb-addon'
            );
          }
          push('Frame cost', pricing.frameCost, 'hpb-addon');
          if (isCanvasSubstrate()) {
            push('Canvas fit', pricing.canvasFitCharge, 'hpb-addon');
            push('Hand finish (brushstroke)', pricing.acrylicBrushstrokeCharge, 'hpb-addon');
          } else {
            push('Glass / fit charge', pricing.glassCharge, 'hpb-addon');
            push('Plexi charge', pricing.plexiCharge, 'hpb-addon');
            push('Foam core (deckled)', pricing.foamCoreFitCharge, 'hpb-addon');
            push('Artwork float charge', pricing.artworkFloatCharge, 'hpb-addon');
          }
          push('Liner cost', pricing.linerCost, 'hpb-addon');
          push('Printed mat fee', pricing.printedMatFee, 'hpb-addon');

          if (!rows.length) return '';
          return (
            '<div class="header-price-breakdown" data-header-pricing>' +
            rows.join('') +
            '</div>'
          );
        }

        function renderHeaderPriceBreakdownForWrap(wrap, pricing, visible, mode) {
          if (!wrap) return;
          const existing = wrap.querySelector('[data-header-pricing]');
          if (!visible) {
            if (existing) existing.remove();
            return;
          }
          const html = buildHeaderPricingBreakdownHtml(pricing, mode);
          if (!html) {
            if (existing) existing.remove();
            return;
          }
          if (existing) {
            existing.outerHTML = html;
          } else {
            wrap.insertAdjacentHTML('beforeend', html);
          }
        }

        /** Full moulding formula + table whenever customization changes price inputs. */
        function shouldUseFullFormulaPricing(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!isCustomerLoggedIn(root)) return false;
          if (isKeepingOriginalSpecifications(root)) return false;
          syncArtworkSizeFromInputs(root);
          const w = parseFloat(customizerState.width);
          const h = parseFloat(customizerState.height);
          if (!isFinite(w) || w <= 0 || !isFinite(h) || h <= 0) return false;
          // Include mat / frame / liner / size changes so glass, plexi, and mat-tier
          // charges update live (not only when artwork width/height is edited).
          return hasAnyPricingAddOns(root);
        }

        /** Base product price + flat $20 / $40 customization fee (frame, mat, liner, substrate — not size). */
        function getCustomizerFlatFeeAddonTotal(root) {
          if (!hasAnyPricingAddOns(root) || shouldUseFullFormulaPricing(root)) return 0;
          return getFormulaAddonChangeFee(root).total;
        }

        function getCustomizerFlatFeeRetailTotal(root) {
          captureBaseRetailArtPrice(root);
          const base = getBaseRetailArtPrice(root);
          const addon = getCustomizerFlatFeeAddonTotal(root);
          return roundSizeInches(base + addon);
        }

        /** Live formula dealer price whenever the formula table is active. */
        function shouldShowFormulaDealerHeaderPrice(customizerRoot) {
          return shouldUseFullFormulaPricing(customizerRoot);
        }

        function getCustomizerFormulaPricing(customizer, cachedFormulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          if (!shouldUseFullFormulaPricing(root)) return null;
          return cachedFormulaPricing || calculateFormulaPricing(root);
        }

        /** Formula pricing used by the review table and customer-facing totals. */
        function getCustomizerReviewFormulaPricing(customizer, cachedFormulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          if (!shouldShowFormulaPricingReview(root)) return null;
          return cachedFormulaPricing || calculateFormulaPricing(root);
        }

        function getCustomizerFormulaFinalTotal(customizer, cachedFormulaPricing) {
          const formulaPricing = getCustomizerReviewFormulaPricing(customizer, cachedFormulaPricing);
          const total = formulaPricing && formulaPricing.formulaFinalTotal;
          return isFinite(total) && total > 0 ? total : null;
        }

        function getCustomizerFormulaDealerPrice(customizer, cachedFormulaPricing) {
          const formulaPricing = getCustomizerFormulaPricing(customizer, cachedFormulaPricing);
          const dealer = formulaPricing && formulaPricing.finalProductPrice;
          return isFinite(dealer) && dealer > 0 ? dealer : null;
        }

        function getAddToOrderPriceDisplay(customizer, cachedFormulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          if (isKeepingOriginalSpecifications(root)) {
            captureBaseRetailArtPrice(customizer);
            return formatMoneyUSD(getBaseRetailArtPrice(customizer));
          }
          const formulaTotal = getCustomizerFormulaFinalTotal(customizer, cachedFormulaPricing);
          if (formulaTotal != null) return formatMoneyUSDDollars(formulaTotal);
          if (getCustomizerFlatFeeAddonTotal(root) > 0) {
            return formatMoneyUSD(getCustomizerFlatFeeRetailTotal(root));
          }
          captureBaseRetailArtPrice(customizer);
          return formatMoneyUSD(getBaseRetailArtPrice(customizer));
        }

        function buildAddToOrderButtonInnerHtml(label, priceText, loading) {
          if (loading) {
            return (
              '<span class="summary-add-cart-btn__spinner" aria-hidden="true"></span>' +
              '<span class="summary-add-cart-btn__label">Adding to order…</span>'
            );
          }
          return (
            '<span class="summary-add-cart-btn__price">' + priceText + '</span>' +
            '<span class="summary-add-cart-btn__label">' + label + '</span>'
          );
        }

        function formatCustomizerMainPriceText(amount, isWholeDollar) {
          if (isWholeDollar) return formatMoneyUSDDollars(amount);
          return formatMoneyUSD(amount);
        }

        function getCustomizerMainPriceDisplay(root, cachedFormulaPricing) {
          captureBaseRetailArtPrice(root);
          const base = getBaseRetailArtPrice(root);

          if (isKeepingOriginalSpecifications(root)) {
            return formatCustomizerMainPriceText(base, false);
          }
          if (productHasOriginalSpec(root) &&
              root.dataset &&
              root.dataset.userModifiedSelections !== '1' &&
              root.dataset.originalSpecFullyApplied !== '1' &&
              !hasGlazingChangeFromOriginal(root)) {
            return formatCustomizerMainPriceText(base, false);
          }

          const finalTotal = getCustomizerFormulaFinalTotal(root, cachedFormulaPricing);
          if (finalTotal != null) {
            return formatCustomizerMainPriceText(finalTotal, true);
          }

          if (getCustomizerFlatFeeAddonTotal(root) > 0) {
            return formatCustomizerMainPriceText(getCustomizerFlatFeeRetailTotal(root), false);
          }
          return formatCustomizerMainPriceText(base, false);
        }

        function syncThemeProductPriceDisplay(priceText) {
          if (!priceText) return;
          const selectors = [
            '[data-product-price]',
            '.price-item--regular .price-item__price',
            '.product__price .price-item--regular',
            '#price .price-item--regular',
            '.product-price',
            '.product__price'
          ];
          selectors.forEach(function (sel) {
            document.querySelectorAll(sel).forEach(function (el) {
              if (el.closest('.customizer')) return;
              if (el.querySelector('.customizer-price-value')) return;
              el.textContent = priceText;
            });
          });
        }

        function updateCustomizerMainPrice(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer') || document;
          if (!root) return;
          if (!isCustomerLoggedIn(root)) {
            root.querySelectorAll('.step-panel').forEach(function (panel) {
              const wrap = panel.querySelector('.customizer-steps-price');
              if (wrap) renderHeaderPriceBreakdownForWrap(wrap, null, false);
            });
            return;
          }
          captureBaseRetailArtPrice(root);

          const formulaPricing = shouldShowFormulaPricingReview(root)
            ? calculateFormulaPricing(root)
            : null;
          const priceText = getCustomizerMainPriceDisplay(root, formulaPricing);

          root.querySelectorAll('.customizer-price-value').forEach(function (priceEl) {
            priceEl.textContent = priceText;
          });
          syncThemeProductPriceDisplay(priceText);

          const currentStep = getCurrentStep(root) || 1;
          const frameTab = currentStep === 3 ? getActiveFrameTab(root) : 'frame';
          const showLive = shouldShowLivePricing(root);
          const pricing = showLive
            ? calculatePricing(root, { step: currentStep, frameTab: frameTab })
            : null;

          root.querySelectorAll('.step-panel').forEach(function (panel) {
            const wrap = panel.querySelector('.customizer-steps-price');
            if (!wrap) return;
            const isActive = panel.classList.contains('active');
            const showBreakdown = isActive && showLive && pricing &&
              shouldShowHeaderBreakdown(root, panel) &&
              !shouldShowFormulaPricingReview(root);
            if (showBreakdown) {
              renderHeaderPriceBreakdownForWrap(wrap, pricing, true, getPanelPricingBreakdownMode(panel));
            } else {
              renderHeaderPriceBreakdownForWrap(wrap, null, false);
            }
          });

          updateFormulaPricingReviewDisplay(root, formulaPricing);

          syncAddToOrderButtonState(
            root.querySelector('.summary-add-cart-btn'),
            root,
            formulaPricing
          );
        }

        const customizerState = {
          substrate: 'Matte Paper',
          treatment: 'Single Mat',
          width: 12,
          height: 15,
          frame: null,
          topMat: null,
          bottomMat: null,
          topMatSizeInches: DEFAULT_TOP_MAT_SIZE_INCHES,
          bottomMatSizeInches: DEFAULT_BOTTOM_MAT_SIZE_INCHES,
          liner: null,
          artworkColor: null,
          colorInstruction: '',
          customTextEnabled: false,
          customerNotes: '',
          imageText: '',
          sideMark: false,
          sideMarkText: '',
          designApproved: false,
          wishlistAdded: false,
          wishlistedProductId: '',
          previewImageBase64: '',
          lockOriginalSubstrateMat: false,
          lockOriginalArtworkSize: false,
          lockOriginalMat: false,
          lockOriginalFrame: false,
          lockOriginalLiner: false,
          // Pricing-guide optional add-ons (defaults OFF unless your UI sets them)
          acrylicBrushstroke: false,
          artworkFloat: true,
          stepNotes: { 1: '', 2: '', 3: '', 4: '', 5: '' },
          defaultSubstrate: '',
          defaultTreatment: '',
          glazing: 'Glass',
          glazingManual: false,
          defaultGlazing: '',
          defaultArtworkWidth: 0,
          defaultArtworkHeight: 0
        };

        function captureDefaultArtworkDimensions(width, height) {
          const w = parseFloat(width);
          const h = parseFloat(height);
          if (isFinite(w) && w > 0) customizerState.defaultArtworkWidth = w;
          if (isFinite(h) && h > 0) customizerState.defaultArtworkHeight = h;
        }

        function getDefaultArtworkDimensions(customizerRoot) {
          const w = parseFloat(customizerState.defaultArtworkWidth);
          const h = parseFloat(customizerState.defaultArtworkHeight);
          if (isFinite(w) && w > 0 && isFinite(h) && h > 0) {
            return { width: w, height: h };
          }
          const resolved = resolveProductArtworkDimensions(customizerRoot);
          if (resolved) return resolved;
          return {
            width: parseFloat(customizerState.width) || 0,
            height: parseFloat(customizerState.height) || 0
          };
        }

        /** Original artwork W×H used to preserve proportions when one side changes. */
        function getProportionalSizeBase(customizerRoot) {
          const defaults = getDefaultArtworkDimensions(customizerRoot);
          let baseW = parseFloat(defaults.width) || 0;
          let baseH = parseFloat(defaults.height) || 0;
          if (baseW <= 0 || baseH <= 0) {
            const root = getActiveCustomizerRoot(customizerRoot);
            const specDim = root
              ? parseMetafieldDimensions(root.dataset.originalDimensions || '')
              : null;
            if (specDim) {
              baseW = specDim.width;
              baseH = specDim.height;
            }
          }
          if (baseW <= 0 || baseH <= 0) {
            baseW = parseFloat(customizerState.width) || 12;
            baseH = parseFloat(customizerState.height) || 15;
          }
          return {
            baseW: baseW,
            baseH: baseH,
            isSquare: baseW === baseH
          };
        }

        function calcProportionalArtworkSize(axis, value, base) {
          const v = snapArtworkDimension(value);
          if (!isFinite(v) || v <= 0 || !base) return null;
          const baseW = parseFloat(base.baseW) || 0;
          const baseH = parseFloat(base.baseH) || 0;
          if (baseW <= 0 || baseH <= 0) return null;
          if (base.isSquare) {
            return { width: v, height: v };
          }
          if (axis === 'width') {
            return {
              width: v,
              height: snapArtworkDimension((v * baseH) / baseW)
            };
          }
          return {
            width: snapArtworkDimension((v * baseW) / baseH),
            height: v
          };
        }

        /** True when step-1 width differs from the product's original artwork width. */
        function hasArtworkWidthChanged(customizerRoot) {
          if (isKeepingOriginalSpecifications(customizerRoot)) return false;
          if (customizerState.lockOriginalArtworkSize) return false;
          const defaults = getDefaultArtworkDimensions();
          const curW = parseFloat(customizerState.width);
          if (!isFinite(defaults.width) || defaults.width <= 0 || !isFinite(curW) || curW <= 0) return false;
          return roundSizeInches(curW) !== roundSizeInches(defaults.width);
        }

        /** True when either artwork dimension differs from the product's original size. */
        function hasArtworkSizeChangedFromDefault(customizerRoot) {
          if (isKeepingOriginalSpecifications(customizerRoot)) return false;
          if (customizerState.lockOriginalArtworkSize) return false;
          const defaults = getDefaultArtworkDimensions();
          const curW = parseFloat(customizerState.width);
          const curH = parseFloat(customizerState.height);
          if (!isFinite(defaults.width) || defaults.width <= 0 || !isFinite(defaults.height) || defaults.height <= 0) {
            return false;
          }
          if (!isFinite(curW) || curW <= 0 || !isFinite(curH) || curH <= 0) return false;
          return roundSizeInches(curW) !== roundSizeInches(defaults.width) ||
            roundSizeInches(curH) !== roundSizeInches(defaults.height);
        }

        function syncArtworkSizeFromInputs(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const wInput = root.querySelector('#cs-width-input');
          const hInput = root.querySelector('#cs-height-input');
          if (wInput) {
            const w = parseFloat(wInput.value);
            if (isFinite(w) && w > 0) customizerState.width = roundSizeInches(w);
          }
          if (hInput) {
            const h = parseFloat(hInput.value);
            if (isFinite(h) && h > 0) customizerState.height = roundSizeInches(h);
          }
        }

        function getFormulaPricingCustomerTotal(root, finalProductPrice, addonFees, printedMatCost) {
          const fees = addonFees || getFormulaAddonChangeFee(root);
          const addonTotal = fees.total || 0;
          const printedAdd = parseFloat(printedMatCost);
          const printedMatAddOn = isFinite(printedAdd) ? printedAdd : 0;
          // The formula table computes a "Final Price Stocking Dealer" (dealer) number.
          // "Final Total" line should match the displayed formula:
          //   Final Price Stocking Dealer + Printed Mat Cost + (Size change OR Customization change fee)
          // Previously, when the customer did not trigger "full formula pricing", we used
          // the product base price here, which makes the table math inconsistent.
          const dealer = parseFloat(finalProductPrice) || 0;
          if (isFinite(dealer) && dealer > 0) {
            return roundFormulaMoneyToDollar(dealer + printedMatAddOn + addonTotal);
          }
          captureBaseRetailArtPrice(root);
          const base = getBaseRetailArtPrice(root);
          return roundFormulaMoneyToDollar(base + printedMatAddOn + addonTotal);
        }

        /** Step / meta labels to highlight when the customer changed that selection. */
        function getFormulaPricingChangedStepKeys(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const changed = new Set();
          if (isKeepingOriginalSpecifications(root) || !hasAnyPricingAddOns(root)) return changed;

          const flags = getOriginalSpecChangeFlags(root);
          const addon = getFormulaAddonChangeFee(root);
          const spec = readOriginalSpecFromCustomizer(root);

          if (hasArtworkDimensionCustomization(root)) {
            changed.add('Image Width');
            changed.add('Image Height');
            changed.add('Glass Width');
            changed.add('Glass Height');
            changed.add('United Inches');
            changed.add('Framed Width');
            changed.add('Framed Height');
            changed.add('Framed United Inches');
            changed.add('BOM FT');
            changed.add('Mat Charge');
            changed.add('Final Price Stocking Dealer');
            changed.add('Size change charge');
          }
          if (hasSubstrateChangeFromOriginal(root) ||
              (spec && !matchesOriginalSubstrateAndTreatment(root, spec))) {
            changed.add('Substrate');
            changed.add('FIT');
            changed.add('FR/FAB');
            changed.add('HFM $ OR AB');
            changed.add('TTL');
            if (addon.deckleFitCharge > 0) changed.add('Deckle fit charge');
          }
          if (hasGlazingChangeFromOriginal(root)) {
            changed.add('FIT');
            changed.add('TTL');
          }
          if (flags.frameChanged) {
            changed.add('Frame');
            changed.add('Frame Code');
            changed.add('MLDG WIDTH');
            changed.add('MLDG COST');
            changed.add('MLDG VENDOR');
            changed.add('BOM FT');
            changed.add('MLDG');
            changed.add('MLDX');
          }
          if (flags.topMatChanged || flags.matSizesChanged) {
            changed.add('MAT 1');
            changed.add('MAT1 Inches');
            changed.add('Mat Charge');
            if (customizerState.topMat && matIsPrinted('topMat', customizerState.topMat)) {
              changed.add('Printed Mat Cost');
            }
          }
          if (flags.bottomMatChanged) {
            changed.add('MAT 2');
            changed.add('MAT2 Inches');
            changed.add('Mat Charge');
            changed.add('Printed Mat Cost');
          }
          if (flags.linerChanged) {
            changed.add('MLDG WIDTH 2');
            changed.add('MLDG OST 2');
            changed.add('MLDG VENDOR 2');
            changed.add('Liner Ost');
            changed.add('Framed Width');
            changed.add('Framed Height');
          }
          if (addon.customizationChangeFee > 0) changed.add('Customization change fee');
          if (addon.sizeChangeCharge > 0) changed.add('Size change charge');
          if (changed.size > 0) changed.add('Final Total');
          return changed;
        }

        function formulaPricingLineLabelHtml(label, changedKeys, esc) {
          const text = esc(label);
          if (!changedKeys || !changedKeys.has(label)) return text;
          return '<span class="formula-pricing-review__changed-mark" title="Changed">●</span> ' + text;
        }

        /** Formula pricing table — always visible for logged-in customers with artwork size. */
        function shouldShowFormulaPricingReview(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!isCustomerLoggedIn(root)) return false;
          syncArtworkSizeFromInputs(root);
          const w = parseFloat(customizerState.width);
          const h = parseFloat(customizerState.height);
          return isFinite(w) && w > 0 && isFinite(h) && h > 0;
        }

        /**
         * Oversize-only mat grid — active only after the customer clicks OK on the notice.
         * Stays sticky while that mat's own sheet size still needs an oversized mat.
         * Disabled while Keep Original Mat Specifications is checked.
         */
        function shouldRestrictToOversizeMatsOnly(customizer, stateKey) {
          if (customizer && isApplyingOriginalSpec(customizer)) return false;
          if (customizerState.lockOriginalMat) return false;
          if (!isUnderGlass()) return false;
          const root = getActiveCustomizerRoot(customizer);
          if (!(root && root.dataset.oversizeMatsFilterActive === '1')) return false;
          return matRequiresOversize(stateKey === 'bottomMat' ? 'bottomMat' : 'topMat', root);
        }

        function getSizeChangeCharge(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer');
          if (root && isKeepingOriginalSpecifications(root)) return 0;
          if (!hasArtworkDimensionCustomization(customizerRoot)) return 0;
          return PRICING_CONFIG.sizeChangeChargeFlat;
        }

        /** Size change $40 OR customization $20/$40 — never both. Max non-size fee is $40. */
        function getFormulaAddonChangeFee(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const glazingChanged = hasGlazingChangeFromOriginal(root);
          if (!glazingChanged && isKeepingOriginalSpecifications(root)) {
            return {
              total: 0,
              sizeChangeCharge: 0,
              customizationChangeFee: 0,
              formula: 'Original specification (no charge)'
            };
          }
          const sizeChangeCharge = getSizeChangeCharge(root);
          const customizationChangeFee = sizeChangeCharge > 0
            ? 0
            : getCustomizationChangeFee(root);
          const total = roundFormulaMoney(sizeChangeCharge + customizationChangeFee);
          let formula = 'No customization change';
          if (sizeChangeCharge > 0) {
            formula = 'Artwork size changed — $' + PRICING_CONFIG.sizeChangeChargeFlat.toFixed(2) + ' flat';
          } else if (customizationChangeFee > 0) {
            const count = countNonSizeCustomizationChanges(root);
            const spec = readOriginalSpecFromCustomizer(root);
            const labels = new Set();
            if (hasSubstrateChangeFromOriginal(root)) labels.add('Substrate');
            if (hasGlazingChangeFromOriginal(root)) labels.add('Glass/Plexi');
            if (hasAcrylicBrushstrokeChange(root)) labels.add('Acrylic brushstroke');
            if (getOriginalSpecChangeFlags(root).frameChanged) labels.add('Frame');
            if (hasMatCustomizationDelta(spec, root)) labels.add('Mat');
            if (getOriginalSpecChangeFlags(root).linerChanged) labels.add('Liner');
            const labelText = labels.size
              ? Array.from(labels).join(' / ')
              : 'frame / mat / liner / substrate / glass-plexi / brushstroke';
            formula = count + ' change' + (count === 1 ? '' : 's') +
              ' (' + labelText + ') — $' + customizationChangeFee.toFixed(2);
          }
          return {
            total: total,
            sizeChangeCharge: roundFormulaMoney(sizeChangeCharge),
            customizationChangeFee: roundFormulaMoney(customizationChangeFee),
            formula: formula
          };
        }

        function getDeckleFloatCharge(customizerRoot) {
          return 0;
        }

        function toInchesFromLegacyWidth(w) {
          const n = parseFloat(w);
          if (isNaN(n) || n <= 0) return 1.2;
          if (n > 2.5) return n / CM_PER_INCH;
          return n;
        }

        function formatSizeInches(w, h) {
          if (!w || !h) return '';
          return w + ' × ' + h + ' in';
        }

        function roundSizeInches(n) {
          const v = parseFloat(n);
          if (isNaN(v)) return 0;
          return Math.round(v * 100) / 100;
        }

        /** Preserve API moulding fractions (e.g. 0.875, 0.5625) — do not use 2-decimal roundSizeInches. */
        function roundMouldingInches(n) {
          const v = parseFloat(n);
          if (isNaN(v)) return 0;
          return Math.round(v * 10000) / 10000;
        }

        function normalizeTreatmentLabel(raw) {
          const t = String(raw || '').trim();
          const low = t.toLowerCase();
          if (!t || low === 'none' || low === 'no mat' || t === 'Straight Fit (No Mats)') return 'No Mat';
          return t;
        }

        const ROUGH_DECKLE_MASK_URL =
          'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/Deckle_Mask_Transparent.png?v=1782217530';
        let roughDeckleMaskImg = null;
        let roughDeckleMaskLoading = null;

        function isDeckleSubstrate() {
          return /deckle/i.test(String(customizerState.substrate || ''));
        }

        function isStraightDeckleSubstrate() {
          return /straight\s+deckle/i.test(String(customizerState.substrate || ''));
        }

        function isRoughDeckleSubstrate() {
          return /rough\s+deckle/i.test(String(customizerState.substrate || ''));
        }

        function ensureRoughDeckleMaskLoaded() {
          if (roughDeckleMaskImg) return Promise.resolve(roughDeckleMaskImg);
          if (roughDeckleMaskLoading) return roughDeckleMaskLoading;
          roughDeckleMaskLoading = new Promise(function (resolve, reject) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
              roughDeckleMaskImg = img;
              resolve(img);
            };
            img.onerror = function () {
              roughDeckleMaskLoading = null;
              reject(new Error('Rough deckle mask failed to load'));
            };
            img.src = ROUGH_DECKLE_MASK_URL;
          });
          return roughDeckleMaskLoading;
        }

        function preloadRoughDeckleMask() {
          if (isRoughDeckleSubstrate()) ensureRoughDeckleMaskLoaded().catch(function () {});
        }

        function drawRoughDeckleMaskedArtwork(ctx, img, mask, artX, artY, artW, artH) {
          const tmp = document.createElement('canvas');
          tmp.width = artW;
          tmp.height = artH;
          const tctx = tmp.getContext('2d');
          if (!tctx) {
            ctx.drawImage(img, artX, artY, artW, artH);
            return;
          }
          tctx.drawImage(img, 0, 0, artW, artH);
          tctx.globalCompositeOperation = 'destination-in';
          tctx.drawImage(mask, 0, 0, artW, artH);
          tctx.globalCompositeOperation = 'source-over';

          const blur = Math.max(6, Math.round(Math.min(artW, artH) * 0.034));
          const offX = Math.max(3, Math.round(blur * 0.5));
          const offY = Math.max(4, Math.round(blur * 0.72));
          ctx.save();
          ctx.shadowColor = 'rgba(0, 0, 0, 0.34)';
          ctx.shadowBlur = blur;
          ctx.shadowOffsetX = offX;
          ctx.shadowOffsetY = offY;
          ctx.drawImage(tmp, artX, artY);
          ctx.restore();
          ctx.drawImage(tmp, artX, artY);
        }

        /** Straight deckle = rect shadow; rough deckle = mask clip + deckle shadow. */
        function drawArtworkInPreview(ctx, img, artX, artY, artW, artH, clipX, clipY, clipW, clipH, onNeedsRedraw) {
          if (!img || artW <= 4 || artH <= 4) return false;
          ctx.save();
          if (clipW > 0 && clipH > 0) {
            ctx.beginPath();
            ctx.rect(clipX, clipY, clipW, clipH);
            ctx.clip();
          }
          if (isRoughDeckleSubstrate()) {
            if (!roughDeckleMaskImg) {
              ensureRoughDeckleMaskLoaded()
                .then(function () {
                  if (typeof onNeedsRedraw === 'function') onNeedsRedraw();
                })
                .catch(function () {
                  ctx.drawImage(img, artX, artY, artW, artH);
                });
              ctx.restore();
              return false;
            }
            drawRoughDeckleMaskedArtwork(ctx, img, roughDeckleMaskImg, artX, artY, artW, artH);
          } else if (isStraightDeckleSubstrate()) {
            var blur = Math.max(5, Math.round(Math.min(artW, artH) * 0.032));
            var offX = Math.max(2, Math.round(blur * 0.45));
            var offY = Math.max(3, Math.round(blur * 0.65));
            ctx.save();
            ctx.shadowColor = 'rgba(0, 0, 0, 0.30)';
            ctx.shadowBlur = blur;
            ctx.shadowOffsetX = offX;
            ctx.shadowOffsetY = offY;
            ctx.drawImage(img, artX, artY, artW, artH);
            ctx.restore();
          } else {
            ctx.drawImage(img, artX, artY, artW, artH);
          }
          ctx.restore();
          return true;
        }

        function updateDeckleArtworkPreviewUI(root) {
          const straight = isStraightDeckleSubstrate();
          const rough = isRoughDeckleSubstrate();
          const scope = root && root.querySelectorAll ? root : document;
          scope.querySelectorAll('.editable-image').forEach(function (img) {
            img.classList.toggle('editable-image--straight-deckle-shadow', straight);
            img.classList.toggle('editable-image--rough-deckle', rough);
          });
          if (rough) preloadRoughDeckleMask();
        }

        function matSizeSelectForStateKey(stateKey) {
          const panelSel = stateKey === 'bottomMat'
            ? '[data-cz-tab-panel="bottommat"]'
            : '[data-cz-tab-panel="topmat"]';
          const panel = document.querySelector(panelSel);
          return panel ? panel.querySelector('.mat-size-select') : null;
        }

        /** Read SIZE (INCHES) dropdown into customizerState before dimension math. */
        function syncMatSizeInchesFromUI(stateKey) {
          if (stateKey !== 'topMat' && stateKey !== 'bottomMat') return;
          const sizeKey = stateKey === 'bottomMat' ? 'bottomMatSizeInches' : 'topMatSizeInches';
          const sel = matSizeSelectForStateKey(stateKey);
          if (!sel) return;
          const inches = parseFloat(sel.value);
          if (isFinite(inches) && inches > 0) customizerState[sizeKey] = inches;
        }

        function syncAllMatSizesFromUI() {
          syncMatSizeInchesFromUI('topMat');
          syncMatSizeInchesFromUI('bottomMat');
        }

        function getTopMatRevealInches(customizerRoot) {
          syncMatSizeInchesFromUI('topMat');
          if (customizerState.topMat) {
            const inches = parseFloat(customizerState.topMatSizeInches);
            return isFinite(inches) && inches > 0 ? inches : 0;
          }
          if (hasPrintedMatSpecificationCharges(customizerRoot)) {
            return getMatSpecificationInchesFromProduct(customizerRoot);
          }
          return 0;
        }

        function getBottomMatRevealInches() {
          syncMatSizeInchesFromUI('bottomMat');
          if (!customizerState.bottomMat) return 0;
          const inches = parseFloat(customizerState.bottomMatSizeInches);
          return isFinite(inches) && inches > 0 ? inches : 0;
        }

        function formatFormulaMatLabel(mat) {
          if (!mat) return 'None';
          const id = String(mat.id || '').trim();
          const name = String(mat.name || '').trim();
          const isPrinted = mat.printedMats ||
            matIsPrinted('topMat', mat) ||
            matIsPrinted('bottomMat', mat);
          const printedSuffix = isPrinted ? ' (Printed)' : '';
          if (id && name) return id + ' — ' + name + printedSuffix;
          return (id || name || 'None') + printedSuffix;
        }

        /** Top / bottom mat id, name, and reveal inches for formula pricing table. */
        function getFormulaMatSelection(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const isCanvas = isCanvasSubstrate();
          const treatment = String(customizerState.treatment || '').trim();
          const hasMats = !isCanvas && (treatment === 'Single Mat' || treatment === 'Double Mat');

          syncAllMatSizesFromUI();

          if (isCanvas) {
            return {
              mat1Label: 'N/A (canvas)',
              mat1Inches: 0,
              mat2Label: 'N/A (canvas)',
              mat2Inches: 0
            };
          }
          if (!hasMats) {
            return {
              mat1Label: 'None',
              mat1Inches: 0,
              mat2Label: 'None',
              mat2Inches: 0
            };
          }
          if (!customizerState.topMat && hasPrintedMatSpecificationCharges(root)) {
            const specRaw = getProductMatSpecificationRaw(root);
            return {
              mat1Label: specRaw || 'Printed mat (specification)',
              mat1Inches: getMatSpecificationInchesFromProduct(root),
              mat2Label: 'None',
              mat2Inches: 0
            };
          }
          if (!customizerState.topMat) {
            return {
              mat1Label: 'None',
              mat1Inches: 0,
              mat2Label: 'None',
              mat2Inches: 0
            };
          }

          const mat1Label = formatFormulaMatLabel(customizerState.topMat);
          const mat1Inches = getTopMatRevealInches(root);
          let mat2Label = 'None';
          let mat2Inches = 0;
          if (treatment === 'Double Mat' && customizerState.bottomMat) {
            mat2Label = formatFormulaMatLabel(customizerState.bottomMat);
            mat2Inches = getBottomMatRevealInches();
          }

          return { mat1Label, mat1Inches, mat2Label, mat2Inches };
        }

        /** Mat reveal per side (top + bottom stack for double mat). */
        function matBorderInchesPerSide(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const treatment = customizerState.treatment || '';
          const hasMatsTreatment = (treatment === 'Single Mat' || treatment === 'Double Mat') &&
            !isCanvasSubstrate();
          if (!hasMatsTreatment) return 0;

          syncAllMatSizesFromUI();
          if (customizerState.topMat) {
            let perSide = getTopMatRevealInches(root);
            if (treatment === 'Double Mat' && customizerState.bottomMat) {
              perSide += getBottomMatRevealInches();
            }
            return perSide;
          }
          if (hasPrintedMatSpecificationCharges(root)) {
            return getMatSpecificationInchesFromProduct(root);
          }
          return 0;
        }

        /** Per-axis frame molding width in inches (API sizeWidth / sizeHeight — not display labels). */
        function getFrameMoldingInches(frame, axis) {
          if (!frame) return 0;
          const key = axis === 'height' ? 'sizeHeightIn' : 'sizeWidthIn';
          const inches = parseFloat(frame[key]);
          if (isFinite(inches) && inches > 0) return inches;
          const fallback = getFrameFaceWidthInches(frame);
          return isFinite(fallback) && fallback > 0 ? fallback : 0;
        }

        /** Face width in inches for frame or liner (used for 2× outside border). */
        function getMoldingFaceWidthInches(entity, kind) {
          if (!entity) return 0;
          if (kind === 'frame') {
            const inches = getFrameFaceWidthInches(entity);
            return isFinite(inches) && inches > 0 ? inches : 0;
          }
          const inches = toInchesFromLegacyWidth(entity.frameW);
          return isFinite(inches) && inches > 0 ? inches : 0;
        }

        /** Adds width on left + right (2× per side) for a molding/mat strip. */
        function borderAdditionInches(faceWidthInches) {
          const w = parseFloat(faceWidthInches);
          if (isNaN(w) || w <= 0) return 0;
          return 2 * w;
        }

        function computeGlassSizeInches() {
          const w = parseFloat(customizerState.width) || 0;
          const h = parseFloat(customizerState.height) || 0;
          const extra = borderAdditionInches(matBorderInchesPerSide());
          return {
            width: roundSizeInches(w + extra),
            height: roundSizeInches(h + extra)
          };
        }

        /** Outside = glass + frame + liner (liner is MLDG WIDTH 2). */
        function computeOverallSizeInches() {
          const glass = computeGlassSizeInches();
          const frame = customizerState.frame;
          const extraW = borderAdditionInches(getFrameMoldingInches(frame, 'width')) +
            borderAdditionInches(getLinerMouldingWidthForFormula(customizerState.liner));
          const extraH = borderAdditionInches(getFrameMoldingInches(frame, 'height')) +
            borderAdditionInches(getLinerMouldingWidthForFormula(customizerState.liner));
          if (extraW <= 0 && extraH <= 0) {
            const w = parseFloat(customizerState.width) || 0;
            const h = parseFloat(customizerState.height) || 0;
            return { width: roundSizeInches(w), height: roundSizeInches(h) };
          }
          return {
            width: roundSizeInches(glass.width + extraW),
            height: roundSizeInches(glass.height + extraH)
          };
        }

        /** Framed (outside) size = Framed Width × Framed Height — matches formula pricing. */
        function computeFramedSizeInches(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          ensureArtworkSizeForPricing(root);
          const width = roundSizeInches(parseFloat(customizerState.width) || 0);
          const height = roundSizeInches(parseFloat(customizerState.height) || 0);
          const areaDims = getFormulaPricingAreaDimensions(root, width, height);
          const formulaWidth = areaDims.width;
          const formulaHeight = areaDims.height;
          const isCanvas = isCanvasSubstrate();
          const formulaMats = getFormulaMatSelection(root);
          const frame = customizerState.frame;
          const formulaGlass = computeFormulaGlassSizeInches(formulaWidth, formulaHeight, {
            isCanvas: isCanvas,
            mat1Inches: formulaMats.mat1Inches,
            mat2Inches: formulaMats.mat2Inches
          });
          const frameMouldingWidth = roundMouldingInches(getFrameMouldingWidthForFormula(frame, 'width'));
          const mldgWidth = frameMouldingWidth;
          const mldgWidth2 = customizerState.liner
            ? roundMouldingInches(getLinerMouldingWidthForFormula(customizerState.liner))
            : 0;
          const computedGlassW = formulaGlass.width;
          const computedGlassH = formulaGlass.height;
          const computedFramedW = (computedGlassW > 0)
            ? roundSizeInches((mldgWidth + mldgWidth2) * 2 + computedGlassW)
            : 0;
          const computedFramedH = (computedGlassH > 0)
            ? roundSizeInches((mldgWidth + mldgWidth2) * 2 + computedGlassH)
            : 0;
          const metafieldDims = applyProductMetafieldFormulaDimensions(
            root,
            computedGlassW,
            computedGlassH,
            computedFramedW,
            computedFramedH,
            mldgWidth,
            mldgWidth2
          );
          return {
            width: roundSizeInches(metafieldDims.framedWidth),
            height: roundSizeInches(metafieldDims.framedHeight)
          };
        }

        function isCanvasSubstrate() {
          return /canvas/i.test(String(customizerState.substrate || ''));
        }

        function isUnderGlass() {
          // Current customizer doesn't expose a dedicated "underglass" toggle.
          // Treat non-canvas substrates as under-glass configurations.
          return !isCanvasSubstrate();
        }

        function isArtworkOversize(w, h) {
          const gw = parseFloat(w) || 0;
          const gh = parseFloat(h) || 0;
          if (gw <= 0 || gh <= 0) return false;
          const shortSide = Math.min(gw, gh);
          const longSide = Math.max(gw, gh);
          return shortSide > ARTWORK_OVERSIZE_SHORT_MAX || longSide > ARTWORK_OVERSIZE_LONG_MAX;
        }

        function isGlassOver32x40(glassW, glassH) {
          // Glass size = artwork + mat borders. Oversize mats apply when glass no longer
          // fits a 32×40 sheet (short > 32 OR long > 40), not when artwork alone is large.
          return isArtworkOversize(glassW, glassH);
        }

        /**
         * Mat border used for oversize gating — uses SIZE (INCHES) even when the mat card
         * was cleared after the notice, so OK can still keep the oversized-only catalog.
         */
        function matBorderInchesForOversizeGate(customizerRoot) {
          if (isCanvasSubstrate() || !isUnderGlass()) return 0;
          const treatment = String(customizerState.treatment || '').trim();
          if (treatment !== 'Single Mat' && treatment !== 'Double Mat') return 0;
          syncAllMatSizesFromUI();
          let perSide = parseFloat(customizerState.topMatSizeInches);
          if (!isFinite(perSide) || perSide <= 0) perSide = 0;
          if (treatment === 'Double Mat') {
            const bottom = parseFloat(customizerState.bottomMatSizeInches);
            if (isFinite(bottom) && bottom > 0) perSide += bottom;
          }
          return perSide;
        }

        function computeGlassSizeForOversizeGate(customizerRoot) {
          const w = parseFloat(customizerState.width) || 0;
          const h = parseFloat(customizerState.height) || 0;
          const extra = borderAdditionInches(matBorderInchesForOversizeGate(customizerRoot));
          return {
            width: roundSizeInches(w + extra),
            height: roundSizeInches(h + extra)
          };
        }

        /** True when configured glass (artwork + mat inches) requires the oversized-mat catalog. */
        function glassRequiresOversizeMats(customizerRoot) {
          if (!isUnderGlass()) return false;
          const glass = computeGlassSizeForOversizeGate(customizerRoot);
          return isGlassOver32x40(glass.width, glass.height);
        }

        /**
         * Sheet size each mat has to be cut from. The top mat covers the full glass, while the
         * bottom mat only reaches the artwork plus its own reveal — so it can stay a normal
         * 32 × 40 sheet even when the top mat must be oversized.
         */
        function matSheetSizeInches(stateKey, customizerRoot) {
          if (stateKey !== 'bottomMat') return computeGlassSizeForOversizeGate(customizerRoot);
          const w = parseFloat(customizerState.width) || 0;
          const h = parseFloat(customizerState.height) || 0;
          syncAllMatSizesFromUI();
          let bottom = parseFloat(customizerState.bottomMatSizeInches);
          if (!isFinite(bottom) || bottom <= 0) bottom = 0;
          const extra = borderAdditionInches(bottom);
          return {
            width: roundSizeInches(w + extra),
            height: roundSizeInches(h + extra)
          };
        }

        /** True when this specific mat (top or bottom) needs an oversized sheet. */
        function matRequiresOversize(stateKey, customizerRoot) {
          if (!isUnderGlass()) return false;
          if (stateKey === 'bottomMat' && String(customizerState.treatment || '').trim() !== 'Double Mat') {
            return false;
          }
          const sheet = matSheetSizeInches(stateKey, customizerRoot);
          return isGlassOver32x40(sheet.width, sheet.height);
        }

        /**
         * Glazing material for display / summary: Glass, Plexi, or None (canvas).
         * Plexi when the auto-Plexi rule is active (40 × 48 or 84″+), or the customer selected Plexi.
         */
        function getGlazingMaterialLabel(customizerRoot) {
          return resolveEffectiveGlazing(customizerRoot);
        }

        function snapArtworkDimension(n) {
          const v = parseFloat(n);
          if (!isFinite(v)) return ARTWORK_SIZE_MIN;
          return Math.round(v * 10) / 10;
        }

        function getArtworkSizeValidationError(w, h) {
          const ew = parseFloat(w);
          const eh = parseFloat(h);
          if (!isFinite(ew) || !isFinite(eh) || ew <= 0 || eh <= 0) {
            return 'Please enter valid width and height.';
          }
          if (ew < ARTWORK_SIZE_MIN || eh < ARTWORK_SIZE_MIN) {
            return 'Each side must be at least ' + ARTWORK_SIZE_MIN + ' inches.';
          }
          if (ew > ARTWORK_SIZE_MAX || eh > ARTWORK_SIZE_MAX) {
            return 'Each side must be no more than ' + ARTWORK_SIZE_MAX + ' inches.';
          }
          return '';
        }

        function normalizeMatColorKey(name) {
          return String(name || '').trim().toLowerCase().replace(/\s+oversize.*$/i, '').trim();
        }

        function getMatColorFilterName(mat) {
          if (!mat) return '';
          const direct = mat.matColorName != null ? String(mat.matColorName).trim() : '';
          if (direct) return direct;
          if (mat.matColor && mat.matColor.colorName != null) {
            return String(mat.matColor.colorName).trim();
          }
          return '';
        }

        function dedupeMatsByColorName(list, needsOversize) {
          if (!Array.isArray(list) || !list.length) return list;
          const map = new Map();
          list.forEach(function (mat) {
            const key = normalizeMatColorKey(getMatColorFilterName(mat)) || String(mat.id || '');
            const prev = map.get(key);
            if (!prev) {
              map.set(key, mat);
              return;
            }
            const matIsOver = !!mat.oversizedMat;
            const prevIsOver = !!prev.oversizedMat;
            const matPickable = mat.pickFrom !== false;
            const prevPickable = prev.pickFrom !== false;
            if (needsOversize) {
              if (matIsOver && !prevIsOver) map.set(key, mat);
              else if (matIsOver === prevIsOver && matPickable && !prevPickable) map.set(key, mat);
            } else if (!matIsOver && prevIsOver) {
              map.set(key, mat);
            } else if (matIsOver === prevIsOver && matPickable && !prevPickable) {
              map.set(key, mat);
            }
          });
          return Array.from(map.values());
        }

        function originalSpecMatsUnavailableAtSize(customizer) {
          if (!glassRequiresOversizeMats(customizer)) return false;
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return false;
          if (spec.topMatId) {
            const m = findMatInCatalogs('topMat', spec.topMatId, spec.topMatName);
            if (m && !matIsOversized('topMat', m)) return true;
          }
          if (spec.bottomMatId && matRequiresOversize('bottomMat', customizer)) {
            const m = findMatInCatalogs('bottomMat', spec.bottomMatId, spec.bottomMatName);
            if (m && !matIsOversized('bottomMat', m)) return true;
          }
          return false;
        }

        function showCustomizerErrorModal(message, onClose) {
          let modal = document.getElementById('cz-error-modal');
          if (!modal) {
            modal = document.createElement('div');
            modal.id = 'cz-error-modal';
            modal.className = 'image-customizer-popup cz-error-modal';
            modal.setAttribute('role', 'alertdialog');
            modal.setAttribute('aria-modal', 'true');
            modal.setAttribute('aria-labelledby', 'cz-error-modal-title');
            modal.innerHTML = `
              <div class="image-customizer-popup__overlay cz-error-modal__overlay" data-cz-error-close></div>
              <div class="image-customizer-popup__inner cz-error-modal__inner">
                <div class="cz-error-modal__icon" aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dfce50" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4M12 16h.01" stroke-linecap="round"/>
                  </svg>
                </div>
                <h3 id="cz-error-modal-title" class="cz-error-modal__title">Notice</h3>
                <p class="cz-error-modal__message"></p>
                <button type="button" class="cz-error-modal__ok-btn" data-cz-error-close>OK</button>
              </div>
            `;
            document.body.appendChild(modal);
            modal.querySelectorAll('[data-cz-error-close]').forEach(function (el) {
              el.addEventListener('click', closeCustomizerErrorModal);
            });
          }
          modal._czOnClose = typeof onClose === 'function' ? onClose : null;
          const msgEl = modal.querySelector('.cz-error-modal__message');
          if (msgEl) msgEl.textContent = message || '';
          modal.hidden = false;
          modal.querySelector('.image-customizer-popup__overlay')?.classList.add('active');
          modal.querySelector('.cz-error-modal__ok-btn')?.focus();
        }

        function closeCustomizerErrorModal() {
          const modal = document.getElementById('cz-error-modal');
          if (!modal) return;
          modal.hidden = true;
          modal.querySelector('.image-customizer-popup__overlay')?.classList.remove('active');
          const onClose = modal._czOnClose;
          modal._czOnClose = null;
          if (typeof onClose === 'function') onClose();
        }

        /** After OK: hide non-oversize mats and show only oversized mats. */
        function activateOversizeMatsFilter(customizerRoot) {
          const roots = Array.prototype.slice.call(document.querySelectorAll('.customizer'));
          if (!roots.length) {
            const fallback = getActiveCustomizerRoot(customizerRoot);
            if (fallback && fallback.classList && fallback.classList.contains('customizer')) {
              roots.push(fallback);
            }
          }
          if (!roots.length) return;

          // Sticky flag on every customizer root so all mat grids re-filter.
          roots.forEach(function (root) {
            root.dataset.oversizeMatsFilterActive = '1';
            root.dataset.oversizeRestrictActive = '1';
            root.dataset.oversizeMatAlertShown = '1';
          });

          const root = getActiveCustomizerRoot(customizerRoot) || roots[0];
          let changedSelection = false;
          if (isUnderGlass()) {
            if (matRequiresOversize('topMat', root) && customizerState.topMat &&
                !matIsOversized('topMat', customizerState.topMat)) {
              customizerState.topMat = null;
              changedSelection = true;
            }
            // Bottom mat is only cleared when its own sheet size passes 32 × 40.
            if (matRequiresOversize('bottomMat', root) && customizerState.bottomMat &&
                !matIsOversized('bottomMat', customizerState.bottomMat)) {
              customizerState.bottomMat = null;
              changedSelection = true;
            }
          }

          function refreshOversizeMatGrids() {
            if (typeof window.__matRefreshTop === 'function') {
              window.__matRefreshTop({ resetPage: true });
            }
            if (typeof window.__matRefreshBottom === 'function') {
              window.__matRefreshBottom({ resetPage: true });
            }
            roots.forEach(function (r) {
              runMatStepFilter(r, 'topMat', { resetPage: true });
              runMatStepFilter(r, 'bottomMat', { resetPage: true });
              syncMatCardsFromState(r);
              updateMatPreview(r);
              updateBottomMatPrintedNote(r);
              updateCustomizerMainPrice(r);
            });
            if (changedSelection) updateMatStepLabel(root);
            if (getCurrentStep(root) === getSummaryStep()) updateSummary(root);
          }

          // Refresh after the modal fully closes so the grid update is visible.
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(function () {
              refreshOversizeMatGrids();
            });
          } else {
            refreshOversizeMatGrids();
          }
        }

        function alertMatUnavailableAtSize(customizerRoot) {
          showCustomizerErrorModal(
            'This mat is unavailable in this size. Please choose one of our oversized mats.',
            function () {
              activateOversizeMatsFilter(customizerRoot);
            }
          );
        }

        /** Oversize-mat notice: mat step only, after a real customer change — never for keep-original locks. */
        function shouldAlertOversizeMatRestriction(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root) return false;
          if (isApplyingOriginalSpec(root)) return false;
          // Keep-original locks restore metafield specs — never show oversize alert.
          if (customizerState.lockOriginalMat) return false;
          if (customizerState.lockOriginalSubstrateMat) return false;
          if (isKeepingOriginalSpecifications(root)) return false;
          // Alert is only for the Mat step (mat inches / mat selection).
          if (getCurrentStep(root) !== 2) return false;
          return root.dataset.userModifiedSelections === '1';
        }

        /** Clear sticky oversize-mat filter so original / full catalog can show again. */
        function clearOversizeMatsFilter(customizerRoot) {
          const roots = customizerRoot
            ? [getActiveCustomizerRoot(customizerRoot)]
            : Array.prototype.slice.call(document.querySelectorAll('.customizer'));
          roots.forEach(function (root) {
            if (!root || !root.dataset) return;
            delete root.dataset.oversizeMatsFilterActive;
            delete root.dataset.oversizeRestrictActive;
            delete root.dataset.oversizeMatAlertShown;
          });
        }

        function getUnitedInches(glassW, glassH) {
          const gw = parseFloat(glassW) || 0;
          const gh = parseFloat(glassH) || 0;
          return roundSizeInches(gw + gh);
        }

        function getPerimeterInches(glassW, glassH) {
          const gw = parseFloat(glassW) || 0;
          const gh = parseFloat(glassH) || 0;
          return roundSizeInches((gw + gh) * 2);
        }

        function getGlassCharge(ui) {
          const u = parseFloat(ui) || 0;
          if (isCanvasSubstrate()) return 0;
          const tiers = PRICING_CONFIG.glassChargeTiers;
          for (let i = 0; i < tiers.length; i++) {
            if (u <= tiers[i].maxUi) return tiers[i].charge;
          }
          return 0;
        }

        function getPlexiChargeFromUnitedInches(ui) {
          const u = parseFloat(ui) || 0;
          if (u <= 0) return 0;
          const tiers = PRICING_CONFIG.plexiChargeTiers || [];
          for (let i = 0; i < tiers.length; i++) {
            if (u <= tiers[i].maxUi) return tiers[i].charge;
          }
          return 0;
        }

        /** Legacy dimension helper; active pricing passes framed UI directly to the tier lookup. */
        function getPlexiCharge(glassW, glassH) {
          if (isCanvasSubstrate() || !isUnderGlass()) return 0;
          const gw = parseFloat(glassW) || 0;
          const gh = parseFloat(glassH) || 0;
          if (gw <= 0 || gh <= 0) return 0;
          return getPlexiChargeFromUnitedInches(getUnitedInches(gw, gh));
        }

        function getAcrylicBrushstrokeCharge(ui) {
          return getHandFinishFitCharge(ui);
        }

        function normalizeProductTypeLabel(value) {
          return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        }

        function getProductTypesMetafield(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          return String(root?.dataset?.productTypes || '').trim();
        }

        function matchesExclusiveGicleeProductType(text) {
          const n = normalizeProductTypeLabel(text);
          if (!n) return false;
          const target = normalizeProductTypeLabel(PRICING_CONFIG.exclusiveGicleeProductType);
          if (n === target || n.indexOf(target) >= 0 || target.indexOf(n) >= 0) return true;
          return /exclusive\s+giclee/i.test(n) &&
            /deckled/i.test(n) &&
            /floating\s+on\s+a\s+mat/i.test(n);
        }

        function isExclusiveGicleeDeckledFloatingProduct(customizerRoot) {
          return matchesExclusiveGicleeProductType(getProductTypesMetafield(customizerRoot));
        }

        /** Size change vs product defaults / spec — no isKeepingOriginalSpecifications (avoids circular checks). */
        function hasArtworkDimensionCustomization(root) {
          syncArtworkSizeFromInputs(root);
          if (customizerState.lockOriginalArtworkSize) return false;

          const curW = parseFloat(customizerState.width);
          const curH = parseFloat(customizerState.height);
          if (!isFinite(curW) || curW <= 0 || !isFinite(curH) || curH <= 0) return false;

          const defaults = getDefaultArtworkDimensions(root);
          if (isFinite(defaults.width) && defaults.width > 0 &&
              isFinite(defaults.height) && defaults.height > 0) {
            if (roundSizeInches(curW) !== roundSizeInches(defaults.width) ||
                roundSizeInches(curH) !== roundSizeInches(defaults.height)) {
              return true;
            }
          }

          return false;
        }

        /** Any delta from original product spec (size, frame, mat, liner, substrate). */
        function hasExclusiveGicleeCustomizerChange(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (isKeepingAllOriginalSpecificationLocks()) return false;
          if (!productHasOriginalSpec(root)) return true;

          if (hasArtworkDimensionCustomization(root)) return true;

          const spec = readOriginalSpecFromCustomizer(root);
          if (!spec) return true;
          if (!matchesOriginalSubstrateAndTreatment(root, spec)) return true;
          if (!matchesOriginalMatSizes(spec)) return true;
          if (isCatalogPieceChangedFromOriginal(spec, 'frame', root)) return true;
          if (isCatalogPieceChangedFromOriginal(spec, 'topMat', root)) return true;
          if (isCatalogPieceChangedFromOriginal(spec, 'bottomMat', root)) return true;
          if (isCatalogPieceChangedFromOriginal(spec, 'liner', root)) return true;

          return false;
        }

        /** Universal $2 artwork float for Exclusive Giclee deckled products — customizer change only. */
        function shouldApplyExclusiveArtworkFloatCharge(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!isExclusiveGicleeDeckledFloatingProduct(root)) return false;
          if (isCanvasSubstrate()) return false;
          if (isArtworkOversize(customizerState.width, customizerState.height)) return false;
          syncArtworkSizeFromInputs(root);
          if (!hasExclusiveGicleeCustomizerChange(root)) return false;
          return true;
        }

        function getArtworkFloatCharge(ui, customizerRoot) {
          if (isCanvasSubstrate()) return 0;
          if (!customizerState.artworkFloat) return 0;
          const root = getActiveCustomizerRoot(customizerRoot);
          if (shouldApplyExclusiveArtworkFloatCharge(root)) {
            return PRICING_CONFIG.exclusiveGicleeArtworkFloatCharge;
          }
          const u = parseFloat(ui) || 0;
          const tiers = PRICING_CONFIG.artworkFloatTiers || [];
          for (let i = 0; i < tiers.length; i++) {
            if (u <= tiers[i].maxUi) return tiers[i].charge;
          }
          return 0;
        }

        function getFrameCostPerInch(frame) {
          if (!frame) return 0;
          const direct = parseFloat(frame.costPerInch || frame.cost_per_inch || frame.costPerIn || frame.costPer);
          if (isFinite(direct) && direct > 0) return direct;
          const byId = PRICING_CONFIG.frameCostPerInchById[String(frame.id || '').trim()];
          return isFinite(byId) ? byId : 0;
        }

        function getLinerCostPerInch(liner) {
          if (!liner) return 0;
          const direct = parseFloat(liner.costPerInch || liner.cost_per_inch || liner.costPerIn);
          if (isFinite(direct) && direct > 0) return direct;
          // Guide defaults: v100* = 0.70, p2663/p2655 = 0.79. Everything else uses fallback until mapped.
          const id = String(liner.id || '').toLowerCase();
          if (id.startsWith('v100')) return 0.70;
          if (id === 'p2663' || id === 'p2655') return 0.79;
          return PRICING_CONFIG.linerCostPerInchFallback;
        }

        function getMatTierForGlass(glassW, glassH) {
          const gw = parseFloat(glassW) || 0;
          const gh = parseFloat(glassH) || 0;
          const w = Math.max(gw, gh);
          const h = Math.min(gw, gh);
          const tiers = PRICING_CONFIG.matTiers;

          // Quarter
          if (w <= Math.max(tiers[0].maxW, tiers[0].maxH) && h <= Math.min(tiers[0].maxW, tiers[0].maxH)) return tiers[0];

          // Half (two orientation options)
          const half = tiers[1];
          const ok1 = (w <= Math.max(half.altMaxW, half.altMaxH) && h <= Math.min(half.altMaxW, half.altMaxH));
          const ok2 = (w <= Math.max(half.altMaxW2, half.altMaxH2) && h <= Math.min(half.altMaxW2, half.altMaxH2));
          if (ok1 || ok2) return half;

          // Full
          if (w <= Math.max(tiers[2].maxW, tiers[2].maxH) && h <= Math.min(tiers[2].maxW, tiers[2].maxH)) return tiers[2];

          // Oversize fallback
          return tiers[3];
        }

        function getNumMatsSelected() {
          const treatment = customizerState.treatment || '';
          const showMats = (treatment === 'Single Mat' || treatment === 'Double Mat') && !isCanvasSubstrate() && !!customizerState.topMat;
          if (!showMats) return 0;
          if (treatment === 'Double Mat' && customizerState.bottomMat) return 2;
          return 1;
        }

        /** Oversized bottom mat in double-mat oversize flow is printed — no sheet mat tier charge. */
        function hasPrintedOversizeBottomMat(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (isCanvasSubstrate()) return false;
          if (customizerState.treatment !== 'Double Mat') return false;
          if (!customizerState.bottomMat) return false;
          if (matIsPrinted('bottomMat', customizerState.bottomMat)) return true;
          if (!matIsOversized('bottomMat', customizerState.bottomMat)) return false;
          if (!matRequiresOversize('bottomMat', root)) return false;
          return true;
        }

        function matIsPrinted(stateKey, mat) {
          if (!mat) return false;
          if (mat.printedMats) return true;
          const label = String(mat.id || '') + ' — ' + String(mat.name || '');
          if (isPrintedMatLikeLabel(label)) return true;
          const catalogMat = findMatInCatalogs(stateKey, mat.id, mat.name);
          return !!(catalogMat && catalogMat.printedMats);
        }

        function hasSelectedPrintedMat(customizerRoot) {
          const treatment = String(customizerState.treatment || '').trim();
          if (treatment !== 'Single Mat' && treatment !== 'Double Mat') return false;
          if (customizerState.topMat && matIsPrinted('topMat', customizerState.topMat)) return true;
          if (treatment === 'Double Mat' && customizerState.bottomMat &&
              matIsPrinted('bottomMat', customizerState.bottomMat)) return true;
          return false;
        }

        function getChargeableMatCount(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (shouldApplyPrintedMatCost(root)) return 0;
          const total = getNumMatsSelected();
          if (total <= 0) return 0;
          return total;
        }

        function calculatePricing(customizerRoot, opts) {
          const root = customizerRoot || document.querySelector('.customizer') || document;
          const baseRetailArt = getBaseRetailArtPrice(root);
          syncCanvasOnlyPricingAddons();

          if (isKeepingOriginalSpecifications(root)) {
            const dimSets = getProductMetafieldDimensionSets(root);
            const glass = dimSets.glass || dimSets.artwork || { width: 0, height: 0 };
            const overall = computeFramedSizeInches(root);
            return {
              baseRetailArt,
              glass,
              overall,
              unitedInches: glass.width > 0 && glass.height > 0
                ? getUnitedInches(glass.width, glass.height)
                : 0,
              perimeter: glass.width > 0 && glass.height > 0
                ? getPerimeterInches(glass.width, glass.height)
                : 0,
              frameRate: 0,
              frameBasePrice: 0,
              linerRate: 0,
              linerBasePrice: 0,
              frameCost: 0,
              glassCharge: 0,
              plexiCharge: 0,
              foamCoreFitCharge: 0,
              acrylicBrushstrokeCharge: 0,
              artworkFloatCharge: 0,
              sizeChangeCharge: 0,
              customizationChangeFee: 0,
              deckleFloatCharge: 0,
              matTier: '',
              matCount: 0,
              matCost: 0,
              printedMatFee: 0,
              linerCost: 0,
              canvasFitCharge: 0,
              retailTotal: roundSizeInches(baseRetailArt),
              keepingOriginalSpec: true
            };
          }

          const glass = computeGlassSizeInches();
          const overall = computeFramedSizeInches(root);
          const ui = getUnitedInches(glass.width, glass.height);
          const perimeter = getPerimeterInches(glass.width, glass.height);

          const frameBase = parseFloat(customizerState.frame && (customizerState.frame.basePrice ?? customizerState.frame.base_price));
          const hasFrameBase = isFinite(frameBase) && frameBase > 0;
          const frameRate = hasFrameBase ? 0 : getFrameCostPerInch(customizerState.frame);
          const linerBase = parseFloat(customizerState.liner && (customizerState.liner.basePrice ?? customizerState.liner.base_price));
          const hasLinerBase = isFinite(linerBase) && linerBase > 0;
          const linerRate = customizerState.liner && !hasLinerBase ? getLinerCostPerInch(customizerState.liner) : 0;

          const frameCost = hasFrameBase ? frameBase : (frameRate > 0 ? perimeter * frameRate : 0);
          const canvas = isCanvasSubstrate();

          let plexiCharge = 0;
          let glassCharge = 0;
          let foamCoreFitCharge = 0;
          let acrylicBrushstrokeCharge = 0;
          let artworkFloatCharge = 0;
          let canvasFitCharge = 0;

          if (canvas) {
            canvasFitCharge = PRICING_CONFIG.canvasFitChargeFlat;
            acrylicBrushstrokeCharge = getAcrylicBrushstrokeCharge(ui);
          } else {
            const artworkOversize = isArtworkOversize(customizerState.width, customizerState.height);
            if (shouldChargePlexi(ui, glass.width, glass.height)) {
              plexiCharge = getPlexiChargeFromUnitedInches(ui);
            } else {
              glassCharge = getGlassCharge(ui);
            }
            foamCoreFitCharge = getFoamCoreFitCharge(ui);
            artworkFloatCharge = (customizerState.artworkFloat && !artworkOversize)
              ? getArtworkFloatCharge(ui, root)
              : 0;
          }

          const matCount = canvas ? 0 : getNumMatsSelected();
          const chargeableMatCount = canvas ? 0 : getChargeableMatCount(root);
          const matTier = chargeableMatCount > 0 ? getMatTierForGlass(glass.width, glass.height) : null;
          const matCost = matTier ? (matTier.cost * chargeableMatCount) : 0;
          const wholesaleForPrintedMat = shouldApplyPrintedMatCost(root)
            ? (baseRetailArt / FORMULA_PRICING_CONFIG.wholesaleArtDivisor)
            : null;
          const printedMatFee = getPrintedMatFee(root, glass, wholesaleForPrintedMat);

          const linerCost = hasLinerBase ? linerBase : (linerRate > 0 ? perimeter * linerRate : 0);
          const sizeChangeCharge = getSizeChangeCharge(root);
          const customizationChangeFee = sizeChangeCharge > 0 ? 0 : getCustomizationChangeFee(root);

          const retailTotal =
            baseRetailArt +
            frameCost +
            matCost +
            printedMatFee +
            glassCharge +
            plexiCharge +
            foamCoreFitCharge +
            acrylicBrushstrokeCharge +
            artworkFloatCharge +
            linerCost +
            canvasFitCharge +
            sizeChangeCharge +
            customizationChangeFee;

          let pricing = {
            baseRetailArt,
            glass,
            overall,
            unitedInches: ui,
            perimeter,
            frameRate,
            frameBasePrice: hasFrameBase ? frameBase : 0,
            linerRate,
            linerBasePrice: hasLinerBase ? linerBase : 0,
            frameCost: roundSizeInches(frameCost),
            glassCharge: roundSizeInches(glassCharge),
            plexiCharge: roundSizeInches(plexiCharge),
            foamCoreFitCharge: roundSizeInches(foamCoreFitCharge),
            acrylicBrushstrokeCharge: roundSizeInches(acrylicBrushstrokeCharge),
            artworkFloatCharge: roundSizeInches(artworkFloatCharge),
            sizeChangeCharge: roundSizeInches(sizeChangeCharge),
            customizationChangeFee: roundSizeInches(customizationChangeFee),
            deckleFloatCharge: 0,
            matTier: matTier ? matTier.name : '',
            matCount,
            matChargeCount: chargeableMatCount,
            matCost: roundSizeInches(matCost),
            printedMatFee: roundSizeInches(printedMatFee),
            linerCost: roundSizeInches(linerCost),
            canvasFitCharge: roundSizeInches(canvasFitCharge),
            retailTotal: roundSizeInches(retailTotal)
          };

          pricing = applyOriginalSpecPricingDeltas(pricing, root, ui);

          const step = (opts && opts.step != null) ? opts.step : getCurrentStep(root);
          const frameTab = (opts && opts.frameTab) || getActiveFrameTab(root);
          if (step == null) return pricing;
          return applyStepScopedPricing(pricing, root, ui, step, { frameTab: frameTab });
        }

        function updateCurrentSizesDisplay(customizer) {
          const root = customizer || document.querySelector('.customizer');
          if (!root) return;

          applyGlazingRules(root);

          let glassText = '—';

          // When `custom.image_size` is provided, treat it as the product's default artwork size.
          // In that case we want the "Current Glass/Outside" labels to reflect the computed sizes
          // derived from the current width/height (even when the rest of the spec is "original").
          const hasImageSizeDefault = !!getProductImageSizeDimensions(root);

          if (isKeepingOriginalSpecifications(root) && !hasImageSizeDefault) {
            const orig = getOriginalProductSizeDisplay(root);
            glassText = orig.glassText || '—';
          } else {
            const glass = computeGlassSizeInches();
            glassText = (glass.width > 0 && glass.height > 0)
              ? formatSizeDisplay(glass.width, glass.height)
              : '—';
          }

          let outsideW = 0;
          let outsideH = 0;
          if (typeof window.__fsGetTestOutsideOverride === 'function') {
            const testOutside = window.__fsGetTestOutsideOverride();
            if (testOutside && testOutside.width > 0 && testOutside.height > 0) {
              outsideW = testOutside.width;
              outsideH = testOutside.height;
            }
          }
          if (!(outsideW > 0 && outsideH > 0)) {
            const framed = computeFramedSizeInches(root);
            outsideW = framed.width;
            outsideH = framed.height;
          }
          const overallText = (outsideW > 0 && outsideH > 0)
            ? formatOutsideSizeDisplay(outsideW, outsideH)
            : '—';

          root.querySelectorAll('[data-glass-size]').forEach(function (el) {
            el.textContent = glassText;
          });
          root.querySelectorAll('[data-outside-size], [data-overall-size]').forEach(function (el) {
            el.textContent = overallText;
          });
          syncOutsideDimTestInputs(root);
        }

        function syncOutsideDimTestInputs(customizer, force) {
          const root = customizer || document.querySelector('.customizer');
          if (!root) return;
          const panel = root.querySelector('[data-outside-dim-test]');
          if (!panel) return;
          const wInput = panel.querySelector('[data-outside-test-w]');
          const hInput = panel.querySelector('[data-outside-test-h]');
          if (!wInput || !hInput) return;
          if (!force && (document.activeElement === wInput || document.activeElement === hInput)) return;
          let outsideW = 0;
          let outsideH = 0;
          if (typeof window.__fsGetTestOutsideOverride === 'function') {
            const testOutside = window.__fsGetTestOutsideOverride();
            if (testOutside && testOutside.width > 0 && testOutside.height > 0) {
              outsideW = testOutside.width;
              outsideH = testOutside.height;
            }
          }
          if (!(outsideW > 0 && outsideH > 0)) {
            const framed = computeFramedSizeInches(root);
            outsideW = framed.width;
            outsideH = framed.height;
          }
          if (outsideW > 0 && outsideH > 0) {
            wInput.value = outsideW;
            hInput.value = outsideH;
          }
        }

        function shouldKeepOriginalSpecMatsOnSizeChange(root) {
          if (!root || !productHasOriginalSpec(root)) return false;
          const spec = readOriginalSpecFromCustomizer(root);
          if (!spec || (!spec.topMatId && !spec.bottomMatId)) return false;
          const flags = getOriginalSpecChangeFlags(root);
          if (flags.topMatChanged || flags.bottomMatChanged) return false;
          return true;
        }

        function restoreOriginalSpecMatsAfterSizeChange(customizer) {
          if (!customizer || !shouldKeepOriginalSpecMatsOnSizeChange(customizer)) return;
          if (isCanvasSubstrate()) return;
          // Keep Original Mat: always restore metafield mats — never alert / unblock.
          if (customizerState.lockOriginalMat) {
            const spec = readOriginalSpecFromCustomizer(customizer);
            if (!spec) return;
            if (spec.topMatId) {
              applyMatSelection(customizer, 'topMat', spec.topMatId, spec.topMatSize, spec.topMatName);
            }
            if (spec.bottomMatId) {
              if (String(customizerState.treatment || '').trim() !== 'Double Mat') {
                setTreatmentOnCustomizer(customizer, 'Double Mat');
              }
              applyMatSelection(customizer, 'bottomMat', spec.bottomMatId, spec.bottomMatSize, spec.bottomMatName);
            }
            syncMatCardsFromState(customizer);
            return;
          }
          // Do not restore standard mats once glass is oversize — only oversized mats are valid.
          if (glassRequiresOversizeMats(customizer) && originalSpecMatsUnavailableAtSize(customizer)) {
            return;
          }
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return;

          if (spec.topMatId && !isOriginalSpecPieceApplied(spec, 'topMat')) {
            applyMatSelection(customizer, 'topMat', spec.topMatId, spec.topMatSize, spec.topMatName);
          }
          if (spec.bottomMatId && !isOriginalSpecPieceApplied(spec, 'bottomMat')) {
            if (String(customizerState.treatment || '').trim() !== 'Double Mat') {
              setTreatmentOnCustomizer(customizer, 'Double Mat');
            }
            applyMatSelection(customizer, 'bottomMat', spec.bottomMatId, spec.bottomMatSize, spec.bottomMatName);
          }
          syncMatCardsFromState(customizer);
        }

        function matIsOversized(stateKey, mat) {
          if (!mat) return false;
          if (mat.oversizedMat) return true;
          const catalogMat = findMatInCatalogs(stateKey, mat.id, mat.name);
          return !!(catalogMat && catalogMat.oversizedMat);
        }

        function shouldApplyPrintedMatCost(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (isCanvasSubstrate()) return false;
          if (hasPrintedMatSpecificationCharges(root)) return true;
          if (hasPrintedMatTreatmentFromProduct(root)) return true;
          if (hasSelectedPrintedMat(root)) return true;
          if (hasPrintedOversizeBottomMat(root)) return true;
          return false;
        }

        function computePrintedMatAreaCost(glassW, glassH, wholesaleArt) {
          if (!(glassW > 0 && glassH > 0)) return 0;
          const glassAreaCharge = roundFormulaMoney(
            glassW * glassH * PRICING_CONFIG.printedMatSpecificationRate
          );
          const wholesale = parseFloat(wholesaleArt);
          if (isFinite(wholesale)) {
            return roundFormulaMoney(glassAreaCharge - wholesale);
          }
          return glassAreaCharge;
        }

        function getPrintedMatFee(customizerRoot, glassOpts, wholesaleArt) {
          const root = customizerRoot || document.querySelector('.customizer');
          if (!shouldApplyPrintedMatCost(root)) return 0;

          let glassW = 0;
          let glassH = 0;
          if (glassOpts && isFinite(glassOpts.width) && isFinite(glassOpts.height)) {
            glassW = parseFloat(glassOpts.width) || 0;
            glassH = parseFloat(glassOpts.height) || 0;
          } else {
            const glass = computeGlassSizeInches();
            glassW = glass.width;
            glassH = glass.height;
          }

          return computePrintedMatAreaCost(glassW, glassH, wholesaleArt);
        }

        function updateBottomMatPrintedNote(customizer) {
          const panel = customizer?.querySelector('.step-panel[data-step="2"]');
          const note = panel?.querySelector('[data-bottom-mat-printed-note]');
          if (!note) return;
          const show = customizerState.treatment === 'Double Mat' &&
            shouldRestrictToOversizeMatsOnly(customizer, 'bottomMat');
          note.hidden = !show;
        }

        function enforceOversizeMatRules(customizerRoot) {
          const root = customizerRoot || document.querySelector('.customizer');
          if (!root) return;
          if (isApplyingOriginalSpec(root)) {
            updateBottomMatPrintedNote(root);
            return;
          }

          // Keep Original Mat / Substrate locks: no oversize alert, keep metafield mats.
          if (customizerState.lockOriginalMat || customizerState.lockOriginalSubstrateMat) {
            clearOversizeMatsFilter(root);
            updateBottomMatPrintedNote(root);
            return;
          }

          const glass = computeGlassSizeInches();
          const needsOversize = isGlassOver32x40(glass.width, glass.height);
          const underGlass = isUnderGlass();
          const needsRestrict = matRequiresOversize('topMat', root);
          const filterActive = root.dataset.oversizeMatsFilterActive === '1';
          const restrictGrid = needsRestrict && filterActive;
          const bottomNeedsRestrict = matRequiresOversize('bottomMat', root) && filterActive;
          const keepSpecMats = shouldKeepOriginalSpecMatsOnSizeChange(root);
          const prevNeeds = root.dataset.oversizeNeeds === '1';
          const prevUnder = root.dataset.oversizeUnderGlass !== '0';
          const prevRestrict = root.dataset.oversizeRestrictActive === '1';

          // Left oversize — restore full mat catalog.
          if (!needsRestrict) {
            delete root.dataset.oversizeMatsFilterActive;
            delete root.dataset.oversizeMatAlertShown;
          }

          root.dataset.oversizeNeeds = needsOversize ? '1' : '0';
          root.dataset.oversizeUnderGlass = underGlass ? '1' : '0';
          root.dataset.oversizeRestrictActive = restrictGrid ? '1' : '0';

          const customerChanged = shouldAlertOversizeMatRestriction(root);
          let changedSelection = false;

          // Notice first; oversized-only grid activates when the customer clicks OK.
          if (needsRestrict && customerChanged && !filterActive &&
              root.dataset.oversizeMatAlertShown !== '1') {
            root.dataset.oversizeMatAlertShown = '1';
            alertMatUnavailableAtSize(root);
          }

          if (restrictGrid && customerChanged) {
            if (customizerState.topMat && !matIsOversized('topMat', customizerState.topMat)) {
              customizerState.topMat = null;
              changedSelection = true;
            }
            // Bottom mat stays normal unless its own sheet size passes 32 × 40.
            if (bottomNeedsRestrict && customizerState.bottomMat &&
                !matIsOversized('bottomMat', customizerState.bottomMat)) {
              customizerState.bottomMat = null;
              changedSelection = true;
            }
          } else if (needsOversize && !underGlass && !keepSpecMats && customerChanged && filterActive) {
            if (customizerState.topMat) changedSelection = true;
            if (customizerState.bottomMat) changedSelection = true;
            customizerState.topMat = null;
            customizerState.bottomMat = null;
          }

          const restrictChanged = restrictGrid !== prevRestrict;
          const needsRerender = (needsOversize !== prevNeeds) || (underGlass !== prevUnder) ||
            changedSelection || restrictChanged;
          if (needsRerender) {
            const resetPage = restrictGrid && restrictChanged;
            if (typeof window.__matRefreshTop === 'function') {
              window.__matRefreshTop({ resetPage: resetPage });
            }
            if (typeof window.__matRefreshBottom === 'function') {
              window.__matRefreshBottom({ resetPage: resetPage });
            }
          }
          syncMatCardsFromState(root);
          updateMatPreview(root);
          if (changedSelection) updateMatStepLabel(root);
          updateBottomMatPrintedNote(root);
        }

        function formatSizeDisplay(w, h, opts) {
          if (!w || !h) return '—';
          const fw = roundSizeInches(w);
          const fh = roundSizeInches(h);
          const dec = opts && opts.decimals != null ? opts.decimals : null;
          if (dec != null) {
            return fw.toFixed(dec) + ' x ' + fh.toFixed(dec);
          }
          const wStr = Number.isInteger(fw) ? String(fw) : fw.toFixed(2).replace(/\.?0+$/, '');
          const hStr = Number.isInteger(fh) ? String(fh) : fh.toFixed(2).replace(/\.?0+$/, '');
          return wStr + ' x ' + hStr;
        }

        /** Outside (framed) dimensions — nearest whole inch for display. */
        function formatOutsideSizeDisplay(w, h) {
          const fw = parseFloat(w);
          const fh = parseFloat(h);
          if (!isFinite(fw) || !isFinite(fh) || fw <= 0 || fh <= 0) return '—';
          return Math.round(fw) + ' x ' + Math.round(fh);
        }

        function isNonProportionalSize(w, h, baseW, baseH) {
          if (!baseW || !baseH || !w || !h) return false;
          const ratioOrig = baseW / baseH;
          const ratioCur = w / h;
          return Math.abs(ratioCur - ratioOrig) > 0.02;
        }

        function stepNumberForNotesField(ta) {
          const panel = ta ? ta.closest('.step-panel') : null;
          const step = panel ? parseInt(panel.dataset.step, 10) : NaN;
          return isNaN(step) ? null : step;
        }

        /** Pull any typed notes from the DOM into state (never clobbers with empty). */
        function collectStepNotesFromDOM(customizer) {
          if (!customizer) return;
          customizer.querySelectorAll('[data-step-notes]').forEach(function (ta) {
            const val = String(ta.value || '').slice(0, 250);
            if (!val) return;
            const step = stepNumberForNotesField(ta);
            if (step == null) return;
            customizerState.stepNotes[step] = val;
          });
        }

        function initCustomizerNotes(customizer) {
          if (!customizer) return;
          syncNotesFields(customizer);
          if (customizer.dataset.notesBound === '1') return;
          customizer.dataset.notesBound = '1';
          // Delegated so notes are captured even if a step's markup is re-rendered.
          customizer.addEventListener('input', function (e) {
            const ta = e.target && e.target.closest
              ? e.target.closest('[data-step-notes]')
              : null;
            if (!ta) return;
            const step = stepNumberForNotesField(ta);
            if (step == null) return;
            customizerState.stepNotes[step] = String(ta.value || '').slice(0, 250);
            // Notes never affect price — only refresh summary cards when that step is open.
            if (getCurrentStep(customizer) === getSummaryStep()) {
              updateSummary(customizer);
            }
          });
        }

        function syncNotesFields(customizer) {
          if (!customizer) return;
          customizer.querySelectorAll('[data-step-notes]').forEach(function (ta) {
            const panel = ta.closest('.step-panel');
            const step = panel ? parseInt(panel.dataset.step, 10) : NaN;
            if (!isNaN(step) && document.activeElement !== ta) {
              ta.value = customizerState.stepNotes[step] || '';
            }
          });
        }

        function formatInchesBadge(inches) {
          const n = parseFloat(inches);
          if (isNaN(n) || n <= 0) return '';
          const rounded = Math.round(n * 100) / 100;
          return rounded + '"';
        }

        function formatMatSizeInchLabel(inches) {
          const n = parseFloat(inches);
          if (isNaN(n) || n <= 0) return '';
          const rounded = Math.round(n * 100) / 100;
          const num = Number.isInteger(rounded)
            ? String(rounded)
            : String(rounded).replace(/\.?0+$/, '');
          return num + 'Inch';
        }

        function summaryMatSubtitle(mat, sizeInches) {
          if (!mat || !mat.id) return 'None';
          const sizeLabel = formatMatSizeInchLabel(sizeInches);
          return sizeLabel ? mat.id + ' / ' + sizeLabel : mat.id;
        }

        if (typeof window !== 'undefined') {
          window.customizerState = customizerState;
          window.computeGlassSizeInches = computeGlassSizeInches;
          window.computeOverallSizeInches = computeOverallSizeInches;
          window.computeFramedSizeInches = computeFramedSizeInches;
          window.updateCurrentSizesDisplay = updateCurrentSizesDisplay;
          window.isKeepingOriginalSpecifications = isKeepingOriginalSpecifications;
          window.refreshCustomizerPricingAndDimensions = refreshCustomizerPricingAndDimensions;
          window.getGlazingMaterialLabel = getGlazingMaterialLabel;
        }

        function isColorMatchProduct() {
          if (typeof document === 'undefined') return false;
          const cz = document.querySelector('.customizer[data-has-color-match]');
          if (!cz) return false;
          return cz.dataset.hasColorMatch === 'true';
        }

        function isMonogramTypographyProduct() {
          if (typeof document === 'undefined') return false;
          const cz = document.querySelector('.customizer[data-has-monogram-typography]');
          if (!cz) return false;
          return cz.dataset.hasMonogramTypography === 'true';
        }

        /** Products that show step 4 (Other) — Color Match or Monogram & Typography */
        function isOtherStepProduct() {
          return isColorMatchProduct() || isMonogramTypographyProduct();
        }

        function getSummaryStep() {
          return isOtherStepProduct() ? 5 : 4;
        }

        function normalizeWizardSteps(customizer) {
          if (!customizer || customizer.dataset.stepsNormalized === '1') return;
          customizer.dataset.stepsNormalized = '1';

          const pillArtwork = customizer.querySelector('.step[data-step="4"]');
          const pillSummary = customizer.querySelector('.step[data-step="5"]');
          const panelArtwork = customizer.querySelector('.step-panel[data-step="4"]');
          const panelSummary = customizer.querySelector('.step-panel[data-step="5"]');

          if (pillArtwork) pillArtwork.dataset.role = pillArtwork.dataset.role || 'artwork-color';
          if (pillSummary) pillSummary.dataset.role = pillSummary.dataset.role || 'summary';
          if (panelArtwork) panelArtwork.dataset.role = panelArtwork.dataset.role || 'artwork-color';
          if (panelSummary) panelSummary.dataset.role = panelSummary.dataset.role || 'summary';

          // If Other step isn't available, make Summary the 4th step.
          if (!isOtherStepProduct()) {
            if (pillArtwork) {
              pillArtwork.dataset.step = '99';
              pillArtwork.classList.add('step--hidden');
              pillArtwork.setAttribute('aria-hidden', 'true');
              pillArtwork.tabIndex = -1;
            }
            if (panelArtwork) {
              panelArtwork.dataset.step = '99';
              panelArtwork.hidden = true;
              panelArtwork.classList.remove('active');
            }

            if (pillSummary) {
              pillSummary.dataset.step = '4';
              const strong = pillSummary.querySelector('strong');
              if (strong) strong.textContent = '4. Summary';
            }
            if (panelSummary) {
              panelSummary.dataset.step = '4';
              panelSummary.hidden = false;
            }

            const unlocked = parseInt(customizer.dataset.maxStepUnlocked || '1', 10) || 1;
            if (unlocked > 4) customizer.dataset.maxStepUnlocked = '4';
          }
        }

        /* =========================
          MAT DATA
        ========================== */
        /* Optional: define per-mat width overrides (in inches), keyed by mat id.
          Example:
          const MAT_WIDTH_BY_ID = { P103: 1.2, B321: 1.4 };
        */
        const MAT_WIDTH_BY_ID = {};

        const MAT_COLORS = [
          { id: 'B321', name: 'Smooth Black',   hex: '#1a1a1a', type: 'Standard', width: 1.2 },
          { id: 'B048', name: 'Sea Green',      hex: '#5f9070', type: 'Standard', width: 0.8 },
          { id: 'B501', name: 'Antique White',  hex: '#f4f0e6', type: 'Standard' },
          { id: 'B252', name: 'Cappuccino',     hex: '#8b6e52', type: 'Standard' },
          { id: 'B053', name: 'Pale Laurel',    hex: '#c8c9a2', type: 'Standard' },
          { id: 'B524', name: 'Bottle Blue',    hex: '#1e4d7b', type: 'Standard' },
          { id: 'B351', name: 'Oyster Ray',     hex: '#c9b99a', type: 'Standard' },
          { id: 'B856', name: 'Dill',           hex: '#6b7c44', type: 'Standard' },
          { id: 'B361', name: 'Pistachio',      hex: '#9ab87a', type: 'Standard' },
          { id: 'B480', name: 'Crop',           hex: '#e8d5b0', type: 'Standard' },
          { id: 'B460', name: 'Pure White',     hex: '#ffffff', type: 'Standard' },
          { id: 'B407', name: 'Cream Linen',    hex: '#f5f0e0', type: 'Standard' },
          { id: 'B452', name: 'Umber',          hex: '#7a5c3a', type: 'Standard' },
          { id: 'B453', name: 'Dark Olive',     hex: '#4a4a2e', type: 'Standard' },
          { id: 'B461', name: 'Splash',         hex: '#5b9bbf', type: 'Standard' },
          { id: 'B516', name: 'Clapboard',      hex: '#8a9ba8', type: 'Standard' },
          { id: 'B542', name: 'Blue',           hex: '#4a7aad', type: 'Standard' },
          { id: 'B555', name: 'Burnt Sienna',   hex: '#8b4513', type: 'Standard' },
          { id: 'B600', name: 'Canvas White',   hex: '#faf7f2', type: 'Standard' },
          { id: 'B609', name: 'Cloud',          hex: '#e8edf2', type: 'Standard' },
          { id: 'B608', name: 'White Silk',     hex: '#f8f6f0', type: 'Standard' },
          { id: 'B305', name: 'Off White Silk', hex: '#f2ede3', type: 'Standard' },
          { id: 'B18',  name: 'Pear',           hex: '#c5c96a', type: 'Standard' },
          { id: 'B522', name: 'Palm',           hex: '#7a9e6a', type: 'Standard' },
          { id: 'B535', name: 'Grey',           hex: '#8e8e8e', type: 'Standard' },
          { id: 'B834', name: 'Fountain Blue',  hex: '#7aa8c4', type: 'Standard' },
          { id: 'B591', name: 'Grey',           hex: '#a0a8b0', type: 'Standard' },
          { id: 'B884', name: 'Fountain Blue',  hex: '#5a8eb0', type: 'Standard' },
          { id: 'B922', name: 'Palm',           hex: '#6b9e5a', type: 'Standard' },
          { id: 'R503', name: 'White Silk',     hex: '#f5f1ea', type: 'Standard' },
          // Premium mats
          { id: 'P101', name: 'Velvet Black',   hex: '#0d0d0d', type: 'Premium' },
          { id: 'P102', name: 'Silk Ivory',     hex: '#f7f3ea', type: 'Premium' },
          { id: 'P103', name: 'Linen Natural',  hex: '#d4c9a8', type: 'Premium' },
          // Fabric mats — use full image swatches, no hex fill
          {
            id: 'FWM-ARR-TOP',
            name: 'Arrow Fabric',
            hex: null,
            type: 'Fabric',
            img: 'https://www.wendoverart.com/media/catalog/product/f/w/FWM-ARR-TOP_614fb811235f3.JPG'
          },
          {
            id: 'FWM-FAU-LTH-CHO',
            name: 'Faux Leather Chocolate',
            hex: null,
            type: 'Fabric',
            img: 'https://www.wendoverart.com/media/catalog/product/F/W/FWM-FAU-LTH-CHO_667d870687353.png'
          },
        ];

        /* Mat width (like frameW for frames/liners)
          - set per mat as `width` (preferred) or `matW`
          - falls back to 1.2 if not provided */
        (function seedMatWidths() {
          MAT_COLORS.forEach(function (m) {
            var byId = MAT_WIDTH_BY_ID && m && m.id ? MAT_WIDTH_BY_ID[m.id] : null;
            var w = parseFloat(
              m.width != null ? m.width :
              m.matW  != null ? m.matW  :
              byId    != null ? byId    :
              1.2
            );
            if (isNaN(w) || w <= 0) w = 1.2;
            m.matW = w;
          });
        })();

        function parseMatRoles(matFor) {
          return String(matFor ?? '')
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(Boolean);
        }

        function mapApiMatRowToLocal(row) {
          const idRaw = row.matCode != null && String(row.matCode).trim() !== ''
            ? String(row.matCode).trim()
            : (row.id != null ? String(row.id) : '');
          if (!idRaw) return null;
          const name = String(row.name || idRaw);
          const type = String(row.matType || 'Standard');
          const hex = row.colorCode ? String(row.colorCode) : null;
          const img = row.imageUrl ? String(row.imageUrl) : null;
          const matColorName = String(
            row.matColorName ||
            (row.matColor && row.matColor.colorName) ||
            ''
          ).trim();
          const oversizedMat = !!row.oversizedMat;
          const printedMats = !!row.printedMats;
          const pickFrom = row.pickFrom !== false;
          const byId = MAT_WIDTH_BY_ID && MAT_WIDTH_BY_ID[idRaw];
          let w = parseFloat(
            row.width != null ? row.width :
            row.matW != null ? row.matW :
            byId != null ? byId :
            1.2
          );
          if (isNaN(w) || w <= 0) w = 1.2;
          return {
            id: idRaw,
            name,
            hex,
            type,
            img: img || null,
            matW: w,
            oversizedMat,
            printedMats,
            pickFrom,
            matCode: idRaw,
            matColorName: matColorName || undefined
          };
        }

        /**
         * Loads mats from the app proxy (default `/apps/customizer/mats`).
         * Optional: set `data-mats-api="/your/path"` on `.customizer` to override the URL.
         * Populates `window.__customizerMatsApiTop` / `__customizerMatsApiBottom` when the
         * response includes active rows; otherwise leaves them null so built-in MAT_COLORS is used.
         */
        async function loadCustomizerMatsFromApi() {
          let url = '/apps/customizer/mats';
          const el = document.querySelector('.customizer[data-mats-api]');
          if (el && el.dataset.matsApi) {
            const u = String(el.dataset.matsApi).trim();
            if (u) url = u;
          }
          window.__customizerMatsApiTop = null;
          window.__customizerMatsApiBottom = null;
          try {
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const json = await res.json();
            const raw = Array.isArray(json?.data) ? json.data : [];
            const active = raw.filter(r => String(r?.status ?? 'active').toLowerCase() === 'active');
            active.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
            const tops = [];
            const bottoms = [];
            active.forEach(r => {
              const m = mapApiMatRowToLocal(r);
              if (!m) return;
              const roles = parseMatRoles(r.matFor);
              if (roles.includes('top')) tops.push(m);
              if (roles.includes('bottom')) bottoms.push(m);
            });
            if (tops.length) window.__customizerMatsApiTop = tops;
            if (bottoms.length) window.__customizerMatsApiBottom = bottoms;
            if (typeof window.__fsPreloadMatImages === 'function') {
              window.__fsPreloadMatImages(tops.concat(bottoms));
            }
            document.querySelectorAll('.customizer').forEach(function (c) {
              if (c.dataset.userModifiedSelections !== '1') scheduleApplyOriginalSpecifications(c);
            });
          } catch (e) {
            console.warn('[customizer] mats API:', e);
            document.querySelectorAll('.customizer').forEach(function (c) {
              if (c.dataset.userModifiedSelections !== '1') scheduleApplyOriginalSpecifications(c);
            });
          }
        }

        function parseApiInchNumber(raw) {
          if (raw == null) return NaN;
          const s = String(raw).replace(/,/g, '.').replace(/\s*inch(es)?\.?/gi, '').trim();
          const n = parseFloat(s);
          return isFinite(n) && n > 0 ? n : NaN;
        }

        function getFrameFinishNameFromApiRow(row) {
          const fin = row && row.frameFinish;
          if (fin && fin.name != null) return String(fin.name).trim();
          return '';
        }

        /** Calculation sizes from API (inches). Preview/calc use width only — not sizeHeight. */
        function getFrameCalcSizesFromApi(row) {
          const sizeWidthIn = parseApiInchNumber(row && row.sizeWidth);
          const sizeHeightIn = parseApiInchNumber(row && row.sizeHeight);
          let frameW = 1.2;
          if (isFinite(sizeWidthIn) && sizeWidthIn > 0) {
            frameW = sizeWidthIn;
          } else if (isFinite(sizeHeightIn) && sizeHeightIn > 0) {
            frameW = sizeHeightIn;
          }
          return {
            sizeWidthIn: isFinite(sizeWidthIn) && sizeWidthIn > 0 ? sizeWidthIn : null,
            sizeHeightIn: isFinite(sizeHeightIn) && sizeHeightIn > 0 ? sizeHeightIn : null,
            frameW
          };
        }

        /** Liner moulding width from API — width fields only (ignore linearHeight / sizeHeight). */
        function getLinerWidthInchesFromApiRow(row) {
          if (!row) return NaN;
          return parseApiInchNumber(row.linearWidth ?? row.sizeWidth ?? row.linearSize);
        }

        /** Display label only — uses frontendSizeWidth / frontendSizeHeight. */
        function formatFrameSizeLabelFromApi(row) {
          if (!row) return '';
          const fw = row.frontendSizeWidth != null ? String(row.frontendSizeWidth).trim() : '';
          const fh = row.frontendSizeHeight != null ? String(row.frontendSizeHeight).trim() : '';
          if (fw && fh) {
            if (fw === fh) return fw + '"';
            return fw + '" x ' + fh + '"';
          }
          if (fw) return fw + '"';
          if (fh) return fh + '"';
          const calc = getFrameCalcSizesFromApi(row);
          if (calc.sizeWidthIn != null && calc.sizeHeightIn != null) {
            if (calc.sizeWidthIn === calc.sizeHeightIn) return calc.sizeWidthIn + '"';
            return calc.sizeWidthIn + '" x ' + calc.sizeHeightIn + '"';
          }
          if (calc.sizeWidthIn != null) return calc.sizeWidthIn + '"';
          if (calc.sizeHeightIn != null) return calc.sizeHeightIn + '"';
          return '';
        }

        function mapApiFrameRowToLocal(row) {
          const id = row.frameCode != null && String(row.frameCode).trim() !== ''
            ? String(row.frameCode).trim()
            : (row.id != null ? String(row.id) : '');
          if (!id) return null;
          const name = String(row.frameTitle || id);
          const finish = getFrameFinishNameFromApiRow(row);
          const type = String(row.frameType || 'Standard');
          const size = formatFrameSizeLabelFromApi(row);
          const calc = getFrameCalcSizesFromApi(row);
          const frameW = calc.frameW;
          const thumb = row.frameImageUrl ? String(row.frameImageUrl) : '';
          const img = row.frameCanvasImageUrl || row.frameStraightImageUrl || row.frameImageUrl || '';
          const dim = row.frameDimensionImageUrl ? String(row.frameDimensionImageUrl) : '';
          const gallery = [];
          [row.frameDimensionImageUrl, row.frameCanvasImageUrl, row.frameStraightImageUrl, row.frameImageUrl]
            .forEach(function (u) {
              if (!u) return;
              const t = String(u).trim();
              if (t && gallery.indexOf(t) === -1) gallery.push(t);
            });
          return {
            id,
            name,
            finish,
            size,
            type,
            frameW,
            sizeWidthIn: calc.sizeWidthIn,
            sizeHeightIn: calc.sizeHeightIn,
            thumb,
            img: img ? String(img) : '',
            dim,
            gallery,
            basePrice: (function () {
              const n = parseFloat(row && (row.basePrice ?? row.base_price));
              return isFinite(n) && n >= 0 ? n : null;
            })(),
            /* false = discontinued — show only when selected / original spec, not newly pickable */
            pickFrom: row.pickFrom !== false && row.pick_from !== false,
            _api: row
          };
        }

        function getFrameFinishLabel(f) {
          if (!f) return '';
          return String(f.finish || '').trim();
        }

        /**
         * Loads frames from the app proxy (default `/apps/customizer/frames`).
         * Optional: set `data-frames-api="/your/path"` on `.customizer` to override the URL.
         */
        async function loadCustomizerFramesFromApi() {
          let url = '/apps/customizer/frames';
          const el = document.querySelector('.customizer[data-frames-api]');
          if (el && el.dataset.framesApi) {
            const u = String(el.dataset.framesApi).trim();
            if (u) url = u;
          }
          window.__customizerFramesApi = null;
          try {
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const json = await res.json();
            const raw = Array.isArray(json?.data) ? json.data : [];
            const active = raw.filter(r => String(r?.status ?? 'active').toLowerCase() === 'active');
            active.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
            const frames = active.map(mapApiFrameRowToLocal).filter(Boolean);
            if (frames.length) {
              window.__customizerFramesApi = frames;
              if (typeof window.__fsBootstrapFrames === 'function') {
                window.__fsBootstrapFrames(frames);
              }
              document.querySelectorAll('.customizer').forEach(function (c) {
                if (c.dataset.userModifiedSelections !== '1') scheduleApplyOriginalSpecifications(c);
              });
            }
          } catch (e) {
            console.warn('[customizer] frames API:', e);
          }
        }

        /**
         * Loads liners ("linear") from the app proxy (default `/apps/customizer/linear`).
         * Optional: set `data-linear-api="/your/path"` on `.customizer` to override the URL.
         * Populates `window.__customizerLinersApi` with active rows.
         */
        async function loadCustomizerLinersFromApi() {
          let url = '/apps/customizer/linear';
          const el = document.querySelector('.customizer[data-linear-api]');
          if (el && el.dataset.linearApi) {
            const u = String(el.dataset.linearApi).trim();
            if (u) url = u;
          }
          window.__customizerLinersApi = null;
          try {
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const json = await res.json();
            const raw = Array.isArray(json?.data) ? json.data : [];
            const active = raw.filter(r => String(r?.status ?? 'active').toLowerCase() === 'active');
            active.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
            const liners = active.map(function (row) {
              const id = row.linearCode != null && String(row.linearCode).trim() !== ''
                ? String(row.linearCode).trim()
                : (row.id != null ? String(row.id) : '');
              if (!id) return null;
              const name = String(row.linearName || id);
              const linerWidthIn = getLinerWidthInchesFromApiRow(row);
              const frameW = isFinite(linerWidthIn) && linerWidthIn > 0
                ? linerWidthIn
                : toInchesFromLegacyWidth(row.frameW || 1.0);
              const thumb = row.linearImage ? String(row.linearImage) : '';
              const img = row.linearCanvasImage || row.linearImage || '';
              const dim = row.linearDimensionImage ? String(row.linearDimensionImage) : '';
              const basePrice = (function () {
                const n = parseFloat(row && (row.basePrice ?? row.base_price));
                return isFinite(n) && n >= 0 ? n : null;
              })();
              return normalizeLinerDef({
                id,
                name,
                type: 'Liner',
                frameW,
                linearSize: isFinite(linerWidthIn) && linerWidthIn > 0 ? linerWidthIn : null,
                thumb,
                img: img ? String(img) : '',
                dim,
                basePrice,
                // keep original fields for debugging if needed
                _api: row
              });
            }).filter(Boolean);
            if (liners.length) {
              window.__customizerLinersApi = liners;
              document.querySelectorAll('.customizer').forEach(function (c) {
                if (c.dataset.userModifiedSelections !== '1') scheduleApplyOriginalSpecifications(c);
              });
            }
          } catch (e) {
            console.warn('[customizer] liner API:', e);
          }
        }

        function getActiveLiners() {
          const raw = window.__customizerLinersApi?.length ? window.__customizerLinersApi : LINER_COLORS;
          return raw.map(function (l) { return normalizeLinerDef(Object.assign({}, l)); });
        }

        function getActiveFrames() {
          const raw = window.__customizerFramesApi?.length ? window.__customizerFramesApi : FRAMES;
          return raw.map(function (f) { return normalizeFrameDef(Object.assign({}, f)); });
        }


        /*
          LINER_COLORS — each liner can have:
            hex      : fallback solid colour for the canvas strip
            frameImg : set automatically at runtime to a procedurally
                      generated liner moulding texture (data URL).
                      Any remote URL here is replaced on init.
            frameW   : liner strip thickness in inches (defaults to ~0.47)
            thumb    : card swatch image shown in the grid
        */
        const LINER_COLORS = [
          {
            id: 'M0596', name: 'White Linen',
          thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/linear-test.png?v=1778049712',
          img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/linear-test.png?v=1778049712',
            frameW: 2.2
          },
          {
            id: 'MC0110', name: 'White',
          thumb: 'https://www.wendoverart.com/media/catalog/product/m/c/MC2278_6143a275967e4.PNG',
          img:   'https://www.wendoverart.com/media/catalog/product/m/c/MC2278_6143a275967e4.PNG',
            frameW: 2.2
          },
          {
            id: 'MC1521', name: 'Natural Linen',
            hex: '#c8b89a',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/MC1521_thumb.png',
            frameW: 1.2
          },
          {
            id: 'L0020', name: 'Natural Linen',
            hex: '#d4c4a0',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/L0020_thumb.png',
            frameW: 1.2
          },
          {
            id: 'MC2278', name: 'Natural Linen',
            hex: '#c0aa88',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/MC2278_thumb.png',
            frameW: 1.2
          },
          {
            id: 'L0004', name: 'White',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/L0004_thumb.png',
            frameW: 1.2
          },
          {
            id: 'L0022SUB2', name: 'Natural Linen',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/L0022SUB2_thumb.png',
            frameW: 1.2
          },
          {
            id: 'M0596SUB2', name: 'White',
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/M0596SUB2_thumb.png',
            frameW: 1.2
          },
          // Colour-only liners — rendered as solid band with procedural texture
          { id: 'L-GOLD',    name: 'Gold',         hex: '#c9a84c', frameW: 1.0 },
          { id: 'L-SILVER',  name: 'Silver',       hex: '#a8a8a8', frameW: 1.0 },
          { id: 'L-BLACK',   name: 'Black',        hex: '#1a1a1a', frameW: 1.0 },
          { id: 'L-ANTIQUE', name: 'Antique Gold', hex: '#b8860b', frameW: 1.0 },
          { id: 'L-BRONZE',  name: 'Bronze',       hex: '#8b6914', frameW: 1.0 },
          { id: 'L-CREAM',   name: 'Cream',        hex: '#f5f0e0', frameW: 1.0 },
          { id: 'L-NAVY',    name: 'Navy',         hex: '#1e3a5f', frameW: 1.0 },
        ];

        function parseInchesFromSizeLabel(sizeStr) {
          if (sizeStr == null || typeof sizeStr !== 'string') return NaN;
          var t = sizeStr.replace(/"/g, '').replace(/\u201d/g, '').replace(/in\.?/gi, '').trim();
          var m = t.match(/(\d+(?:\.\d+)?)/);
          return m ? parseFloat(m[1]) : NaN;
        }

        function faceWidthBucketInches(inches) {
          if (!isFinite(inches) || inches <= 0) return '';
          if (inches < 1) return '0-1';
          if (inches < 2) return '1-2';
          if (inches < 3) return '2-3';
          return '3+';
        }

        /** Face width for preview/filter — API width only (sizeHeight is display-only). */
        function getFrameFaceWidthInches(f) {
          if (!f) return NaN;
          const sw = parseFloat(f.sizeWidthIn);
          if (isFinite(sw) && sw > 0) return sw;
          const sh = parseFloat(f.sizeHeightIn);
          if (isFinite(sh) && sh > 0) return sh;
          return toInchesFromLegacyWidth(f.frameW);
        }

        function faceWidthBucketFromFrameDef(f) {
          return faceWidthBucketInches(getFrameFaceWidthInches(f));
        }

        function faceWidthBucketFromLinerDef(mat) {
          return faceWidthBucketInches(toInchesFromLegacyWidth(mat && mat.frameW));
        }

        function normalizeFrameDef(f) {
          if (!f) return f;
          f.pickFrom = f.pickFrom !== false && f.pick_from !== false;
          if (f.sizeWidthIn != null || f.sizeHeightIn != null) {
            const inches = getFrameFaceWidthInches(f);
            if (isFinite(inches) && inches > 0) f.frameW = inches;
            return f;
          }
          const inches = getFrameFaceWidthInches(f);
          if (isFinite(inches) && inches > 0) f.frameW = inches;
          return f;
        }

        function normalizeLinerDef(liner) {
          if (!liner) return liner;
          liner.frameW = toInchesFromLegacyWidth(liner.frameW);
          return liner;
        }

        LINER_COLORS.forEach(normalizeLinerDef);

        const ARTWORK_COLORS = [
          { id: 'AC001', name: 'Original',   hex: null },
          { id: 'AC002', name: 'Warm Sepia', hex: '#a0785a' },
          { id: 'AC003', name: 'Cool Blue',  hex: '#5a7fa0' },
          { id: 'AC004', name: 'Grayscale',  hex: '#808080' },
          { id: 'AC005', name: 'Vintage',    hex: '#c8a86e' },
          { id: 'AC006', name: 'Vivid',      hex: '#e05050' },
        ];

        /* =========================
          FRAME DATA
        ========================== */
        const FRAMES = [
          {
            id: '84041', name: 'Golden Beaded', size: '1.5"', type: 'Ornate', frameW: 4.5,
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/84041.jpg?v=1777007596',
            img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/84041_piec.png?v=1777007596',
            dim:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/84041_dim.jpg?v=1777007596',
            /* Up to 4 tooltip slider images (order = slide order). Add a 4th URL when you have another angle. */
            gallery: [
              'https://iconicpineapple.com/cdn/shop/files/test-product-hover-img.webp?v=1768813854&width=480',
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/84041_dim.jpg?v=1777007596'
            ]
          },
          {
            id: '024-700', name: 'Golden Beaded', size: '1.5"', type: 'Ornate', frameW: 1.2,
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-700-1.png?v=1777901845',
            img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-700-1.png?v=1777901845',
            dim:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-700-1.png?v=1777901845',
            gallery: [
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-700-1.png?v=1777901845'
            ]
          },
          {
            id: 'H4287', name: 'Golden Beaded', size: '1.5"', type: 'Ornate', frameW: 1.2,
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/H4287-1.png?v=1777902179',
            img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/H4287-1.png?v=1777902179',
            dim:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/H4287-1.png?v=1777902179',
            gallery: [
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/H4287-1.png?v=1777902179'
            ]
          },
          {
            id: 'H428722', name: 'Golden Beaded', size: '1.5"', type: 'Ornate', frameW: 1.2,
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/B927.png?v=1778146265',
            img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/B927.png?v=1778146265',
            dim:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/028-800.png?v=1778144514',
            gallery: [
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/028-800.png?v=1778144514',
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/B927.png?v=1778146265'
            ]
          },
          {
            id: '24800', name: 'Golden Beaded', size: '1.5"', type: 'Ornate', frameW: 1.2,
            thumb: 'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-800.png?v=1778146415',
            img:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-800_0df07996-472e-4907-a28a-72b376cd95de.png?v=1778148264',
            dim:   'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-800_0df07996-472e-4907-a28a-72b376cd95de.png?v=1778148264',
            gallery: [
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-800.png?v=1778146415',
              'https://cdn.shopify.com/s/files/1/0660/2204/7931/files/024-800_0df07996-472e-4907-a28a-72b376cd95de.png?v=1778148264'
            ]
          },
        ];

        /* =========================================================
          PROCEDURAL LINER FRAME TEXTURE GENERATOR
          ─────────────────────────────────────────────────────────
          Generates a 6 × 80 px canvas that represents the cross-
          section (moulding profile) of a liner strip:

            y = 0   → inner edge (faces the artwork)  — lightest
            y = 80  → outer edge (meets outer frame)  — darkest

          _drawFrameStrip() tiles this canvas horizontally and
          applies miter clips at the corners, producing a proper
          secondary inner frame with no external image required.
        ========================================================= */
        function generateLinerCanvas(liner) {
          var TILE_W = 6;
          var TILE_H = 80;

          var c   = document.createElement('canvas');
          c.width  = TILE_W;
          c.height = TILE_H;
          var ctx = c.getContext('2d');

          /* Parse hex colour */
          function hexToRgb(hex) {
            var clean = (hex || '#b0956a').replace('#', '');
            if (clean.length === 3)
              clean = clean[0]+clean[0]+clean[1]+clean[1]+clean[2]+clean[2];
            return {
              r: parseInt(clean.slice(0, 2), 16),
              g: parseInt(clean.slice(2, 4), 16),
              b: parseInt(clean.slice(4, 6), 16)
            };
          }
          function clamp(v) { return Math.max(0, Math.min(255, Math.round(v))); }
          function rgb(r, g, b) { return 'rgb('+clamp(r)+','+clamp(g)+','+clamp(b)+')'; }
          function rgba(r, g, b, a) { return 'rgba('+clamp(r)+','+clamp(g)+','+clamp(b)+','+a.toFixed(3)+')'; }

          var col  = hexToRgb(liner.hex || '#b0956a');
          var r = col.r, g = col.g, b = col.b;
          var name = (liner.name || '').toLowerCase();

          var isGold   = name.includes('gold') || name.includes('bronze') || name.includes('antique');
          var isSilver = name.includes('silver');
          var isLinen  = name.includes('linen') || name.includes('natural');
          var isDark   = (r + g + b) < 200;

          /* ── Base gradient (bevel profile from inner → outer edge) ── */
          var grad = ctx.createLinearGradient(0, 0, 0, TILE_H);

          if (isGold) {
            grad.addColorStop(0.00, rgb(r+90, g+80, b+30));
            grad.addColorStop(0.08, rgb(r+120,g+105,b+45));
            grad.addColorStop(0.18, rgb(r+60, g+55, b+20));
            grad.addColorStop(0.36, rgb(r,    g,    b   ));
            grad.addColorStop(0.55, rgb(r-25, g-25, b-10));
            grad.addColorStop(0.70, rgb(r+15, g+12, b+5 ));
            grad.addColorStop(0.84, rgb(r-40, g-40, b-15));
            grad.addColorStop(1.00, rgb(r-65, g-60, b-25));
          } else if (isSilver) {
            grad.addColorStop(0.00, rgb(r+80, g+80, b+80));
            grad.addColorStop(0.10, rgb(r+100,g+100,b+100));
            grad.addColorStop(0.22, rgb(r+45, g+45, b+45));
            grad.addColorStop(0.42, rgb(r,    g,    b   ));
            grad.addColorStop(0.60, rgb(r-30, g-30, b-30));
            grad.addColorStop(0.75, rgb(r+10, g+10, b+10));
            grad.addColorStop(0.88, rgb(r-50, g-50, b-50));
            grad.addColorStop(1.00, rgb(r-70, g-70, b-70));
          } else if (isLinen) {
            grad.addColorStop(0.00, rgb(r+35, g+35, b+30));
            grad.addColorStop(0.15, rgb(r+15, g+15, b+12));
            grad.addColorStop(0.40, rgb(r,    g,    b   ));
            grad.addColorStop(0.65, rgb(r-12, g-12, b-10));
            grad.addColorStop(0.82, rgb(r-5,  g-5,  b-4 ));
            grad.addColorStop(1.00, rgb(r-35, g-35, b-28));
          } else if (isDark) {
            grad.addColorStop(0.00, rgb(r+55, g+55, b+55));
            grad.addColorStop(0.12, rgb(r+30, g+30, b+30));
            grad.addColorStop(0.30, rgb(r+8,  g+8,  b+8 ));
            grad.addColorStop(0.55, rgb(r,    g,    b   ));
            grad.addColorStop(0.75, rgb(r-8,  g-8,  b-8 ));
            grad.addColorStop(1.00, rgb(r-20, g-20, b-20));
          } else {
            grad.addColorStop(0.00, rgb(r+55, g+50, b+40));
            grad.addColorStop(0.12, rgb(r+28, g+25, b+20));
            grad.addColorStop(0.30, rgb(r,    g,    b   ));
            grad.addColorStop(0.52, rgb(r-18, g-16, b-12));
            grad.addColorStop(0.72, rgb(r-8,  g-8,  b-6 ));
            grad.addColorStop(0.87, rgb(r-38, g-34, b-26));
            grad.addColorStop(1.00, rgb(r-58, g-52, b-38));
          }

          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, TILE_W, TILE_H);

          /* ── Fabric / linen grain overlay ── */
          if (isLinen) {
            ctx.globalAlpha = 0.055;
            for (var yi = 0; yi < TILE_H; yi += 2) {
              ctx.fillStyle = (yi % 4 === 0)
                ? 'rgba(255,255,255,0.9)'
                : 'rgba(0,0,0,0.6)';
              ctx.fillRect(0, yi, TILE_W, 1);
            }
            ctx.globalAlpha = 1;
          }

          /* ── Specular highlight at inner edge ── */
          ctx.strokeStyle = rgba(255, 255, 255, (isGold || isSilver) ? 0.55 : 0.38);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(0, 0.5);
          ctx.lineTo(TILE_W, 0.5);
          ctx.stroke();

          /* ── Shadow line at outer edge ── */
          ctx.strokeStyle = rgba(0, 0, 0, isDark ? 0.25 : 0.32);
          ctx.beginPath();
          ctx.moveTo(0, TILE_H - 0.5);
          ctx.lineTo(TILE_W, TILE_H - 0.5);
          ctx.stroke();

          /* ── Thin sub-highlight 2px in from inner edge ── */
          ctx.strokeStyle = rgba(0, 0, 0, 0.10);
          ctx.beginPath();
          ctx.moveTo(0, 2.5);
          ctx.lineTo(TILE_W, 2.5);
          ctx.stroke();

          return c;
        }

        /* =========================================================
          PRE-GENERATE ALL LINER TEXTURES
          ─────────────────────────────────────────────────────────
          Runs immediately (synchronously) after LINER_COLORS is
          defined.  Each liner gets:
            • liner._texCanvas  — the raw canvas element
            • liner.frameImg    — a unique cache key string
              (we use '__liner_<id>' so the IIFE's linerImgCache
                can be pre-populated with the canvas itself —
                no async fetch required at all)
        ========================================================= */
        (function seedLinerTextures() {
          LINER_COLORS.forEach(function (liner) {
            try {
              /* If a liner provides a real moulding image, use it. Otherwise generate
                the procedural texture + cache key used by drawLinerBand(). */
              if (liner.img) {
                liner.frameImg = liner.img;
                delete liner._texCanvas;
              } else {
                liner._texCanvas = generateLinerCanvas(liner);
                liner.frameImg   = '__liner_' + liner.id;
              }
            } catch (e) {
              delete liner.frameImg;
            }
          });
        })();

        /* =========================================================
          TREATMENT → ENABLED STEPS
        ========================================================= */
        function getEnabledSteps() {
          const t = customizerState.treatment;
          const steps = [1, 3, getSummaryStep()];
          if ((t === 'Double Mat' || t === 'Single Mat') && !isCanvasSubstrate()) steps.push(2);
          if (isOtherStepProduct()) steps.push(4);
          return steps.sort((a, b) => a - b);
        }

        function isStepEnabled(step) {
          return getEnabledSteps().includes(step);
        }

        function isSummaryStepNum(step) {
          return step === getSummaryStep();
        }

        function isArtworkStepNum(step) {
          return isOtherStepProduct() && step === 4;
        }

        function requiresMatSelection() {
          if (isCanvasSubstrate() || isNoMatTreatment()) return false;
          if (isDeckleSubstrate()) return true;
          const t = customizerState.treatment;
          return t === 'Single Mat' || t === 'Double Mat';
        }

        function isMatStepFilled() {
          if (!requiresMatSelection()) return true;
          if (!customizerState.topMat) return false;
          if (customizerState.treatment === 'Double Mat' && !customizerState.bottomMat) return false;
          return true;
        }

        function isOptionalConfigStep(step) {
          return step === 3 || isArtworkStepNum(step);
        }

        function isStepFilled(customizer, step) {
          if (!step || isNaN(step)) return false;
          if (isSummaryStepNum(step)) return false;
          if (isArtworkStepNum(step)) {
            if (isMonogramTypographyProduct()) {
              return !!String(customizerState.customerNotes || '').trim();
            }
            return !!customizerState.artworkColor;
          }

          switch (step) {
            case 1:
              return !!(
                customizerState.substrate &&
                customizerState.treatment &&
                customizerState.width > 0 &&
                customizerState.height > 0
              );
            case 2:
              return isMatStepFilled();
            case 3:
              return !!(customizerState.frame || customizerState.liner);
            default:
              return false;
          }
        }

        function getClearedSteps(customizer) {
          if (!customizer) return [];
          try {
            const raw = JSON.parse(customizer.dataset.clearedSteps || '[]');
            return Array.isArray(raw) ? raw.map(Number).filter(s => !isNaN(s)) : [];
          } catch {
            return [];
          }
        }

        function markStepCleared(customizer, step) {
          const steps = getClearedSteps(customizer);
          if (!steps.includes(step)) steps.push(step);
          customizer.dataset.clearedSteps = JSON.stringify(steps);
        }

        function unmarkStepCleared(customizer, step) {
          if (!customizer) return;
          const steps = getClearedSteps(customizer).filter(s => s !== step);
          customizer.dataset.clearedSteps = JSON.stringify(steps);
        }

        function isSummaryPill(pill, stepNum) {
          return isSummaryStepNum(stepNum) || pill?.dataset?.role === 'summary';
        }

        function getMaxStepVisited(customizer) {
          return parseInt(customizer.dataset.maxStepVisited || '1', 10) || 1;
        }

        function setMaxStepVisited(customizer, step) {
          if (!customizer) return;
          const prev = getMaxStepVisited(customizer);
          customizer.dataset.maxStepVisited = String(Math.max(prev, step));
        }

        function getMaxVisitCapAfterClear(customizer) {
          const ordered = getEnabledSteps().filter(s => !isSummaryStepNum(s));
          const cleared = getClearedSteps(customizer);
          let lastFilled = 1;
          for (const step of ordered) {
            if (isStepFilled(customizer, step)) lastFilled = step;
            else break;
          }
          const idx = ordered.indexOf(lastFilled);
          if (idx >= 0 && idx < ordered.length - 1) {
            const next = ordered[idx + 1];
            if (!cleared.includes(next)) return next;
          }
          return lastFilled;
        }

        function isStepClickable(customizer, step) {
          if (!customizer || !isStepEnabled(step)) return false;

          if (isSummaryStepNum(step)) return true;

          const ordered = getEnabledSteps().filter(s => !isSummaryStepNum(s));
          const priors = ordered.filter(s => s < step);
          if (!priors.every(s => isStepFilled(customizer, s) || isOptionalConfigStep(s))) return false;

          if (isStepFilled(customizer, step)) return true;

          const cur = getCurrentStep(customizer);
          if (cur === step) return true;

          if (getClearedSteps(customizer).includes(step)) return false;

          return step <= getMaxStepVisited(customizer);
        }

        function syncStepUnlockState(customizer) {
          if (!customizer) return;
          let max = 1;
          customizer.querySelectorAll('.step[data-step]').forEach(pill => {
            const s = parseInt(pill.dataset.step, 10);
            if (!isNaN(s) && isStepClickable(customizer, s)) max = Math.max(max, s);
          });
          customizer.dataset.maxStepUnlocked = String(max);
        }

        function afterStepClear(customizer, clearedStep) {
          if (!customizer || !clearedStep) return;
          if (!isStepFilled(customizer, clearedStep)) {
            markStepCleared(customizer, clearedStep);
            customizer.dataset.maxStepVisited = String(
              Math.min(getMaxStepVisited(customizer), getMaxVisitCapAfterClear(customizer))
            );
          }
          refreshStepPillStates(customizer);
        }

        function refreshStepPillStates(customizer) {
          if (!customizer) return;
          getClearedSteps(customizer).forEach(s => {
            if (isStepFilled(customizer, s)) unmarkStepCleared(customizer, s);
          });
          syncStepUnlockState(customizer);
          const isCanvas = isCanvasSubstrate();
          customizer.querySelectorAll('.step[data-step]').forEach(pill => {
            const s = parseInt(pill.dataset.step, 10);
            const hidden = pill.classList.contains('step--hidden') || pill.getAttribute('aria-hidden') === 'true' || s === 99;
            const isSummary = isSummaryPill(pill, s);
            const disabled = !hidden && !isStepClickable(customizer, s);
            const matUnavailable = s === 2 && isCanvas;
            pill.classList.toggle('step--disabled', disabled);
            pill.toggleAttribute('aria-disabled', disabled);
            pill.classList.toggle('step--mat-unavailable', matUnavailable);
            pill.classList.toggle('step--summary', isSummary);
            pill.classList.toggle(
              'completed',
              !hidden && !matUnavailable && !isSummary && isStepFilled(customizer, s)
            );
          });
          updateMatStepLabel(customizer);
          updatePanelNavState(customizer);
        }

        /* =========================
          UTILITY
        ========================== */
        function getSavedCropData(imageEl) {
          if (!imageEl?.dataset?.cropData) return null;
          try { return JSON.parse(imageEl.dataset.cropData); } catch { return null; }
        }

        function syncCustomizerPopupState() {
          if (!document.body) return;
          const hasOpenPopup = document.querySelector('.image-customizer-popup:not([hidden])');
          document.body.classList.toggle('image-customizer-popup-open', !!hasOpenPopup);
          document.body.style.overflow = hasOpenPopup ? 'hidden' : '';
          if (!hasOpenPopup) hideArtworkHoverPreview();
        }

        function hideArtworkHoverPreview() {
          document.getElementById('cz-art-hover-popup')?.classList.remove('cz-art-hover-popup--visible');
        }

        function isElementVisuallyHidden(el) {
          if (!el) return true;
          if (el.hidden) return true;
          const st = getComputedStyle(el);
          if (st.display === 'none' || st.visibility === 'hidden' || parseFloat(st.opacity) === 0) return true;
          const r = el.getBoundingClientRect();
          return r.width < 2 || r.height < 2;
        }

        function getArtHoverPreviewCanvas(col) {
          if (!col) return null;
          const preview = col.querySelector('.customizer-preview-canvas');
          if (preview && preview.width > 0 && preview.height > 0 && !isElementVisuallyHidden(preview)) return preview;
          const fs = col.querySelector('#fs-canvas');
          if (fs && fs.width > 0 && fs.height > 0 && col.contains(fs) && !isElementVisuallyHidden(fs)) return fs;
          return null;
        }

        function fillArtHoverPreviewImg(col, imgEl) {
          if (!col || !imgEl) return false;
          const canvas = getArtHoverPreviewCanvas(col);
          if (canvas) {
            try {
              const url = canvas.toDataURL('image/png');
              if (url && url.length > 64) {
                imgEl.src = url;
                return true;
              }
            } catch (_) { /* tainted or unsupported */ }
          }
          const im = col.querySelector('img.editable-image');
          if (im) {
            const src =
              (!isElementVisuallyHidden(im) && im.src
                ? (im.dataset.originalSrc || im.dataset.framedSrc || im.currentSrc || im.src)
                : null) ||
              im.dataset.framedSrc ||
              im.dataset.originalSrc ||
              im.src;
            if (src) {
              imgEl.src = src;
              return true;
            }
          }
          imgEl.removeAttribute('src');
          return false;
        }

        function positionArtHoverPopup(popup, anchorRect) {
          const cx = anchorRect.left + anchorRect.width / 2;
          const cy = anchorRect.top + anchorRect.height / 2;
          const pad = 16;
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const maxW = Math.min(720, vw - pad * 2);
          const maxH = Math.min(900, vh - pad * 2);
          popup.style.left = Math.round(Math.min(Math.max(cx, pad + maxW / 2), vw - pad - maxW / 2)) + 'px';
          popup.style.top = Math.round(Math.min(Math.max(cy, pad + maxH / 2), vh - pad - maxH / 2)) + 'px';
        }

        function isArtHoverSurface(el, customizer) {
          if (!(el instanceof Element) || !customizer.contains(el)) return false;
          const col = el.closest('.customizer-image-col');
          if (!col) return false;
          if (el.matches('img.editable-image')) return true;
          if (el.tagName !== 'CANVAS') return false;
          if (el.id === 'fs-canvas') return true;
          if (el.classList.contains('customizer-preview-canvas')) return true;
          return false;
        }

        function initArtworkHoverPreview(customizer) {
          if (!customizer || customizer.dataset.artHoverInit === '1') return;
          customizer.dataset.artHoverInit = '1';

          let hideTimer = null;
          let popupEl = null;
          let imgEl = null;

          function ensurePopup() {
            popupEl = document.getElementById('cz-art-hover-popup');
            if (!popupEl) {
              popupEl = document.createElement('div');
              popupEl.id = 'cz-art-hover-popup';
              popupEl.className = 'cz-art-hover-popup';
              popupEl.setAttribute('role', 'tooltip');
              const img = document.createElement('img');
              img.className = 'cz-art-hover-popup__img';
              img.alt = 'Enlarged artwork preview';
              popupEl.appendChild(img);
              document.body.appendChild(popupEl);
              popupEl.addEventListener('mouseenter', cancelHide);
              popupEl.addEventListener('mouseleave', scheduleHide);
              if (!window.__czArtHoverGlobalBound) {
                window.__czArtHoverGlobalBound = true;
                const hide = () => hideArtworkHoverPreview();
                window.addEventListener('scroll', hide, true);
                window.addEventListener('resize', hide);
              }
            }
            imgEl = popupEl.querySelector('img');
          }

          function cancelHide() {
            if (hideTimer) {
              clearTimeout(hideTimer);
              hideTimer = null;
            }
          }

          function scheduleHide() {
            cancelHide();
            hideTimer = setTimeout(() => hideArtworkHoverPreview(), 160);
          }

          /* Only the artwork image and preview canvases — not the whole .customizer-image-col */
          customizer.addEventListener('mouseover', function (e) {
            const t = e.target;
            if (!isArtHoverSurface(t, customizer)) return;
            cancelHide();
            ensurePopup();
            const col = t.closest('.customizer-image-col');
            if (!fillArtHoverPreviewImg(col, imgEl)) return;
            positionArtHoverPopup(popupEl, t.getBoundingClientRect());
            popupEl.classList.add('cz-art-hover-popup--visible');
          });

          customizer.addEventListener('mouseout', function (e) {
            const t = e.target;
            if (!isArtHoverSurface(t, customizer)) return;
            const rel = e.relatedTarget;
            if (rel instanceof Element) {
              if (isArtHoverSurface(rel, customizer)) return;
              const pop = document.getElementById('cz-art-hover-popup');
              if (pop && pop.contains(rel)) return;
            }
            scheduleHide();
          });
        }

        function getCustomizerImages(customizer) {
          if (!customizer) return [];
          return Array.from(customizer.querySelectorAll('.editable-image'));
        }

        function getSharedCropData(customizer) {
          const state = customizerCropState.get(customizer);
          if (state?.cropData) return state.cropData;
          const images = getCustomizerImages(customizer);
          for (const image of images) {
            const data = getSavedCropData(image);
            if (data) return data;
          }
          return null;
        }

        function updateStepLabel(stepNum, value) {
          const text = value || 'None';
          document.querySelectorAll('.customizer').forEach(function (c) {
            const el = c.querySelector('#step' + stepNum + '_insides');
            if (el) el.textContent = text;
          });
        }

        const CANVAS_MAT_TREATMENT = 'None';
        const NO_MAT_TREATMENT = 'No Mat';
        const MAT_TREATMENT_OPTIONS = [NO_MAT_TREATMENT, 'Single Mat', 'Double Mat'];
        const ALL_MAT_TREATMENT_OPTIONS = [CANVAS_MAT_TREATMENT, ...MAT_TREATMENT_OPTIONS];
        const BOTTOM_MAT_SIZE_OPTIONS = [0.25, 0.375, 0.5, 0.75, 1];
        const TOP_MAT_SIZE_MAX = 6;
        const TOP_MAT_SIZE_OPTIONS = [
          0.25, 0.5, 0.75,
          1, 1.25, 1.5, 1.75,
          2, 2.25, 2.5, 2.75,
          3, 3.25, 3.5,3.75,
          4, 4.25, 4.5,4.75,
          5, 5.25, 5.5,5.75,
          6
        ];

        function isNoMatTreatment(treatment) {
          const t = String(treatment != null ? treatment : customizerState.treatment || '').trim();
          const low = t.toLowerCase();
          return !t || low === 'none' || low === 'no mat';
        }

        function syncTreatmentSelectOptions(treatmentSelect, isCanvas) {
          if (!treatmentSelect) return;
          Array.from(treatmentSelect.options).forEach(o => {
            const v = String(o.value || o.textContent || '').trim();
            const isCanvasOnly = o.dataset.canvasOnly != null || v === CANVAS_MAT_TREATMENT;
            if (isCanvasOnly) {
              o.hidden = !isCanvas;
              o.disabled = !isCanvas;
              return;
            }
            if (MAT_TREATMENT_OPTIONS.includes(v) || v === NO_MAT_TREATMENT) {
              o.hidden = !!isCanvas;
              o.disabled = !!isCanvas;
            }
          });
        }

        function updateMatStepHints(customizer) {
          const root = customizer || document.querySelector('.customizer');
          if (!root) return;
          const deckle = isDeckleSubstrate();
          const topSub = root.querySelector('[data-cz-tab-panel="topmat"] .fs-sub');
          if (topSub) {
            topSub.textContent = deckle
              ? 'Deckle substrates require at least one mat.'
              : 'Optional top mat selection.';
          }
        }

        const MAT_UNAVAILABLE_CANVAS_MSG = 'Mat is not available for canvas.';

        function updateMatStepLabel(customizer) {
          const targets = customizer ? [customizer] : document.querySelectorAll('.customizer');
          targets.forEach(c => {
            const pill = c.querySelector('.step[data-step="2"]');
            const el = c.querySelector('#step2_insides');
            const isCanvas = isCanvasSubstrate();

            if (pill) {
              pill.classList.toggle('step--mat-unavailable', isCanvas);
            }
            if (el) {
              el.textContent = isCanvas
                ? MAT_UNAVAILABLE_CANVAS_MSG
                : (customizerState.topMat ? customizerState.topMat.id : 'Top: None') +
                  ' / ' +
                  (customizerState.bottomMat ? customizerState.bottomMat.id : 'Bottom: None');
            }
          });
        }

        function updateFrameLinerStepLabel() {
          const frame = customizerState.frame ? customizerState.frame.id : 'Frame: None';
          const liner = customizerState.liner ? customizerState.liner.id : 'Liner: None';
          updateStepLabel(3, frame + ' / ' + liner);
        }

        function updateSubstrateStepLabel() {
          const w = customizerState.width;
          const h = customizerState.height;
          const sizePart = (w && h) ? (' / ' + formatSizeInches(w, h)) : '';
          updateStepLabel(1, customizerState.substrate + ' / ' + customizerState.treatment + sizePart);
        }

        function showEmptyState(grid, type) {
          if (!grid) return;
          const labels = {
            frame: 'No Frame Available',
            mat: 'No Mat Available',
            topMat: 'No Mat Available',
            bottomMat: 'No Mat Available',
            liner: 'No Liner Available',
          };
          const msg = labels[type] || 'No Results Found';
          grid.innerHTML = `
            <div class="grid-empty-state">
              <img src="https://cdn.shopify.com/s/files/1/0660/2204/7931/files/iconoir_emoji-sad.svg?v=1777617074" alt="No results" class="grid-empty-icon"/>
              <span>${msg}</span>
            </div>
          `;
        }


        /* =========================================================
          updateMatPreview
        ========================================================= */
        function updateMatPreview(customizer) {
          if (!customizer) return;
          customizer.querySelectorAll('.mat-preview-canvas').forEach(c => c.remove());
          customizer.querySelectorAll('.editable-image').forEach(img => {
            img.style.opacity = '';
          });
          if (typeof window.__fsRedrawWithMats === 'function') {
            window.__fsRedrawWithMats();
          }
        }

        function hidePreviewCanvases(customizer) {
          if (!customizer) return;
          customizer.querySelectorAll('.customizer-preview-canvas').forEach(canvas => {
            canvas.hidden = true;
            const img = canvas.previousElementSibling;
            if (img && img.tagName === 'IMG') img.hidden = false;
          });
        }

        function ensurePreviewCanvas(image) {
          if (!image || image.tagName !== 'IMG') return null;
          const next = image.nextElementSibling;
          if (next && next.tagName === 'CANVAS' && next.classList.contains('customizer-preview-canvas')) {
            return next;
          }
          const canvas = document.createElement('canvas');
          canvas.className = 'customizer-preview-canvas';
          canvas.hidden = true;
          canvas.style.display = 'block';
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.margin = '0';
          image.insertAdjacentElement('afterend', canvas);
          return canvas;
        }

        function getPreviewCanvasMaxWidth(wrapper) {
          return wrapper && wrapper.clientWidth > 0 ? wrapper.clientWidth : 400;
        }

        function copyFsCanvasToPreviewCanvases(customizer) {
          if (!customizer) return false;
          const fsCanvas = document.getElementById('fs-canvas');
          if (!fsCanvas || fsCanvas.width < 1 || fsCanvas.height < 1) return false;
          const images = Array.from(customizer.querySelectorAll('.editable-image'));
          let copied = false;
          images.forEach(image => {
            const canvas = ensurePreviewCanvas(image);
            if (!canvas) return;
            const wrapper = image.closest('.image-preview-wrapper');
            const maxW = getPreviewCanvasMaxWidth(wrapper);
            const srcW = fsCanvas.width;
            const srcH = fsCanvas.height;
            const scale = Math.min(1, maxW / srcW);
            const dw = Math.max(1, Math.round(srcW * scale));
            const dh = Math.max(1, Math.round(srcH * scale));
            canvas.width = dw;
            canvas.height = dh;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, dw, dh);
            ctx.drawImage(fsCanvas, 0, 0, dw, dh);
            canvas.hidden = false;
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
            canvas.style.maxWidth = '100%';
            image.hidden = true;
            copied = true;
          });
          return copied;
        }

        function redrawFrameCanvasWithMats() {
          if (typeof window.__fsRedrawWithMats === 'function') {
            window.__fsRedrawWithMats();
          }
        }

        /* =========================================================
          COMBINED MAT TAB — BOTTOM MAT AVAILABILITY
        ========================================================= */
        function updateBottomMatStepAvailability(customizer) {
          const panel = customizer.querySelector('.step-panel[data-step="2"]');
          if (!panel) return;

          const isDoublemat = customizerState.treatment === 'Double Mat';
          const bottomSection = panel.querySelector('.combined-bottom-mat-section');
          if (!bottomSection) return;
          const contentCol = bottomSection;

          let disabledMsg = contentCol.querySelector('.bottom-mat-disabled-msg');

          /* Bottom Mat tab stays clickable regardless of treatment — it just
            shows an info message inside the panel when treatment ≠ Double Mat. */
          const bottomTabBtn = panel.querySelector('.cz-tab[data-cz-tab="bottommat"]');
          if (bottomTabBtn) {
            bottomTabBtn.classList.remove('cz-tab--disabled');
            bottomTabBtn.removeAttribute('aria-disabled');
          }

          if (!isDoublemat) {
            if (!disabledMsg) {
              disabledMsg = document.createElement('div');
              disabledMsg.className = 'bottom-mat-disabled-msg';
              disabledMsg.innerHTML = `
                <div class="mat-disabled-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dfce50" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4M12 16h.01" stroke-linecap="round"/>
                  </svg>
                </div>
                <h3 class="mat-disabled-title">Bottom Mat Not Available</h3>
                <p class="mat-disabled-body">
                  Bottom Mat is available only when you select <strong>Double Mat</strong> treatment from <strong>Step 1</strong>.
                </p>
                <button type="button" class="mat-go-step1-btn">← Go to Step 1 &amp; Change Treatment</button>
              `;
              disabledMsg.querySelector('.mat-go-step1-btn').addEventListener('click', () => {
                goToStep(customizer, 1);
              });
              const matBody = contentCol.querySelector('.combined-mat-body');
              contentCol.insertBefore(disabledMsg, matBody || contentCol.firstChild);
            }
            disabledMsg.style.display = 'flex';
            contentCol.querySelectorAll('.combined-mat-body').forEach(el => { el.style.display = 'none'; });

          } else {
            if (disabledMsg) disabledMsg.style.display = 'none';
            contentCol.querySelectorAll('.combined-mat-body').forEach(el => { el.style.display = ''; });
          }
          updateBottomMatPrintedNote(customizer);
        }

        /* =========================================================
          CANVAS — MAT STEP UNAVAILABLE
        ========================================================= */
        function bindCropButton(customizer) {
          if (!customizer || customizer.dataset.cropBtnBound === '1') return;
          const cropBtn = customizer.querySelector('.step-panel[data-step="1"] .customizer-crop-btn');
          if (!cropBtn) return;
          customizer.dataset.cropBtnBound = '1';
          updateCropResetButtonVisibility(customizer);
          preloadCustomizerCropSources(customizer);
          const cropSection = customizer.querySelector('[data-crop-section]');
          cropSection?.addEventListener('mouseenter', function () {
            preloadCustomizerCropSources(customizer);
          }, { passive: true });
          cropBtn.addEventListener('click', function () {
            const img = customizer.querySelector('.step-panel[data-step="1"] .editable-image');
            if (img) openCropPopup(img, customizer);
          });
        }

        function bindCanvasBrushstrokeToggle(customizer) {
          if (!customizer || customizer.dataset.brushstrokeBound === '1') return;
          const cb = customizer.querySelector('#canvas-brushstroke-cb');
          if (!cb) return;
          customizer.dataset.brushstrokeBound = '1';
          cb.addEventListener('change', function () {
            if (isApplyingOriginalSpec(customizer)) return;
            markUserModifiedSelections(customizer);
            customizerState.acrylicBrushstroke = !!cb.checked;
            reconcileOriginalSpecAfterUserChange(customizer);
            updateCustomizerMainPrice(customizer);
            updateSummary(customizer);
          });
        }

        function updateCanvasBrushstrokeUI(customizer) {
          if (!customizer) return;
          const wrap = customizer.querySelector('[data-canvas-brushstroke-wrap]');
          const cb = customizer.querySelector('#canvas-brushstroke-cb');
          const show = isCanvasSubstrate();
          if (wrap) wrap.hidden = !show;
          if (cb) {
            cb.checked = show ? !!customizerState.acrylicBrushstroke : false;
            cb.disabled = !show;
            if (!show) customizerState.acrylicBrushstroke = false;
          }
        }

        function updateCanvasLinerAvailability(customizer) {
          if (!customizer) return;
          const panel = customizer.querySelector('.step-panel[data-step="3"]');
          if (!panel) return;
          const isCanvas = isCanvasSubstrate();
          const linerTab = panel.querySelector('.cz-tab[data-cz-tab="liner"]');
          const linerPanel = panel.querySelector('[data-cz-tab-panel="liner"]');
          const blockLiner = isCanvas;

          if (isCanvas) customizerState.liner = null;

          if (linerTab) {
            linerTab.style.display = blockLiner ? 'none' : '';
            linerTab.classList.toggle('cz-tab--disabled', !!blockLiner);
            if (blockLiner) linerTab.setAttribute('aria-disabled', 'true');
            else linerTab.removeAttribute('aria-disabled');
          }
          if (linerPanel) {
            linerPanel.style.display = blockLiner ? 'none' : '';
            if (blockLiner && linerPanel.classList.contains('cz-tab-panel--active')) {
              activateCzTab(panel, 'frame');
            }
          }
          if (blockLiner) {
            panel.querySelectorAll('[data-lock-liner]').forEach(function (el) {
              el.style.display = 'none';
            });
          } else {
            panel.querySelectorAll('[data-lock-liner]').forEach(function (el) {
              el.style.display = '';
            });
          }
        }

        function updateCanvasMatStepAvailability(customizer) {
          if (!customizer) return;
          const panel = customizer.querySelector('.step-panel[data-step="2"]');
          if (!panel) return;

          const isCanvas = isCanvasSubstrate();
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;

          const clearBtn = contentCol.querySelector('.js-step-clear-btn');
          if (clearBtn) clearBtn.style.display = isCanvas ? 'none' : '';

          const tabsRow = contentCol.querySelector('.cz-tabs');
          let disabledMsg = contentCol.querySelector('.canvas-mat-disabled-msg');

          if (isCanvas) {
            if (!disabledMsg) {
              disabledMsg = document.createElement('div');
              disabledMsg.className = 'canvas-mat-disabled-msg bottom-mat-disabled-msg';
              disabledMsg.innerHTML = `
                <div class="mat-disabled-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dfce50" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4M12 16h.01" stroke-linecap="round"/>
                  </svg>
                </div>
                <h3 class="mat-disabled-title">Mat Not Available</h3>
                <p class="mat-disabled-body">Mat is not available for canvas.</p>
              `;
              contentCol.insertBefore(disabledMsg, tabsRow || contentCol.firstChild);
            }
            disabledMsg.style.display = 'flex';
            if (tabsRow) tabsRow.style.display = 'none';
            contentCol.querySelectorAll('.cz-tab-panel').forEach(el => { el.style.display = 'none'; });
          } else {
            if (disabledMsg) disabledMsg.style.display = 'none';
            if (tabsRow) tabsRow.style.display = '';
            contentCol.querySelectorAll('.cz-tab-panel').forEach(el => { el.style.display = ''; });
            updateBottomMatStepAvailability(customizer);
          }
          updateMatStepLabel(customizer);
          refreshStepPillStates(customizer);
        }

        function getNextCzTabKey(panel) {
          if (!panel) return null;
          const tabs = Array.from(panel.querySelectorAll('.cz-tab:not(.cz-tab--disabled)'));
          const activeIdx = tabs.findIndex(t => t.classList.contains('cz-tab--active'));
          if (activeIdx >= 0 && activeIdx < tabs.length - 1) {
            return tabs[activeIdx + 1].dataset.czTab || null;
          }
          return null;
        }

        function getPrevCzTabKey(panel) {
          if (!panel) return null;
          const tabs = Array.from(panel.querySelectorAll('.cz-tab:not(.cz-tab--disabled)'));
          const activeIdx = tabs.findIndex(t => t.classList.contains('cz-tab--active'));
          if (activeIdx > 0) {
            return tabs[activeIdx - 1].dataset.czTab || null;
          }
          return null;
        }

        function updatePanelNavState(customizer) {
          if (!customizer) return;
          const ordered = getEnabledSteps();
          const current = getCurrentStep(customizer);
          const idx = current ? ordered.indexOf(current) : -1;
          const isFirst = idx <= 0;
          const isLast = idx >= 0 && idx === ordered.length - 1;
          customizer.querySelectorAll('.customizer-panel-nav').forEach(function (nav) {
            nav.classList.toggle('is-first-step', isFirst);
            nav.classList.toggle('is-last-step', isLast);
            const prevBtn = nav.querySelector('[data-prev-tab]');
            const nextBtn = nav.querySelector('[data-next-tab]');
            if (prevBtn) {
              prevBtn.hidden = isFirst;
              prevBtn.disabled = isFirst;
              prevBtn.setAttribute('aria-hidden', isFirst ? 'true' : 'false');
            }
            if (nextBtn) {
              nextBtn.hidden = isLast;
              nextBtn.disabled = isLast;
              nextBtn.setAttribute('aria-hidden', isLast ? 'true' : 'false');
            }
          });
        }

        function scrollCustomizerToTop(customizer) {
          if (!customizer) return;
          const candidates = [
            customizer.closest('.image-customizer-popup__inner'),
            customizer.closest('.image-customizer-popup__content'),
            customizer.querySelector('.customizer-body'),
            customizer
          ];
          candidates.forEach(function (el) {
            if (el && el.scrollHeight > el.clientHeight + 8) {
              el.scrollTo({ top: 0, behavior: 'smooth' });
            }
          });
        }

        function isArtCustomizerPopup(popup) {
          return !!(popup && popup.classList.contains('image-customizer-popup') &&
            !popup.classList.contains('image-customizer-popup--view-in-room'));
        }

        function getArtCustomizerPopup() {
          return document.querySelector('.image-customizer-popup.img-customize-popup') ||
            document.querySelector('.image-customizer-popup:not(.image-customizer-popup--view-in-room)');
        }

        function getActiveCzTabKey(customizer) {
          const current = getCurrentStep(customizer);
          if (!current) return '';
          const panel = customizer.querySelector('.step-panel[data-step="' + current + '"]');
          return panel?.querySelector('.cz-tab.cz-tab--active')?.dataset.czTab || '';
        }

        function pushCustomizerHistory(customizer) {
          if (customizerHistoryLock || customizerHistoryClosing || !customizer) return;
          const popup = customizer.closest('.image-customizer-popup');
          if (!isArtCustomizerPopup(popup) || popup.hidden) return;
          const step = getCurrentStep(customizer);
          if (!step) return;
          const tab = getActiveCzTabKey(customizer);
          const prev = window.history.state;
          if (prev && prev.ipCz && Number(prev.step) === Number(step) && String(prev.tab || '') === String(tab)) {
            return;
          }
          const depth = (prev && prev.ipCz && typeof prev.d === 'number') ? prev.d + 1 : 1;
          try {
            window.history.pushState({ ipCz: true, step: step, tab: tab, d: depth }, '', window.location.href);
          } catch (err) { /* ignore */ }
        }

        function showArtCustomizerPopupFromHistory(popup) {
          if (!popup) return;
          popup.hidden = false;
          popup.classList.remove('popup-animate-in');
          void popup.offsetWidth;
          popup.classList.add('popup-animate-in');
          popup.querySelector('.image-customizer-popup__overlay')?.classList.add('active');
          syncCustomizerPopupState();
        }

        function applyCustomizerHistoryState(state) {
          const popup = getArtCustomizerPopup();
          if (!popup || !state || !state.ipCz) return;
          const customizer = popup.querySelector('.customizer');
          if (!customizer) return;
          if (popup.hidden) showArtCustomizerPopupFromHistory(popup);
          const step = parseInt(state.step, 10);
          customizerHistoryLock = true;
          try {
            if (!isNaN(step)) goToStep(customizer, step, { force: true, skipHistory: true });
            if (state.tab) {
              const panel = customizer.querySelector('.step-panel[data-step="' + step + '"]');
              if (panel) activateCzTab(panel, state.tab);
              updateCustomizerMainPrice(customizer);
            }
          } finally {
            customizerHistoryLock = false;
          }
        }

        function hideImageCustomizerPopup(popup) {
          if (!popup) return;
          popup.classList.remove('popup-animate-in');
          popup.hidden = true;
          popup.querySelector('.image-customizer-popup__overlay')?.classList.remove('active');
          syncCustomizerPopupState();
        }

        function closeImageCustomizerPopup(popup, opts) {
          opts = opts || {};
          if (!popup || popup.hidden) return;
          hideImageCustomizerPopup(popup);
          if (opts.fromHistory || !isArtCustomizerPopup(popup)) return;
          const st = window.history.state;
          if (!st || !st.ipCz || typeof st.d !== 'number' || st.d < 1) return;
          customizerHistoryClosing = true;
          try {
            window.history.go(-st.d);
          } catch (err) {
            customizerHistoryClosing = false;
            return;
          }
          window.setTimeout(function () {
            customizerHistoryClosing = false;
          }, 400);
        }

        /* =========================================================
          TAB SWITCHING (used in Step 2 + Step 3)
        ========================================================= */
        function activateCzTab(scope, tabKey) {
          if (!scope || !tabKey) return;
          scope.querySelectorAll('.cz-tab').forEach(t => {
            const isActive = t.dataset.czTab === tabKey;
            t.classList.toggle('cz-tab--active', isActive);
            t.setAttribute('aria-selected', String(isActive));
          });
          scope.querySelectorAll('.cz-tab-panel').forEach(p => {
            const isActive = p.dataset.czTabPanel === tabKey;
            p.classList.toggle('cz-tab-panel--active', isActive);
          });
        }

        function bindMatStepPanelClear(customizer) {
          const panel = customizer.querySelector('.step-panel[data-step="2"]');
          if (!panel || panel.dataset.matClearBound === '1') return;
          panel.dataset.matClearBound = '1';
          const contentCol = panel.querySelector('.customizer-content-col');
          const clearBtn = contentCol?.querySelector('.js-step-clear-btn');
          if (!clearBtn) return;

          clearBtn.addEventListener('click', () => {
            const activeTab = panel.querySelector('.cz-tab.cz-tab--active')?.dataset.czTab || 'topmat';
            const stateKey = activeTab === 'bottommat' ? 'bottomMat' : 'topMat';
            const stepNum = stateKey === 'bottomMat' ? 5 : 4;
            const grid = contentCol.querySelector('#mat-grid-step' + stepNum);
            const inputScope = grid?.closest('.cz-tab-panel') || contentCol;

            const lockMatCb = panel.querySelector('.lock-original-mat-cb');
            if (lockMatCb) {
              lockMatCb.checked = false;
              lockMatCb.dispatchEvent(new Event('change'));
            }

            clearMatSelection(stateKey, grid, inputScope);
            if (stateKey === 'bottomMat' && typeof window.__matRefreshBottom === 'function') {
              window.__matRefreshBottom();
            } else if (typeof window.__matRefreshTop === 'function') {
              window.__matRefreshTop();
            }
            updateMatStepLabel();
            updateMatPreview(customizer);
            if (getCurrentStep(customizer) === getSummaryStep()) updateSummary(customizer);
            afterStepClear(customizer, 2);
          });
        }

        function bindFrameStepPanelClear(customizer) {
          const panel = customizer.querySelector('.step-panel[data-step="3"]');
          if (!panel || panel.dataset.frameClearBound === '1') return;
          panel.dataset.frameClearBound = '1';
          const contentCol = panel.querySelector('.customizer-content-col');
          const clearBtn = contentCol?.querySelector('.js-step-clear-btn');
          if (!clearBtn) return;

          clearBtn.addEventListener('click', () => {
            const tabsRow = panel.querySelector('.customizer-content-col > .cz-tabs');
            const activeTab = tabsRow?.querySelector('.cz-tab.cz-tab--active')?.dataset.czTab || 'frame';
            if (activeTab === 'liner') {
              const grid = contentCol.querySelector('#mat-grid-step6');
              const inputScope = grid?.closest('.combined-liner-section') || contentCol;
              const lockLinerCb = panel.querySelector('[data-cz-tab-panel="liner"] .lock-original-liner-only-cb');
              if (lockLinerCb) {
                lockLinerCb.checked = false;
                lockLinerCb.dispatchEvent(new Event('change'));
              }
              clearMatSelection('liner', grid, inputScope);
              if (typeof window.__matRefreshLiner === 'function') window.__matRefreshLiner();
              updateFrameLinerStepLabel();
              updateMatPreview(customizer);
              if (getCurrentStep(customizer) === getSummaryStep()) updateSummary(customizer);
              afterStepClear(customizer, 3);
              return;
            }
            const lockFrameCb = panel.querySelector('[data-cz-tab-panel="frame"] .lock-original-frame-only-cb');
            if (lockFrameCb) {
              lockFrameCb.checked = false;
              lockFrameCb.dispatchEvent(new Event('change'));
            }
            if (typeof window.fsResetFrame === 'function') window.fsResetFrame();
            updateFrameLinerStepLabel();
            updateMatPreview(customizer);
            if (getCurrentStep(customizer) === getSummaryStep()) updateSummary(customizer);
            afterStepClear(customizer, 3);
          });
        }

        function bindCzTabs(customizer) {
          if (!customizer || customizer.dataset.czTabsBound === '1') return;
          customizer.dataset.czTabsBound = '1';

          customizer.addEventListener('click', (e) => {
            const tab = e.target.closest('.cz-tab');
            if (!tab) return;
            if (tab.classList.contains('cz-tab--disabled')) return;
            /* Find the closest scope so we don't accidentally cross to another step */
            const panel = tab.closest('.step-panel') || customizer;
            const key   = tab.dataset.czTab;
            if (!key) return;
            activateCzTab(panel, key);
            updateCustomizerMainPrice(customizer);
            pushCustomizerHistory(customizer);
          });
        }

        /* =========================
          OPEN CROP POPUP
        ========================== */
        function fitCropBoxToImage(cropperInstance) {
          if (!cropperInstance || typeof cropperInstance.getCanvasData !== 'function') return;
          const canvasData = cropperInstance.getCanvasData();
          if (!canvasData || canvasData.width <= 0 || canvasData.height <= 0) return;
          cropperInstance.setCropBoxData({
            left: canvasData.left,
            top: canvasData.top,
            width: canvasData.width,
            height: canvasData.height
          });
        }

        function getCropBaseArtworkDimensions(customizer) {
          const state = customizer ? customizerCropState.get(customizer) : null;
          if (state?.baseWidth > 0 && state?.baseHeight > 0) {
            return { width: state.baseWidth, height: state.baseHeight };
          }
          syncArtworkSizeFromInputs(customizer);
          const w = parseFloat(customizerState.width);
          const h = parseFloat(customizerState.height);
          if (isFinite(w) && w > 0 && isFinite(h) && h > 0) {
            return { width: w, height: h };
          }
          return resolveProductArtworkDimensions(customizer) || { width: w, height: h };
        }

        function ensureCropBaseDimensions(customizer) {
          if (!customizer) return null;
          const prev = customizerCropState.get(customizer) || {};
          if (prev.baseWidth > 0 && prev.baseHeight > 0) return prev;
          const base = getCropBaseArtworkDimensions(customizer);
          if (!(base.width > 0 && base.height > 0)) return prev;
          const next = Object.assign({}, prev, { baseWidth: base.width, baseHeight: base.height });
          customizerCropState.set(customizer, next);
          return next;
        }

        function calculateArtworkSizeFromCrop(cropperInstance, baseW, baseH) {
          if (!cropperInstance || !(baseW > 0) || !(baseH > 0)) return null;
          const imageData = cropperInstance.getImageData();
          const cropData = cropperInstance.getData(true);
          if (!imageData?.naturalWidth || !imageData?.naturalHeight || !cropData?.width || !cropData?.height) {
            return null;
          }
          const ratioW = cropData.width / imageData.naturalWidth;
          const ratioH = cropData.height / imageData.naturalHeight;
          let newW = snapArtworkDimension(baseW * ratioW);
          let newH = snapArtworkDimension(baseH * ratioH);
          newW = Math.min(ARTWORK_SIZE_MAX, Math.max(ARTWORK_SIZE_MIN, newW));
          newH = Math.min(ARTWORK_SIZE_MAX, Math.max(ARTWORK_SIZE_MIN, newH));
          return { width: newW, height: newH };
        }

        function hasCustomizerCrop(customizer) {
          if (!customizer) return false;
          const state = customizerCropState.get(customizer);
          if (state?.cropData || state?.croppedSrc) return true;
          return getCustomizerImages(customizer).some(function (img) {
            return !!(img?.dataset?.cropData);
          });
        }

        function updateCustomCropSizeNote(customizer) {
          if (!customizer) return;
          let note = customizer.querySelector('#customizer-crop-size-note');
          if (!note) {
            const lockToggle = customizer.querySelector('.customizer-lock-toggle--size');
            if (!lockToggle) return;
            note = document.createElement('p');
            note.id = 'customizer-crop-size-note';
            note.className = 'customizer-crop-size-note';
            note.hidden = true;
            note.setAttribute('aria-live', 'polite');
            lockToggle.parentNode.insertBefore(note, lockToggle);
          }
          if (!hasCustomizerCrop(customizer)) {
            note.hidden = true;
            note.textContent = '';
            return;
          }
          const w = parseFloat(customizerState.width);
          const h = parseFloat(customizerState.height);
          if (!(w > 0) || !(h > 0)) {
            note.hidden = true;
            note.textContent = '';
            return;
          }
          note.textContent = 'Image size custom cropped ' + formatSizeDisplay(w, h);
          note.hidden = false;
        }

        function resetCropForCustomizer(customizer) {
          const result = { restored: false, baseWidth: 0, baseHeight: 0 };
          if (!customizer || !hasCustomizerCrop(customizer)) return result;
          const cropState = customizerCropState.get(customizer) || {};
          result.baseWidth = parseFloat(cropState.baseWidth) || 0;
          result.baseHeight = parseFloat(cropState.baseHeight) || 0;
          customizerCropState.delete(customizer);
          const images = getCustomizerImages(customizer);
          let originalSrc = '';
          images.forEach(function (image) {
            if (!image?.dataset) return;
            delete image.dataset.cropData;
            if (image.dataset.originalSrc) {
              originalSrc = image.dataset.originalSrc;
              image.src = image.dataset.originalSrc;
            }
          });
          if (!originalSrc) originalSrc = window.__fsProductImg || '';
          if (typeof window.__fsSetCroppedPhoto === 'function' && originalSrc) {
            window.__fsSetCroppedPhoto(originalSrc);
          }
          result.restored = true;
          updateCustomCropSizeNote(customizer);
          updateCropResetButtonVisibility(customizer);
          return result;
        }

        function updateCropSizeReadout(cropperInstance, customizer) {
          const el = document.getElementById('cropper-size-readout');
          if (!el || !cropperInstance) return;
          const cropData = cropperInstance.getData(true);
          if (!cropData?.width || !cropData?.height) {
            el.textContent = '';
            el.hidden = true;
            return;
          }
          const pxW = Math.round(cropData.width);
          const pxH = Math.round(cropData.height);
          const base = getCropBaseArtworkDimensions(customizer);
          const sized = calculateArtworkSizeFromCrop(cropperInstance, base.width, base.height);
          const inchPart = sized
            ? ' · Print size: ' + formatSizeDisplay(sized.width, sized.height)
            : '';
          el.textContent = 'Crop: ' + pxW + ' × ' + pxH + ' px' + inchPart;
          el.hidden = false;
        }

        function updateCropResetButtonVisibility(customizer) {
          const roots = customizer ? [customizer] : Array.from(document.querySelectorAll('.customizer'));
          roots.forEach(function (root) {
            const btn = root.querySelector('.customizer-crop-reset-btn');
            if (!btn) return;
            btn.hidden = !hasCustomizerCrop(root);
          });
        }

        function restoreCropToOriginal(customizer) {
          if (!customizer || !hasCustomizerCrop(customizer)) return;
          const pending = customizerCropState.get(customizer) || {};
          const restoreW = parseFloat(pending.baseWidth) || 0;
          const restoreH = parseFloat(pending.baseHeight) || 0;
          resetCropForCustomizer(customizer);
          if (restoreW > 0 && restoreH > 0) {
            syncArtworkSizeAfterCrop(customizer, restoreW, restoreH);
          } else {
            applyDefaultArtworkSizeFromProduct(customizer);
            updateCustomCropSizeNote(customizer);
          }
          updateCropResetButtonVisibility(customizer);
        }

        function syncArtworkSizeAfterCrop(customizer, width, height) {
          if (!customizer || !(width > 0) || !(height > 0)) return;
          applyArtworkSizeToCustomizer(customizer, width, height);
          customizerState.artworkFloat = !isArtworkOversize(width, height);
          updateSubstrateStepLabel();
          updateSummary(customizer);
          updateCurrentSizesDisplay(customizer);
          enforceOversizeMatRules(customizer);
          refreshStepPillStates(customizer);
          updateCustomizerMainPrice(customizer);
          redrawFrameCanvasWithMats();
          updateCustomCropSizeNote(customizer);
          updateCropResetButtonVisibility(customizer);
          const disclaimer = customizer.querySelector('.step2-custom-size-disclaimer');
          if (disclaimer) disclaimer.hidden = true;
        }

        function setCropPopupLoading(show) {
          const loader = document.getElementById('cropper-loading');
          const containers = document.querySelector('.cropper-containers');
          if (loader) loader.hidden = !show;
          if (containers) containers.classList.toggle('cropper-containers--loading', !!show);
        }

        function preloadCropSource(url) {
          const src = String(url || '').trim();
          if (!src) return Promise.resolve(null);
          if (cropImagePreloadCache.has(src)) return cropImagePreloadCache.get(src);
          const promise = new Promise(function (resolve, reject) {
            const img = new Image();
            if (/^https?:\/\//i.test(src)) img.crossOrigin = 'anonymous';
            img.onload = function () { resolve(img); };
            img.onerror = function () { reject(new Error('Crop image failed to load')); };
            img.src = src;
          });
          cropImagePreloadCache.set(src, promise);
          return promise;
        }

        function preloadCustomizerCropSources(customizer) {
          if (!customizer) return;
          customizer.querySelectorAll('.editable-image').forEach(function (img) {
            const src = img.dataset?.originalSrc || img.currentSrc || img.src;
            if (src) preloadCropSource(src).catch(function () {});
          });
        }

        function initCropperInstance(cropImg, customizer) {
          if (typeof Cropper === 'undefined') {
            console.warn('Cropper.js is not loaded — crop cannot initialize.');
            return false;
          }
          if (cropper) { cropper.destroy(); cropper = null; }
          cropper = new Cropper(cropImg, {
            viewMode: 1, autoCropArea: 1, background: false, guides: true,
            center: true, highlight: false, scalable: true, zoomable: false,
            movable: false, cropBoxMovable: true, cropBoxResizable: true,
            toggleDragModeOnDblclick: false, minContainerWidth: 0, minContainerHeight: 0,
            crop() {
              updateCropSizeReadout(cropper, activeCustomizer);
            },
            ready() {
              if (!cropper) return;
              const sharedCropData = getSharedCropData(activeCustomizer);
              if (sharedCropData) cropper.setData(sharedCropData);
              else fitCropBoxToImage(cropper);
              updateCropSizeReadout(cropper, activeCustomizer);
              setCropPopupLoading(false);
            }
          });
          return true;
        }

        function openCropPopup(img, customizer) {
          if (!img || !customizer) return;
          const cropImg = document.getElementById('cropper-image');
          const popup   = document.getElementById('crop-popup');
          if (!cropImg || !popup) return;

          activeImage      = img;
          activeCustomizer = customizer;
          const cropSrc = img.dataset?.originalSrc || img.src;
          const openToken  = ++cropOpenToken;

          popup.hidden = false;
          popup.classList.remove('popup-animate-in');
          void popup.offsetWidth;
          popup.classList.add('popup-animate-in');
          popup.querySelector('.image-customizer-popup__overlay')?.classList.add('active');
          syncCustomizerPopupState();
          ensureCropBaseDimensions(customizer);
          setCropPopupLoading(true);
          let cropperStarted = false;

          function startCropper() {
            if (cropperStarted || openToken !== cropOpenToken) return;
            cropperStarted = true;
            if (!initCropperInstance(cropImg, customizer)) setCropPopupLoading(false);
          }

          function assignCropImageSrc() {
            if (openToken !== cropOpenToken) return;
            cropImg.removeAttribute('crossorigin');
            if (cropSrc && /^https?:\/\//i.test(cropSrc)) cropImg.crossOrigin = 'anonymous';
            if (cropImg.src === cropSrc && cropImg.complete && cropImg.naturalWidth > 0) {
              startCropper();
              return;
            }
            cropImg.onload = function () {
              cropImg.onload = null;
              cropImg.onerror = null;
              startCropper();
            };
            cropImg.onerror = function () {
              cropImg.onload = null;
              cropImg.onerror = null;
              if (openToken !== cropOpenToken) return;
              setCropPopupLoading(false);
              console.warn('Crop image failed to load.');
            };
            cropImg.src = cropSrc;
            if (cropImg.complete && cropImg.naturalWidth > 0) startCropper();
          }

          preloadCropSource(cropSrc)
            .then(assignCropImageSrc)
            .catch(assignCropImageSrc);
        }

        function parseDefaultAttr(raw) {
          try {
            if (raw == null) return '';
            var s = String(raw).trim();
            if (!s) return '';
            if (s[0] === '{' || s[0] === '[' || s[0] === '"') {
              var v = JSON.parse(s);
              if (Array.isArray(v)) return String(v[0] || '').trim();
              if (v && typeof v === 'object') {
                if (Array.isArray(v.value)) return String(v.value[0] || '').trim();
                if (v.value != null) return String(v.value).trim();
              }
              return String(v || '').trim();
            }
            return s;
          } catch (e) {
            return String(raw || '').trim();
          }
        }

        function parseDatasetBoolean(raw) {
          if (raw == null) return false;
          const v = String(raw).trim().toLowerCase();
          return v === 'true' || v === '1' || v === 'yes' || v === 'on';
        }

        /** Map metafield substrate labels (e.g. "Paper", ["Paper"]) to <select> option text. */
        function resolveSubstrateForSelect(customizer, raw) {
          const parsed = parseDefaultAttr(raw);
          if (!parsed || /^none$/i.test(parsed)) return '';
          const substrateSelect = customizer && customizer.querySelector('[id^="customizer-substrate-"]');
          if (!substrateSelect) return parsed;
          const norm = parsed.trim().toLowerCase();
          const aliases = {
            paper: 'Matte Paper',
            'matte paper': 'Matte Paper',
            canvas: 'Canvas',
            'high gloss photo paper': 'High Gloss Photo Paper',
            'high gloss paper': 'High Gloss Photo Paper',
            'high gloss paper — straight deckle (floating)': 'High Gloss Photo Paper',
            'high gloss paper — rough deckle (floating)': 'High Gloss Photo Paper'
          };
          const candidates = [parsed, aliases[norm]].filter(Boolean);
          for (let i = 0; i < candidates.length; i++) {
            const c = candidates[i];
            const ok = Array.from(substrateSelect.options).some(function (o) {
              return o.value === c || String(o.textContent || '').trim() === c;
            });
            if (ok) return c;
          }
          const fuzzy = Array.from(substrateSelect.options).find(function (o) {
            const t = String(o.value || o.textContent || '').trim().toLowerCase();
            return t.indexOf(norm) >= 0 || norm.indexOf(t) >= 0;
          });
          return fuzzy ? (fuzzy.value || fuzzy.textContent) : parsed;
        }

        function setTreatmentOnCustomizer(customizer, want) {
          const treatment = String(want || '').trim();
          if (!treatment || /^none$/i.test(treatment)) return false;
          const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
          if (!treatmentSelect) {
            customizerState.treatment = treatment;
            return true;
          }
          const opt = Array.from(treatmentSelect.options).find(function (o) {
            const v = String(o.value || o.textContent || '').trim();
            return v === treatment || v.toLowerCase() === treatment.toLowerCase();
          });
          if (!opt) return false;
          const val = String(opt.value || opt.textContent || '').trim();
          treatmentSelect.value = val;
          customizerState.treatment = val;
          return true;
        }

        /** Apply Double Mat / Single Mat / No Mat from product spec (metafields + mat ids). */
        function applyOriginalTreatmentFromSpec(spec, customizer) {
          if (!spec || !customizer) return;
          let want = '';
          if (spec.bottomMatId && !/^none$/i.test(spec.bottomMatId)) {
            want = 'Double Mat';
          } else if (spec.treatment && !/^none$/i.test(spec.treatment)) {
            const rawTreatment = String(spec.treatment).trim();
            const inferred = inferTreatmentFromMatSpecification(rawTreatment);
            if (inferred) {
              applyMatInchesFromProductSpecification(customizer);
              want = inferred;
            } else {
              want = rawTreatment;
              if (/^no\s*mat$/i.test(want)) want = NO_MAT_TREATMENT;
            }
          } else {
            want = parseDefaultAttr(customizer.dataset.defaultTreatment);
          }
          if (/^no\s*mat$/i.test(want)) want = NO_MAT_TREATMENT;
          /* Mats are never available on canvas: force "None" regardless of the
             spec/metafield default, but remember the spec treatment so it can
             be restored if the customer switches to a non-canvas substrate. */
          if (isCanvasSubstrate()) {
            if (MAT_TREATMENT_OPTIONS.includes(want)) {
              customizer.dataset.lastNonCanvasTreatment = want;
            }
            const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
            if (treatmentSelect) {
              syncTreatmentSelectOptions(treatmentSelect, true);
              const hasNone = Array.from(treatmentSelect.options).some(function (o) {
                return String(o.value || o.textContent || '').trim() === CANVAS_MAT_TREATMENT;
              });
              if (hasNone) treatmentSelect.value = CANVAS_MAT_TREATMENT;
            }
            customizerState.treatment = CANVAS_MAT_TREATMENT;
            customizerState.topMat = null;
            customizerState.bottomMat = null;
            return;
          }
          if (want === 'Double Mat' || want === 'Single Mat' || want === NO_MAT_TREATMENT) {
            setTreatmentOnCustomizer(customizer, want);
          }
        }

        function parseSpecCatalogId(raw) {
          const s = String(raw || '').trim();
          if (!s || /^none$/i.test(s)) return '';
          // Metafields may space the code (e.g. "srm 130 — White..." → SRM130).
          const leadSpaced = s.match(/^([A-Za-z]+\s+\d+[A-Za-z0-9\-_]*)/i);
          if (leadSpaced) return leadSpaced[1].replace(/\s+/g, '').toUpperCase();
          // Catalog codes may include hyphens/underscores (e.g. 104-100, 520-1007, EC1916_1).
          const lead = s.match(/^([A-Za-z0-9][A-Za-z0-9\-_]*)/i);
          if (lead) return lead[1].toUpperCase();
          const afterSep = s.match(/[—–-]\s*([A-Za-z0-9][A-Za-z0-9\-_]*)/i);
          if (afterSep) return afterSep[1].toUpperCase();
          const embedded = s.match(/\b([A-Za-z]{0,4}\s*\d+[A-Za-z0-9\-_]*)\b/i);
          return embedded ? embedded[1].replace(/\s+/g, '').toUpperCase() : '';
        }

        function normalizeCatalogId(id) {
          return String(id || '').trim().toUpperCase();
        }

        function catalogIdsMatch(a, b) {
          return normalizeCatalogId(a) === normalizeCatalogId(b);
        }

        window.__catalogIdsMatch = catalogIdsMatch;

        /** Name segment from labels like "PM66 — Linen Printed — 2.75 Inch". */
        function parseSpecMatName(raw) {
          const s = String(raw || '').trim();
          if (!s || /^none$/i.test(s)) return '';
          let rest = s.replace(/^[A-Za-z]+\s+\d+[A-Za-z0-9\-_]*\s*[—–-]\s*/i, '');
          if (rest === s) {
            rest = s.replace(/^[A-Za-z0-9][A-Za-z0-9\-_]*\s*[—–-]\s*/i, '');
          }
          rest = rest.replace(/\s*[—–-]\s*\d+(?:\.\d+)?\s*["']?\s*(?:inch|inches|in)\b.*$/i, '').trim();
          return rest;
        }

        function normalizeMatNameForMatch(name) {
          return String(name || '')
            .trim()
            .toLowerCase()
            .replace(/\s*\(printed\)\s*/gi, ' printed ')
            .replace(/\s+/g, ' ')
            .trim();
        }

        function matDisplayNameForMatch(mat) {
          if (!mat) return '';
          let name = String(mat.name || '').trim();
          if (mat.printedMats && !/\bprinted\b/i.test(name)) {
            name = name + ' Printed';
          }
          return normalizeMatNameForMatch(name);
        }

        /** True when spec label name matches a catalog mat (handles duplicate codes). */
        function matNamesMatch(specName, matName, matObj) {
          const specNorm = normalizeMatNameForMatch(specName);
          if (!specNorm) return true;
          const matNorm = matDisplayNameForMatch(matObj || { name: matName });
          if (specNorm === matNorm) return true;
          const specBase = specNorm.replace(/\s+printed\s*$/i, '').trim();
          const matBase = normalizeMatNameForMatch(matName).replace(/\s+printed\s*$/i, '').trim();
          return !!(specBase && specBase === matBase);
        }

        function matsStateMatchesCatalogMat(stateMat, catalogMat, stateKey) {
          if (!stateMat || !catalogMat) return false;
          if (!catalogIdsMatch(stateMat.id, catalogMat.id)) return false;
          const duplicates = findAllMatsInCatalogs(stateKey, stateMat.id);
          if (duplicates.length <= 1) return true;
          return matNamesMatch(stateMat.name, catalogMat.name, catalogMat);
        }

        function matMatchesSearchQuery(mat, rawQuery) {
          const q = String(rawQuery || '').trim();
          if (!q) return true;
          const qLower = q.toLowerCase();
          const id = String(mat?.id || '');
          const code = String(mat?.matCode || mat?.id || '');
          const name = String(mat?.name || '');
          if (id.toLowerCase().includes(qLower)) return true;
          if (code.toLowerCase().includes(qLower)) return true;
          if (name.toLowerCase().includes(qLower)) return true;
          const parsedCode = parseSpecCatalogId(q);
          if (parsedCode && catalogIdsMatch(parsedCode, id)) return true;
          if (parsedCode && mat?.matCode && catalogIdsMatch(parsedCode, mat.matCode)) return true;
          const qCompact = qLower.replace(/[\s\-_]/g, '');
          const idCompact = normalizeCatalogId(id).replace(/[\s\-_]/g, '').toLowerCase();
          return !!(qCompact && idCompact && idCompact.includes(qCompact));
        }

        /** Keep the active mat visible when browsing without active filters. */
        function pinSelectedMatInFilteredList(list, stateKey, filterCtx) {
          if (stateKey === 'liner') return list;
          if (filterCtx && filterCtx.active) return list;
          const current = customizerState[stateKey];
          if (!current || !current.id) return list;
          const next = Array.isArray(list) ? list.slice() : [];
          const alreadyListed = next.some(function (m) {
            return matsStateMatchesCatalogMat(current, m, stateKey);
          });
          if (alreadyListed) return next;
          const fromCatalog = findMatInCatalogs(stateKey, current.id, current.name);
          const pinned = fromCatalog || {
            id: current.id,
            name: current.name,
            hex: current.hex,
            type: current.type,
            img: current.img || null,
            matW: current.matW || 1.2,
            oversizedMat: !!current.oversizedMat,
            printedMats: !!current.printedMats,
            pickFrom: current.pickFrom !== false,
            matColorName: current.matColorName
          };
          return [pinned].concat(next);
        }

        function finalizeMatFilterResults(list, stateKey, filterCtx) {
          return pinSelectedMatInFilteredList(
            applyPickFromConstraintsOnList(list, stateKey, filterCtx),
            stateKey,
            filterCtx
          );
        }

        function applyPickFromConstraintsOnList(list, stateKey, filterCtx) {
          if (stateKey === 'liner') return list;
          if (filterCtx && filterCtx.active) {
            return (list || []).filter(function (m) { return m.pickFrom !== false; });
          }
          let next = list.filter(function (m) { return m.pickFrom !== false; });
          const current = customizerState[stateKey];
          if (!current || !current.id) return next;
          const fromCatalog = findMatInCatalogs(stateKey, current.id, current.name);
          const isSpecOnly = (fromCatalog && fromCatalog.pickFrom === false) || current.pickFrom === false;
          if (!isSpecOnly) return next;
          const alreadyListed = next.some(function (m) {
            return matsStateMatchesCatalogMat(current, m, stateKey);
          });
          if (!alreadyListed) {
            const pinned = fromCatalog || {
              id: current.id,
              name: current.name,
              hex: current.hex,
              type: current.type,
              img: current.img || null,
              matW: current.matW || 1.2,
              oversizedMat: !!current.oversizedMat,
              printedMats: !!current.printedMats,
              pickFrom: false,
              matColorName: current.matColorName
            };
            next = [pinned].concat(next);
          }
          return next;
        }

        function markUserModifiedSelections(customizer) {
          if (!customizer || isApplyingOriginalSpec(customizer)) return;
          customizer.dataset.userModifiedSelections = '1';
          /* Freeze product-form default cost fields so customizer changes never overwrite them. */
          freezeNochangeFormulaCostInputs();
          stopOriginalSpecRetryLoop(customizer);
        }

        /** Permanently lock nochange formula-cost inputs for this page load. */
        function freezeNochangeFormulaCostInputs() {
          document.querySelectorAll(
            'input[data-formula-cost][nochange], input[data-formula-cost][data-nochange="1"]'
          ).forEach(function (input) {
            input.dataset.formulaCostFrozen = '1';
          });
        }

        window.markUserModifiedSelections = markUserModifiedSelections;

        function resetUserModifiedSelections(customizer) {
          if (!customizer) return;
          stopOriginalSpecRetryLoop(customizer);
          delete customizer.dataset.userModifiedSelections;
          delete customizer.dataset.originalSpecFullyApplied;
          delete customizer.dataset.pendingOriginalSpec;
        }

        /**
         * When the customer reverts to the product’s original specification (e.g. picks the
         * same frame again), clear the “user modified” flag so pricing matches initial load.
         */
        function reconcileOriginalSpecAfterUserChange(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          if (!root || !productHasOriginalSpec(root)) return;
          if (isApplyingOriginalSpec(root)) return;
          syncSelectionsForOriginalSpecCheck(root);
          if (!isKeepingOriginalSpecifications(root)) return;
          root.dataset.originalSpecFullyApplied = '1';
          delete root.dataset.userModifiedSelections;
          if (!hasGlazingChangeFromOriginal(root)) customizerState.glazingManual = false;
          captureOriginalSpecMatBaselines(root);
        }

        function isOriginalSpecPieceAppliedOrLocked(spec, piece) {
          if (piece === 'frame' && customizerState.lockOriginalFrame) return true;
          if ((piece === 'topMat' || piece === 'bottomMat') && customizerState.lockOriginalMat) return true;
          if (piece === 'liner' && customizerState.lockOriginalLiner) return true;
          return isOriginalSpecPieceApplied(spec, piece);
        }

        function specPieceIdMatchesCurrent(spec, piece, root) {
          const wantId = piece === 'frame' ? spec.frameId
            : piece === 'topMat' ? spec.topMatId
              : piece === 'bottomMat' ? spec.bottomMatId
                : piece === 'liner' ? spec.linerId
                  : '';
          const statePiece = piece === 'frame' ? customizerState.frame
            : piece === 'topMat' ? customizerState.topMat
              : piece === 'bottomMat' ? customizerState.bottomMat
                : piece === 'liner' ? customizerState.liner
                  : null;
          const currentId = getCurrentCatalogIdForSpecPiece(root, piece);

          // Spec has no piece — only match when the customer also has none.
          // (Previously returned true here, so adding a mat was invisible to pricing / Add to Order.)
          if (!wantId) return !currentId;
          if (!currentId) return false;
          if (!catalogIdsMatch(currentId, wantId)) return false;

          // Same catalog code can have multiple colorways — require name match when known.
          if (piece === 'topMat' || piece === 'bottomMat' || piece === 'liner') {
            const wantName = piece === 'topMat' ? spec.topMatName
              : piece === 'bottomMat' ? spec.bottomMatName
                : spec.linerName;
            if (wantName && statePiece && statePiece.name) {
              if (!matNamesMatch(wantName, statePiece.name, statePiece)) return false;
            }
          }
          return true;
        }

        function isOriginalSpecPieceApplied(spec, piece) {
          if (!spec) return true;
          const root = getActiveCustomizerRoot();
          return specPieceIdMatchesCurrent(spec, piece, root);
        }

        function isOriginalSpecFullyApplied(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return true;
          const treatment = String(customizerState.treatment || '').trim();
          /* On canvas, mats are forced off, so the spec's mat treatment/mat
             pieces can never be applied — expect "None" instead. */
          if (isCanvasSubstrate()) {
            return treatment === CANVAS_MAT_TREATMENT
              && isOriginalSpecPieceAppliedOrLocked(spec, 'frame')
              && isOriginalSpecPieceAppliedOrLocked(spec, 'liner');
          }
          let wantTreatment = '';
          if (spec.bottomMatId && !/^none$/i.test(spec.bottomMatId)) wantTreatment = 'Double Mat';
          else if (spec.treatment && !/^none$/i.test(spec.treatment)) wantTreatment = String(spec.treatment).trim();
          const treatmentOk = treatmentsMatchOriginal(treatment, wantTreatment);
          return treatmentOk
            && isOriginalSpecPieceAppliedOrLocked(spec, 'frame')
            && isOriginalSpecPieceAppliedOrLocked(spec, 'topMat')
            && (treatment !== 'Double Mat' || !spec.bottomMatId || isOriginalSpecPieceAppliedOrLocked(spec, 'bottomMat'))
            && isOriginalSpecPieceAppliedOrLocked(spec, 'liner');
        }

        var _applyOriginalSpecDebounceTimer = 0;
        var _applyOriginalSpecDebouncePending = new Set();
        var _originalSpecRetryTimers = new WeakMap();

        function areOriginalSpecCatalogsReady(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return true;
          if (spec.frameId) {
            const frames = typeof window.__customizerFramesApi !== 'undefined' && window.__customizerFramesApi
              ? window.__customizerFramesApi
              : (typeof FRAMES !== 'undefined' ? FRAMES : []);
            if (!frames || !frames.length) return false;
            const hasFrame = frames.some(function (f) {
              return catalogIdsMatch(f.id, spec.frameId);
            });
            if (!hasFrame) return false;
          }
          if (spec.topMatId && !findMatInCatalogs('topMat', spec.topMatId, spec.topMatName)) return false;
          if (spec.bottomMatId && !findMatInCatalogs('bottomMat', spec.bottomMatId, spec.bottomMatName)) return false;
          if (spec.linerId && !findMatInCatalogs('liner', spec.linerId, spec.linerName)) return false;
          return true;
        }

        function stopOriginalSpecRetryLoop(customizer) {
          const t = _originalSpecRetryTimers.get(customizer);
          if (t) {
            clearTimeout(t);
            _originalSpecRetryTimers.delete(customizer);
          }
        }

        /** Keep trying until every spec piece is in state (APIs + frame module can load late). */
        function startOriginalSpecRetryLoop(customizer) {
          if (!customizer || !productHasOriginalSpec(customizer)) return;
          if (customizer.dataset.userModifiedSelections === '1') return;
          stopOriginalSpecRetryLoop(customizer);

          var attempts = 0;
          var maxAttempts = 40;

          function tick() {
            if (!customizer.isConnected || customizer.dataset.userModifiedSelections === '1') {
              stopOriginalSpecRetryLoop(customizer);
              return;
            }
            applyOriginalSpecifications(customizer);
            if (isOriginalSpecFullyApplied(customizer) || attempts >= maxAttempts) {
              stopOriginalSpecRetryLoop(customizer);
              return;
            }
            attempts += 1;
            var delay = attempts < 8 ? 120 : 280;
            _originalSpecRetryTimers.set(customizer, setTimeout(tick, delay));
          }

          tick();
        }

        /** Debounced entry — waits for init + retries until spec is fully applied. */
        function scheduleApplyOriginalSpecifications(customizer) {
          if (!customizer || !productHasOriginalSpec(customizer)) return;
          if (customizer.dataset.userModifiedSelections === '1') return;

          if (customizer.dataset.customizerInited !== '1') {
            customizer.dataset.pendingOriginalSpec = '1';
            return;
          }

          _applyOriginalSpecDebouncePending.add(customizer);
          clearTimeout(_applyOriginalSpecDebounceTimer);
          _applyOriginalSpecDebounceTimer = setTimeout(function () {
            var pending = Array.from(_applyOriginalSpecDebouncePending);
            _applyOriginalSpecDebouncePending.clear();
            pending.forEach(function (c) {
              startOriginalSpecRetryLoop(c);
            });
          }, 30);
        }

        window.__scheduleApplyOriginalSpecifications = scheduleApplyOriginalSpecifications;
        window.__startOriginalSpecRetryLoop = startOriginalSpecRetryLoop;

        function parseMatSizeFromSpecLabel(raw) {
          const s = String(raw || '');
          const patterns = [
            /(\d+(?:\.\d+)?)\s*["']?\s*(?:inch|inches|in)\b/i,
            /(\d+(?:\.\d+)?)\s*inch/i
          ];
          for (let i = 0; i < patterns.length; i++) {
            const m = s.match(patterns[i]);
            if (m) return parseFloat(m[1]);
          }
          return null;
        }

        /** Printed mat in treatment metafield or top/bottom mat spec (incl. "prtined" typo). */
        function isPrintedMatLikeLabel(raw) {
          const s = String(raw || '').trim();
          if (!s || /^none$/i.test(s)) return false;
          if (/pr[i]?t?i?n?ed\s*mat/i.test(s)) return true;
          const id = parseSpecCatalogId(s);
          if (/^PR[INT]*ED$/i.test(id)) return true;
          const name = parseSpecMatName(s);
          if (/pr[i]?t?i?n?ed\s*mat/i.test(name)) return true;
          return /pr[i]?t?i?n?ed\s*mat/i.test(s);
        }

        function isPrintedMatTreatmentSpec(raw) {
          return isPrintedMatLikeLabel(raw);
        }

        function getProductPrintedMatSpecSources(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          return [
            parseDefaultAttr(root.dataset.originalTreatment),
            parseDefaultAttr(root.dataset.defaultTreatment),
            String(root.dataset.originalTopMat || '').trim(),
            String(root.dataset.originalBottomMat || '').trim()
          ].filter(function (s) { return s && !/^none$/i.test(s); });
        }

        function getProductMatSpecificationRaw(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          const sources = getProductPrintedMatSpecSources(root);
          for (let i = 0; i < sources.length; i++) {
            if (isPrintedMatLikeLabel(sources[i])) return sources[i];
          }
          return sources[0] || '';
        }

        function getMatSpecificationInchesFromProduct(customizerRoot) {
          const sources = getProductPrintedMatSpecSources(customizerRoot);
          for (let i = 0; i < sources.length; i++) {
            const spec = sources[i];
            if (!isPrintedMatLikeLabel(spec)) continue;
            const inches = parseMatSizeFromSpecLabel(spec);
            if (isFinite(inches) && inches > 0) {
              return snapTopMatSizeInches(inches);
            }
          }
          return 0;
        }

        function hasPrintedMatTreatmentFromProduct(customizerRoot) {
          const sources = getProductPrintedMatSpecSources(customizerRoot);
          return sources.some(isPrintedMatLikeLabel);
        }

        function hasPrintedMatSpecificationCharges(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!hasPrintedMatTreatmentFromProduct(root)) return false;
          return getMatSpecificationInchesFromProduct(root) > 0;
        }

        function applyMatInchesFromProductSpecification(customizer) {
          if (!customizer) return;
          const inches = getMatSpecificationInchesFromProduct(customizer);
          if (inches > 0) customizerState.topMatSizeInches = inches;
        }

        function inferTreatmentFromMatSpecification(raw) {
          const spec = String(raw || '').trim();
          if (!spec || /^none$/i.test(spec)) return '';
          if (isPrintedMatLikeLabel(spec)) return 'Single Mat';
          if (/^no\s*mat$/i.test(spec)) return NO_MAT_TREATMENT;
          if (spec === 'Double Mat' || spec === 'Single Mat' || spec === NO_MAT_TREATMENT) return spec;
          return '';
        }

        function getDefaultMatSizeInches(stateKey, customizer) {
          if (stateKey === 'bottomMat') {
            if (customizer) {
              const spec = readOriginalSpecFromCustomizer(customizer);
              if (spec && spec.bottomMatId && !/^none$/i.test(spec.bottomMatId)) {
                const fromLabel = parseMatSizeFromSpecLabel(customizer.dataset.originalBottomMat);
                if (fromLabel > 0) return snapMatSizeInches(fromLabel, 'bottomMat');
                return spec.bottomMatSize;
              }
            }
            return DEFAULT_BOTTOM_MAT_SIZE_INCHES;
          }
          if (stateKey === 'topMat') {
            if (customizer) {
              const spec = readOriginalSpecFromCustomizer(customizer);
              if (spec && spec.topMatId) {
                const fromLabel = parseMatSizeFromSpecLabel(customizer.dataset.originalTopMat);
                if (fromLabel > 0) return snapMatSizeInches(fromLabel, 'topMat');
                return spec.topMatSize;
              }
            }
            return DEFAULT_TOP_MAT_SIZE_INCHES;
          }
          return 0;
        }

        function snapTopMatSizeInches(inches) {
          const n = parseFloat(inches);
          if (!isFinite(n) || n <= 0) return DEFAULT_TOP_MAT_SIZE_INCHES;
          let closest = TOP_MAT_SIZE_OPTIONS[0];
          let minDiff = Math.abs(n - closest);
          TOP_MAT_SIZE_OPTIONS.forEach(function (opt) {
            const diff = Math.abs(n - opt);
            if (diff < minDiff) {
              minDiff = diff;
              closest = opt;
            }
          });
          return closest;
        }

        function snapBottomMatSizeInches(inches) {
          const n = parseFloat(inches);
          if (!isFinite(n) || n <= 0) return DEFAULT_BOTTOM_MAT_SIZE_INCHES;
          let closest = BOTTOM_MAT_SIZE_OPTIONS[0];
          let minDiff = Math.abs(n - closest);
          BOTTOM_MAT_SIZE_OPTIONS.forEach(function (opt) {
            const diff = Math.abs(n - opt);
            if (diff < minDiff) {
              minDiff = diff;
              closest = opt;
            }
          });
          return closest;
        }

        function snapMatSizeInches(inches, stateKey) {
          return stateKey === 'bottomMat'
            ? snapBottomMatSizeInches(inches)
            : snapTopMatSizeInches(inches);
        }

        function readOriginalSpecFromCustomizer(customizer) {
          if (!customizer) return null;
          const substrateRaw = customizer.dataset.originalSubstrate ||
            customizer.dataset.defaultSubstrate;
          const treatmentRaw = customizer.dataset.originalTreatment ||
            customizer.dataset.defaultTreatment;
          return {
            substrate: parseDefaultAttr(substrateRaw),
            treatment: parseDefaultAttr(treatmentRaw),
            dimensions: String(customizer.dataset.originalDimensions || '').trim(),
            frameId: parseSpecCatalogId(customizer.dataset.originalFrame),
            topMatId: parseSpecCatalogId(customizer.dataset.originalTopMat),
            topMatName: parseSpecMatName(customizer.dataset.originalTopMat),
            bottomMatId: parseSpecCatalogId(customizer.dataset.originalBottomMat),
            bottomMatName: parseSpecMatName(customizer.dataset.originalBottomMat),
            linerId: parseSpecCatalogId(customizer.dataset.originalLiner),
            linerName: parseSpecMatName(customizer.dataset.originalLiner),
            glazing: readOriginalGlazingMetafield(customizer),
            topMatSize: snapMatSizeInches(
              parseMatSizeFromSpecLabel(customizer.dataset.originalTopMat) || DEFAULT_TOP_MAT_SIZE_INCHES,
              'topMat'
            ),
            bottomMatSize: snapMatSizeInches(
              parseMatSizeFromSpecLabel(customizer.dataset.originalBottomMat) || DEFAULT_BOTTOM_MAT_SIZE_INCHES,
              'bottomMat'
            )
          };
        }

        function productHasOriginalSpec(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return false;
          if (spec.frameId || spec.topMatId || spec.bottomMatId || spec.linerId) return true;
          if (parseMetafieldDimensions(spec.dimensions)) return true;
          if (spec.substrate && !/^none$/i.test(spec.substrate)) return true;
          if (spec.treatment && !/^none$/i.test(spec.treatment)) return true;
          return false;
        }

        function setMatSizeInches(stateKey, inches) {
          if (stateKey !== 'topMat' && stateKey !== 'bottomMat') return;
          const sizeKey = stateKey === 'bottomMat' ? 'bottomMatSizeInches' : 'topMatSizeInches';
          const val = snapMatSizeInches(inches, stateKey);
          customizerState[sizeKey] = val;
          const sel = matSizeSelectForStateKey(stateKey);
          if (sel) sel.value = String(val);
        }

        function getMatCatalog(stateKey) {
          if (stateKey === 'liner') return getActiveLiners();
          if (stateKey === 'topMat' && window.__customizerMatsApiTop?.length) return window.__customizerMatsApiTop;
          if (stateKey === 'bottomMat' && window.__customizerMatsApiBottom?.length) return window.__customizerMatsApiBottom;
          return MAT_COLORS;
        }

        function getMatCatalogLists(stateKey) {
          const lists = [];
          const primary = getMatCatalog(stateKey);
          if (primary && primary.length) lists.push(primary);
          if (stateKey === 'topMat' || stateKey === 'bottomMat') {
            if (MAT_COLORS && MAT_COLORS.length) lists.push(MAT_COLORS);
            if (stateKey === 'bottomMat' && window.__customizerMatsApiTop?.length) {
              lists.push(window.__customizerMatsApiTop);
            }
            if (stateKey === 'topMat' && window.__customizerMatsApiBottom?.length) {
              lists.push(window.__customizerMatsApiBottom);
            }
          }
          if (stateKey === 'liner' && typeof LINER_COLORS !== 'undefined' && LINER_COLORS.length) {
            lists.push(LINER_COLORS);
          }
          return lists;
        }

        /** All catalog rows sharing the same code (e.g. PM66 White Silk vs PM66 Linen). */
        function findAllMatsInCatalogs(stateKey, matId) {
          const id = normalizeCatalogId(matId);
          if (!id) return [];
          const lists = getMatCatalogLists(stateKey);
          const found = [];
          const seen = new Set();
          for (let i = 0; i < lists.length; i++) {
            lists[i].forEach(function (m) {
              if (!catalogIdsMatch(m.id, id)) return;
              const key = normalizeCatalogId(m.id) + '|' + matDisplayNameForMatch(m);
              if (seen.has(key)) return;
              seen.add(key);
              found.push(m);
            });
          }
          return found;
        }

        /** Search API list, then built-in MAT_COLORS / LINER_COLORS for a catalog id. */
        function findMatInCatalogs(stateKey, matId, matName) {
          const id = normalizeCatalogId(matId);
          if (!id) return null;
          const all = findAllMatsInCatalogs(stateKey, id);
          if (!all.length) return null;
          if (all.length === 1) return all[0];
          const name = String(matName || '').trim();
          if (!name) return all[0];
          const matched = all.filter(function (m) {
            return matNamesMatch(name, m.name, m);
          });
          return matched.length ? matched[0] : all[0];
        }

        function inferOriginalTreatmentFromSpec(spec, customizer) {
          applyOriginalTreatmentFromSpec(spec, customizer);
        }

        function assignMatStubFromSpec(customizer, stateKey, matId, rawLabel) {
          const id = String(matId || '').trim();
          if (!id) return false;
          const specName = parseSpecMatName(rawLabel);
          const catalogMat = findMatInCatalogs(stateKey, id, specName);
          if (catalogMat) {
            assignMatToState(catalogMat, stateKey);
            return true;
          }
          const name = String(rawLabel || id).replace(/^[A-Za-z0-9\-_]+\s*[—–-]\s*/i, '').trim() || id;
          if (stateKey === 'liner') {
            customizerState.liner = {
              id: id,
              name: name,
              hex: null,
              type: null,
              img: null,
              frameW: null,
              frameImg: null,
              basePrice: null
            };
          } else {
            customizerState[stateKey] = {
              id: id,
              name: name,
              hex: null,
              type: null,
              img: null,
              matW: 1.2
            };
          }
          return true;
        }

        function syncMatCardsFromState(customizer) {
          if (!customizer) return;
          const pairs = [
            { key: 'topMat', panel: '[data-cz-tab-panel="topmat"]' },
            { key: 'bottomMat', panel: '[data-cz-tab-panel="bottommat"]' },
            { key: 'liner', panel: '[data-cz-tab-panel="liner"]' }
          ];
          pairs.forEach(function (p) {
            const mat = customizerState[p.key];
            const panel = customizer.querySelector(p.panel);
            if (!panel) return;
            panel.querySelectorAll('.mat-card').forEach(function (c) {
              let active = false;
              if (mat && catalogIdsMatch(c.dataset.matId, mat.id)) {
                const cardName = (c.querySelector('.mat-card-nm')?.textContent || '').trim();
                const duplicates = findAllMatsInCatalogs(p.key, mat.id);
                if (duplicates.length <= 1) {
                  active = true;
                } else {
                  const cardMat = duplicates.find(function (m) {
                    return matNamesMatch(m.name, cardName, m) ||
                      matDisplayNameForMatch(m) === normalizeMatNameForMatch(cardName);
                  });
                  active = !!(cardMat && matsStateMatchesCatalogMat(mat, cardMat, p.key));
                }
              }
              c.classList.toggle('mat-active', active);
            });
          });
        }

        function assignMatToState(mat, stateKey) {
          if (!mat) return;
          if (stateKey === 'liner') {
            customizerState.liner = {
              id: mat.id,
              name: mat.name,
              hex: mat.hex,
              type: mat.type,
              img: mat.img || null,
              frameW: mat.frameW || null,
              linearSize: mat.linearSize ?? mat.frameW ?? null,
              frameImg: mat.frameImg || mat.img || null,
              basePrice: mat.basePrice ?? null,
              _api: mat._api || null
            };
            return;
          }
          customizerState[stateKey] = {
            id: mat.id,
            name: mat.name,
            hex: mat.hex,
            type: mat.type,
            img: mat.img || null,
            matW: mat.matW || 1.2,
            oversizedMat: !!mat.oversizedMat,
            printedMats: !!mat.printedMats,
            pickFrom: mat.pickFrom !== false,
            matColorName: getMatColorFilterName(mat) || undefined
          };
        }

        function applyMatSelection(customizer, stateKey, matId, sizeInches, matName) {
          const id = normalizeCatalogId(matId);
          if (!id) return false;
          const resolvedName = matName != null && String(matName).trim()
            ? String(matName).trim()
            : (stateKey === 'topMat'
              ? parseSpecMatName(customizer && customizer.dataset.originalTopMat)
              : stateKey === 'bottomMat'
                ? parseSpecMatName(customizer && customizer.dataset.originalBottomMat)
                : parseSpecMatName(customizer && customizer.dataset.originalLiner));
          const current = customizerState[stateKey];
          if (current && catalogIdsMatch(current.id, id)) {
            const duplicates = findAllMatsInCatalogs(stateKey, id);
            if (duplicates.length <= 1 || matNamesMatch(resolvedName, current.name, current)) {
              if (stateKey === 'topMat' || stateKey === 'bottomMat') {
                const inches = parseFloat(sizeInches) > 0
                  ? sizeInches
                  : getDefaultMatSizeInches(stateKey, customizer);
                setMatSizeInches(stateKey, inches);
              }
              return true;
            }
          }
          const mat = findMatInCatalogs(stateKey, id, resolvedName);
          if (!mat) {
            const rawLabel = stateKey === 'topMat'
              ? customizer.dataset.originalTopMat
              : stateKey === 'bottomMat'
                ? customizer.dataset.originalBottomMat
                : customizer.dataset.originalLiner;
            if (!assignMatStubFromSpec(customizer, stateKey, id, rawLabel)) return false;
          } else {
            assignMatToState(mat, stateKey);
          }
          if (stateKey === 'topMat' || stateKey === 'bottomMat') {
            const inches = parseFloat(sizeInches) > 0
              ? sizeInches
              : getDefaultMatSizeInches(stateKey, customizer);
            setMatSizeInches(stateKey, inches);
          }
          if (stateKey === 'topMat' && typeof window.__matRefreshTop === 'function') window.__matRefreshTop();
          if (stateKey === 'bottomMat' && typeof window.__matRefreshBottom === 'function') window.__matRefreshBottom();
          if (stateKey === 'liner' && typeof window.__matRefreshLiner === 'function') window.__matRefreshLiner();
          return true;
        }

        function restoreOriginalSubstrateAndTreatment(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return;
          const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
          const substrateRaw = customizer.dataset.originalSubstrate ||
            customizer.dataset.defaultSubstrate;
          const substrateVal = resolveSubstrateForSelect(customizer, substrateRaw || spec.substrate);
          if (substrateSelect && substrateVal && !/^none$/i.test(substrateVal)) {
            substrateSelect.value = substrateVal;
            customizerState.substrate = substrateVal;
          }
          applyOriginalTreatmentFromSpec(spec, customizer);
          if (!customizerState.glazingManual) {
            customizerState.glazing = customizerState.defaultGlazing ||
              resolveDefaultGlazingFromOriginalSpec(customizer);
          }
          applyGlazingRules(customizer);
        }

        function restoreOriginalArtworkSize(customizer) {
          applyDefaultArtworkSizeFromProduct(customizer);
        }

        function restoreOriginalMats(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec) return;
          if (spec.topMatId) {
            applyMatSelection(customizer, 'topMat', spec.topMatId, spec.topMatSize, spec.topMatName);
          } else {
            customizerState.topMat = null;
          }
          if (spec.bottomMatId && !/^none$/i.test(String(spec.bottomMatId))) {
            if (String(customizerState.treatment || '').trim() !== 'Double Mat') {
              setTreatmentOnCustomizer(customizer, 'Double Mat');
            }
            applyMatSelection(customizer, 'bottomMat', spec.bottomMatId, spec.bottomMatSize, spec.bottomMatName);
          } else if (String(customizerState.treatment || '').trim() !== 'Double Mat') {
            customizerState.bottomMat = null;
          }
          syncMatCardsFromState(customizer);
          updateMatStepLabel(customizer);
        }

        function applyOriginalFrame(customizer, frameId) {
          if (!frameId) return false;
          if (typeof window.__fsSelectFrameById === 'function' && window.__fsSelectFrameById(frameId)) {
            return true;
          }
          const f = getActiveFrames().find(function (x) {
            return catalogIdsMatch(x.id, frameId);
          });
          if (!f) return false;
          onFrameSelected(f.id, f.name);
          if (typeof window.__fsSelectFrameById === 'function') {
            window.__fsSelectFrameById(f.id);
          }
          return true;
        }

        function restoreOriginalFrame(customizer) {
          const root = customizer || document.querySelector('.customizer');
          const spec = readOriginalSpecFromCustomizer(root);
          if (!spec || !spec.frameId) return false;
          return applyOriginalFrame(root, spec.frameId);
        }

        /**
         * When substrate returns to the product's original substrate, re-select the
         * original-spec frame if it is allowed for that substrate (e.g. floater on Canvas).
         */
        function restoreOriginalFrameForCurrentSubstrate(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          if (!root || !productHasOriginalSpec(root)) return false;
          const spec = readOriginalSpecFromCustomizer(root);
          if (!spec || !spec.frameId) return false;
          if (!matchesOriginalSubstrate(root, spec)) return false;
          if (customizerState.frame && catalogIdsMatch(customizerState.frame.id, spec.frameId)) {
            return true;
          }
          root.dataset.pendingOriginalFrameId = spec.frameId;
          return !!restoreOriginalFrame(root);
        }

        function restoreOriginalLiner(customizer) {
          const spec = readOriginalSpecFromCustomizer(customizer);
          if (!spec || !spec.linerId) return;
          applyMatSelection(customizer, 'liner', spec.linerId, undefined, spec.linerName);
        }

        /** Uncheck all keep-original toggles so the customer can customize freely. */
        function releaseOriginalSpecLocks(customizer) {
          customizerState.lockOriginalSubstrateMat = false;
          customizerState.lockOriginalArtworkSize = false;
          customizerState.lockOriginalMat = false;
          customizerState.lockOriginalFrame = false;
          customizerState.lockOriginalLiner = false;
          if (!customizer) return;
          const selectors = [
            '#lock-original-substrate-mat-cb',
            '#lock-original-artwork-size-cb',
            '.lock-original-mat-cb',
            '.lock-original-frame-only-cb',
            '.lock-original-liner-only-cb'
          ];
          selectors.forEach(function (sel) {
            customizer.querySelectorAll(sel).forEach(function (el) {
              if (el.checked) {
                el.checked = false;
                el.dispatchEvent(new Event('change'));
              }
            });
          });
        }

        function isApplyingOriginalSpec(customizer) {
          return !!(customizer && customizer.dataset.applyingOriginalSpec === '1');
        }

        function applyOriginalSpecifications(customizer) {
          if (!customizer || !productHasOriginalSpec(customizer)) return;
          if (customizer.dataset.userModifiedSelections === '1') return;

          customizer.dataset.applyingOriginalSpec = '1';
          try {
            const spec = readOriginalSpecFromCustomizer(customizer);
            if (spec.frameId) customizer.dataset.pendingOriginalFrameId = spec.frameId;

            restoreOriginalSubstrateAndTreatment(customizer);
            restoreOriginalArtworkSize(customizer);
            applyOriginalTreatmentFromSpec(spec, customizer);
            syncCanvasOnlyPricingAddons();

            const treatment = String(customizerState.treatment || '').trim();
            if (treatment === 'Single Mat' && !spec.bottomMatId) customizerState.bottomMat = null;
            if ((treatment === 'None' || treatment === CANVAS_MAT_TREATMENT || isNoMatTreatment(treatment)) &&
                !spec.topMatId && !spec.bottomMatId) {
              customizerState.topMat = null;
              customizerState.bottomMat = null;
            }

            if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
              window.__fsEnforceFrameSubstrateRules({ skipReset: true });
            }

            const isCanvasSpecSubstrate = isCanvasSubstrate();
            if (!isCanvasSpecSubstrate && spec.topMatId && !isOriginalSpecPieceApplied(spec, 'topMat')) {
              applyMatSelection(customizer, 'topMat', spec.topMatId, spec.topMatSize, spec.topMatName);
            }
            if (!isCanvasSpecSubstrate && spec.bottomMatId && !isOriginalSpecPieceApplied(spec, 'bottomMat')) {
              if (String(customizerState.treatment || '').trim() !== 'Double Mat') {
                setTreatmentOnCustomizer(customizer, 'Double Mat');
              }
              applyMatSelection(customizer, 'bottomMat', spec.bottomMatId, spec.bottomMatSize, spec.bottomMatName);
            }
            if (spec.linerId && !isOriginalSpecPieceApplied(spec, 'liner')) {
              applyMatSelection(customizer, 'liner', spec.linerId, undefined, spec.linerName);
            }

            if (spec.frameId && !isOriginalSpecPieceApplied(spec, 'frame')) {
              applyOriginalFrame(customizer, spec.frameId);
            }

            const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
            const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
            if (substrateSelect) substrateSelect.value = customizerState.substrate;
            if (treatmentSelect) treatmentSelect.value = customizerState.treatment;

            if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
              window.__fsEnforceFrameSubstrateRules({ skipReset: true });
            }
            if (typeof window.fsFilter === 'function') window.fsFilter();

            updateMatStepLabel();
            updateFrameLinerStepLabel();
            updateSubstrateStepLabel();
            updateBottomMatStepAvailability(customizer);
            updateCanvasMatStepAvailability(customizer);
            updateCanvasLinerAvailability(customizer);
            updateCanvasBrushstrokeUI(customizer);
            updateMatStepHints(customizer);
            updateCurrentSizesDisplay(customizer);
            syncMatCardsFromState(customizer);
            refreshCustomizerAfterSelectionChange(customizer, { skipDomSync: true });
            document.querySelectorAll('.customizer').forEach(function (c) {
              refreshFormulaPricingFromProductDefaults(c);
              updateSummary(c);
            });

            if (isOriginalSpecFullyApplied(customizer)) {
              customizer.dataset.originalSpecFullyApplied = '1';
              delete customizer.dataset.pendingOriginalFrameId;
              captureOriginalProductSizeDisplay(customizer);
              captureOriginalSpecMatBaselines(customizer);
              if (!customizerState.glazingManual) {
                customizerState.defaultGlazing = resolveDefaultGlazingFromOriginalSpec(customizer);
                customizerState.glazing = customizerState.defaultGlazing;
              }
              applyGlazingRules(customizer);
              /* Snapshot default formula costs into nochange inputs, then lock. */
              try {
                const formula = shouldShowFormulaPricingReview(customizer)
                  ? calculateFormulaPricing(customizer)
                  : null;
                syncProductFormFormulaCosts(customizer, formula);
              } catch (eSnap) { /* ignore */ }
              freezeNochangeFormulaCostInputs();
            } else {
              delete customizer.dataset.originalSpecFullyApplied;
            }
          } finally {
            delete customizer.dataset.applyingOriginalSpec;
            enforceOversizeMatRules(customizer);
          }
        }

        /* =========================
          STEP 1 — SUBSTRATE / TREATMENT
        ========================== */
        function removeHighGlossDeckleSubstrateOptions(select) {
          if (!select) return;
          Array.from(select.options).forEach(function (opt) {
            const val = String(opt.value || opt.textContent || '');
            if (/high gloss/i.test(val) && /deckle/i.test(val)) opt.remove();
          });
        }

        function initStep1(customizer) {
          const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
          const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
          const glazingSelect = customizer.querySelector('[id^="customizer-glazing-"]');
          removeHighGlossDeckleSubstrateOptions(substrateSelect);
          bindCropButton(customizer);
          preloadCustomizerCropSources(customizer);
          bindCanvasBrushstrokeToggle(customizer);
          updateCanvasBrushstrokeUI(customizer);

          function applyMetafieldDefaults() {
            if (!customizer || customizer.dataset.defaultsApplied === '1') return;
            const defaultAcrylicBrushstroke = parseDatasetBoolean(customizer.dataset.defaultAcrylicBrushstroke);
            if (productHasOriginalSpec(customizer)) {
              captureOriginalProductSizeDisplay(customizer);
              const spec0 = readOriginalSpecFromCustomizer(customizer);
              const substrateVal0 = resolveSubstrateForSelect(
                customizer,
                customizer.dataset.originalSubstrate || customizer.dataset.defaultSubstrate
              );
              if (substrateSelect && substrateVal0) {
                substrateSelect.value = substrateVal0;
                customizerState.substrate = substrateVal0;
              }
              if (spec0) applyOriginalTreatmentFromSpec(spec0, customizer);
              customizerState.acrylicBrushstroke = isCanvasSubstrate() && defaultAcrylicBrushstroke;
              updateCanvasBrushstrokeUI(customizer);
              customizerState.defaultSubstrate = customizerState.substrate;
              customizerState.defaultTreatment = customizerState.treatment;
              customizerState.defaultGlazing = resolveDefaultGlazingFromOriginalSpec(customizer);
              customizerState.glazing = customizerState.defaultGlazing;
              applyGlazingRules(customizer);
              customizer.dataset.defaultsApplied = '1';
              return;
            }
            var ds = parseDefaultAttr(customizer.dataset.defaultSubstrate);
            var dt = parseDefaultAttr(customizer.dataset.defaultTreatment);

            if (substrateSelect && ds) {
              var okS = Array.from(substrateSelect.options).some(function (o) { return o.value === ds || o.textContent === ds; });
              if (okS) { substrateSelect.value = ds; customizerState.substrate = ds; }
            }
            if (treatmentSelect && dt) {
              const isCanvas = String(customizerState.substrate || substrateSelect?.value || '')
                .trim().toLowerCase() === 'canvas';
              if (isCanvas) {
                if (dt !== CANVAS_MAT_TREATMENT) dt = CANVAS_MAT_TREATMENT;
              } else if (isPrintedMatTreatmentSpec(dt)) {
                applyMatInchesFromProductSpecification(customizer);
                dt = inferTreatmentFromMatSpecification(dt) || 'Single Mat';
              } else if (dt !== 'Single Mat' && dt !== 'Double Mat' && dt !== NO_MAT_TREATMENT) {
                dt = 'Single Mat';
              }
              var okT = Array.from(treatmentSelect.options).some(function (o) { return o.value === dt || o.textContent === dt; });
              if (okT) { treatmentSelect.value = dt; customizerState.treatment = dt; }
            }
            customizerState.acrylicBrushstroke = isCanvasSubstrate() && defaultAcrylicBrushstroke;
            updateCanvasBrushstrokeUI(customizer);
            customizerState.defaultSubstrate = customizerState.substrate;
            customizerState.defaultTreatment = customizerState.treatment;
            customizerState.defaultGlazing = resolveDefaultGlazingFromOriginalSpec(customizer);
            customizerState.glazing = customizerState.defaultGlazing;
            applyGlazingRules(customizer);
            customizer.dataset.defaultsApplied = '1';
          }

          if (treatmentSelect) {
            Array.from(treatmentSelect.options).forEach(o => {
              const v = String(o.value || o.textContent || '').trim();
              if (o.dataset.canvasOnly != null) return;
              if (!ALL_MAT_TREATMENT_OPTIONS.includes(v)) o.remove();
            });
            if (!customizerState.treatment) {
              customizerState.treatment = 'Single Mat';
            }
          }

          /* Re-apply defaults whenever initStep1 runs (e.g. on modal open) */
          applyMetafieldDefaults();

          function setTreatmentValue(next) {
            if (!treatmentSelect) return false;
            const ok = Array.from(treatmentSelect.options).some(o => o.value === next || o.textContent === next);
            if (!ok) return false;
            treatmentSelect.value = next;
            customizerState.treatment = next;
            return true;
          }

          function enforceDeckleSubstrateRules() {
            if (!treatmentSelect || isCanvasSubstrate()) return;
            const cur = String(customizerState.treatment || '').trim();
            if (isDeckleSubstrate() && (isNoMatTreatment(cur) || cur === NO_MAT_TREATMENT)) {
              setTreatmentValue('Single Mat');
            } else if (!MAT_TREATMENT_OPTIONS.includes(cur)) {
              setTreatmentValue('Single Mat');
            }
          }

          function enforceCanvasNoMats() {
            if (!substrateSelect || !treatmentSelect) return;

            const isCanvas = String(substrateSelect.value || '').trim().toLowerCase() === 'canvas';

            treatmentSelect.disabled = isCanvas || !!customizerState.lockOriginalSubstrateMat;
            if (substrateSelect) substrateSelect.disabled = !!customizerState.lockOriginalSubstrateMat;

            syncTreatmentSelectOptions(treatmentSelect, isCanvas);

            if (isCanvas) {
              const prev = String(treatmentSelect.value || customizerState.treatment || '').trim();
              if (MAT_TREATMENT_OPTIONS.includes(prev)) {
                customizer.dataset.lastNonCanvasTreatment = prev;
              }
              setTreatmentValue(CANVAS_MAT_TREATMENT);
              customizerState.treatment = CANVAS_MAT_TREATMENT;
              customizerState.topMat = null;
              customizerState.bottomMat = null;
              customizerState.liner = null;
              syncCanvasOnlyPricingAddons();
              updateMatStepLabel();
              updateCanvasLinerAvailability(customizer);
            } else {
              if (substrateSelect) substrateSelect.disabled = !!customizerState.lockOriginalSubstrateMat;
              const cur = String(treatmentSelect.value || customizerState.treatment || '').trim();
              if (cur === CANVAS_MAT_TREATMENT) {
                const last = String(customizer.dataset.lastNonCanvasTreatment || '').trim();
                if (MAT_TREATMENT_OPTIONS.includes(last)) {
                  setTreatmentValue(last);
                } else {
                  setTreatmentValue('Single Mat');
                }
              }
              enforceDeckleSubstrateRules();
            }
          }

          function onTreatmentChange() {
            if (isApplyingOriginalSpec(customizer)) return;
            if (isCanvasSubstrate()) {
              customizerState.treatment = CANVAS_MAT_TREATMENT;
              if (treatmentSelect && treatmentSelect.value !== CANVAS_MAT_TREATMENT) {
                setTreatmentValue(CANVAS_MAT_TREATMENT);
              }
              customizerState.topMat = null;
              customizerState.bottomMat = null;
            } else {
              customizerState.treatment = treatmentSelect ? treatmentSelect.value : customizerState.treatment;
              const t = String(customizerState.treatment || '').trim();
              if (!MAT_TREATMENT_OPTIONS.includes(t)) {
                setTreatmentValue('Single Mat');
              }
              const treatment = String(customizerState.treatment || '').trim();
              if (isNoMatTreatment(treatment) || treatment === NO_MAT_TREATMENT) {
                customizerState.topMat = null;
                customizerState.bottomMat = null;
                clearAllMatSelections(customizer);
              } else if (treatment === 'Single Mat') {
                customizerState.bottomMat = null;
                const bottomGrid = customizer.querySelector('#mat-grid-step5');
                const bottomScope = bottomGrid?.closest('[data-cz-tab-panel="bottommat"]') || customizer;
                if (bottomGrid) clearMatSelection('bottomMat', bottomGrid, bottomScope);
              } else if (treatment === 'Double Mat') {
                const spec = readOriginalSpecFromCustomizer(customizer);
                const hasSpecBottom = spec && spec.bottomMatId && !/^none$/i.test(spec.bottomMatId);
                if (!hasSpecBottom && !customizerState.bottomMat) {
                  setMatSizeInches('bottomMat', getDefaultMatSizeInches('bottomMat', customizer));
                }
              }
            }
            updateSubstrateStepLabel();
            updateSummary(customizer);
            refreshStepPillStates(customizer);
            updateMatPreview(customizer);
            redrawFrameCanvasWithMats();
            updateBottomMatStepAvailability(customizer);
            updateCanvasMatStepAvailability(customizer);
            updateMatStepHints(customizer);
            updateCurrentSizesDisplay(customizer);
            enforceOversizeMatRules(customizer);

            const cur = getCurrentStep(customizer);
            if (cur && !isStepEnabled(cur)) {
              const maxStep = getMaxWizardStep(customizer);
              let next = cur + 1;
              while (next <= maxStep && !isStepEnabled(next)) next++;
              if (next <= maxStep) goToStep(customizer, next);
            }
          }

          /* Bind listeners once per customizer */
          if (!customizer.dataset.step1Bound) {
            if (substrateSelect) {
              substrateSelect.value = customizerState.substrate;
              substrateSelect.addEventListener('change', () => {
                if (isApplyingOriginalSpec(customizer)) return;
                markUserModifiedSelections(customizer);
                customizerState.substrate = substrateSelect.value;
                const isCanvas = String(substrateSelect.value || '').trim().toLowerCase() === 'canvas';
                syncCanvasOnlyPricingAddons();
                enforceCanvasNoMats();
                updateCanvasLinerAvailability(customizer);
                updateCanvasBrushstrokeUI(customizer);
                enforceDeckleSubstrateRules();
                if (!isCanvas) {
                  const cur = String(customizerState.treatment || treatmentSelect?.value || '').trim();
                  if (!MAT_TREATMENT_OPTIONS.includes(cur)) {
                    setTreatmentValue('Single Mat');
                  }
                }
                /* Clear incompatible frames BEFORE redraw (floater off Mat Paper, etc.). */
                if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
                  window.__fsEnforceFrameSubstrateRules();
                } else if (typeof window.fsFilter === 'function') {
                  window.fsFilter();
                }
                /* Back on original substrate → re-apply original-spec frame when allowed. */
                restoreOriginalFrameForCurrentSubstrate(customizer);
                updateSubstrateStepLabel();
                updateSummary(customizer);
                refreshStepPillStates(customizer);
                updateMatStepHints(customizer);
                updateCurrentSizesDisplay(customizer);
                updateCustomizerMainPrice(customizer);
                updateDeckleArtworkPreviewUI(customizer);
                redrawFrameCanvasWithMats();
                /* Ensure step availability + preview updates if treatment changed by enforcement */
                onTreatmentChange();
              });
            }

            if (treatmentSelect) {
              treatmentSelect.value = customizerState.treatment;
              treatmentSelect.addEventListener('change', function (e) {
                if (e && e.isTrusted) markUserModifiedSelections(customizer);
                onTreatmentChange();
              });
            }
            if (glazingSelect) {
              applyGlazingRules(customizer);
              glazingSelect.addEventListener('change', function (e) {
                if (isApplyingOriginalSpec(customizer)) return;
                freezeOriginalGlazingChoice(customizer);
                const picked = normalizeGlazingChoice(glazingSelect.value) || 'Glass';
                customizerState.glazing = picked;
                const orig = getOriginalGlazingChoice(customizer);
                const matchesOrig = picked === orig ||
                  (orig === 'None' && (picked === 'Glass' || picked === 'None'));
                customizerState.glazingManual = !matchesOrig;
                applyGlazingRules(customizer);
                refreshCustomizerPricingAndDimensions(customizer);
                updateSubstrateStepLabel();
              });
            }
            customizer.dataset.step1Bound = '1';
          } else {
            if (substrateSelect) substrateSelect.value = customizerState.substrate;
            if (treatmentSelect) treatmentSelect.value = customizerState.treatment;
            applyGlazingRules(customizer);
          }

          const clearBtn = customizer.querySelector('.step-panel[data-step="1"] .js-step-clear-btn');
          if (clearBtn && !clearBtn.dataset.stepClearBound) {
            clearBtn.dataset.stepClearBound = '1';
            clearBtn.addEventListener('click', () => {
              const ds = customizerState.defaultSubstrate || 'Matte Paper';
              const dt = customizerState.defaultTreatment || 'Single Mat';
              customizerState.substrate = ds;
              customizerState.treatment = dt;
              const lockSubMatCb = customizer.querySelector('#lock-original-substrate-mat-cb');
              if (lockSubMatCb) {
                lockSubMatCb.checked = false;
                lockSubMatCb.dispatchEvent(new Event('change'));
              }
              if (substrateSelect) {
                substrateSelect.disabled = false;
                substrateSelect.value = ds;
              }
              if (treatmentSelect) {
                treatmentSelect.value = dt;
              }
              customizerState.glazingManual = false;
              customizerState.glazing = customizerState.defaultGlazing ||
                resolveDefaultGlazingFromOriginalSpec(customizer) || 'Glass';
              applyGlazingRules(customizer);
              onTreatmentChange();
              afterStepClear(customizer, 1);
            });
          }

          if (productHasOriginalSpec(customizer)) {
            const specInit = readOriginalSpecFromCustomizer(customizer);
            if (specInit) applyOriginalTreatmentFromSpec(specInit, customizer);
          }
          enforceCanvasNoMats();
          enforceDeckleSubstrateRules();
          updateDeckleArtworkPreviewUI(customizer);
          updateCanvasMatStepAvailability(customizer);
          updateCanvasLinerAvailability(customizer);
          updateCanvasBrushstrokeUI(customizer);
          updateMatStepHints(customizer);
          updateSubstrateStepLabel();
          updateSummary(customizer);
          updateCurrentSizesDisplay(customizer);
          refreshStepPillStates(customizer);
          initLockToggles(customizer);
          initCustomizerNotes(customizer);
          if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
            window.__fsEnforceFrameSubstrateRules();
          }
          if (productHasOriginalSpec(customizer) && customizer.dataset.customizerInited === '1') {
            scheduleApplyOriginalSpecifications(customizer);
          }
        }

        /* =========================
          IMAGE SIZE (lives inside Step 1)
        ========================== */
        function parseMetafieldDimensions(raw) {
          if (!raw) return null;
          const cleaned = String(raw).replace(/\s+/g, '');
          const parts = cleaned.split(/x|X|×|\*/);
          if (parts.length < 2) return null;
          const w = parseFloat(parts[0]);
          const h = parseFloat(parts[1]);
          if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return null;
          return { width: w, height: h };
        }

        function getProductImageSizeDimensions(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root) return null;
          return parseMetafieldDimensions(root.dataset.imageSize || '');
        }

        /** Default artwork W×H: image_size metafield, then dimensions metafield, then proportion calculator. */
        function resolveProductArtworkDimensions(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root) return null;
          const imageSizeDim = getProductImageSizeDimensions(root);
          if (imageSizeDim) return imageSizeDim;
          const dimLabel = String(root.dataset.originalDimensions || '').trim();
          if (dimLabel && !/^none$/i.test(dimLabel)) {
            const specDim = parseMetafieldDimensions(dimLabel);
            if (specDim) return specDim;
          }
          const propCalcEl = document.querySelector('.proportion-calculator');
          return parseMetafieldDimensions(propCalcEl ? (propCalcEl.dataset.dimensions || '') : '');
        }

        function getArtworkSizeFormulaLabel(customizerRoot) {
          if (getProductImageSizeDimensions(customizerRoot)) {
            return 'Product metafield — Image size';
          }
          const root = getActiveCustomizerRoot(customizerRoot);
          const dimLabel = root ? String(root.dataset.originalDimensions || '').trim() : '';
          if (dimLabel && !/^none$/i.test(dimLabel) && parseMetafieldDimensions(dimLabel)) {
            return 'Product metafield — Dimensions';
          }
          return 'From customizer (artwork size)';
        }

        function applyDefaultArtworkSizeFromProduct(customizer) {
          const dim = resolveProductArtworkDimensions(customizer);
          if (!dim) return false;
          customizerState.width = dim.width;
          customizerState.height = dim.height;
          captureDefaultArtworkDimensions(dim.width, dim.height);
          const panel = customizer.querySelector('.step-panel[data-step="1"]');
          const wInput = panel?.querySelector('#cs-width-input');
          const hInput = panel?.querySelector('#cs-height-input');
          if (wInput) wInput.value = dim.width;
          if (hInput) hInput.value = dim.height;
          if (typeof window.__fsSyncSizeFromStep2 === 'function') {
            window.__fsSyncSizeFromStep2(dim.width, dim.height);
          }
          return true;
        }

        function ensureArtworkSizeForPricing(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          syncArtworkSizeFromInputs(root);
          const w = parseFloat(customizerState.width);
          const h = parseFloat(customizerState.height);
          if (isFinite(w) && w > 0 && isFinite(h) && h > 0) return;
          applyDefaultArtworkSizeFromProduct(root);
        }

        function refreshFormulaPricingFromProductDefaults(customizerRoot) {
          const root = getActiveCustomizerRoot(customizerRoot);
          if (!root || !isCustomerLoggedIn(root)) return;
          ensureArtworkSizeForPricing(root);
          updateCustomizerMainPrice(root);
          updateCurrentSizesDisplay(root);
        }

        function initStep2(customizer) {
          const panel = customizer.querySelector('.step-panel[data-step="1"]');
          if (!panel) return;
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;
          const sizeHost = contentCol.querySelector('[data-size-section]');
          if (!sizeHost) return;
          if (sizeHost.dataset.sizeFormInjected === '1' && sizeHost.querySelector('#cs-width-input')) {
            applyDefaultArtworkSizeFromProduct(customizer);
            refreshFormulaPricingFromProductDefaults(customizer);
            updateCustomCropSizeNote(customizer);
            return;
          }
          sizeHost.dataset.sizeFormInjected = '1';

          const metaDim = resolveProductArtworkDimensions(customizer);
          const propBase = getProportionalSizeBase(customizer);
          const baseW = propBase.baseW;
          const baseH = propBase.baseH;

          if (metaDim) {
            customizerState.width = metaDim.width;
            customizerState.height = metaDim.height;
            captureDefaultArtworkDimensions(metaDim.width, metaDim.height);
          } else {
            captureDefaultArtworkDimensions(baseW, baseH);
          }

          sizeHost.innerHTML = `
            <div class="customizer-steps-about_inside customizer-size-heading">
              <strong>Customize Artwork Size Only</strong>
            </div>
            <div class="fs-size-row">
              <div class="fs-size-field fs-size-field--stepped">
                <label for="cs-width-input">WIDTH (INCHES)</label>
                <div class="fs-size-stepper">
                  <button type="button" class="fs-size-step-btn fs-size-step-btn--down" data-size-step="width" data-size-dir="-1" aria-label="Decrease width by 0.1 inches">−</button>
                  <input type="number" id="cs-width-input" value="${customizerState.width}" min="${ARTWORK_SIZE_MIN}" max="${ARTWORK_SIZE_MAX}" step="${ARTWORK_SIZE_STEP}" inputmode="decimal" placeholder="${baseW}" />
                  <button type="button" class="fs-size-step-btn fs-size-step-btn--up" data-size-step="width" data-size-dir="1" aria-label="Increase width by 0.1 inches">+</button>
                </div>
              </div>
              <div class="fs-size-field fs-size-field--stepped">
                <label for="cs-height-input">HEIGHT (INCHES)</label>
                <div class="fs-size-stepper">
                  <button type="button" class="fs-size-step-btn fs-size-step-btn--down" data-size-step="height" data-size-dir="-1" aria-label="Decrease height by 0.1 inches">−</button>
                  <input type="number" id="cs-height-input" value="${customizerState.height}" min="${ARTWORK_SIZE_MIN}" max="${ARTWORK_SIZE_MAX}" step="${ARTWORK_SIZE_STEP}" inputmode="decimal" placeholder="${baseH}" />
                  <button type="button" class="fs-size-step-btn fs-size-step-btn--up" data-size-step="height" data-size-dir="1" aria-label="Increase height by 0.1 inches">+</button>
                </div>
              </div>
            </div>
            <p class="customizer-size-error" id="customizer-size-error" role="alert"></p>
            <p class="customizer-crop-size-note" id="customizer-crop-size-note" hidden aria-live="polite"></p>
            <label class="customizer-lock-toggle customizer-lock-toggle--size">
              <input type="checkbox" id="lock-original-artwork-size-cb" autocomplete="off" />
              <span>Keep original artwork size</span>
            </label>
            <div class="step2-custom-size-disclaimer" hidden>
              <p>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M22.5601 16.3L14.8901 3.58C14.2598 2.594 13.1703 1.99738 12.0001 1.99738C10.8299 1.99738 9.74038 2.594 9.1101 3.58L1.4401 16.3C0.888647 17.2192 0.869586 18.3629 1.3901 19.3C1.99207 20.3551 3.11533 21.0046 4.3301 21H19.6701C20.8766 21.0129 21.9979 20.3797 22.6101 19.34C23.1462 18.3926 23.1271 17.2292 22.5601 16.3ZM12.0001 17C11.4478 17 11.0001 16.5523 11.0001 16C11.0001 15.4477 11.4478 15 12.0001 15C12.5524 15 13.0001 15.4477 13.0001 16C13.0001 16.5523 12.5524 17 12.0001 17ZM12.0001 14C12.5524 14 13.0001 13.5523 13.0001 13V9C13.0001 8.44771 12.5524 8 12.0001 8C11.4478 8 11.0001 8.44771 11.0001 9V13C11.0001 13.5523 11.4478 14 12.0001 14Z" fill="#C7B007"/>
                </svg>
                <span>
                  Non-proportional sizing is a custom request. Please contact
                  <a href="mailto:Sales@iconicpineapple.com">Sales@iconicpineapple.com</a>
                  for assistance.
                </span>
              </p>
            </div>
          `;

          const clearBtn = contentCol.querySelector('.js-step-clear-btn');
          const wInput   = contentCol.querySelector('#cs-width-input');
          const hInput   = contentCol.querySelector('#cs-height-input');
          const lockSizeCb = contentCol.querySelector('#lock-original-artwork-size-cb');
          const customSizeDisclaimer = contentCol.querySelector('.step2-custom-size-disclaimer');
          const sizeErrorEl = contentCol.querySelector('#customizer-size-error');

          let isUpdating = false;

          function showSizeValidationError(msg) {
            if (!sizeErrorEl) return;
            if (msg) {
              sizeErrorEl.textContent = msg;
              sizeErrorEl.classList.add('is-visible');
            } else {
              sizeErrorEl.textContent = '';
              sizeErrorEl.classList.remove('is-visible');
            }
          }

          function clampArtworkDimensions(w, h) {
            let nw = snapArtworkDimension(w);
            let nh = snapArtworkDimension(h);
            nw = Math.min(ARTWORK_SIZE_MAX, Math.max(ARTWORK_SIZE_MIN, nw));
            nh = Math.min(ARTWORK_SIZE_MAX, Math.max(ARTWORK_SIZE_MIN, nh));
            return { width: nw, height: nh };
          }

          function updateProportionalDisclaimer() {
            if (!customSizeDisclaimer) return;
            const w = parseFloat(customizerState.width);
            const h = parseFloat(customizerState.height);
            const base = getProportionalSizeBase(customizer);
            const show = !customizerState.lockOriginalArtworkSize &&
              !hasCustomizerCrop(customizer) &&
              isNonProportionalSize(w, h, base.baseW, base.baseH);
            customSizeDisclaimer.hidden = !show;
          }

          function applyArtworkSizeLock() {
            const locked = !!customizerState.lockOriginalArtworkSize;
            const base = getProportionalSizeBase(customizer);
            if (wInput) wInput.disabled = locked;
            if (hInput) hInput.disabled = locked;
            if (locked) {
              if (wInput) wInput.value = base.baseW;
              if (hInput) hInput.value = base.baseH;
              syncPreview(base.baseW, base.baseH);
            }
            updateProportionalDisclaimer();
          }

          lockSizeCb?.addEventListener('change', function () {
            customizerState.lockOriginalArtworkSize = lockSizeCb.checked;
            if (lockSizeCb.checked) {
              resetCropForCustomizer(customizer);
              restoreOriginalArtworkSize(customizer);
            }
            applyArtworkSizeLock();
          });

          function applySteppedSize(axis, direction) {
            if (customizerState.lockOriginalArtworkSize) return;
            const input = axis === 'height' ? hInput : wInput;
            if (!input) return;
            const propBase = getProportionalSizeBase(customizer);
            const cur = parseFloat(input.value);
            const stepBase = isFinite(cur) && cur > 0
              ? cur
              : (axis === 'height' ? propBase.baseH : propBase.baseW);
            const stepped = stepBase + (direction * ARTWORK_SIZE_STEP);
            const sized = calcProportionalArtworkSize(axis, stepped, propBase);
            if (!sized) return;
            const next = clampArtworkDimensions(sized.width, sized.height);
            isUpdating = true;
            wInput.value = next.width;
            hInput.value = next.height;
            syncPreview(next.width, next.height);
            isUpdating = false;
          }

          sizeHost.querySelectorAll('[data-size-step]').forEach(function (btn) {
            btn.addEventListener('click', function () {
              const axis = btn.getAttribute('data-size-step');
              const dir = parseInt(btn.getAttribute('data-size-dir'), 10) || 0;
              if (!axis || !dir) return;
              applySteppedSize(axis, dir);
            });
          });
        
          function round1(n) { return Math.round(n * 10) / 10; }

          function syncPreview(w, h) {
            const err = getArtworkSizeValidationError(w, h);
            showSizeValidationError(err);
            if (err) return;
            if (typeof window.__fsClearTestOutsideOverride === 'function') {
              window.__fsClearTestOutsideOverride();
            }
            /*
             * Freeze nochange form costs only when the customer actually changes size
             * away from the product default — not on init / original-spec sync.
             */
            if (!isApplyingOriginalSpec(customizer)) {
              const base = getProportionalSizeBase(customizer);
              const sizeChanged = isFinite(base.baseW) && isFinite(base.baseH) &&
                (roundSizeInches(w) !== roundSizeInches(base.baseW) ||
                  roundSizeInches(h) !== roundSizeInches(base.baseH));
              if (sizeChanged) {
                markUserModifiedSelections(customizer);
              }
            }
            customizerState.width  = w;
            customizerState.height = h;
            customizerState.artworkFloat = !isArtworkOversize(w, h);
            updateSubstrateStepLabel();
            updateSummary(customizer);
            updateCurrentSizesDisplay(customizer);
            enforceOversizeMatRules(customizer);
            restoreOriginalSpecMatsAfterSizeChange(customizer);
            updateProportionalDisclaimer();
            refreshStepPillStates(customizer);
            updateCustomizerMainPrice(customizer);
            if (typeof window.__fsSyncSizeFromStep2 === 'function') {
              window.__fsSyncSizeFromStep2(w, h);
            }
          }

          function commitArtworkSizeFromInputs(axis) {
            if (customizerState.lockOriginalArtworkSize) return;
            const base = getProportionalSizeBase(customizer);
            let w = parseFloat(wInput ? wInput.value : '');
            let h = parseFloat(hInput ? hInput.value : '');
            if (axis === 'width' && isFinite(w) && w > 0) {
              h = base.isSquare ? round1(w) : round1((w * base.baseH) / base.baseW);
            } else if (axis === 'height' && isFinite(h) && h > 0) {
              w = base.isSquare ? round1(h) : round1((h * base.baseW) / base.baseH);
            } else if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) {
              return;
            }
            const next = clampArtworkDimensions(w, h);
            isUpdating = true;
            if (wInput) wInput.value = next.width;
            if (hInput) hInput.value = next.height;
            syncPreview(next.width, next.height);
            isUpdating = false;
          }

          function onWidthInput() {
            if (isUpdating || customizerState.lockOriginalArtworkSize) return;
            const base = getProportionalSizeBase(customizer);
            const width = parseFloat(wInput.value);
            if (isNaN(width) || width <= 0) {
              if (hInput) hInput.value = '';
              return;
            }
            isUpdating = true;
            const height = base.isSquare
              ? round1(width)
              : round1((width * base.baseH) / base.baseW);
            if (hInput) hInput.value = height;
            syncPreview(width, height);
            isUpdating = false;
          }

          function onHeightInput() {
            if (isUpdating || customizerState.lockOriginalArtworkSize) return;
            const base = getProportionalSizeBase(customizer);
            const height = parseFloat(hInput.value);
            if (isNaN(height) || height <= 0) {
              if (wInput) wInput.value = '';
              return;
            }
            isUpdating = true;
            const width = base.isSquare
              ? round1(height)
              : round1((height * base.baseW) / base.baseH);
            if (wInput) wInput.value = width;
            syncPreview(width, height);
            isUpdating = false;
          }

          wInput?.addEventListener('input', onWidthInput);
          hInput?.addEventListener('input', onHeightInput);
          wInput?.addEventListener('blur', function () { commitArtworkSizeFromInputs('width'); });
          hInput?.addEventListener('blur', function () { commitArtworkSizeFromInputs('height'); });
          wInput?.addEventListener('change', function () { commitArtworkSizeFromInputs('width'); });
          hInput?.addEventListener('change', function () { commitArtworkSizeFromInputs('height'); });

          if (clearBtn && !clearBtn.dataset.sizeClearAugmented) {
            clearBtn.dataset.sizeClearAugmented = '1';
            const origClear = clearBtn.onclick;
            clearBtn.addEventListener('click', function onSizeClear() {
              if (lockSizeCb) {
                lockSizeCb.checked = false;
                lockSizeCb.dispatchEvent(new Event('change'));
              }
              const resetBase = getProportionalSizeBase(customizer);
              customizerState.width = resetBase.baseW;
              customizerState.height = resetBase.baseH;
              if (wInput) wInput.value = resetBase.baseW;
              if (hInput) hInput.value = resetBase.baseH;
              syncPreview(resetBase.baseW, resetBase.baseH);
              applyArtworkSizeLock();
            }, { capture: true });
          }

          applyArtworkSizeLock();
          syncPreview(customizerState.width, customizerState.height);
          refreshFormulaPricingFromProductDefaults(customizer);
          updateCustomCropSizeNote(customizer);
        }

        function initLockToggles(customizer) {
          if (!customizer || customizer.dataset.lockTogglesBound === '1') return;
          customizer.dataset.lockTogglesBound = '1';

          const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
          const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
          const glazingSelect = customizer.querySelector('[id^="customizer-glazing-"]');
          const lockSubMatCb = customizer.querySelector('#lock-original-substrate-mat-cb');

          if (lockSubMatCb && !lockSubMatCb.dataset.bound) {
            lockSubMatCb.dataset.bound = '1';
            customizerState.defaultSubstrate = customizerState.defaultSubstrate ||
              (substrateSelect ? substrateSelect.value : customizerState.substrate);
            customizerState.defaultTreatment = customizerState.defaultTreatment ||
              (treatmentSelect ? treatmentSelect.value : customizerState.treatment);

            function applySubstrateMatLock() {
              const locked = !!customizerState.lockOriginalSubstrateMat;
              if (substrateSelect) substrateSelect.disabled = locked;
              if (treatmentSelect) {
                treatmentSelect.disabled = locked || isCanvasSubstrate();
              }
              if (locked) {
                // Keep Original Substrate And Mat Specs — never show oversize-mat alert.
                clearOversizeMatsFilter(customizer);
                const modal = document.getElementById('cz-error-modal');
                if (modal && !modal.hidden) {
                  modal._czOnClose = null;
                  closeCustomizerErrorModal();
                }
                restoreOriginalSubstrateAndTreatment(customizer);
                restoreOriginalMats(customizer);
                substrateSelect?.dispatchEvent(new Event('change', { bubbles: true }));
                refreshCustomizerAfterSelectionChange(customizer);
              } else {
                applyGlazingRules(customizer);
              }
              if (glazingSelect) {
                glazingSelect.disabled = locked || getCalculatedGlazingChoice(customizer) === 'None';
              }
            }

            lockSubMatCb.addEventListener('change', function () {
              customizerState.lockOriginalSubstrateMat = lockSubMatCb.checked;
              applySubstrateMatLock();
            });
            lockSubMatCb.checked = !!customizerState.lockOriginalSubstrateMat;
            // Do not run locked restore on init (avoids alert / side effects on open).
            if (substrateSelect) substrateSelect.disabled = !!customizerState.lockOriginalSubstrateMat;
            if (treatmentSelect) {
              treatmentSelect.disabled = !!customizerState.lockOriginalSubstrateMat || isCanvasSubstrate();
            }
            applyGlazingRules(customizer);
          }

          function setMatPanelLockUI(matPanel, locked) {
            if (!matPanel) return;
            matPanel.querySelectorAll(
              '.mat-grid, .mat-filters, .mat-search-input, .mat-search-btn, .mat-size-select, .mat-color-filter, .mat-type-filter, .mat-width-filter'
            ).forEach(function (el) {
              if (el.classList.contains('mat-grid')) {
                el.style.pointerEvents = locked ? 'none' : '';
                el.style.opacity = locked ? '0.55' : '';
              } else if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'BUTTON') {
                el.disabled = locked;
              }
            });
          }

          customizer.querySelectorAll('.lock-original-mat-cb').forEach(function (cb) {
            if (cb.dataset.bound) return;
            cb.dataset.bound = '1';
            cb.addEventListener('change', function () {
              customizerState.lockOriginalMat = !!cb.checked;
              const matPanel = customizer.querySelector('.step-panel[data-step="2"]');
              if (cb.checked) {
                // Keep Original Mat: restore metafield mats — no oversize alert.
                clearOversizeMatsFilter(customizer);
                const modal = document.getElementById('cz-error-modal');
                if (modal && !modal.hidden) {
                  modal._czOnClose = null;
                  closeCustomizerErrorModal();
                }
                restoreOriginalMats(customizer);
                if (typeof window.__matRefreshTop === 'function') {
                  window.__matRefreshTop({ resetPage: true });
                }
                if (typeof window.__matRefreshBottom === 'function') {
                  window.__matRefreshBottom({ resetPage: true });
                }
                syncMatCardsFromState(customizer);
                refreshCustomizerAfterSelectionChange(customizer);
              }
              setMatPanelLockUI(matPanel, cb.checked);
            });
            cb.checked = !!customizerState.lockOriginalMat;
            if (cb.checked) {
              clearOversizeMatsFilter(customizer);
              restoreOriginalMats(customizer);
            }
            setMatPanelLockUI(customizer.querySelector('.step-panel[data-step="2"]'), cb.checked);
          });

          function applyFrameOnlyLock(customizerRoot) {
            const locked = !!customizerState.lockOriginalFrame;
            const grid = document.getElementById('fs-grid');
            if (grid) {
              grid.style.pointerEvents = locked ? 'none' : '';
              grid.style.opacity = locked ? '0.55' : '';
            }
            ['fs-q', 'fs-f-color', 'fs-f-width', 'fs-f-type'].forEach(function (id) {
              const el = document.getElementById(id);
              if (el) el.disabled = locked;
            });
            if (locked) {
              restoreOriginalFrame(customizerRoot);
              if (typeof window.fsFilter === 'function') window.fsFilter();
            }
            refreshCustomizerAfterSelectionChange(customizerRoot);
            updateCanvasLinerAvailability(customizerRoot);
          }

          function applyLinerOnlyLock(customizerRoot) {
            const locked = !!customizerState.lockOriginalLiner;
            const linerPanel = customizerRoot.querySelector('[data-cz-tab-panel="liner"]');
            if (!linerPanel) return;
            linerPanel.querySelectorAll(
              '.mat-grid, .mat-search-input, .mat-search-btn, .mat-size-select, .mat-color-filter, .mat-type-filter, .mat-width-filter'
            ).forEach(function (el) {
              if (el.classList.contains('mat-grid')) {
                el.style.pointerEvents = locked ? 'none' : '';
                el.style.opacity = locked ? '0.55' : '';
              } else if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'BUTTON') {
                el.disabled = locked;
              }
            });
            if (locked) restoreOriginalLiner(customizerRoot);
            refreshCustomizerAfterSelectionChange(customizerRoot);
          }

          customizer.querySelectorAll('.lock-original-frame-only-cb').forEach(function (cb) {
            if (cb.dataset.bound) return;
            cb.dataset.bound = '1';
            cb.addEventListener('change', function () {
              customizerState.lockOriginalFrame = cb.checked;
              applyFrameOnlyLock(customizer);
            });
            cb.checked = !!customizerState.lockOriginalFrame;
            applyFrameOnlyLock(customizer);
          });

          customizer.querySelectorAll('.lock-original-liner-only-cb').forEach(function (cb) {
            if (cb.dataset.bound) return;
            cb.dataset.bound = '1';
            cb.addEventListener('change', function () {
              customizerState.lockOriginalLiner = cb.checked;
              applyLinerOnlyLock(customizer);
            });
            cb.checked = !!customizerState.lockOriginalLiner;
            applyLinerOnlyLock(customizer);
          });

          customizer.querySelector('[data-substrate-terms]')?.addEventListener('click', function (e) {
            e.preventDefault();
            const url = customizer.dataset.substrateTermsUrl || 'https://iconicpineapple.com/pages/substrate-terms-page';
            if (url && url !== '#') window.open(url, '_blank', 'noopener');
          });
        }

        /* =========================
          STEP 3 — FRAME
        ========================== */
        function onFrameSelected(frameId, frameName) {
          if (frameId) {
            const f = getActiveFrames().find(x => x.id === frameId) || null;
            customizerState.frame = {
              id: frameId,
              name: frameName,
              img: f?.img || null,
              thumb: f?.thumb || null,
              dim: f?.dim || null,
              size: f?.size || null,
              type: f?.type || null,
              finish: f?.finish || null,
              frameW: f?.frameW ?? null,
              sizeWidthIn: f?.sizeWidthIn ?? null,
              sizeHeightIn: f?.sizeHeightIn ?? null,
              basePrice: f?.basePrice ?? null,
              pickFrom: f ? f.pickFrom !== false : true
            };
          } else {
            customizerState.frame = null;
          }
          updateFrameLinerStepLabel();
          document.querySelectorAll('.customizer').forEach(function (c) {
            refreshCustomizerPricingAndDimensions(c);
          });
        }

        /* =========================
          STEPS 4, 5, 6 — MAT / LINER
        ========================== */
        function buildMatSizeSelectOptions(stateKey) {
          let html = '';
          if (stateKey === 'bottomMat') {
            BOTTOM_MAT_SIZE_OPTIONS.forEach(function (val) {
              const label = val + '"';
              html += '<option value="' + val + '">' + label + '</option>';
            });
          } else {
            TOP_MAT_SIZE_OPTIONS.forEach(function (val) {
              const label = val >= TOP_MAT_SIZE_MAX ? '6+"' : val + '"';
              html += '<option value="' + val + '">' + label + '</option>';
            });
          }
          return html;
        }

        function ensureMatSizeDropdown(inputScope, stateKey) {
          if (!inputScope || inputScope.querySelector('.mat-size-select')) return;
          const filters = inputScope.querySelector('.mat-filters');
          if (!filters) return;
          const sizeKey = stateKey === 'bottomMat' ? 'bottomMatSizeInches' : 'topMatSizeInches';
          const defaultSize = getDefaultMatSizeInches(
            stateKey,
            inputScope?.closest('.customizer') || document.querySelector('.customizer')
          );
          const current = customizerState[sizeKey] || defaultSize;
          const field = document.createElement('div');
          field.className = 'mat-filter-field mat-size-field';
          field.innerHTML =
            '<label>SIZE (INCHES)</label>' +
            '<select class="mat-size-select" aria-label="Mat size in inches">' +
            buildMatSizeSelectOptions(stateKey) +
            '</select>';
          const sel = field.querySelector('.mat-size-select');
          if (sel) sel.value = String(snapMatSizeInches(current, stateKey));
          sel?.addEventListener('change', () => {
            const inches = parseFloat(sel.value);
            const snapped = snapMatSizeInches(inches, stateKey);
            customizerState[sizeKey] = snapped;
            if (String(sel.value) !== String(snapped)) sel.value = String(snapped);
            const root = inputScope?.closest('.customizer') || document.querySelector('.customizer');
            if (root) markUserModifiedSelections(root);
            document.querySelectorAll('.customizer').forEach(function (c) {
              // Full refresh: glass/outside sizes, oversize-mat filter, plexi, and price.
              refreshCustomizerPricingAndDimensions(c);
            });
            if (typeof window.__fsRedrawWithMats === 'function') window.__fsRedrawWithMats();
          });
          filters.insertBefore(field, filters.firstChild);
        }

        function resetSelectToFirstOption(select) {
          if (!select || !select.options.length) return;
          select.selectedIndex = 0;
          select.value = select.options[0].value;
        }

        function resetFiltersInScope(scope) {
          if (!scope) return;
          scope.querySelectorAll('.mat-search-input').forEach(function (el) {
            el.value = '';
          });
          scope.querySelectorAll('.mat-color-filter, .mat-type-filter, .mat-width-filter, .mat-size-select')
            .forEach(resetSelectToFirstOption);
        }

        function resetFrameFilterDropdowns() {
          var ids = ['fs-q', 'fs-f-color', 'fs-f-width', 'fs-f-type'];
          ids.forEach(function (id) {
            var el = document.getElementById(id);
            if (!el) return;
            if (el.tagName === 'SELECT') resetSelectToFirstOption(el);
            else el.value = '';
          });
          if (typeof window.fsFilter === 'function') window.fsFilter();
        }

        function clearMatSelection(stateKey, grid, inputScope) {
          customizerState[stateKey] = null;
          resetFiltersInScope(inputScope);
          const sizeSelect = inputScope?.querySelector('.mat-size-select');
          if (sizeSelect) {
            const customizerRoot = inputScope?.closest('.customizer') || document.querySelector('.customizer');
            const defaultSize = getDefaultMatSizeInches(stateKey, customizerRoot);
            sizeSelect.value = String(defaultSize);
            if (stateKey === 'topMat') customizerState.topMatSizeInches = defaultSize;
            if (stateKey === 'bottomMat') customizerState.bottomMatSizeInches = defaultSize;
          }
          grid?.querySelectorAll('.mat-card').forEach(c => c.classList.remove('mat-active'));
          inputScope?.querySelectorAll('.mat-color-filter, .mat-type-filter, .mat-width-filter')
            .forEach(function (sel) {
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }

        function refreshCustomizerAfterSelectionChange(customizer, opts) {
          const root = customizer || document.querySelector('.customizer');
          if (!root) return;
          const skipDomSync = !!(opts && opts.skipDomSync);
          updateMatStepLabel();
          updateFrameLinerStepLabel();
          updateSubstrateStepLabel();
          document.querySelectorAll('.customizer').forEach(function (c) {
            if (!skipDomSync && !isApplyingOriginalSpec(c)) {
              syncCustomizerSelectionsFromDOM(c);
            }
            updateMatPreview(c);
          });
          refreshCustomizerPricingAndDimensions(root, {
            skipSummary: !!(opts && opts.skipSummary)
          });
        }

        function clearAllMatSelections(customizer) {
          customizerState.topMat = null;
          customizerState.bottomMat = null;
          const panel = customizer?.querySelector('.step-panel[data-step="2"]');
          if (!panel) return;
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;

          const topGrid = contentCol.querySelector('#mat-grid-step4');
          const topScope = topGrid?.closest('[data-cz-tab-panel="topmat"]') || contentCol;
          if (topGrid) clearMatSelection('topMat', topGrid, topScope);

          const bottomGrid = contentCol.querySelector('#mat-grid-step5');
          const bottomScope = bottomGrid?.closest('[data-cz-tab-panel="bottommat"]') || contentCol;
          if (bottomGrid) clearMatSelection('bottomMat', bottomGrid, bottomScope);

          if (typeof window.__matRefreshTop === 'function') window.__matRefreshTop();
          if (typeof window.__matRefreshBottom === 'function') window.__matRefreshBottom();
          refreshCustomizerAfterSelectionChange(customizer);
        }

        function clearLinerSelection(customizer) {
          customizerState.liner = null;
          const panel = customizer?.querySelector('.step-panel[data-step="3"]');
          const grid = panel?.querySelector('#mat-grid-step6');
          const inputScope = grid?.closest('.combined-liner-section') ||
            panel?.querySelector('[data-cz-tab-panel="liner"]');
          if (grid && inputScope) clearMatSelection('liner', grid, inputScope);
          if (typeof window.__matRefreshLiner === 'function') window.__matRefreshLiner();
          refreshCustomizerAfterSelectionChange(customizer);
        }

        function clearFrameSelection() {
          if (typeof window.fsResetFrame === 'function') {
            window.fsResetFrame();
          } else {
            customizerState.frame = null;
            document.querySelectorAll('.fs-card').forEach(function (c) {
              c.classList.remove('fs-active');
            });
          }
          refreshCustomizerAfterSelectionChange();
        }

        const matStepControllers = new WeakMap();

        function getMatControllersMap(customizer) {
          if (!customizer) return {};
          let map = matStepControllers.get(customizer);
          if (!map) {
            map = {};
            matStepControllers.set(customizer, map);
          }
          return map;
        }

        function registerMatStepController(customizer, stateKey, controller) {
          if (!customizer || !stateKey || !controller) return;
          getMatControllersMap(customizer)[stateKey] = controller;
        }

        function getMatStateKeyFromScope(el) {
          if (!el) return null;
          const panel = el.closest('[data-cz-tab-panel]');
          const key = panel && panel.dataset ? panel.dataset.czTabPanel : '';
          if (key === 'topmat') return 'topMat';
          if (key === 'bottommat') return 'bottomMat';
          if (key === 'liner') return 'liner';
          return null;
        }

        function populateMatColorFilterOptions(colorFilter, catalog) {
          if (!colorFilter) return;
          const current = colorFilter.value || '';
          const names = Array.from(new Set((catalog || []).map(getMatColorFilterName).filter(Boolean))).sort();
          while (colorFilter.options.length > 1) {
            colorFilter.remove(1);
          }
          names.forEach(function (name) {
            if (Array.from(colorFilter.options).some(function (o) { return o.value === name; })) return;
            const opt = document.createElement('option');
            opt.value = opt.textContent = name;
            colorFilter.appendChild(opt);
          });
          if (current && Array.from(colorFilter.options).some(function (o) { return o.value === current; })) {
            colorFilter.value = current;
          } else if (!current) {
            colorFilter.selectedIndex = 0;
          }
        }

        function runMatStepFilter(customizer, stateKey, opts) {
          const ctrl = getMatControllersMap(customizer)[stateKey];
          if (!ctrl || typeof ctrl.filterMats !== 'function') return;
          ctrl.filterMats(opts);
        }

        function refreshMatStepsOnAllCustomizers(stateKey, opts) {
          document.querySelectorAll('.customizer').forEach(function (customizer) {
            runMatStepFilter(customizer, stateKey, opts);
          });
        }

        function bindMatFilterDelegation(customizer) {
          if (!customizer || customizer.dataset.matFilterDelegate === '1') return;
          customizer.dataset.matFilterDelegate = '1';

          customizer.addEventListener('input', function (e) {
            if (!(e.target instanceof Element) || !e.target.matches('.mat-search-input')) return;
            const stateKey = getMatStateKeyFromScope(e.target);
            if (!stateKey) return;
            runMatStepFilter(customizer, stateKey);
          });

          customizer.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' || !(e.target instanceof Element) || !e.target.matches('.mat-search-input')) return;
            e.preventDefault();
            const stateKey = getMatStateKeyFromScope(e.target);
            if (!stateKey) return;
            runMatStepFilter(customizer, stateKey);
          });

          customizer.addEventListener('click', function (e) {
            if (!(e.target instanceof Element)) return;
            const btn = e.target.closest('.mat-search-btn');
            if (!btn) return;
            e.preventDefault();
            e.stopPropagation();
            const stateKey = getMatStateKeyFromScope(btn);
            if (!stateKey) return;
            runMatStepFilter(customizer, stateKey);
          });

          customizer.addEventListener('change', function (e) {
            if (!(e.target instanceof Element)) return;
            if (!e.target.matches('.mat-color-filter, .mat-type-filter, .mat-width-filter')) return;
            const stateKey = getMatStateKeyFromScope(e.target);
            if (!stateKey) return;
            runMatStepFilter(customizer, stateKey);
          });
        }

        if (typeof window !== 'undefined') {
          window.__matRefreshTop = function (opts) {
            refreshMatStepsOnAllCustomizers('topMat', opts);
          };
          window.__matRefreshBottom = function (opts) {
            refreshMatStepsOnAllCustomizers('bottomMat', opts);
          };
          window.__matRefreshLiner = function (opts) {
            refreshMatStepsOnAllCustomizers('liner', opts);
          };
        }

        function initMatStep(customizer, stepNum, stateKey, label, panelStepNum) {
          const panel = customizer.querySelector(`.step-panel[data-step="${panelStepNum || stepNum}"]`);
          if (!panel) return;
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;

          const bindKey = 'matBound_' + stateKey;
          const isLiner = stateKey === 'liner';

          function resolveMatCatalog() {
            if (isLiner) return getActiveLiners();
            if (stateKey === 'topMat' && window.__customizerMatsApiTop?.length) {
              return window.__customizerMatsApiTop;
            }
            if (stateKey === 'bottomMat' && window.__customizerMatsApiBottom?.length) {
              return window.__customizerMatsApiBottom;
            }
            return MAT_COLORS;
          }

          let colors = resolveMatCatalog();

          const grid = contentCol.querySelector(`#mat-grid-step${stepNum}`);
          const matWrap = grid?.parentElement || null;
          const inputScope =
            grid?.closest('.cz-tab-panel') ||
            grid?.closest('.combined-bottom-mat-section') ||
            grid?.closest('.combined-liner-section') ||
            contentCol;

          if (!isLiner) ensureMatSizeDropdown(inputScope, stateKey);

          let filteredMats = [];
          let matPage = 1;
          const PAGE_SIZE = 16;

          function ensureMatPageShowsSelection() {
            const mat = customizerState[stateKey];
            if (!mat || !filteredMats.length) return;
            const target = normalizeCatalogId(mat.id);
            const idx = filteredMats.findIndex(function (m) {
              return matsStateMatchesCatalogMat(mat, m, stateKey);
            });
            if (idx < 0) return;
            const neededPage = Math.ceil((idx + 1) / PAGE_SIZE);
            if (neededPage > matPage) matPage = neededPage;
          }

          function applyMatResultFilters(list, filterCtx) {
            if (isLiner) return list;
            if (filterCtx && filterCtx.active && (!list || !list.length)) return [];
            return finalizeMatFilterResults(applyOversizeConstraints(list), stateKey, filterCtx);
          }

          function applyOversizeConstraints(list) {
            if (isLiner) return list;
            let next = list;
            if (shouldRestrictToOversizeMatsOnly(customizer, stateKey)) {
              if (!isUnderGlass()) return [];
              next = (list || []).filter(function (m) {
                return matIsOversized(stateKey, m);
              });
              const current = customizerState[stateKey];
              if (current && current.id && matIsOversized(stateKey, current)) {
                const alreadyListed = next.some(function (m) {
                  return matsStateMatchesCatalogMat(current, m, stateKey);
                });
                if (!alreadyListed) {
                  const fromCatalog = findMatInCatalogs(stateKey, normalizeCatalogId(current.id), current.name);
                  const pinned = fromCatalog || {
                    id: current.id,
                    name: current.name,
                    hex: current.hex,
                    type: current.type,
                    img: current.img || null,
                    matW: current.matW || 1.2,
                    oversizedMat: true,
                    printedMats: !!current.printedMats,
                    pickFrom: fromCatalog ? fromCatalog.pickFrom !== false : current.pickFrom !== false
                  };
                  next = [pinned].concat(next);
                }
              }
            }
            return next;
          }

          function getMatLoadMoreEl() {
            if (matWrap) {
              const scoped = matWrap.querySelector(`#mat-load-step${stepNum}`);
              if (scoped) return scoped;
            }
            return contentCol.querySelector(`#mat-load-step${stepNum}`);
          }

          function renderMatGrid() {
            if (!grid) return;
            grid.innerHTML = '';

            if (isLiner) {
              const noneCard = document.createElement('div');
              noneCard.className = 'mat-card' + (!customizerState.liner ? ' mat-active' : '');
              noneCard.dataset.matId = 'NONE';
              noneCard.innerHTML = `
                <div class="mat-card-swatch" style="background:#f4f4f4;border:1px dashed rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;font-weight:700;color:#444;">
                  None
                </div>
                <span class="mat-card-id">—</span>
                <span class="mat-card-nm">No Liner</span>
              `;
              noneCard.addEventListener('click', () => {
                clearLinerSelection(customizer);
              });
              grid.appendChild(noneCard);
            }

            if (filteredMats.length === 0) {
              showEmptyState(grid, isLiner ? 'liner' : stateKey);
              if (!isLiner && shouldRestrictToOversizeMatsOnly(customizer, stateKey)) {
                const needsOversize = matRequiresOversize(stateKey, customizer);
                if (needsOversize && !isUnderGlass()) {
                  const note = document.createElement('div');
                  note.className = 'grid-empty-note';
                  note.textContent = 'Oversized mats are only available when framed under glass for sizes over 32 x 40.';
                  grid.appendChild(note);
                } else if (needsOversize) {
                  const note = document.createElement('div');
                  note.className = 'grid-empty-note';
                  note.textContent = 'Only oversized mats are available for glass sizes over 32 x 40.';
                  grid.appendChild(note);
                }
              }
              const lmEl = getMatLoadMoreEl();
              if (lmEl) {
                lmEl.style.display = 'none';
                lmEl.hidden = true;
                lmEl.classList.remove('mat-load-more--visible');
              }
              return;
            }

            filteredMats.slice(0, matPage * PAGE_SIZE).forEach(mat => {
              const isFabric = mat.type === 'Fabric';
              const isSpecOnly = !isLiner && mat.pickFrom === false;
              let nameHtml = mat.name;
              if (!isLiner) {
                if (mat.printedMats) {
                  nameHtml += ' <span class="mat-card-printed-label">Printed</span>';
                }
                if (mat.oversizedMat) {
                  nameHtml += ' <span class="mat-card-oversize-label">Oversize available</span>';
                }
                if (isSpecOnly) {
                  nameHtml += ' <span class="mat-card-draft-label">Included</span>';
                }
              }
              const card = document.createElement('div');
              card.className = 'mat-card' + (
                customizerState[stateKey] && matsStateMatchesCatalogMat(customizerState[stateKey], mat, stateKey) ? ' mat-active' : ''
              );
              if (isFabric) card.classList.add('mat-card--fabric');
              if (isSpecOnly) card.classList.add('mat-card--draft');
              card.dataset.matId = mat.id;
              if (!isLiner) {
                card.dataset.oversizedMat = mat.oversizedMat ? '1' : '0';
                card.dataset.printedMats = mat.printedMats ? '1' : '0';
                card.dataset.pickFrom = mat.pickFrom === false ? '0' : '1';
              }

              if (isFabric && mat.hex) {
                card.innerHTML = `
                  <div class="mat-card-swatch" style="position:relative;background:${mat.hex};border:1px solid rgba(0,0,0,0.1);">
                    <span class="mat-card-fabric-badge">Fabric</span>
                  </div>
                  <span class="mat-card-id">${mat.id}</span>
                  <span class="mat-card-nm">${nameHtml}</span>
                `;
              } else if (isFabric) {
                const fallbackBg = 'background:#ccc;';
                card.innerHTML = `
                  <div class="mat-card-swatch mat-card-swatch--fabric" style="${fallbackBg}border:1px solid rgba(0,0,0,0.1);overflow:hidden;position:relative;">
                    <img
                      src="${mat.img || ''}"
                      alt="${mat.name}"
                      style="width:100%;height:100%;object-fit:cover;display:block;"
                      loading="lazy"
                      onerror="this.style.display='none';"
                    />
                    <span class="mat-card-fabric-badge">Fabric</span>
                  </div>
                  <span class="mat-card-id">${mat.id}</span>
                  <span class="mat-card-nm">${nameHtml}</span>
                `;
            } else if (!isLiner && mat.hex) {
              card.innerHTML = `
                <div class="mat-card-swatch" style="background:${mat.hex};border:1px solid rgba(0,0,0,0.1);"></div>
                <span class="mat-card-id">${mat.id}</span>
                <span class="mat-card-nm">${nameHtml}</span>
              `;
            } else if (!isLiner && mat.img) {
              const fallbackBg = mat.hex ? `background:${mat.hex};` : 'background:#e8e4df;';
              card.innerHTML = `
                <div class="mat-card-swatch mat-card-swatch--fabric" style="${fallbackBg}border:1px solid rgba(0,0,0,0.1);overflow:hidden;position:relative;">
                  <img
                    src="${mat.img || ''}"
                    alt="${mat.name}"
                    style="width:100%;height:100%;object-fit:cover;display:block;"
                    loading="lazy"
                    onerror="this.style.display='none';"
                  />
                </div>
                <span class="mat-card-id">${mat.id}</span>
                <span class="mat-card-nm">${nameHtml}</span>
              `;
              } else if (isLiner && mat.thumb) {
                card.innerHTML = `
                  <div class="mat-card-swatch mat-card-swatch--liner-thumb">
                    <img
                      src="${mat.thumb}"
                      alt="${mat.name}"
                      loading="lazy"
                      onerror="this.style.display='none';this.parentElement.style.background='${mat.hex || '#ccc'}';"
                    />
                  </div>
                  <span class="mat-card-id">${mat.id}</span>
                  <span class="mat-card-nm">${mat.name}</span>
                  ${isLiner ? `<span class="mat-card-width-badge">${formatInchesBadge(toInchesFromLegacyWidth(mat.frameW))}</span>` : ''}
                `;
              } else {
                card.innerHTML = `
                  <div class="mat-card-swatch" style="background:${mat.hex || '#ccc'};border:1px solid rgba(0,0,0,0.1);"></div>
                  <span class="mat-card-id">${mat.id}</span>
                  <span class="mat-card-nm">${isLiner ? mat.name : nameHtml}</span>
                  ${isLiner ? `<span class="mat-card-width-badge">${formatInchesBadge(toInchesFromLegacyWidth(mat.frameW))}</span>` : ''}
                `;
              }

              if (!isSpecOnly) {
                card.addEventListener('click', () => {
                  markUserModifiedSelections(customizer);
                  assignMatToState(mat, stateKey);
                  grid.querySelectorAll('.mat-card').forEach(c => c.classList.remove('mat-active'));
                  card.classList.add('mat-active');
                  if (stateKey === 'topMat' || stateKey === 'bottomMat') updateMatStepLabel();
                  else if (stateKey === 'liner') updateFrameLinerStepLabel();
                  else updateStepLabel(stepNum, mat.id + ' — ' + mat.name);
                  if (mat.img && typeof window.__fsPreloadMatImages === 'function') {
                    window.__fsPreloadMatImages([mat]);
                  }
                  if (!isLiner) syncMatSizeInchesFromUI(stateKey);
                  updateMatPreview(customizer);
                  refreshStepPillStates(customizer);
                  refreshCustomizerAfterSelectionChange(customizer);
                  ensureMatPageShowsSelection();
                  renderMatGrid();
                });
              }
              grid.appendChild(card);
            });
            const lmEl = getMatLoadMoreEl();
            if (lmEl) {
              const hasMore = filteredMats.length > matPage * PAGE_SIZE;
              lmEl.style.display = hasMore ? 'flex' : 'none';
              lmEl.hidden = !hasMore;
              lmEl.classList.toggle('mat-load-more--visible', hasMore);
            }
          }

          function filterMats(opts) {
            colors = resolveMatCatalog();
            populateMatColorFilterOptions(inputScope.querySelector('.mat-color-filter'), colors);
            const searchInput = inputScope.querySelector('.mat-search-input');
            const colorFilter = inputScope.querySelector('.mat-color-filter');
            const typeFilter  = inputScope.querySelector('.mat-type-filter');
            const widthFilter = inputScope.querySelector('.mat-width-filter');
            const q  = (searchInput?.value || '').trim();
            const nm = colorFilter?.value || '';
            const tp = typeFilter?.value || '';
            const wb = widthFilter?.value || '';
            filteredMats = colors.filter(m => {
              if (nm && getMatColorFilterName(m) !== nm) return false;
              if (tp && m.type !== tp) return false;
              if (isLiner && wb) {
                const bucket = faceWidthBucketFromLinerDef(m);
                if (!bucket || bucket !== wb) return false;
              }
              if (!matMatchesSearchQuery(m, q)) return false;
              return true;
            });
            const filterCtx = { active: !!(q || nm || tp || (isLiner && wb)) };
            filteredMats = isLiner
              ? filteredMats
              : applyMatResultFilters(filteredMats, filterCtx);
            if (opts && opts.resetPage === false) {
              ensureMatPageShowsSelection();
            } else {
              matPage = 1;
            }
            renderMatGrid();
          }

          registerMatStepController(customizer, stateKey, {
            filterMats: filterMats,
            renderMatGrid: renderMatGrid,
            resetPage: function () { matPage = 1; }
          });

          bindMatFilterDelegation(customizer);
          filterMats();

          if (contentCol.dataset[bindKey] === '1') return;
          contentCol.dataset[bindKey] = '1';

          const lmBtn = getMatLoadMoreEl()?.querySelector('.mat-load-btn');
          const lmWrap = getMatLoadMoreEl();
          if (lmBtn && lmWrap && lmWrap.dataset.loadBound !== '1') {
            lmWrap.dataset.loadBound = '1';
            lmBtn.addEventListener('click', function () { matPage++; renderMatGrid(); });
          }

          if (stepNum === 5) updateBottomMatStepAvailability(customizer);

          if (!isLiner && colors.length && typeof window.__fsPreloadMatImages === 'function') {
            window.__fsPreloadMatImages(colors.slice(0, PAGE_SIZE * 2));
          }
        }

        /* =========================
          STEP 4 — OTHER
        ========================== */
        function initMonogramStep7(customizer, contentCol) {
          const defaultAbout = contentCol.querySelector('[data-other-about="default"]');
          const colorOpts = contentCol.querySelector('[data-other-mode="color"]');
          const monogramOpts = contentCol.querySelector('[data-other-mode="monogram"]');
          if (defaultAbout) defaultAbout.hidden = true;
          if (colorOpts) colorOpts.hidden = true;
          if (monogramOpts) monogramOpts.hidden = false;

          const textInput = contentCol.querySelector('#monogram-custom-text');

          function syncMonogramState() {
            const text = String(customizerState.customerNotes || '').trim();
            const hasText = !!text;
            customizerState.customTextEnabled = hasText;
            customizerState.imageText = '';
            customizerState.artworkColor = hasText ? { id: 'CUSTOM_TEXT', name: 'Custom Text' } : null;
            updateStepLabel(4, hasText ? 'Custom Text' : 'None');
            updateSummary(customizer);
            refreshStepPillStates(customizer);
          }

          textInput?.addEventListener('input', () => {
            customizerState.customerNotes = textInput.value;
            syncMonogramState();
          });
          syncMonogramState();

          const artClearBtn = contentCol.querySelector('.js-step-clear-btn');
          if (artClearBtn && !artClearBtn.dataset.stepClearBound) {
            artClearBtn.dataset.stepClearBound = '1';
            artClearBtn.addEventListener('click', () => {
              customizerState.customTextEnabled = false;
              customizerState.artworkColor = null;
              customizerState.customerNotes = '';
              customizerState.imageText = '';
              if (textInput) textInput.value = '';
              syncMonogramState();
              afterStepClear(customizer, 4);
            });
          }
        }

        function initStep7(customizer) {
          const panel = customizer.querySelector('.step-panel[data-step="4"]');
          if (!panel) return;
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;

          if (isMonogramTypographyProduct()) {
            initMonogramStep7(customizer, contentCol);
            return;
          }

          const defaultAbout = contentCol.querySelector('[data-other-about="default"]');
          const colorOpts = contentCol.querySelector('[data-other-mode="color"]');
          const monogramOpts = contentCol.querySelector('[data-other-mode="monogram"]');
          if (defaultAbout) defaultAbout.hidden = false;
          if (colorOpts) colorOpts.hidden = false;
          if (monogramOpts) monogramOpts.hidden = true;

          const checkbox  = contentCol.querySelector('#artwork-customize-color');
          const colorGrid = contentCol.querySelector('#artwork-color-grid');
          const instrInput = contentCol.querySelector('#artwork-color-instruction');

          if (colorGrid) {
            colorGrid.innerHTML = '';
            colorGrid.style.display = 'none';
          }

          function syncInstructionState() {
            const enabled = !!checkbox?.checked;
            if (instrInput) {
              instrInput.disabled = !enabled;
              instrInput.closest('.artwork-instruction-wrap')?.classList.toggle('artwork-instruction-wrap--disabled', !enabled);
              if (enabled) instrInput.focus();
            }
            customizerState.artworkColor = enabled ? { id: 'CUSTOM', name: 'Custom Instructions' } : null;
            updateStepLabel(4, enabled ? 'Custom Instructions' : 'None');
            updateSummary(customizer);
            refreshStepPillStates(customizer);
          }

          checkbox?.addEventListener('change', syncInstructionState);
          syncInstructionState();

          if (instrInput) instrInput.addEventListener('input', () => {
            customizerState.colorInstruction = instrInput.value;
            updateSummary(customizer);
          });

          const artClearBtn = contentCol.querySelector('.js-step-clear-btn');
          if (artClearBtn && !artClearBtn.dataset.stepClearBound) {
            artClearBtn.dataset.stepClearBound = '1';
            artClearBtn.addEventListener('click', () => {
              customizerState.artworkColor     = null;
              customizerState.colorInstruction = '';
              if (checkbox)   checkbox.checked        = false;
              if (colorGrid)  colorGrid.style.display = 'none';
              if (instrInput) instrInput.value = '';
              syncInstructionState();
              afterStepClear(customizer, 4);
            });
          }
        }

        /* =========================
          STEP 5 — SUMMARY
        ========================== */
        function invalidateCustomizerPreviewBase64() {
          /* Preview is captured live from the canvas; no cached base64 to bust. */
        }

        function getCustomizerWishlistImageBase64(customizer) {
          const fsCanvas = document.getElementById('fs-canvas');
          if (fsCanvas && fsCanvas.tagName === 'CANVAS' && fsCanvas.width > 10) {
            try {
              // Use JPEG to keep the payload smaller
              return fsCanvas.toDataURL('image/jpeg', 0.88);
            } catch (e) { /* ignore */ }
          }
          const panel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          const img = panel?.querySelector('.editable-image') || customizer.querySelector('.editable-image');
          const framed = img?.dataset?.framedSrc || img?.getAttribute('src') || '';
          if (framed && String(framed).startsWith('data:image/')) return framed;
          const dataUrl = getCustomizedImageDataUrl(customizer);
          if (dataUrl && String(dataUrl).startsWith('data:image/')) return dataUrl;
          return '';
        }

        function parseProductCustomizationFromUrl() {
          try {
            const raw = new URLSearchParams(window.location.search).get('product-customization');
            if (!raw) return null;
            let parsed = JSON.parse(raw);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            return parsed && typeof parsed === 'object' ? parsed : null;
          } catch (e1) {
            try {
              const raw2 = new URLSearchParams(window.location.search).get('product-customization');
              if (!raw2) return null;
              const unescaped = String(raw2).trim()
                .replace(/^"|"$/g, '')
                .replace(/\\"/g, '"');
              const parsed2 = JSON.parse(unescaped);
              return parsed2 && typeof parsed2 === 'object' ? parsed2 : null;
            } catch (e2) {
              return null;
            }
          }
        }

        function buildEditCustomizerShareUrl(customizer) {
          const url = new URL(window.location.href);
          url.searchParams.delete('product-customization');
          let pricing = null;
          let formulaPricing = null;
          if (isCustomerLoggedIn(customizer)) {
            try {
              pricing = calculatePricing(customizer, { step: getSummaryStep() });
            } catch (ePrice) { /* ignore */ }
            try {
              if (shouldShowFormulaPricingReview(customizer)) {
                formulaPricing = calculateFormulaPricing(customizer);
              }
            } catch (eFormula) { /* ignore */ }
          }
          const payload = buildWishlistCustomizationFromState(customizer, pricing, formulaPricing);
          url.searchParams.set('product-customization', JSON.stringify(JSON.stringify(payload)));
          return url.toString();
        }

        function parsePayloadCustomizationNumber(raw) {
          const s = String(raw == null ? '' : raw).trim();
          if (!s || /^none$/i.test(s) || /^—+$/.test(s)) return 0;
          const n = parseFloat(s.replace(/[^\d.]/g, ''));
          return isFinite(n) && n > 0 ? n : 0;
        }

        function getCustomizationPayloadValue(payload, key) {
          if (!payload || typeof payload !== 'object' || key == null) return '';
          const base = String(key).replace(/^_+/, '');
          if (!base) return '';
          if (payload[base] != null && String(payload[base]).trim() !== '') return payload[base];
          if (payload['_' + base] != null && String(payload['_' + base]).trim() !== '') {
            return payload['_' + base];
          }
          return '';
        }

        function parsePayloadImageDimensions(payload) {
          if (!payload || typeof payload !== 'object') return null;
          const w = parsePayloadCustomizationNumber(getCustomizationPayloadValue(payload, 'Image Width'));
          const h = parsePayloadCustomizationNumber(getCustomizationPayloadValue(payload, 'Image Height'));
          if (w > 0 && h > 0) return { width: w, height: h };
          const sizeRaw = String(getCustomizationPayloadValue(payload, 'Image Size') || '').trim();
          if (!sizeRaw) return null;
          const parsed = parseMetafieldDimensions(sizeRaw.replace(/\s*[x×]\s*/gi, 'X'));
          if (parsed && parsed.width > 0 && parsed.height > 0) return parsed;
          return null;
        }

        function parseMatInchesFromEntityLabel(label) {
          const m = String(label || '').match(/[—–-]\s*([\d.]+)\s*Inch/i);
          if (!m) return undefined;
          const n = parseFloat(m[1]);
          return isFinite(n) && n > 0 ? n : undefined;
        }

        function parsePayloadMatInches(payload, aliases, labelFallback) {
          if (!payload || typeof payload !== 'object') return undefined;
          const keys = aliases || [];
          for (let i = 0; i < keys.length; i++) {
            const n = parsePayloadCustomizationNumber(getCustomizationPayloadValue(payload, keys[i]));
            if (n > 0) return n;
          }
          return parseMatInchesFromEntityLabel(labelFallback);
        }

        function applyArtworkSizeToCustomizer(customizer, width, height) {
          const w = snapArtworkDimension(width);
          const h = snapArtworkDimension(height);
          if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return false;
          customizerState.width = roundSizeInches(w);
          customizerState.height = roundSizeInches(h);
          const panel = customizer.querySelector('.step-panel[data-step="1"]');
          const wInput = panel?.querySelector('#cs-width-input');
          const hInput = panel?.querySelector('#cs-height-input');
          if (wInput) wInput.value = customizerState.width;
          if (hInput) hInput.value = customizerState.height;
          if (typeof window.__fsSyncSizeFromStep2 === 'function') {
            window.__fsSyncSizeFromStep2(customizerState.width, customizerState.height);
          }
          return true;
        }

        function applySubstrateFromPayload(customizer, payload) {
          const substrate = String(getCustomizationPayloadValue(payload, 'Substrate') || '').trim();
          if (!substrate || /^none$/i.test(substrate)) return false;
          const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
          const val = resolveSubstrateForSelect(customizer, substrate);
          if (!val) return false;
          if (substrateSelect) {
            const hasOpt = Array.from(substrateSelect.options).some(function (o) {
              return o.value === val || String(o.textContent || '').trim() === val;
            });
            if (hasOpt) substrateSelect.value = val;
          }
          customizerState.substrate = val;
          try { substrateSelect?.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
          return true;
        }

        function applyStepNotesFromPayload(payload) {
          const map = {
            'Substrate & Treatment Notes': 1,
            'Mat Notes': 2,
            'Frame and Liner Notes': 3,
            'Other Notes': 4,
            'Summary Notes': 5
          };
          Object.keys(map).forEach(function (label) {
            const text = String(getCustomizationPayloadValue(payload, label) || '').trim();
            if (text) customizerState.stepNotes[map[label]] = text;
          });
        }

        function applyOtherOptionsFromPayload(customizer, payload) {
          const colorInst = String(
            getCustomizationPayloadValue(payload, 'Artwork Color Instructions') ||
            getCustomizationPayloadValue(payload, 'Color Instruction') ||
            ''
          ).trim();
          if (colorInst) {
            customizerState.colorInstruction = colorInst;
            customizerState.artworkColor = { id: 'CUSTOM', name: 'Custom Instructions' };
            const panel = customizer.querySelector('.step-panel[data-step="4"]');
            const checkbox = panel?.querySelector('#artwork-customize-color');
            const instrInput = panel?.querySelector('#artwork-color-instruction');
            if (checkbox) checkbox.checked = true;
            if (instrInput) {
              instrInput.disabled = false;
              instrInput.value = colorInst;
              instrInput.closest('.artwork-instruction-wrap')
                ?.classList.remove('artwork-instruction-wrap--disabled');
            }
          }

          const customText = String(
            getCustomizationPayloadValue(payload, 'Custom Text') ||
            getCustomizationPayloadValue(payload, 'Customer Notes') ||
            ''
          ).trim();
          if (customText) {
            customizerState.customerNotes = customText;
            if (isMonogramTypographyProduct()) {
              customizerState.customTextEnabled = true;
              customizerState.artworkColor = { id: 'CUSTOM_TEXT', name: 'Custom Text' };
              const panel = customizer.querySelector('.step-panel[data-step="4"]');
              const textInput = panel?.querySelector('#monogram-custom-text');
              if (textInput) textInput.value = customText;
            }
          }

          const imageUrl = String(getCustomizationPayloadValue(payload, 'Image URL') || '').trim();
          if (imageUrl) customizerState.shopifyUploadedImageUrl = imageUrl;

          if (/^yes$/i.test(String(
            getCustomizationPayloadValue(payload, 'Side Mark or Additional Notes') ||
            getCustomizationPayloadValue(payload, 'Side Mark') ||
            ''
          ).trim())) {
            customizerState.sideMark = true;
            const sm = String(
              getCustomizationPayloadValue(payload, 'Side Mark or Additional Notes Text') ||
              getCustomizationPayloadValue(payload, 'Side Mark Text') ||
              ''
            ).trim();
            if (sm) customizerState.sideMarkText = sm;
          }
        }

        function applyEditCustomizationFromPayload(customizer, payload) {
          if (!customizer || !payload || typeof payload !== 'object') return false;

          applySubstrateFromPayload(customizer, payload);

          const imageDim = parsePayloadImageDimensions(payload);
          if (imageDim) applyArtworkSizeToCustomizer(customizer, imageDim.width, imageDim.height);

          const treatment = String(getCustomizationPayloadValue(payload, 'Mat Specification') || '').trim();
          if (treatment && !/^none$/i.test(treatment)) {
            setTreatmentOnCustomizer(customizer, treatment);
          }

          const frameCode = String(getCustomizationPayloadValue(payload, 'Frame code') || '').trim();
          if (frameCode && !/^none$/i.test(frameCode)) {
            applyOriginalFrame(customizer, frameCode);
            if (typeof window.__fsSelectFrameById === 'function') {
              window.__fsSelectFrameById(frameCode);
            }
          } else {
            customizerState.frame = null;
          }

          const topMatLabel = String(getCustomizationPayloadValue(payload, 'Top Mat') || '').trim();
          const topMatInches = parsePayloadMatInches(payload, [
            'Top Mat Inches', 'MAT1 Inches', 'Top Mat Size'
          ], topMatLabel);
          if (topMatLabel && !/^none$/i.test(topMatLabel)) {
            const id = parseSpecCatalogId(topMatLabel);
            const topMatName = parseSpecMatName(topMatLabel);
            if (id) applyMatSelection(customizer, 'topMat', id, topMatInches, topMatName);
          } else {
            customizerState.topMat = null;
          }

          const bottomMatLabel = String(getCustomizationPayloadValue(payload, 'Bottom Mat') || '').trim();
          const bottomMatInches = parsePayloadMatInches(payload, [
            'Bottom Mat Inches', 'MAT2 Inches', 'Bottom Mat Size'
          ], bottomMatLabel);
          const activeTreatment = String(customizerState.treatment || treatment || '').trim();
          if (bottomMatLabel && !/^none$/i.test(bottomMatLabel) && activeTreatment === 'Double Mat') {
            const id = parseSpecCatalogId(bottomMatLabel);
            const bottomMatName = parseSpecMatName(bottomMatLabel);
            if (id) applyMatSelection(customizer, 'bottomMat', id, bottomMatInches, bottomMatName);
          } else {
            customizerState.bottomMat = null;
          }

          if (topMatInches != null && customizerState.topMat) {
            setMatSizeInches('topMat', topMatInches);
          }
          if (bottomMatInches != null && customizerState.bottomMat) {
            setMatSizeInches('bottomMat', bottomMatInches);
          }

          const linerLabel = String(getCustomizationPayloadValue(payload, 'Liner') || '').trim();
          if (linerLabel && !/^none$/i.test(linerLabel)) {
            const id = parseSpecCatalogId(linerLabel);
            const linerName = parseSpecMatName(linerLabel);
            if (id) applyMatSelection(customizer, 'liner', id, undefined, linerName);
          } else {
            customizerState.liner = null;
          }

          applyStepNotesFromPayload(payload);
          applyOtherOptionsFromPayload(customizer, payload);

          syncMatCardsFromState(customizer);
          markUserModifiedSelections(customizer);
          refreshCustomizerAfterSelectionChange(customizer, { skipDomSync: true });
          return true;
        }

        function bootstrapCustomizerFromEditPayload(customizer, payload) {
          if (!customizer || !payload) return;
          customizer.dataset.editUrlBootstrap = '1';
          applyEditCustomizationFromPayload(customizer, payload);
          const summaryStep = getSummaryStep();
          customizer.dataset.maxStepUnlocked = String(summaryStep);
          customizer.dataset.maxStepVisited = String(summaryStep);
          refreshStepPillStates(customizer);
          refreshCustomizerNav(customizer);
          updateCanvasMatStepAvailability(customizer);
          refreshCustomizerPricingAndDimensions(customizer);
          captureBaseRetailArtPrice(customizer);
          updateCustomizerMainPrice(customizer);
          updateSummary(customizer);
          if (typeof window.__fsRedrawWithMats === 'function') {
            window.__fsRedrawWithMats();
          }
          delete customizer.dataset.editUrlBootstrap;
        }

        async function copyEditCustomizerLink(customizer, btn) {
          if (!customizer || getCurrentStep(customizer) !== getSummaryStep()) return;
          const url = buildEditCustomizerShareUrl(customizer);
          const labelEl = btn?.querySelector('.summary-copy-link-btn__label');
          const defaultLabel = btn?.dataset.defaultLabel || 'Copy Link';
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(url);
            } else {
              const ta = document.createElement('textarea');
              ta.value = url;
              ta.setAttribute('readonly', '');
              ta.style.position = 'fixed';
              ta.style.left = '-9999px';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
            }
            try {
              if (url !== window.location.href) {
                const prev = (window.history.state && typeof window.history.state === 'object')
                  ? window.history.state
                  : {};
                window.history.replaceState(Object.assign({}, prev, { editCustomizer: true }), '', url);
              }
            } catch (eUrl) { /* ignore */ }
            if (labelEl) {
              labelEl.textContent = 'Copied!';
              setTimeout(function () {
                labelEl.textContent = defaultLabel;
              }, 2000);
            }
          } catch (e) {
            showCustomizerErrorModal('Could not copy the link. Please copy it from your browser address bar.');
          }
        }

        function formatCustomizerDimensionsProperty(w, h) {
          const fw = parseFloat(w);
          const fh = parseFloat(h);
          if (!isFinite(fw) || !isFinite(fh) || fw <= 0 || fh <= 0) return '';
          return Math.round(fw) + 'X' + Math.round(fh);
        }

        /** Current outside dimensions for cart / wishlist (e.g. 31.23X31.23). */
        function getCustomizerOutsideDimensionsProperty(customizer, pricing) {
          const root = getActiveCustomizerRoot(customizer);
          let overall = null;
          if (pricing && pricing.overall && pricing.overall.width > 0 && pricing.overall.height > 0) {
            overall = pricing.overall;
          } else {
            overall = computeFramedSizeInches(root);
          }
          if ((!overall || overall.width <= 0 || overall.height <= 0) &&
              isKeepingOriginalSpecifications(root)) {
            const orig = getOriginalProductSizeDisplay(root);
            const parsed = parseMetafieldDimensions(orig.overallText);
            if (parsed) {
              overall = {
                width: roundSizeInches(parsed.width),
                height: roundSizeInches(parsed.height)
              };
            }
          }
          if (overall && overall.width > 0 && overall.height > 0) {
            return formatCustomizerDimensionsProperty(overall.width, overall.height);
          }
          return '';
        }

        function formatMatSizePropertyLabel(inches) {
          const n = parseFloat(inches);
          if (isNaN(n) || n <= 0) return '';
          const rounded = Math.round(n * 100) / 100;
          const num = Number.isInteger(rounded)
            ? String(rounded)
            : String(rounded).replace(/\.?0+$/, '');
          return num + ' Inch';
        }

        function labelMatEntityForProperties(mat, sizeInches) {
          if (!mat) return 'None';
          const id = String(mat.id || '').trim();
          const name = String(mat.name || '').trim();
          const sizeLabel = formatMatSizePropertyLabel(sizeInches);
          let label = id;
          if (name) label += ' — ' + name;
          if (sizeLabel) label += ' — ' + sizeLabel;
          return label || 'None';
        }

        function formatMatSizeInchesPropertyValue(inches) {
          const n = parseFloat(inches);
          if (isNaN(n) || n <= 0) return '';
          const rounded = Math.round(n * 100) / 100;
          return Number.isInteger(rounded)
            ? String(rounded)
            : String(rounded).replace(/\.?0+$/, '');
        }

        function appendCustomizerStepNotesToProperties(payload) {
          if (!payload || typeof payload !== 'object') return;
          const notes = customizerState.stepNotes || {};
          const stepLabels = {
            1: 'Substrate & Treatment Notes',
            2: 'Mat Notes',
            3: 'Frame and Liner Notes',
            4: 'Other Notes',
            5: 'Summary Notes'
          };
          Object.keys(stepLabels).forEach(function (step) {
            const text = String(notes[step] || '').trim();
            if (text) payload[stepLabels[step]] = text;
          });
        }

        function buildWishlistCustomizationFromState(customizer, pricing, formulaPricing) {
          return buildCustomizerCartLineProperties(customizer, pricing, formulaPricing);
        }

        function customizerMetafield(key, value, namespace, type) {
          const fieldType = type || 'single_line_text_field';
          let raw = value;
          if (fieldType === 'list.single_line_text_field') {
            const list = Array.isArray(value)
              ? value
              : [String(value == null ? '' : value).trim()];
            raw = JSON.stringify(list.filter(function (item) {
              return String(item || '').trim() !== '';
            }).map(String));
          }
          return {
            namespace: namespace || 'customizer',
            key: key,
            type: fieldType,
            value: String(raw == null ? '' : raw).slice(0, 5000)
          };
        }

        function formatCustomSpecDimension(w, h) {
          const rw = roundSizeInches(w);
          const rh = roundSizeInches(h);
          if (!(rw > 0) || !(rh > 0)) return '';
          return rw + ' × ' + rh;
        }

        /**
         * Latest customizer selections written to `custom.*` on the NEW product only.
         * List-type keys match the live catalog metafield definitions so create does not 500.
         * Live catalog products are never updated.
         */
        function buildLatestCustomSpecMetafields(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          const treatment = String(customizerState.treatment || '').trim() || 'None';
          const substrate = String(customizerState.substrate || '').trim() || 'None';
          const sizeText = formatCustomSpecDimension(customizerState.width, customizerState.height);
          let glassText = '';
          let outsideText = '';
          try {
            const glass = computeGlassSizeInches();
            glassText = formatCustomSpecDimension(glass.width, glass.height);
          } catch (eGlass) { /* ignore */ }
          try {
            const overall = computeOverallSizeInches();
            outsideText = formatCustomSpecDimension(overall.width, overall.height);
          } catch (eOutside) { /* ignore */ }
          if (!outsideText) {
            try {
              outsideText = getCustomizerOutsideDimensionsProperty(root) || '';
            } catch (eProp) { /* ignore */ }
          }
          const glazing = getGlazingMaterialLabel(root) || 'None';
          const showMats = (treatment === 'Single Mat' || treatment === 'Double Mat') && !isCanvasSubstrate();
          const showBottomMat = treatment === 'Double Mat';
          const frame = customizerState.frame;
          const topMat = showMats ? customizerState.topMat : null;
          const bottomMat = showBottomMat ? customizerState.bottomMat : null;

          const fields = [
            { key: 'substrate', value: substrate, type: 'list.single_line_text_field' },
            { key: 'mat_specification', value: treatment, type: 'list.single_line_text_field' },
            { key: 'dimensions', value: sizeText },
            { key: 'image_size', value: sizeText },
            { key: 'frame', value: labelCustomizerEntity(frame) },
            { key: 'frame_code', value: frame ? (String(frame.id || '').trim() || 'None') : 'None' },
            { key: 'top_mat', value: showMats
              ? labelMatEntityForProperties(topMat, customizerState.topMatSizeInches)
              : 'None' },
            { key: 'bottom_mat', value: showBottomMat
              ? labelMatEntityForProperties(bottomMat, customizerState.bottomMatSizeInches)
              : 'None' },
            { key: 'liner', value: labelCustomizerEntity(customizerState.liner) },
            { key: 'glazing', value: glazing, type: 'list.single_line_text_field' },
            { key: 'glass_size', value: glassText },
            { key: 'outside_dimensions', value: outsideText }
          ];

          return fields
            .filter(function (field) {
              return field.value != null && String(field.value).trim() !== '';
            })
            .map(function (field) {
              return customizerMetafield(field.key, field.value, 'custom', field.type);
            });
        }

        function stripCustomNamespaceMetafields(payload) {
          if (!payload || !Array.isArray(payload.metafields)) return payload;
          return Object.assign({}, payload, {
            metafields: payload.metafields.filter(function (field) {
              return !field || field.namespace !== 'custom';
            })
          });
        }

        function labelCustomizerEntity(entity) {
          if (!entity) return 'None';
          const id = String(entity.id || '').trim();
          const name = String(entity.name || '').trim();
          if (id && name) return id + ' — ' + name;
          return id || name || 'None';
        }

        function getCustomizerDataset(customizer, camelKey) {
          const root = customizer?.closest?.('.customizer') || customizer;
          if (!root || !root.dataset) return '';
          return String(root.dataset[camelKey] || '').trim();
        }

        function escapeCustomizerHtml(s) {
          return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        }

      function buildCustomizerOrderSku(customizer) {
          return getCustomizerDataset(customizer, 'productSku');
        }

        function resolveCustomizerOrderFormulaPricing(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          if (!shouldUseFullFormulaPricing(root)) return null;
          try {
            return calculateFormulaPricing(root);
          } catch (e) {
            return null;
          }
        }

        function resolveCustomizerOrderFinalPrice(customizer, pricing, formulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          const formula = formulaPricing || resolveCustomizerOrderFormulaPricing(root);
          const formulaTotal = formula && formula.formulaFinalTotal;
          if (isFinite(formulaTotal) && formulaTotal > 0) return formulaTotal;
          if (pricing && isFinite(pricing.retailTotal) && pricing.retailTotal > 0) {
            return pricing.retailTotal;
          }
          return getBaseRetailArtPrice(root);
        }

        function formatCustomizerOrderFinalPrice(customizer, pricing, formulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          const formula = formulaPricing || resolveCustomizerOrderFormulaPricing(root);
          const amount = resolveCustomizerOrderFinalPrice(root, pricing, formula);
          if (formula && isFinite(formula.formulaFinalTotal) && formula.formulaFinalTotal > 0) {
            return formatMoneyUSDDollars(amount);
          }
          return formatMoneyUSD(amount);
        }


        /** Plain-text + structured summary (mirrors updateSummary). */
        function buildCustomizerOrderSummaryContext(customizer, orderNumber, pricing, formulaPricing) {
          const treatment = String(customizerState.treatment || 'None').trim() || 'None';
          const showMats = (treatment === 'Single Mat' || treatment === 'Double Mat');
          const showBottom = (treatment === 'Double Mat');
          const sub = String(customizerState.substrate || 'None');
          const w = customizerState.width;
          const h = customizerState.height;
          const sizeText = (w && h) ? formatSizeInches(w, h) : '';
          const topMat = customizerState.topMat;
          const bottomMat = customizerState.bottomMat;
          const frame = customizerState.frame;
          const liner = customizerState.liner;
          const art = customizerState.artworkColor;
          const inst = String(customizerState.colorInstruction || '').trim();
          const notes = customizerState.stepNotes || {};
          const noteStep1 = String(notes[1] || '').trim();
          const noteStep2 = String(notes[2] || '').trim();
          const noteStep3 = String(notes[3] || '').trim();
          const noteStep4 = String(notes[4] || '').trim();
          const wish = buildWishlistCustomizationFromState(customizer, pricing);
          const sku = buildCustomizerOrderSku(customizer);
          const finalPrice = formatCustomizerOrderFinalPrice(customizer, pricing, formulaPricing);

          const substrateDesc = 'Material Type: ' + sub + ' with ' + treatment + ' treatment' +
            (sizeText ? ' (' + sizeText + ')' : '');
          const glazingLabel = getGlazingMaterialLabel(customizer);

          const topMatDesc = (showMats && topMat)
            ? 'Mat Selection: ' + labelCustomizerEntity(topMat) +
              (formatMatSizeInchLabel(customizerState.topMatSizeInches)
                ? ' — ' + formatMatSizeInchLabel(customizerState.topMatSizeInches) : '')
            : 'No top mat included';

          const bottomMatDesc = (showBottom && bottomMat)
            ? 'Base Mat: ' + labelCustomizerEntity(bottomMat) +
              (formatMatSizeInchLabel(customizerState.bottomMatSizeInches)
                ? ' — ' + formatMatSizeInchLabel(customizerState.bottomMatSizeInches) : '')
            : (showBottom ? 'No bottom mat selected' : 'N/A (not double mat)');

          const frameDesc = frame
            ? 'Frame Model: ' + labelCustomizerEntity(frame)
            : 'No frame selected';

          const linerDesc = liner
            ? 'Liner Model: ' + labelCustomizerEntity(liner)
            : 'No liner selected';

          let otherDesc = art
            ? 'Artwork Color: ' + (art.name || art.id) + (art.hex ? ' (' + art.hex + ')' : '')
            : 'Ready for display';
          if (isMonogramTypographyProduct()) {
            otherDesc = customizerState.customerNotes.trim()
              ? customizerState.customerNotes.trim()
              : 'No custom text provided';
          }

          const matSummary = treatment === 'None'
            ? 'No mat'
            : treatment +
              (topMat ? ' — Top: ' + labelCustomizerEntity(topMat) : '') +
              (bottomMat ? ' — Bottom: ' + labelCustomizerEntity(bottomMat) : '');

          const pricingLines = [];
          if (pricing) {
            if (pricing.keepingOriginalSpec) {
              pricingLines.push('Base art (original specification): ' + formatMoneyUSD(pricing.baseRetailArt));
              pricingLines.push('No customization charges — selections match original product specification.');
              pricingLines.push('Final product price: ' + formatMoneyUSD(pricing.retailTotal));
            } else {
            pricingLines.push('Base art: ' + formatMoneyUSD(pricing.baseRetailArt));
            const sizeChangeFee = (parseFloat(pricing.sizeChangeCharge) || 0) +
              (parseFloat(pricing.customizationChangeFee) || 0);
            if (sizeChangeFee > 0) {
              pricingLines.push('Size change charge: ' + formatMoneyUSD(sizeChangeFee));
            }
            pricingLines.push('Glass size: ' + formatSizeDisplay(pricing.glass.width, pricing.glass.height));
            pricingLines.push('United inches: ' + pricing.unitedInches);
            pricingLines.push('Perimeter: ' + pricing.perimeter + ' in');
            pricingLines.push('Frame cost: ' + formatMoneyUSD(pricing.frameCost));
            pricingLines.push('Mat cost: ' + formatMoneyUSD(pricing.matCost) +
              (pricing.matCost > 0
                ? ' (' + (pricing.matChargeCount != null ? pricing.matChargeCount : pricing.matCount) +
                  '× ' + (pricing.matTier || '') + ')'
                : ''));
            pricingLines.push(
              (isCanvasSubstrate() ? 'Canvas fit: ' : 'Glass / fit: ') +
              formatMoneyUSD(isCanvasSubstrate() ? pricing.canvasFitCharge : pricing.glassCharge)
            );
            if (!isCanvasSubstrate() && pricing.plexiCharge > 0) {
              pricingLines.push('Plexi charge: ' + formatMoneyUSD(pricing.plexiCharge));
            }
            if (!isCanvasSubstrate() && pricing.foamCoreFitCharge > 0) {
              pricingLines.push('Foam core (deckled): ' + formatMoneyUSD(pricing.foamCoreFitCharge));
            }
            if (isCanvasSubstrate() && pricing.acrylicBrushstrokeCharge > 0) {
              pricingLines.push('Hand finish (brushstroke): ' + formatMoneyUSD(pricing.acrylicBrushstrokeCharge));
            }
            if (!isCanvasSubstrate() && pricing.artworkFloatCharge > 0) {
              pricingLines.push('Artwork float charge: ' + formatMoneyUSD(pricing.artworkFloatCharge));
            }
            pricingLines.push('Liner cost: ' + formatMoneyUSD(pricing.linerCost));
            pricingLines.push('Final product price: ' + formatMoneyUSD(pricing.retailTotal));
            }
          }

          return {
            orderNumber: String(orderNumber),
            sku: sku,
            finalPrice: finalPrice,
            matSummary: matSummary,
            substrateDesc: substrateDesc,
            glazingLabel: glazingLabel,
            topMatDesc: topMatDesc,
            bottomMatDesc: bottomMatDesc,
            frameDesc: frameDesc,
            linerDesc: linerDesc,
            otherDesc: otherDesc,
            colorInstruction: inst,
            noteStep1: noteStep1,
            noteStep2: noteStep2,
            noteStep3: noteStep3,
            noteStep4: noteStep4,
            sideMark: customizerState.sideMark
              ? (customizerState.sideMarkText || 'Enabled')
              : 'None',
            wish: wish,
            pricingLines: pricingLines,
            sourceTitle: getCustomizerDataset(customizer, 'productTitle') || 'Custom Art'
          };
        }

        function buildCustomizerProductDescription(customizer, orderNumber, pricing, formulaPricing) {
          const ctx = buildCustomizerOrderSummaryContext(customizer, orderNumber, pricing, formulaPricing);
          const esc = escapeCustomizerHtml;
          const formula = formulaPricing || resolveCustomizerOrderFormulaPricing(customizer);

          function section(title, lines) {
            const body = (lines || []).filter(Boolean).map(function (line) {
              return '<p>' + esc(line) + '</p>';
            }).join('');
            if (!body) return '';
            return '<h3>' + esc(title) + '</h3>' + body;
          }

          let html = '<p>Custom framed art from the product customizer.</p>';
          html += '<p><strong>Order #</strong> ' + esc(ctx.orderNumber) + '</p>';
          html += '<p><strong>SKU</strong> ' + esc(ctx.sku) + '</p>';
          html += '<p><strong>Source product</strong> ' + esc(ctx.sourceTitle) + '</p>';
          html += '<p><strong>Final Total</strong> ' + esc(ctx.finalPrice) + '</p>';

          html += section('Substrate & Treatment', [
            ctx.substrateDesc,
            ctx.glazingLabel && ctx.glazingLabel !== 'None' ? 'Glazing: ' + ctx.glazingLabel : '',
            ctx.noteStep1 ? 'Notes: ' + ctx.noteStep1 : ''
          ]);
          html += section('Mat', [
            ctx.topMatDesc,
            ctx.bottomMatDesc,
            ctx.noteStep2 ? 'Notes: ' + ctx.noteStep2 : ''
          ]);
          html += section('Frame and Liner', [
            ctx.frameDesc,
            ctx.linerDesc,
            ctx.noteStep3 ? 'Notes: ' + ctx.noteStep3 : ''
          ]);

          if (isOtherStepProduct()) {
            html += section('Other', [
              ctx.otherDesc,
              ctx.colorInstruction ? 'Instructions: ' + ctx.colorInstruction : '',
              ctx.noteStep4 ? 'Notes: ' + ctx.noteStep4 : ''
            ]);
          }

          if (ctx.sideMark !== 'None') {
            html += section('Side Mark or Additional Notes', [ctx.sideMark]);
          }

          if (formula) {
            const formulaTable = buildFormulaPricingReviewTableHtml(formula, esc, {
              forProductDescription: true
            });
            if (formulaTable) html += formulaTable;
          }

          return html;
        }

        function buildCustomizerProductMetafields(customizer, orderNumber, pricing, formulaPricing) {
          const ctx = buildCustomizerOrderSummaryContext(customizer, orderNumber, pricing, formulaPricing);
          const wish = ctx.wish;

          return [
            customizerMetafield('order_number', ctx.orderNumber),
            customizerMetafield('sku', ctx.sku),
            customizerMetafield('frame', wish['Frame Name'] !== 'None' ? wish['Frame Name'] : (customizerState.frame ? labelCustomizerEntity(customizerState.frame) : 'None')),
            customizerMetafield('mat', ctx.matSummary),
            customizerMetafield('substrate', String(customizerState.substrate || 'None')),
            customizerMetafield('treatment', String(customizerState.treatment || 'None')),
            customizerMetafield('dimensions', (customizerState.width && customizerState.height)
              ? formatSizeInches(customizerState.width, customizerState.height)
              : 'N/A'),
            customizerMetafield('top_mat', labelCustomizerEntity(customizerState.topMat)),
            customizerMetafield('bottom_mat', labelCustomizerEntity(customizerState.bottomMat)),
            customizerMetafield('liner', labelCustomizerEntity(customizerState.liner)),
            customizerMetafield('price_total', ctx.finalPrice),
            customizerMetafield('summary_substrate', ctx.substrateDesc),
            customizerMetafield('summary_top_mat', ctx.topMatDesc),
            customizerMetafield('summary_bottom_mat', ctx.bottomMatDesc),
            customizerMetafield('summary_frame', ctx.frameDesc),
            customizerMetafield('summary_liner', ctx.linerDesc),
            customizerMetafield('summary_other', ctx.otherDesc),
            customizerMetafield('summary_pricing', ctx.pricingLines.join(' | ')),
            customizerMetafield('notes_step1', ctx.noteStep1 || 'None'),
            customizerMetafield('notes_step2', ctx.noteStep2 || 'None'),
            customizerMetafield('notes_step3', ctx.noteStep3 || 'None'),
            customizerMetafield('notes_step4', ctx.noteStep4 || 'None'),
            customizerMetafield('color_instruction', ctx.colorInstruction || 'None'),
            customizerMetafield('source_product_id', getCustomizerDataset(customizer, 'productId')),
            customizerMetafield('source_product_handle', getCustomizerDataset(customizer, 'productHandle')),
            customizerMetafield('source_product_title', getCustomizerDataset(customizer, 'productTitle')),
            customizerMetafield('source_product_sku', getCustomizerDataset(customizer, 'productSku')),
            customizerMetafield('source_product_image', getCustomizerDataset(customizer, 'productImageUrl')),
            customizerMetafield('source_variant_id', getCustomizerDataset(customizer, 'productVariantId')),
            customizerMetafield('mat_specification', wish['Mat Specification'] || 'None'),
            customizerMetafield('frame_code', wish['Frame code'] || 'None'),
            customizerMetafield('frame_type', wish['Frame Type'] || 'None'),
            customizerMetafield('frame_finish', wish['Frame Finish Category'] || 'None'),
            customizerMetafield('side_mark', ctx.sideMark)
          ].concat(buildLatestCustomSpecMetafields(customizer));
        }

        async function resolveCustomizerPreviewImageForOrder(customizer) {
          const uploaded = String(customizerState.shopifyUploadedImageUrl || '').trim();
          if (uploaded) return { imageUrl: uploaded, imageBase: '' };

          const imageBase = getCustomizerWishlistImageBase64(customizer);
          if (!imageBase) throw new Error('No preview image available. Complete your design preview first.');

          try {
            const res = await fetch('/apps/customizer/image-upload', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                imageBase: imageBase,
                filename: 'custom-art-order-' + Date.now() + '.png'
              })
            });

            const json = await res.json().catch(function () { return null; });
            if (res.ok && json && json.ok === true) {
              const imageUrl = json.data && json.data.imageUrl ? String(json.data.imageUrl).trim() : '';
              if (imageUrl) {
                customizerState.shopifyUploadedImageUrl = imageUrl;
                return { imageUrl: imageUrl, imageBase: '' };
              }
            }
          } catch (e) {
            console.warn('Customizer image upload failed; create payload will use base64.', e);
          }

          return { imageUrl: '', imageBase: imageBase };
        }

        function buildCustomizerProductCreatePayload(customizer, orderNumber, imageFields, pricing, formulaPricing) {
          const baseTitle = getCustomizerDataset(customizer, 'productTitle') || 'Custom Art';
          const finalTotal = resolveCustomizerOrderFinalPrice(customizer, pricing, formulaPricing);
          const frame = customizerState.frame;
          const sizeSuffix = (customizerState.width && customizerState.height)
            ? ' — ' + formatSizeInches(customizerState.width, customizerState.height)
            : '';

          const payload = {
            orderNumber: orderNumber,
            title: baseTitle + (frame ? ' — (' + String(frame.id || frame.name || '').trim() + ')' : '') + sizeSuffix,
            status: 'UNLISTED',
            price: finalTotal.toFixed(2),
            description: buildCustomizerProductDescription(customizer, orderNumber, pricing, formulaPricing),
            sku: buildCustomizerOrderSku(customizer),
            filename: 'custom-art-order-' + orderNumber + '-' + Date.now() + '.png',
            metafields: buildCustomizerProductMetafields(customizer, orderNumber, pricing, formulaPricing),
            tags: ['art-customizer', 'customizer-product'],
            orderTags: ['art-customizer']
          };

          if (imageFields.imageUrl) payload.imageUrl = imageFields.imageUrl;
          if (imageFields.imageBase) payload.imageBase = imageFields.imageBase;
          return payload;
        }

        function normalizeShopifyVariantId(id) {
          const s = String(id || '').trim();
          if (!s) return '';
          if (/^\d+$/.test(s)) return s;
          const gidMatch = s.match(/ProductVariant\/(\d+)/i);
          if (gidMatch) return gidMatch[1];
          const digits = s.replace(/\D/g, '');
          return digits || '';
        }

        function resolveCreatedCustomizerVariantId(json) {
          const d = json && json.data ? json.data : {};
          const product = d.product || {};
          const fromRoot = normalizeShopifyVariantId(
            d.variantId || d.variant_id || (d.variant && d.variant.id)
          );
          if (fromRoot) return fromRoot;
          const variants = product.variants;
          if (Array.isArray(variants) && variants.length) {
            const first = variants.find(function (v) { return v && v.available !== false; }) || variants[0];
            return normalizeShopifyVariantId(first && (first.id || first.legacyResourceId));
          }
          return '';
        }

        function resolveCreatedCustomizerProductHandle(json) {
          const d = json && json.data ? json.data : {};
          const product = d.product || {};
          return String(product.handle || d.handle || d.productHandle || '').trim();
        }

        function normalizeShopifyProductId(id) {
          const s = String(id || '').trim();
          if (!s) return '';
          if (/^\d+$/.test(s)) return s;
          const gidMatch = s.match(/Product\/(\d+)/i);
          if (gidMatch) return gidMatch[1];
          const digits = s.replace(/\D/g, '');
          return digits || '';
        }

        function resolveCreatedCustomizerProductId(json) {
          const d = json && json.data ? json.data : {};
          const product = d.product || {};
          return normalizeShopifyProductId(
            d.productId || d.product_id || product.id || product.legacyResourceId
          );
        }

        async function fetchCreatedCustomizerProductJs(handle) {
          const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin'
          });
          if (!res.ok) throw new Error('Could not load the new custom product.');
          return res.json();
        }

        async function resolveCreatedCustomizerProductRefs(json) {
          let productId = resolveCreatedCustomizerProductId(json);
          let handle = resolveCreatedCustomizerProductHandle(json);
          let variantId = resolveCreatedCustomizerVariantId(json);

          if (handle && (!productId || !variantId)) {
            const product = await fetchCreatedCustomizerProductJs(handle);
            if (!productId && product && product.id) productId = String(product.id);
            if (!handle && product && product.handle) handle = String(product.handle);
            if (!variantId) {
              const variants = product && product.variants ? product.variants : [];
              const variant = variants.find(function (v) { return v && v.available; }) || variants[0];
              if (variant && variant.id) variantId = String(variant.id);
            }
          }

          return { productId: productId, handle: handle, variantId: variantId };
        }

        async function fetchVariantIdByProductHandle(handle) {
          const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin'
          });
          if (!res.ok) throw new Error('Could not load the new product variant for cart.');
          const product = await res.json();
          const variants = product && product.variants ? product.variants : [];
          const variant = variants.find(function (v) { return v && v.available; }) || variants[0];
          const id = variant && variant.id;
          if (!id) throw new Error('No variant available for the custom product.');
          return String(id);
        }
        
        /**
         * Set a cart line property. Visible keys show on cart/checkout;
         * hidden keys use Shopify's "_" prefix (still stored for ops/admin).
         */
        function setCartLineProperty(props, key, value, visible) {
          if (!props || value == null) return;
          const str = String(value);
          if (!str.trim()) return;
          const base = String(key || '').replace(/^_+/, '');
          if (!base) return;
          props[visible ? base : ('_' + base)] = str;
        }

        /** Which cart line properties the customer changed vs original product spec. */
        function getCartLinePropertyVisibility(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          const flags = getOriginalSpecChangeFlags(root);
          const spec = readOriginalSpecFromCustomizer(root);
          const hasSpec = productHasOriginalSpec(root);
          const substrateOrTreatmentChanged = !hasSpec || !spec ||
            !matchesOriginalSubstrateAndTreatment(root, spec);
          const sizeChanged = !!(flags.sizeChanged || hasArtworkDimensionCustomization(root));
          const crop = getCustomizerCropInfo(root);
          // Glass / outside only shift when size, frame, mat width, or liner changes.
          // Also when mat selection changes glass (printed/oversize path).
          const dimsAffected = sizeChanged || flags.frameChanged || flags.matSizesChanged ||
            flags.topMatChanged || flags.bottomMatChanged || flags.linerChanged || crop.hasCrop;

          return {
            Substrate: substrateOrTreatmentChanged,
            'Mat Specification': substrateOrTreatmentChanged,
            'Frame Name': !!flags.frameChanged,
            'Frame code': !!flags.frameChanged,
            'Top Mat': !!(flags.topMatChanged || flags.matSizesChanged),
            'Bottom Mat': !!flags.bottomMatChanged,
            Liner: !!flags.linerChanged,
            'Image Size': sizeChanged || crop.hasCrop,
            'Image Width': sizeChanged || crop.hasCrop,
            'Image Height': sizeChanged || crop.hasCrop,
            'Glass Size': dimsAffected,
            Glazing: dimsAffected || !isKeepingOriginalSpecifications(root),
            'Current Outside Dimensions': dimsAffected,
            'Custom Crop': true,
            'Cropped To': true,
            'Crop Reference Image': true,
            'Side Mark or Additional Notes': true,
            'Side Mark or Additional Notes Text': true,
            'Image URL': false
          };
        }

        function buildCustomizerCartLineProperties(customizer, pricing, formulaPricing) {
          const root = getActiveCustomizerRoot(customizer);
          const treatment = String(customizerState.treatment || '').trim() || 'None';
          const frame = customizerState.frame || null;
          const topMat = customizerState.topMat || null;
          const bottomMat = customizerState.bottomMat || null;
          const liner = customizerState.liner || null;
          const showMats = (treatment === 'Single Mat' || treatment === 'Double Mat') && !isCanvasSubstrate();
          const showBottomMat = treatment === 'Double Mat';
          const visibility = getCartLinePropertyVisibility(root);

          const w = roundSizeInches(customizerState.width);
          const h = roundSizeInches(customizerState.height);
          const sizeText = (w > 0 && h > 0)
            ? formatSizeInches(w, h).replace(' × ', ' x ')
            : '';

          const glass = computeGlassSizeInches();
          const glassText = (glass.width > 0 && glass.height > 0)
            ? formatSizeInches(glass.width, glass.height).replace(' × ', ' x ')
            : '';

          const dimensions = getCustomizerOutsideDimensionsProperty(root, pricing);
          const props = {};
          const inventoryNumber = buildCustomizerOrderSku(root);
          setCartLineProperty(props, 'Inventory #', inventoryNumber, true);

          setCartLineProperty(
            props,
            'Substrate',
            String(customizerState.substrate || '').trim() || 'None',
            visibility.Substrate
          );
          setCartLineProperty(props, 'Mat Specification', treatment, visibility['Mat Specification']);
          setCartLineProperty(
            props,
            'Frame Name',
            frame ? (String(frame.name || '').trim() || 'None') : 'None',
            visibility['Frame Name']
          );
          setCartLineProperty(
            props,
            'Frame code',
            frame ? (String(frame.id || '').trim() || 'None') : 'None',
            visibility['Frame code']
          );
          setCartLineProperty(
            props,
            'Top Mat',
            showMats && topMat
              ? labelMatEntityForProperties(topMat, customizerState.topMatSizeInches)
              : 'None',
            visibility['Top Mat']
          );
          setCartLineProperty(
            props,
            'Bottom Mat',
            showBottomMat && bottomMat
              ? labelMatEntityForProperties(bottomMat, customizerState.bottomMatSizeInches)
              : 'None',
            visibility['Bottom Mat']
          );
          setCartLineProperty(props, 'Liner', labelCustomizerEntity(liner), visibility.Liner);

          const crop = getCustomizerCropInfo(root);
          if (crop.hasCrop) {
            setCartLineProperty(
              props,
              'Custom Crop',
              'Image size custom cropped ' + (sizeText || '—'),
              visibility['Custom Crop']
            );
            setCartLineProperty(
              props,
              'Cropped To',
              'Cropped to: ' + (crop.sizeText || '—') +
                (crop.pxText ? ' (' + crop.pxText + ')' : ''),
              visibility['Cropped To']
            );
            if (crop.referenceUrl) {
              setCartLineProperty(
                props,
                'Crop Reference Image',
                crop.referenceUrl,
                visibility['Crop Reference Image']
              );
            }
          }

          if (sizeText) {
            setCartLineProperty(props, 'Image Size', sizeText, visibility['Image Size']);
            setCartLineProperty(props, 'Image Width', String(w), visibility['Image Width']);
            setCartLineProperty(props, 'Image Height', String(h), visibility['Image Height']);
          }
          if (glassText) {
            setCartLineProperty(props, 'Glass Size', glassText, visibility['Glass Size']);
          }
          const glazingLabel = getGlazingMaterialLabel(customizer);
          if (glazingLabel && glazingLabel !== 'None') {
            setCartLineProperty(props, 'Glazing', glazingLabel, visibility['Glazing']);
          }
          if (dimensions) {
            setCartLineProperty(
              props,
              'Current Outside Dimensions',
              dimensions,
              visibility['Current Outside Dimensions']
            );
          }

          const imageUrl = String(customizerState.shopifyUploadedImageUrl || '').trim();
          if (imageUrl) {
            setCartLineProperty(props, 'Image URL', imageUrl, visibility['Image URL']);
          }

          // Side Mark or Additional Notes: always visible when enabled (customer-facing change).
          if (customizerState.sideMark) {
            setCartLineProperty(props, 'Side Mark or Additional Notes', 'Yes', visibility['Side Mark or Additional Notes']);
            const sideMarkText = String(customizerState.sideMarkText || '').trim();
            if (sideMarkText) {
              setCartLineProperty(props, 'Side Mark or Additional Notes Text', sideMarkText, visibility['Side Mark or Additional Notes Text']);
            }
          }

          // Internal-only values — always hidden from the customer.
          let formula = formulaPricing || null;
          if (!formula) {
            try { formula = calculateFormulaPricing(root); } catch (eFormula) { formula = null; }
          }
          if (formula) {
            if (isFinite(formula.mfgPriceAtMargin)) {
              props['_Framer Cost'] = formatMoneyUSD(formula.mfgPriceAtMargin);
            }
            if (isFinite(formula.bomFt) && formula.bomFt > 0) {
              props['_BOM FT'] = String(roundFormulaMoney(formula.bomFt));
            }
          }
          if (w > 0 && h > 0) {
            props['_Print Cost'] = formatMoneyUSD(w * h * 0.01);
          }
          const publisher = getProductPublisher(root);
          if (publisher) props['_Publisher'] = publisher;

          const sourceHandle = getCustomizerDataset(root, 'productHandle');
          if (sourceHandle) props['_Source Product Handle'] = sourceHandle;

          // Step notes are customer-entered — keep visible.
          appendCustomizerStepNotesToProperties(props);

          // Never pass this flag as a line-item property.
          delete props['_art_customizer'];
          delete props['art_customizer'];
          delete props['_art-customizer'];
          delete props['art-customizer'];

          return props;
        }

        async function markCustomizerOrderInCart() {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              credentials: 'same-origin',
              body: JSON.stringify({
                attributes: {
                  'art-customizer': 'true'
                }
              })
            });
          } catch (e) { /* ignore */ }
        }

        async function addCreatedCustomizerProductToCart(customizer, json, pricing, formulaPricing) {
          let variantId = resolveCreatedCustomizerVariantId(json);
          if (!variantId) {
            const handle = resolveCreatedCustomizerProductHandle(json);
            if (!handle) throw new Error('Product created but handle was missing for cart add.');
            variantId = await fetchVariantIdByProductHandle(handle);
          }

          const numericId = parseInt(variantId, 10);
          if (!isFinite(numericId)) throw new Error('Invalid variant id for cart add.');

          const properties = buildCustomizerCartLineProperties(customizer, pricing, formulaPricing);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{
                id: numericId,
                quantity: 1,
                properties: properties
              }]
            })
          });

          const cartJson = await res.json().catch(function () { return null; });
          if (!res.ok) {
            throw new Error(
              (cartJson && (cartJson.description || cartJson.message)) ||
              'Could not add custom product to cart.'
            );
          }

          await markCustomizerOrderInCart();

          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          } catch (e) { /* ignore */ }

          return cartJson;
        }

        function syncAddToOrderButtonState(btn, customizer, cachedFormulaPricing) {
          if (!btn) return;
          const root = customizer || document.querySelector('.customizer');
          if (!isCustomerLoggedIn(root)) {
            btn.dataset.defaultLabel = 'Login to Order';
            btn.classList.remove('summary-add-cart-btn--loading');
            btn.innerHTML = '<span class="summary-add-cart-btn__label">Login to Order</span>';
            btn.disabled = true;
            btn.classList.add('summary-add-cart-btn--disabled');
            return;
          }

          const label = 'Add To Order';
          btn.dataset.defaultLabel = label;
          if (!btn.classList.contains('summary-add-cart-btn--loading')) {
            const priceText = getAddToOrderPriceDisplay(root, cachedFormulaPricing);
            btn.innerHTML = buildAddToOrderButtonInnerHtml(label, priceText, false);
            btn.disabled = !customizerState.designApproved;
            btn.classList.toggle('summary-add-cart-btn--disabled', !customizerState.designApproved);
          }
        }

        /**
         * Returns an error message if Add To Order should be blocked, otherwise null.
         * - Unchanged original specs → buy from the product-page Add to Cart instead
         * - Frame is always required for customizer orders
         */
        function getAddToOrderValidationError(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          syncCustomizerSelectionsFromDOM(root);

          // Side Mark / Additional Notes alone is a valid customizer change (no price impact).
          if (productHasOriginalSpec(root) && isKeepingOriginalSpecifications(root) &&
              !hasSideMarkOrAdditionalNotes()) {
            return 'You have not made any changes in the customizer. If you need to purchase this item without customization, please close this window and use the Add to Cart button on the product page.';
          }

          if (!customizerState.frame || !String(customizerState.frame.id || '').trim()) {
            return 'Please select a frame before adding to order. A frame is required for customized orders.';
          }

          return null;
        }

        function setAddToOrderButtonLoading(btn, loading) {
          if (!btn) return;
          const customizer = btn.closest('.customizer');
          if (!isCustomerLoggedIn(customizer)) {
            syncAddToOrderButtonState(btn, customizer);
            return;
          }

          const label = btn.dataset.defaultLabel || 'Add To Order';
          if (!btn.dataset.defaultLabel) btn.dataset.defaultLabel = label;

          if (loading) {
            btn.disabled = true;
            btn.classList.add('summary-add-cart-btn--loading');
            btn.innerHTML = buildAddToOrderButtonInnerHtml(label, '', true);
            return;
          }

          btn.classList.remove('summary-add-cart-btn--loading');
          const formulaPricing = shouldShowFormulaPricingReview(customizer)
            ? calculateFormulaPricing(customizer)
            : null;
          const priceText = getAddToOrderPriceDisplay(customizer, formulaPricing);
          btn.innerHTML = buildAddToOrderButtonInnerHtml(label, priceText, false);
          btn.disabled = !customizerState.designApproved;
          btn.classList.toggle('summary-add-cart-btn--disabled', !customizerState.designApproved);
        }

        async function postCustomizerProductCreate(createUrl, payload) {
          const res = await fetch(createUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const text = await res.text();
          let json = null;
          try { json = JSON.parse(text); } catch (e) { /* not json */ }
          return { res: res, json: json, text: text };
        }

        async function createCustomizerProductViaApi(customizer) {
          syncCustomizerSelectionsFromDOM(customizer);
          updateSummary(customizer);

          const createUrl = getCustomizerDataset(customizer, 'customizerCreateUrl') ||
            '/apps/customizer/customizer-product-create';
          const pricing = calculatePricing(customizer, { step: getSummaryStep() });
          let formulaPricing = null;
          try {
            if (shouldShowFormulaDealerHeaderPrice(customizer)) {
              formulaPricing = calculateFormulaPricing(customizer);
            }
          } catch (eFormula) { /* ignore */ }
          const orderNumber = Date.now();
          const imageFields = await resolveCustomizerPreviewImageForOrder(customizer);
          let payload = buildCustomizerProductCreatePayload(
            customizer,
            orderNumber,
            imageFields,
            pricing,
            formulaPricing
          );

          let posted = await postCustomizerProductCreate(createUrl, payload);
          if ((!posted.json || !posted.res.ok || posted.json.ok === false) &&
              Array.isArray(payload.metafields) &&
              payload.metafields.some(function (field) { return field && field.namespace === 'custom'; })) {
            payload = stripCustomNamespaceMetafields(payload);
            posted = await postCustomizerProductCreate(createUrl, payload);
          }

          if (!posted.json) {
            throw new Error('Unexpected API response. Check console for details.');
          }
          if (!posted.res.ok || posted.json.ok === false) {
            throw new Error(posted.json.message || posted.json.error || 'Could not create custom product');
          }

          return { json: posted.json, pricing: pricing, formulaPricing: formulaPricing };
        }

        async function createCustomizerOrderProduct(customizer) {
          const created = await createCustomizerProductViaApi(customizer);
          await addCreatedCustomizerProductToCart(
            customizer,
            created.json,
            created.pricing,
            created.formulaPricing
          );

          const cartUrl = getCustomizerDataset(customizer, 'cartUrl') || '/cart';
          return { json: created.json, pricing: created.pricing, cartUrl: cartUrl };
        }

        function syncWishlistButton(customizer) {
          const panel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          const el = panel?.querySelector('#uploaded-shopify-image.wf-wishlist-button') ||
            customizer.querySelector('#uploaded-shopify-image.wf-wishlist-button');
          if (!el) return;

          function notifyWishlistGuru(target) {
            if (!target) return;
            // Wishlist Guru / some wishlist widgets cache attributes on init.
            // Emitting a custom event is the safest non-destructive refresh signal.
            try { target.dispatchEvent(new CustomEvent('wf-wishlist:updated', { bubbles: true })); } catch (e) {}
            try { window.dispatchEvent(new CustomEvent('wf-wishlist:updated', { detail: { el: target } })); } catch (e) {}
            try { target.dispatchEvent(new CustomEvent('wishlist:refresh', { bubbles: true })); } catch (e2) {}
          }

          // Only use data from the current session (no persistence on reload).
          const uploaded = String(customizerState.shopifyUploadedImageUrl || '').trim();
          if (uploaded) el.setAttribute('product-image', uploaded);
          else {
            const imgBase64 = getCustomizerWishlistImageBase64(customizer);
            if (imgBase64) el.setAttribute('product-image', imgBase64);
          }

          let pricing = null;
          let formulaPricing = null;
          if (isCustomerLoggedIn(customizer)) {
            try {
              pricing = calculatePricing(customizer, { step: getSummaryStep() });
            } catch (ePrice) { /* ignore */ }
            try {
              if (shouldShowFormulaPricingReview(customizer)) {
                formulaPricing = calculateFormulaPricing(customizer);
              }
            } catch (eFormula) { /* ignore */ }
          }

          const payload = buildWishlistCustomizationFromState(customizer, pricing, formulaPricing);
          el.setAttribute('product-customization', JSON.stringify(payload));

          try {
            const createdHandle = String(el.getAttribute('product-handle') || '').trim();
            const sourceHandle = getCustomizerDataset(customizer, 'productHandle');
            if (createdHandle && createdHandle !== sourceHandle) {
              el.setAttribute('product-url', window.location.origin + '/products/' + encodeURIComponent(createdHandle));
            } else {
              const shareUrl = buildEditCustomizerShareUrl(customizer);
              el.setAttribute('product-url', shareUrl);
            }
          } catch (eShareUrl) { /* ignore */ }

          // Wishlist Guru also reads product-price attribute separately.
          if (isCustomerLoggedIn(customizer)) {
            const finalTotal = getCustomizerFormulaFinalTotal(customizer, formulaPricing);
            if (finalTotal != null) {
              el.setAttribute('product-price', String(finalTotal));
            } else if (pricing && isFinite(pricing.retailTotal)) {
              el.setAttribute('product-price', String(roundSizeInches(pricing.retailTotal)));
            } else {
              try {
                el.setAttribute('product-price', String(roundSizeInches(getBaseRetailArtPrice(customizer))));
              } catch (ePrice2) { /* ignore */ }
            }
          } else {
            try { el.removeAttribute('product-price'); } catch (ePrice3) { /* ignore */ }
          }

          // If Wishlist Guru injects an inner clickable element, mirror attrs there too.
          const inner = el.querySelector('.wf-wishlist, button, a');
          if (inner && inner !== el) {
            try { inner.setAttribute('product-image', el.getAttribute('product-image') || ''); } catch (e3) {}
            try { inner.setAttribute('product-customization', el.getAttribute('product-customization') || '{}'); } catch (e4) {}
            try { inner.setAttribute('product-price', el.getAttribute('product-price') || ''); } catch (e5) {}
            try { inner.setAttribute('product-url', el.getAttribute('product-url') || ''); } catch (e6) {}
            try { inner.setAttribute('product-id', el.getAttribute('product-id') || ''); } catch (e7) {}
            try { inner.setAttribute('product-handle', el.getAttribute('product-handle') || ''); } catch (e8) {}
            try { inner.setAttribute('variant-id', el.getAttribute('variant-id') || ''); } catch (e9) {}
            notifyWishlistGuru(inner);
          }
          notifyWishlistGuru(el);
        }

        function bindWishlistGuruFreshData(customizer) {
          const panel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          if (!panel) return;
          const el = panel.querySelector('#uploaded-shopify-image.wf-wishlist-button');
          if (!el) return;
          if (el.dataset.wgBound === '1') return;
          el.dataset.wgBound = '1';

          // 1) Click-time sync: ensure latest attrs right before app handles the click.
          function syncNow() { syncWishlistButton(customizer); }
          // Some widgets read on pointerdown/mousedown (before click), so bind early.
          ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown'].forEach(function (evt) {
            el.addEventListener(evt, syncNow, true);
          });

          // 2) MutationObserver: if app injects/replaces inner nodes, re-apply attrs.
          const mo = new MutationObserver(function () {
            syncWishlistButton(customizer);
          });
          mo.observe(el, { childList: true, subtree: true });

          // 3) If Wishlist Guru fires global lifecycle events, sync right before/after.
          // (The app is loaded externally, so we listen defensively.)
          const wgEvents = [
            'WgWishlistBeforeAdd',
            'WgWishlistAdd',
            'WgWishlistAdded',
            'WgWishlistItemAdd',
            'WgWishlistItemAdded',
            'WgWishlistButtonClick'
          ];
          wgEvents.forEach(function (name) {
            window.addEventListener(name, syncNow, true);
          });
        }

        function applyCreatedProductAttrsToWishlistEl(el, refs) {
          if (!el || !refs) return;
          if (refs.productId) {
            el.setAttribute('product-id', refs.productId);
            el.setAttribute('productId', refs.productId);
          }
          if (refs.handle) {
            el.setAttribute('product-handle', refs.handle);
            el.setAttribute('productHandle', refs.handle);
          }
          if (refs.variantId) {
            el.setAttribute('variant-id', refs.variantId);
            el.setAttribute('variantId', refs.variantId);
          }
        }

        function notifyWishlistGuru(target) {
          if (!target) return;
          try { target.dispatchEvent(new CustomEvent('wf-wishlist:updated', { bubbles: true })); } catch (e) {}
          try { window.dispatchEvent(new CustomEvent('wf-wishlist:updated', { detail: { el: target } })); } catch (e) {}
          try { target.dispatchEvent(new CustomEvent('wishlist:refresh', { bubbles: true })); } catch (e2) {}
        }

        async function bindWishlistButtonToCreatedProduct(customizer, json) {
          const el = getWishlistButtonEl(customizer);
          if (!el) throw new Error('Wishlist button was not found.');
          const refs = await resolveCreatedCustomizerProductRefs(json);
          if (!refs.productId && !refs.handle) {
            throw new Error('Product created but id/handle was missing for wishlist.');
          }
          if (!refs.productId && refs.handle) {
            const product = await fetchCreatedCustomizerProductJs(refs.handle);
            if (product && product.id) refs.productId = String(product.id);
            if (!refs.variantId && product && product.variants && product.variants[0]) {
              refs.variantId = String(product.variants[0].id);
            }
          }
          if (!refs.productId) {
            throw new Error('Could not resolve the new custom product id for wishlist.');
          }

          applyCreatedProductAttrsToWishlistEl(el, refs);
          const inner = el.querySelector('.wf-wishlist, button, a');
          if (inner && inner !== el) applyCreatedProductAttrsToWishlistEl(inner, refs);

          const existingInner = el.querySelector('.wf-wishlist');
          if (existingInner) {
            const fresh = document.createElement('div');
            fresh.className = 'wf-wishlist';
            applyCreatedProductAttrsToWishlistEl(fresh, refs);
            existingInner.replaceWith(fresh);
            notifyWishlistGuru(fresh);
          }
          notifyWishlistGuru(el);
          syncWishlistButton(customizer);
          applyCreatedProductAttrsToWishlistEl(el, refs);
          return refs;
        }

        function wishlistElProductId(el) {
          if (!el) return '';
          return normalizeShopifyProductId(
            el.getAttribute('product-id') || el.getAttribute('productId') || ''
          );
        }

        function isWishlistElMarked(el) {
          if (!el) return false;
          if (el.classList.contains('wishlisted') || el.classList.contains('wf-wishlisted')) return true;
          if (el.getAttribute('aria-pressed') === 'true') return true;
          if (el.getAttribute('data-wishlisted') === 'true') return true;
          if (el.querySelector('.wishlisted, .wf-wishlisted, [aria-pressed="true"]')) return true;
          return !!el.closest('.wishlisted');
        }

        function waitForWishlistBoundToProduct(customizer, productId, timeoutMs) {
          const want = normalizeShopifyProductId(productId);
          return new Promise(function (resolve) {
            function bound() {
              const el = getWishlistButtonEl(customizer);
              if (!el || !want) return !want;
              const id = wishlistElProductId(el);
              const inner = el.querySelector('.wf-wishlist, button, a');
              const innerId = wishlistElProductId(inner);
              return id === want || innerId === want;
            }
            if (bound()) {
              resolve(true);
              return;
            }
            const started = Date.now();
            const iv = setInterval(function () {
              if (bound() || Date.now() - started > (timeoutMs || 2500)) {
                clearInterval(iv);
                resolve(bound());
              }
            }, 80);
          });
        }

        function waitForWishlistMarked(customizer, productId, timeoutMs) {
          const want = normalizeShopifyProductId(productId);
          return new Promise(function (resolve) {
            function marked() {
              const el = getWishlistButtonEl(customizer);
              if (!el) return false;
              const boundId = wishlistElProductId(el);
              if (want && boundId && boundId !== want) return false;
              return isWishlistElMarked(el);
            }
            if (marked()) {
              resolve(true);
              return;
            }
            const el = getWishlistButtonEl(customizer);
            const mo = el ? new MutationObserver(function () {
              if (marked()) {
                mo.disconnect();
                clearTimeout(fail);
                resolve(true);
              }
            }) : null;
            if (mo && el) mo.observe(el, { attributes: true, childList: true, subtree: true });
            const fail = setTimeout(function () {
              if (mo) mo.disconnect();
              resolve(marked());
            }, timeoutMs || 4000);
          });
        }

        function setCustomizerWishlistButtonState(btn, state) {
          if (!btn) return;
          const defaultLabel = btn.dataset.defaultLabel || 'Add to wishlist';
          btn.classList.remove('customizer-wishlist-dummy-btn--loading', 'customizer-wishlist-dummy-btn--added');
          if (state === 'loading') {
            btn.classList.add('customizer-wishlist-dummy-btn--loading');
            btn.disabled = true;
            btn.setAttribute('aria-busy', 'true');
            btn.innerHTML =
              '<span class="customizer-wishlist-dummy-btn__spinner" aria-hidden="true"></span>' +
              '<span class="customizer-wishlist-dummy-btn__label">Adding...</span>';
            return;
          }
          if (state === 'added') {
            btn.classList.add('customizer-wishlist-dummy-btn--added');
            btn.disabled = true;
            btn.setAttribute('aria-busy', 'false');
            btn.innerHTML = '<span class="customizer-wishlist-dummy-btn__label">Added to wishlist</span>';
            return;
          }
          btn.disabled = false;
          btn.setAttribute('aria-busy', 'false');
          btn.innerHTML = '<span class="customizer-wishlist-dummy-btn__label">' + defaultLabel + '</span>';
        }

        async function createCustomizerProductAndAddToWishlist(customizer) {
          const created = await createCustomizerProductViaApi(customizer);
          const panel = customizer.querySelector('.upload-image-shopify');
          if (panel) panel.style.display = 'block';
          const refs = await bindWishlistButtonToCreatedProduct(customizer, created.json);
          await waitForWishlistBoundToProduct(customizer, refs.productId, 2500);
          await triggerRealWishlistClick(customizer, refs.productId);
          await waitForWishlistMarked(customizer, refs.productId, 4000);
          if (panel) panel.style.display = 'none';
          return { json: created.json, refs: refs };
        }

        function getWishlistButtonEl(customizer) {
          const panel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          return panel?.querySelector('#uploaded-shopify-image.wf-wishlist-button') ||
            customizer.querySelector('#uploaded-shopify-image.wf-wishlist-button') ||
            null;
        }

        function triggerRealWishlistClick(customizer, expectedProductId) {
          return new Promise(function (resolve) {
            const el = getWishlistButtonEl(customizer);
            if (!el) {
              resolve(false);
              return;
            }
            syncWishlistButton(customizer);
            const want = normalizeShopifyProductId(expectedProductId);
            if (want) {
              applyCreatedProductAttrsToWishlistEl(el, {
                productId: want,
                handle: el.getAttribute('product-handle') || '',
                variantId: el.getAttribute('variant-id') || ''
              });
            }

            function clickTarget() {
              if (want) {
                const boundId = wishlistElProductId(el);
                if (boundId && boundId !== want) {
                  resolve(false);
                  return;
                }
              }
              const inner = el.querySelector('.wf-wishlist, button, a');
              const target = inner && inner !== el ? inner : el;
              try { target.click(); } catch (e) { /* ignore */ }
              resolve(true);
            }

            if (el.querySelector('.wf-wishlist, button, a')) {
              clickTarget();
              return;
            }

            const mo = new MutationObserver(function () {
              if (el.querySelector('.wf-wishlist, button, a')) {
                mo.disconnect();
                clearTimeout(fallback);
                clickTarget();
              }
            });
            mo.observe(el, { childList: true, subtree: true });
            const fallback = setTimeout(function () {
              mo.disconnect();
              clickTarget();
            }, 600);
          });
        }

        async function uploadCustomizerImageToShopify(customizer, uploadBtn, panel) {
          const imageBase = getCustomizerWishlistImageBase64(customizer);
          if (!imageBase) throw new Error('No image data found.');

          const res = await fetch('/apps/customizer/image-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageBase: imageBase,
              filename: 'custom-art-preview.png'
            })
          });

          const json = await res.json().catch(() => null);
          if (!res.ok) throw new Error((json && (json.message || json.error)) || ('Upload failed (HTTP ' + res.status + ')'));
          if (!json || json.ok !== true) throw new Error((json && json.message) || 'Upload failed');

          const imageUrl = json.data && json.data.imageUrl ? String(json.data.imageUrl).trim() : '';
          if (!imageUrl) throw new Error('Image URL not found');

          customizerState.shopifyUploadedImageUrl = imageUrl;
          markUserModifiedSelections(customizer);

          if (uploadBtn) uploadBtn.style.display = 'none';
          if (panel) panel.style.display = 'block';
          syncWishlistButton(customizer);
          await triggerRealWishlistClick(customizer);
        }

        function readActiveMatIdFromDOM(customizer, stateKey) {
          if (!customizer) return '';
          const panelSel = stateKey === 'bottomMat'
            ? '[data-cz-tab-panel="bottommat"]'
            : stateKey === 'liner'
              ? '[data-cz-tab-panel="liner"]'
              : '[data-cz-tab-panel="topmat"]';
          const card = customizer.querySelector(panelSel + ' .mat-card.mat-active[data-mat-id]');
          if (!card || !card.dataset.matId || card.dataset.matId === 'NONE') return '';
          return String(card.dataset.matId).trim();
        }

        function assignMatFromDomCard(customizer, stateKey, matId) {
          const id = String(matId || '').trim().toUpperCase();
          if (!id) return false;
          const sizeInches = stateKey === 'bottomMat'
            ? customizerState.bottomMatSizeInches
            : customizerState.topMatSizeInches;
          if (applyMatSelection(customizer, stateKey, id, sizeInches)) return true;

          const panelSel = stateKey === 'bottomMat'
            ? '[data-cz-tab-panel="bottommat"]'
            : stateKey === 'liner'
              ? '[data-cz-tab-panel="liner"]'
              : '[data-cz-tab-panel="topmat"]';
          const card = customizer.querySelector(
            panelSel + ' .mat-card.mat-active[data-mat-id="' + id + '"]'
          ) || customizer.querySelector(panelSel + ' .mat-card.mat-active[data-mat-id]');
          if (!card) return false;
          const name = (card.querySelector('.mat-card-nm')?.textContent || '').trim();
          const catalogMat = findMatInCatalogs(stateKey, id, name);
          if (catalogMat) {
            assignMatToState(catalogMat, stateKey);
            return true;
          }
          const displayId = String(card.dataset.matId || id).trim();
          const oversizedMat = card.dataset.oversizedMat === '1';
          const printedMats = card.dataset.printedMats === '1';
          const pickFrom = card.dataset.pickFrom !== '0';
          if (stateKey === 'liner') {
            customizerState.liner = {
              id: displayId,
              name: name,
              hex: null,
              type: null,
              img: null,
              frameW: null,
              frameImg: null,
              basePrice: null
            };
          } else {
            customizerState[stateKey] = {
              id: displayId,
              name: name,
              hex: null,
              type: null,
              img: null,
              matW: 1.2,
              oversizedMat: oversizedMat,
              printedMats: printedMats,
              pickFrom: pickFrom
            };
          }
          return true;
        }

        function syncFrameFromDOM(customizer) {
          let frameId = '';
          if (typeof window.__fsGetSelectedFrameId === 'function') {
            frameId = String(window.__fsGetSelectedFrameId() || '').trim();
          }
          if (!frameId) {
            const card = document.querySelector('.fs-card.fs-active[data-fs-id]');
            frameId = card ? String(card.dataset.fsId || '').trim() : '';
          }
          if (!frameId) return;
          if (customizerState.frame && String(customizerState.frame.id).trim().toUpperCase() === frameId.toUpperCase()) {
            return;
          }
          const f = getActiveFrames().find(function (x) {
            return String(x.id).trim().toUpperCase() === frameId.toUpperCase();
          });
          if (f) {
            onFrameSelected(f.id, f.name);
            return;
          }
          const card = document.querySelector('.fs-card.fs-active[data-fs-id]');
          const name = card?.querySelector('.fs-card-nm')?.textContent?.trim() || '';
          onFrameSelected(frameId, name);
        }

        /** Align customizerState with active mat/frame cards when UI and state diverge. */
        function syncCustomizerSelectionsFromDOM(customizer) {
          if (!customizer) return;
          syncAllMatSizesFromUI();

          const treatment = String(customizerState.treatment || '').trim();
          const topId = readActiveMatIdFromDOM(customizer, 'topMat');
          if (topId) assignMatFromDomCard(customizer, 'topMat', topId);

          if (treatment === 'Double Mat') {
            const bottomId = readActiveMatIdFromDOM(customizer, 'bottomMat');
            if (bottomId) assignMatFromDomCard(customizer, 'bottomMat', bottomId);
          }

          const linerId = readActiveMatIdFromDOM(customizer, 'liner');
          if (linerId) assignMatFromDomCard(customizer, 'liner', linerId);

          syncFrameFromDOM(customizer);
        }

        function syncSubstrateTreatmentFromSelects(customizer) {
          if (!customizer) return;
          const substrateSelect = customizer.querySelector('[id^="customizer-substrate-"]');
          const treatmentSelect = customizer.querySelector('[id^="customizer-treatment-"]');
          if (substrateSelect && substrateSelect.value) {
            customizerState.substrate = substrateSelect.value;
          }
          if (treatmentSelect && treatmentSelect.value) {
            customizerState.treatment = treatmentSelect.value;
          }
          const glazingSelect = customizer.querySelector('[id^="customizer-glazing-"]');
          if (glazingSelect && glazingSelect.value && !glazingSelect.disabled && !wrapIsHidden()) {
            const picked = normalizeGlazingChoice(glazingSelect.value);
            if (picked === 'Glass' || picked === 'Plexi') customizerState.glazing = picked;
          }

          function wrapIsHidden() {
            const wrap = customizer.querySelector('[data-glazing-option]');
            return !!(wrap && wrap.hidden);
          }
        }

        function getSelectedCatalogIdFromDOM(customizer, piece) {
          if (!customizer) return '';
          if (piece === 'frame') {
            if (typeof window.__fsGetSelectedFrameId === 'function') {
              const frameApiId = String(window.__fsGetSelectedFrameId() || '').trim();
              if (frameApiId) return normalizeCatalogId(frameApiId);
            }
            const frameCard = customizer.querySelector('.fs-card.fs-active[data-fs-id]') ||
              document.querySelector('.fs-card.fs-active[data-fs-id]');
            if (frameCard && frameCard.dataset.fsId) {
              return normalizeCatalogId(frameCard.dataset.fsId);
            }
            return '';
          }
          if (piece === 'topMat') return normalizeCatalogId(readActiveMatIdFromDOM(customizer, 'topMat'));
          if (piece === 'bottomMat') return normalizeCatalogId(readActiveMatIdFromDOM(customizer, 'bottomMat'));
          if (piece === 'liner') return normalizeCatalogId(readActiveMatIdFromDOM(customizer, 'liner'));
          return '';
        }

        function getCurrentCatalogIdForSpecPiece(customizer, piece) {
          let stateId = '';
          if (piece === 'frame' && customizerState.frame) stateId = customizerState.frame.id;
          else if (piece === 'topMat' && customizerState.topMat) stateId = customizerState.topMat.id;
          else if (piece === 'bottomMat' && customizerState.bottomMat) stateId = customizerState.bottomMat.id;
          else if (piece === 'liner' && customizerState.liner) stateId = customizerState.liner.id;
          stateId = normalizeCatalogId(stateId);
          if (stateId) return stateId;
          return getSelectedCatalogIdFromDOM(customizer, piece);
        }

        /** Sync live UI + state before comparing metafield spec to current selections for pricing. */
        function syncSelectionsForOriginalSpecCheck(customizer) {
          if (!customizer) return;
          syncSubstrateTreatmentFromSelects(customizer);
          const wInput = customizer.querySelector('#cs-width-input');
          const hInput = customizer.querySelector('#cs-height-input');
          if (wInput) {
            const w = parseFloat(wInput.value);
            if (isFinite(w) && w > 0) customizerState.width = w;
          }
          if (hInput) {
            const h = parseFloat(hInput.value);
            if (isFinite(h) && h > 0) customizerState.height = h;
          }
          syncCustomizerSelectionsFromDOM(customizer);
        }

        function getSummaryDisplaySizes(customizer, pricing) {
          let glassText = '—';
          if (isKeepingOriginalSpecifications(customizer) && !getProductImageSizeDimensions(customizer)) {
            const orig = getOriginalProductSizeDisplay(customizer);
            glassText = orig.glassText || '—';
          } else {
            const glass = (pricing && pricing.glass) ? pricing.glass : computeGlassSizeInches();
            glassText = (glass.width > 0 && glass.height > 0)
              ? formatSizeDisplay(glass.width, glass.height)
              : '—';
          }
          const framed = (pricing && pricing.overall)
            ? pricing.overall
            : computeFramedSizeInches(customizer);
          return {
            glassText: glassText,
            overallText: (framed.width > 0 && framed.height > 0)
              ? formatOutsideSizeDisplay(framed.width, framed.height)
              : '—'
          };
        }

        /** Structured pricing rows for tear sheet PDF (with-pricing download). */
        function getTearSheetPricingRows(customizer) {
          const root = getActiveCustomizerRoot(customizer);
          if (!isCustomerLoggedIn(root)) {
            return { available: false, rows: [], total: null, note: null };
          }
          if (!isApplyingOriginalSpec(root)) {
            syncCustomizerSelectionsFromDOM(root);
          }

          const rows = [];
          function addMoneyRow(label, amount, skipZero) {
            const n = parseFloat(amount) || 0;
            if (skipZero && n <= 0) return;
            rows.push({ label: label, value: formatMoneyUSD(n) });
          }

          if (isKeepingOriginalSpecifications(root)) {
            captureBaseRetailArtPrice(root);
            const base = getBaseRetailArtPrice(root);
            rows.push({ label: 'Base art (original specification)', value: formatMoneyUSD(base) });
            return {
              available: true,
              rows: rows,
              total: { label: 'Final product price', value: formatMoneyUSD(base) },
              note: 'No customization charges — your selections match the original product specification.'
            };
          }

          if (shouldShowFormulaPricingReview(root)) {
            const formula = calculateFormulaPricing(root);
            if (isFinite(formula.finalProductPrice) && formula.finalProductPrice > 0) {
              rows.push({
                label: 'Configured product price',
                value: formatMoneyUSDDollars(formula.finalProductPrice)
              });
            }
            addMoneyRow('Size change charge', formula.sizeChangeChargeDetail, true);
            addMoneyRow('Customization change fee', formula.customizationChangeFee, true);
            addMoneyRow('Printed mat fee', formula.printedMatCost, true);
            const total = formula.formulaFinalTotal;
            return {
              available: true,
              rows: rows,
              total: {
                label: 'Final product price',
                value: isFinite(total) && total > 0
                  ? formatMoneyUSDDollars(total)
                  : getAddToOrderPriceDisplay(root, formula)
              },
              note: null
            };
          }

          const pricing = calculatePricing(root);
          rows.push({ label: 'Base art', value: formatMoneyUSD(pricing.baseRetailArt) });
          const sizeChangeFee = (parseFloat(pricing.sizeChangeCharge) || 0) +
            (parseFloat(pricing.customizationChangeFee) || 0);
          addMoneyRow('Size change charge', sizeChangeFee, true);
          addMoneyRow('Frame cost', pricing.frameCost, true);
          if (pricing.matCost > 0) {
            const count = pricing.matChargeCount != null ? pricing.matChargeCount : pricing.matCount;
            rows.push({
              label: 'Mat cost (' + count + '× ' + (pricing.matTier || '') + ')',
              value: formatMoneyUSD(pricing.matCost)
            });
          }
          addMoneyRow('Printed mat fee', pricing.printedMatFee, true);
          if (isCanvasSubstrate()) {
            addMoneyRow('Canvas fit', pricing.canvasFitCharge, true);
            addMoneyRow('Hand finish (brushstroke)', pricing.acrylicBrushstrokeCharge, true);
          } else {
            addMoneyRow('Glass / fit charge', pricing.glassCharge, true);
            addMoneyRow('Plexi charge', pricing.plexiCharge, true);
            addMoneyRow('Foam core (deckled)', pricing.foamCoreFitCharge, true);
            addMoneyRow('Artwork float charge', pricing.artworkFloatCharge, true);
          }
          addMoneyRow('Liner cost', pricing.linerCost, true);

          return {
            available: true,
            rows: rows,
            total: { label: 'Final product price', value: formatMoneyUSD(pricing.retailTotal) },
            note: null
          };
        }

        function buildSummaryPricingBreakdownHtml(customizer, pricing, escapeHtml) {
          const sizes = getSummaryDisplaySizes(customizer, pricing);
          const dimBlock = `
              <div class="spb-row spb-row--dimensions"><span>Current glass size</span><span>${escapeHtml(sizes.glassText)}</span></div>
              <div class="spb-row spb-row--dimensions"><span>Current outside dimensions</span><span>${escapeHtml(sizes.overallText)}</span></div>`;

          if (pricing.keepingOriginalSpec) {
            return `
            <div class="summary-price-breakdown summary-price-breakdown--original-spec" data-summary-pricing>
              ${dimBlock}
              <div class="spb-row spb-base"><span>Base art (original specification)</span><span>${formatMoneyUSD(pricing.baseRetailArt)}</span></div>
              <p class="summary-price-note">No customization charges — your selections match the original product specification.</p>
              <div class="spb-row spb-total"><span>Final product price</span><span>${formatMoneyUSD(pricing.retailTotal)}</span></div>
            </div>`;
          }

          const sizeChangeFee = (parseFloat(pricing.sizeChangeCharge) || 0) +
            (parseFloat(pricing.customizationChangeFee) || 0);
          return `
            <div class="summary-price-breakdown" data-summary-pricing>
              ${dimBlock}
              <div class="spb-row spb-base"><span>Base art</span><span>${formatMoneyUSD(pricing.baseRetailArt)}</span></div>
              ${(sizeChangeFee > 0) ? `<div class="spb-row spb-addon"><span>Size change charge</span><span>${formatMoneyUSD(sizeChangeFee)}</span></div>` : ''}
              ${(pricing.frameCost > 0 || pricing.matCost > 0 || pricing.glassCharge > 0 || pricing.canvasFitCharge > 0) ? `<div class="spb-row"><span>Glass size (W×H)</span><span>${formatSizeDisplay(pricing.glass.width, pricing.glass.height)}</span></div>` : ''}
              ${(pricing.frameCost > 0) ? `<div class="spb-row spb-addon"><span>Frame cost</span><span>${formatMoneyUSD(pricing.frameCost)}</span></div>` : ''}
              ${(pricing.matCost > 0) ? `<div class="spb-row spb-addon"><span>Mat cost (${(pricing.matChargeCount != null ? pricing.matChargeCount : pricing.matCount) + '× ' + (pricing.matTier || '')})</span><span>${formatMoneyUSD(pricing.matCost)}</span></div>` : ''}
              ${(pricing.printedMatFee > 0) ? `<div class="spb-row spb-addon"><span>Printed mat fee</span><span>${formatMoneyUSD(pricing.printedMatFee)}</span></div>` : ''}
              ${(isCanvasSubstrate() && pricing.canvasFitCharge > 0) ? `<div class="spb-row spb-addon"><span>Canvas fit</span><span>${formatMoneyUSD(pricing.canvasFitCharge)}</span></div>` : ''}
              ${(!isCanvasSubstrate() && pricing.glassCharge > 0) ? `<div class="spb-row spb-addon"><span>Glass / fit charge</span><span>${formatMoneyUSD(pricing.glassCharge)}</span></div>` : ''}
              ${(!isCanvasSubstrate() && pricing.plexiCharge > 0) ? `<div class="spb-row spb-addon"><span>Plexi charge</span><span>${formatMoneyUSD(pricing.plexiCharge)}</span></div>` : ''}
              ${(!isCanvasSubstrate() && pricing.foamCoreFitCharge > 0) ? `<div class="spb-row spb-addon"><span>Foam core (deckled)</span><span>${formatMoneyUSD(pricing.foamCoreFitCharge)}</span></div>` : ''}
              ${(isCanvasSubstrate() && pricing.acrylicBrushstrokeCharge > 0) ? `<div class="spb-row spb-addon"><span>Hand finish (brushstroke)</span><span>${formatMoneyUSD(pricing.acrylicBrushstrokeCharge)}</span></div>` : ''}
              ${(!isCanvasSubstrate() && pricing.artworkFloatCharge > 0) ? `<div class="spb-row spb-addon"><span>Artwork float charge</span><span>${formatMoneyUSD(pricing.artworkFloatCharge)}</span></div>` : ''}
              ${(pricing.linerCost > 0) ? `<div class="spb-row spb-addon"><span>Liner cost</span><span>${formatMoneyUSD(pricing.linerCost)}</span></div>` : ''}
              <div class="spb-row spb-total"><span>Final product price</span><span>${formatMoneyUSD(pricing.retailTotal)}</span></div>
            </div>`;
        }

        function formatFormulaPricingStepValue(step, esc) {
          if (step.isText) {
            const text = step.value == null || step.value === '' ? '—' : String(step.value);
            return esc(text);
          }
          if (step.isPercent) {
            return esc(formatFormulaPercent(step.value));
          }
          if (step.isDimension) {
            const n = roundMouldingInches(step.value);
            if (!isFinite(n) || n <= 0) return '—';
            const text = Number.isInteger(n)
              ? String(n)
              : String(n).replace(/\.?0+$/, '');
            return esc(text + '"');
          }
          if (step.isFinalDealer || step.isFormulaGrandTotal || step.isWholeDollar) {
            return esc(formatMoneyUSDDollars(step.value));
          }
          return esc(formatMoneyUSD(step.value));
        }

        function buildFormulaPricingReviewTableHtml(formulaPricing, escapeHtml, opts) {
          if (!formulaPricing || !formulaPricing.steps || !formulaPricing.steps.length) return '';
          const esc = escapeHtml || escapeCustomizerHtml;
          const forProduct = !!(opts && opts.forProductDescription);
          const changedKeys = (opts && opts.changedKeys) || null;
          const tableStyle = forProduct
            ? 'border-collapse:collapse;width:100%;max-width:720px;border:1px solid #ccc;'
            : '';
          const cellStyle = forProduct ? 'border:1px solid #ccc;padding:6px 8px;vertical-align:top;' : '';
          const thStyle = forProduct ? cellStyle + 'background:#f5f5f5;text-align:left;' : '';
          const grandTotalStyle = forProduct ? ' style="' + cellStyle + 'font-weight:bold;"' : '';

          const metaRows = [
            ['Substrate', formulaPricing.substrate ? esc(formulaPricing.substrate) : '—'],
            ['Glass/Plexi', formulaPricing.glazing ? esc(formulaPricing.glazing) : '—'],
            ['Material rate (Retail Art)', formulaPricing.retailArtRate
              ? esc(formatMoneyUSD(formulaPricing.retailArtRate) + '/sq in')
              : '—'],
            ['Frame', esc(formulaPricing.frameLabel || '—')]
          ];
          if (formulaPricing.glassLabel && formulaPricing.glassLabel !== '—') {
            metaRows.push(['Current Glass Size', esc(formulaPricing.glassLabel)]);
          }
          if (formulaPricing.outsideLabel && formulaPricing.outsideLabel !== '—') {
            metaRows.push(['Current Outside Dimensions', esc(formulaPricing.outsideLabel)]);
          }

          const metaHtml = metaRows.map(function (row) {
            const label = row[0];
            const isChanged = changedKeys && changedKeys.has(label);
            if (forProduct) {
              return '<tr><td colspan="2" style="' + cellStyle + '">' + label +
                '</td><td style="' + cellStyle + '">' + row[1] + '</td></tr>';
            }
            const metaClass = 'formula-pricing-review__meta' + (isChanged ? ' formula-pricing-review__meta--changed' : '');
            return '<tr class="' + metaClass + '"><td colspan="2">' +
              formulaPricingLineLabelHtml(label, changedKeys, esc) +
              '</td><td>' + row[1] + '</td></tr>';
          }).join('');

          const stepRows = formulaPricing.steps.map(function (step) {
            if (step.isSectionHeader) {
              if (forProduct) {
                return '<tr><td colspan="3" style="' + cellStyle + 'font-weight:bold;background:#f9f9f9;">' +
                  esc(step.name) + '</td></tr>';
              }
              return '<tr class="formula-pricing-review__section"><td colspan="3">' +
                esc(step.name) + '</td></tr>';
            }
            let rowClass = '';
            if (step.isTotal) rowClass = ' formula-pricing-review__total-row';
            if (step.isMfgTotal) rowClass = ' formula-pricing-review__mfg-total-row';
            if (step.isFinalDealer) rowClass = ' formula-pricing-review__final-dealer-row';
            if (step.isFormulaGrandTotal) rowClass = ' formula-pricing-review__grand-total-row';
            if (changedKeys && changedKeys.has(step.name)) {
              rowClass += ' formula-pricing-review__step--changed';
            }
            const resultCell = formatFormulaPricingStepValue(step, esc);
            const lineLabel = forProduct
              ? esc(step.name)
              : formulaPricingLineLabelHtml(step.name, changedKeys, esc);
            if (forProduct) {
              const rowStyle = step.isFormulaGrandTotal || step.isFinalDealer
                ? grandTotalStyle
                : ' style="' + cellStyle + '"';
              return '<tr>' +
                '<td' + rowStyle + '>' + lineLabel + '</td>' +
                '<td' + rowStyle + '>' + esc(step.formula || '') + '</td>' +
                '<td' + rowStyle + '>' + resultCell + '</td>' +
                '</tr>';
            }
            return '<tr class="formula-pricing-review__step' + rowClass + '">' +
              '<td>' + lineLabel + '</td>' +
              '<td>' + esc(step.formula || '') + '</td>' +
              '<td>' + resultCell + '</td>' +
              '</tr>';
          }).join('');

          const noteText = 'Formula pricing table — all manufacturing lines plus add-on fees. ' +
            'Green ● marks rows affected by your customization.';

          if (forProduct) {
            return (
              '<h3>Formula pricing calculation</h3>' +
              '<p>' + esc(noteText) + '</p>' +
              '<table style="' + tableStyle + '">' +
                '<thead><tr>' +
                  '<th style="' + thStyle + '">Line</th>' +
                  '<th style="' + thStyle + '">Formula</th>' +
                  '<th style="' + thStyle + '">Result</th>' +
                '</tr></thead>' +
                '<tbody>' + metaHtml + stepRows + '</tbody>' +
              '</table>'
            );
          }

          return (
            '<div class="formula-pricing-review formula-pricing-review--open" data-formula-pricing-review>' +
              '<button type="button" class="formula-pricing-review__toggle" data-formula-pricing-toggle aria-expanded="true">' +
                '<span class="formula-pricing-review__toggle-label">Formula pricing calculation</span>' +
                '<span class="formula-pricing-review__chevron" aria-hidden="true">' +
                  '<svg viewBox="0 0 16 16" width="16" height="16" focusable="false">' +
                    '<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="2" ' +
                      'stroke-linecap="round" stroke-linejoin="round"/>' +
                  '</svg>' +
                '</span>' +
              '</button>' +
              '<div class="formula-pricing-review__body">' +
                '<p class="formula-pricing-review__note">' + noteText + '</p>' +
                '<div class="formula-pricing-review__table-wrap">' +
                  '<table class="formula-pricing-review__table">' +
                    '<thead><tr>' +
                      '<th>Line</th><th>Formula</th><th>Result</th>' +
                    '</tr></thead>' +
                    '<tbody>' + metaHtml + stepRows + '</tbody>' +
                  '</table>' +
                '</div>' +
              '</div>' +
            '</div>'
          );
        }

        function applyFormulaPricingReviewOpenState(block, isOpen) {
          if (!block) return;
          block.classList.toggle('formula-pricing-review--open', !!isOpen);
          const btn = block.querySelector('[data-formula-pricing-toggle]');
          if (btn) btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }

        function replaceFormulaPricingReviewBlock(block, html) {
          const wasOpen = block && block.classList.contains('formula-pricing-review--open');
          const parent = block.parentElement;
          block.outerHTML = html;
          const next = parent && parent.querySelector('[data-formula-pricing-review]');
          // Preserve prior open/closed state across re-renders (default HTML is open).
          applyFormulaPricingReviewOpenState(next, !!wasOpen);
          return next;
        }

        function bindFormulaPricingReviewToggle(customizer) {
          if (!customizer || customizer.dataset.formulaPricingToggleBound === '1') return;
          customizer.dataset.formulaPricingToggleBound = '1';
          customizer.addEventListener('click', function (e) {
            const btn = e.target.closest('[data-formula-pricing-toggle]');
            if (!btn) return;
            e.preventDefault();
            const block = btn.closest('[data-formula-pricing-review]');
            if (!block) return;
            const isOpen = !block.classList.contains('formula-pricing-review--open');
            applyFormulaPricingReviewOpenState(block, isOpen);
          });
        }

        /**
         * Fill product-form hidden inputs from formula pricing review values:
         * _Glass Size, _BOM FT, _Framer Cost, _Print Cost.
         */
        function syncProductFormFormulaCosts(customizerRoot, cachedFormulaPricing) {
          const root = customizerRoot || document.querySelector('.customizer');
          let formula = cachedFormulaPricing || null;
          if (!formula && root && typeof calculateFormulaPricing === 'function') {
            try {
              if (shouldShowFormulaPricingReview(root)) {
                formula = calculateFormulaPricing(root);
              }
            } catch (eFormula) {
              formula = null;
            }
          }

          const glassW = formula ? parseFloat(formula.glassWidth) : 0;
          const glassH = formula ? parseFloat(formula.glassHeight) : 0;
          const glassSize = (isFinite(glassW) && glassW > 0 && isFinite(glassH) && glassH > 0)
            ? (formatSizeDisplay(glassW, glassH) + ' in')
            : '';

          const bomFt = formula && isFinite(formula.bomFt) && formula.bomFt > 0
            ? String(roundFormulaMoney(formula.bomFt))
            : '';

          const framerCost = formula && isFinite(formula.mfgPriceAtMargin)
            ? formatMoneyUSD(formula.mfgPriceAtMargin)
            : '';

          const imgW = formula ? parseFloat(formula.width) : 0;
          const imgH = formula ? parseFloat(formula.height) : 0;
          const printCost = (isFinite(imgW) && imgW > 0 && isFinite(imgH) && imgH > 0)
            ? formatMoneyUSD(imgW * imgH * 0.01)
            : '';

          function setFormulaInput(key, value, propName) {
            document.querySelectorAll('input[data-formula-cost="' + key + '"]').forEach(function (input) {
              /*
               * nochange: write defaults until frozen.
               * Frozen after original spec snapshot OR first customer edit — never unfrozen.
               */
              if (input.hasAttribute('nochange') || input.getAttribute('data-nochange') === '1') {
                if (input.dataset.formulaCostFrozen === '1') return;
                if (!value) return;
                if (propName) input.name = propName;
                input.value = value;
                return;
              }
              if (!value) {
                input.value = '';
                return;
              }
              if (propName) input.name = propName;
              input.value = value;
            });
          }

          setFormulaInput('glass-size', glassSize, 'properties[_Glass Size]');
          setFormulaInput('bom-ft', bomFt, 'properties[_BOM FT]');
          setFormulaInput('framer-cost', framerCost, 'properties[_Framer Cost]');
          setFormulaInput('print-cost', printCost, 'properties[_Print Cost]');
        }

        function updateFormulaPricingReviewDisplay(customizerRoot, cachedFormulaPricing) {
          const root = customizerRoot || document.querySelector('.customizer');
          if (!root) return;
          if (!shouldShowFormulaPricingReview(root)) {
            root.querySelectorAll('[data-formula-pricing-review]').forEach(function (el) { el.remove(); });
            syncProductFormFormulaCosts(root, null);
            return;
          }

          const formulaPricing = cachedFormulaPricing || calculateFormulaPricing(root);
          syncProductFormFormulaCosts(root, formulaPricing);
          const changedKeys = getFormulaPricingChangedStepKeys(root);
          const html = buildFormulaPricingReviewTableHtml(formulaPricing, escapeCustomizerHtml, {
            changedKeys: changedKeys
          });
          if (!html) return;

          root.querySelectorAll('.customizer-steps-price').forEach(function (wrap) {
            const block = wrap.querySelector('[data-formula-pricing-review]');
            if (block) {
              replaceFormulaPricingReviewBlock(block, html);
            } else {
              wrap.insertAdjacentHTML('beforeend', html);
            }
          });

          const summaryPricing = root.querySelector('[data-summary-pricing]');
          if (summaryPricing) {
            let block = summaryPricing.parentElement &&
              summaryPricing.parentElement.querySelector('[data-formula-pricing-review]');
            if (!block && summaryPricing.parentElement) {
              block = summaryPricing.nextElementSibling;
              if (block && !block.matches('[data-formula-pricing-review]')) block = null;
            }
            if (block) {
              replaceFormulaPricingReviewBlock(block, html);
            } else if (summaryPricing.parentElement) {
              summaryPricing.insertAdjacentHTML('afterend', html);
            }
          }
        }

        function ensureSummaryCurrentSizesAboveApproval(summaryPanel) {
          if (!summaryPanel) return;
          const approval = summaryPanel.querySelector('.summary-design-approval');
          if (!approval || !approval.parentNode) return;

          let sizes = summaryPanel.querySelector('.customizer-current-sizes--summary');
          if (!sizes) {
            summaryPanel.querySelectorAll('.customizer-current-sizes').forEach(function (el) {
              if (!sizes && !el.closest('[data-dimensions-notes]')) sizes = el;
            });
            if (sizes) sizes.classList.add('customizer-current-sizes--summary');
          }
          if (!sizes) {
            sizes = document.createElement('div');
            sizes.className = 'customizer-current-sizes customizer-current-sizes--summary';
            sizes.setAttribute('data-current-sizes', '');
            sizes.setAttribute('aria-live', 'polite');
            sizes.innerHTML =
              '<p class="customizer-current-sizes__row">' +
                '<strong>Current Glass Size:</strong> ' +
                '<span data-glass-size>—</span>' +
              '</p>' +
              '<p class="customizer-current-sizes__row">' +
                '<strong>Current Outside Dimensions:</strong> ' +
                '<span data-outside-size>—</span>' +
              '</p>';
          }

          if (approval.previousElementSibling !== sizes) {
            approval.parentNode.insertBefore(sizes, approval);
          }
        }

        function updateSummary(customizer) {
          if (!customizer) return;
          if (!isApplyingOriginalSpec(customizer)) {
            syncCustomizerSelectionsFromDOM(customizer);
          }
          collectStepNotesFromDOM(customizer);

          const summaryPanel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          if (!summaryPanel) return;
          const summaryBody = summaryPanel.querySelector('.summary-body');
          if (!summaryBody) return;

          const treatment = String(customizerState.treatment || 'None').trim() || 'None';
          const showMats   = (treatment === 'Single Mat' || treatment === 'Double Mat');
          const showBottom = (treatment === 'Double Mat');
          const sub        = customizerState.substrate || 'None';
          const w          = customizerState.width;
          const h          = customizerState.height;
          const sizeText   = (w && h) ? formatSizeInches(w, h) : '';

          const topMat    = customizerState.topMat;
          const bottomMat = customizerState.bottomMat;
          const frame     = customizerState.frame;
          const liner     = customizerState.liner;
          const art       = customizerState.artworkColor;
          const inst      = customizerState.colorInstruction || '';
          const notes     = customizerState.stepNotes || {};
          const noteStep1 = String(notes[1] || '').trim();
          const noteStep2 = String(notes[2] || '').trim();
          const noteStep3 = String(notes[3] || '').trim();
          const noteStep4 = String(notes[4] || '').trim();

          const escapeHtml = (s) => String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

          /* ── Card 01: Substrate ── */
          const glazingLabel = getGlazingMaterialLabel(customizer);
          const substrateSubtitle = sub + (sizeText ? ' • ' + sizeText : '');
          const substrateDesc     = 'Material Type : ' + sub +
            ' with ' + treatment + ' treatment' +
            (sizeText ? ' (' + sizeText + ')' : '') +
            (glazingLabel !== 'None' ? ' — Glazing: ' + glazingLabel : '');

          /* ── Card 02: Mat ── */
          const topMatSubtitle = (showMats && topMat)
            ? summaryMatSubtitle(topMat, customizerState.topMatSizeInches)
            : 'None';
          const topMatSizeLabel = formatMatSizeInchLabel(customizerState.topMatSizeInches);
          const topMatDesc = (showMats && topMat)
            ? 'Mat Selection : ' + topMat.id + (topMat.name ? ' — ' + topMat.name : '') +
              (topMat.type ? ' (' + topMat.type + ')' : '') +
              (topMatSizeLabel ? ' — ' + topMatSizeLabel : '')
            : 'Mat Selection : No top mat included - direct artwork presentation';

          const bottomMatSubtitle = (showBottom && bottomMat)
            ? summaryMatSubtitle(bottomMat, customizerState.bottomMatSizeInches)
            : 'None';
          const bottomMatSizeLabel = formatMatSizeInchLabel(customizerState.bottomMatSizeInches);
          const bottomMatDesc = (showBottom && bottomMat)
            ? 'Base Mat : ' + bottomMat.id + (bottomMat.name ? ' – ' + bottomMat.name : '') +
              (bottomMatSizeLabel ? ' — ' + bottomMatSizeLabel : '') +
              ' – Provides foundation and archival protection'
            : (showBottom
                ? 'No bottom mat selected'
                : 'Bottom mat requires Double Mat treatment');

          /* ── Card 03: Frame & Liner ── */
          const frameSubtitle = frame ? frame.id : 'None';
          const frameDesc     = frame
            ? 'Frame Model : ' + frame.id + (frame.name ? ' – ' + frame.name : '')
            : 'No frame selected';

          const linerSubtitle = liner ? liner.id : 'None';
          const linerDesc     = liner
            ? 'Liner Model : ' + liner.id + (liner.name ? ' – ' + liner.name : '')
            : 'No liner selected';

          /* ── Card 04: Other (color or monogram text) ── */
          let artSubtitle = art ? (art.name || 'Custom Color') : 'Ready for Display';
          let artDesc     = art
            ? ('Artwork Color : ' + (art.name || art.id) + (art.hex ? ' (' + art.hex + ')' : ''))
            : 'Your custom frame is fully assembled and ready to hang on any wall';
          if (isMonogramTypographyProduct()) {
            artSubtitle = customizerState.customTextEnabled ? 'Custom Text' : 'None';
            artDesc = customizerState.customerNotes.trim()
              ? customizerState.customerNotes.trim()
              : 'No custom text provided';
          }

          const cards = [];
          cards.push(`
            <div class="summary-card-body">
              <h3 class="summary-section-title">Substrate &amp; Treatment</h3>
              <p class="summary-card-subtitle">${escapeHtml(substrateSubtitle)}</p>
              <p class="summary-card-desc">${escapeHtml(substrateDesc)}</p>
              ${glazingLabel !== 'None' ? `<p class="summary-card-desc"><strong>Glazing:</strong> ${escapeHtml(glazingLabel)}</p>` : ''}
              ${noteStep1 ? `<p class="summary-card-desc"><strong>Notes:</strong> ${escapeHtml(noteStep1)}</p>` : ''}
            </div>`);
          cards.push(`
            <div class="summary-card-body">
              <h3 class="summary-section-title">Mat</h3>
              <div class="summary-card-body--cols">
                <div class="summary-card-col">
                  <h4 class="summary-col-title">Top Mat :</h4>
                  <p class="summary-card-subtitle">${escapeHtml(topMatSubtitle)}</p>
                  <p class="summary-card-desc">${escapeHtml(topMatDesc)}</p>
                </div>
                <div class="summary-card-col">
                  <h4 class="summary-col-title">Bottom Mat</h4>
                  <p class="summary-card-subtitle">${escapeHtml(bottomMatSubtitle)}</p>
                  <p class="summary-card-desc">${escapeHtml(bottomMatDesc)}</p>
                </div>
              </div>
              ${noteStep2 ? `<p class="summary-card-desc"><strong>Notes:</strong> ${escapeHtml(noteStep2)}</p>` : ''}
            </div>`);
          cards.push(`
            <div class="summary-card-body">
              <h3 class="summary-section-title">Frame and Liner</h3>
              <div class="summary-card-body--cols">
                <div class="summary-card-col">
                  <h4 class="summary-col-title">Frame</h4>
                  <p class="summary-card-subtitle">${escapeHtml(frameSubtitle)}</p>
                  <p class="summary-card-desc">${escapeHtml(frameDesc)}</p>
                </div>
                <div class="summary-card-col">
                  <h4 class="summary-col-title">Liner</h4>
                  <p class="summary-card-subtitle">${escapeHtml(linerSubtitle)}</p>
                  <p class="summary-card-desc">${escapeHtml(linerDesc)}</p>
                </div>
              </div>
              ${noteStep3 ? `<p class="summary-card-desc"><strong>Notes:</strong> ${escapeHtml(noteStep3)}</p>` : ''}
            </div>`);
          if (isOtherStepProduct()) {
            const instBlock = (!isMonogramTypographyProduct() && inst)
              ? `<p class="summary-card-desc"><strong>Instructions:</strong> ${escapeHtml(inst)}</p>`
              : '';
            cards.push(`
              <div class="summary-card-body">
                <h3 class="summary-section-title">Other</h3>
                <p class="summary-card-subtitle">${escapeHtml(artSubtitle)}</p>
                <p class="summary-card-desc">${escapeHtml(artDesc)}</p>
                ${instBlock}
                ${noteStep4 ? `<p class="summary-card-desc"><strong>Notes:</strong> ${escapeHtml(noteStep4)}</p>` : ''}
              </div>`);
          }

          if (customizerState.sideMark) {
            cards.push(`
              <div class="summary-card-body">
                <h3 class="summary-section-title">Side Mark or Additional Notes</h3>
                <p class="summary-card-subtitle">Enabled</p>
                <p class="summary-card-desc">${customizerState.sideMarkText
                  ? escapeHtml(customizerState.sideMarkText)
                  : 'No side mark or additional notes provided'}</p>
              </div>`);
          }

          updateCustomizerMainPrice(customizer);
          ensureSummaryCurrentSizesAboveApproval(summaryPanel);
          updateCurrentSizesDisplay(customizer);

          summaryBody.innerHTML = cards.map((body, i) => `
            <div class="summary-card">
              <div class="summary-num">${String(i + 1).padStart(2, '0')}</div>
              ${body}
            </div>`).join('');

          updateFormulaPricingReviewDisplay(customizer);

          syncAddToOrderButtonState(
            customizer.querySelector('.summary-add-cart-btn'),
            customizer,
            shouldShowFormulaPricingReview(customizer) ? calculateFormulaPricing(customizer) : null
          );
          syncWishlistButton(customizer);
        }

        if (typeof window !== 'undefined') {
          window.updateSummary = updateSummary;
          window.syncCustomizerSelectionsFromDOM = syncCustomizerSelectionsFromDOM;
          window.getSummaryStep = getSummaryStep;
          window.calculateFormulaPricing = calculateFormulaPricing;
          window.updateFormulaPricingReviewDisplay = updateFormulaPricingReviewDisplay;
          window.syncProductFormFormulaCosts = syncProductFormFormulaCosts;
          window.shouldShowFormulaPricingReview = shouldShowFormulaPricingReview;
          window.getTearSheetPricingRows = getTearSheetPricingRows;
        }

        function getCustomizedImageDataUrl(customizer) {
          let dataUrl = null;

          const fsCanvas = document.getElementById('fs-canvas');
          if (fsCanvas && fsCanvas.tagName === 'CANVAS') {
            try { dataUrl = fsCanvas.toDataURL('image/png'); } catch (e) { dataUrl = null; }
          }

          const previewCanvas = customizer.querySelector('.customizer-preview-canvas') || document.querySelector('.customizer-preview-canvas');
          if (previewCanvas && previewCanvas.tagName === 'CANVAS') {
            try { dataUrl = previewCanvas.toDataURL('image/png'); } catch (e) { dataUrl = null; }
          }

          if (!dataUrl && typeof window.__fsGetCanvasDataURL === 'function') {
            try { dataUrl = window.__fsGetCanvasDataURL(); } catch (e) { dataUrl = null; }
          }

          return dataUrl;
        }

        function downloadCustomizedImage(customizer) {
          const dataUrl = getCustomizedImageDataUrl(customizer);

          if (!dataUrl) {
            alert('No customized canvas available to download yet.');
            return;
          }

          const a = document.createElement('a');
          a.href     = dataUrl;
          a.download = 'customized-artwork.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }

        function getSummaryShareText(customizer) {
          const treatment = customizerState.treatment || 'None';
          const sizeText = (customizerState.width && customizerState.height)
            ? formatSizeInches(customizerState.width, customizerState.height).replace(' × ', ' x ')
            : 'None';

          const glass = computeGlassSizeInches();
          const framed = computeFramedSizeInches(customizer);
          const glassText = (glass.width && glass.height)
            ? formatSizeInches(glass.width, glass.height).replace(' × ', ' x ')
            : 'None';
          const overallText = (framed.width && framed.height)
            ? formatOutsideSizeDisplay(framed.width, framed.height)
            : 'None';

          const productUrl = customizer ? buildEditCustomizerShareUrl(customizer) : window.location.href;
          const imageUrl = String(customizerState.shopifyUploadedImageUrl || '').trim();

          return [
            'Configuration Summary',
            'Image URL: ' + (imageUrl || '—'),
            'Product Url: ' + (productUrl || window.location.href),
            'Substrate: ' + (customizerState.substrate || 'None'),
            'Treatment: ' + treatment,
            'Glazing: ' + getGlazingMaterialLabel(customizer),
            'Image Size: ' + sizeText,
            'Glass Size: ' + glassText,
            'Overall Size: ' + overallText,
            'Top Mat: ' + (customizerState.topMat ? customizerState.topMat.id + ' - ' + customizerState.topMat.name : 'None'),
            'Bottom Mat: ' + (customizerState.bottomMat ? customizerState.bottomMat.id + ' - ' + customizerState.bottomMat.name : 'None'),
            'Frame: ' + (customizerState.frame ? customizerState.frame.id + ' - ' + customizerState.frame.name : 'None'),
            'Liner: ' + (customizerState.liner ? customizerState.liner.id + ' - ' + customizerState.liner.name : 'None'),
            (isMonogramTypographyProduct()
              ? ('Custom Text: ' + (customizerState.customerNotes || 'None'))
              : ('Artwork Color Instructions: ' + (customizerState.colorInstruction || 'None')))
          ].join('\n');
        }

        function dataUrlToFile(dataUrl, filename) {
          const parts = dataUrl.split(',');
          const match = parts[0].match(/:(.*?);/);
          const mime = match ? match[1] : 'image/png';
          const binary = atob(parts[1] || '');
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          return new File([bytes], filename, { type: mime });
        }

        async function uploadCustomizerImageForShare(customizer) {
          const existing = String(customizerState.shopifyUploadedImageUrl || '').trim();
          if (existing) return existing;
          const imageBase = getCustomizerWishlistImageBase64(customizer);
          if (!imageBase) return '';
          const res = await fetch('/apps/customizer/image-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageBase: imageBase,
              filename: 'custom-art-preview.png'
            })
          });
          const json = await res.json().catch(() => null);
          if (!res.ok || !json || json.ok !== true) return '';
          const imageUrl = json.data && json.data.imageUrl ? String(json.data.imageUrl).trim() : '';
          if (imageUrl) customizerState.shopifyUploadedImageUrl = imageUrl;
          return imageUrl;
        }

        function buildShareConfigurationSummary(customizer, shareUrl, imageUrl) {
          const root = getActiveCustomizerRoot(customizer);
          const productUrl = shareUrl || window.location.href;
          const substrate = String(customizerState.substrate || '—').trim();
          const treatment = String(customizerState.treatment || '—').trim();
          const w = roundSizeInches(customizerState.width);
          const h = roundSizeInches(customizerState.height);
          const sizeText = (w > 0 && h > 0) ? (w + ' x ' + h + ' in') : '—';
          const glass = computeGlassSizeInches();
          const framed = computeFramedSizeInches(root);

          const topMat = customizerState.topMat
            ? (customizerState.topMat.id + (customizerState.topMat.name ? ' - ' + customizerState.topMat.name : ''))
            : 'None';
          const bottomMat = customizerState.bottomMat
            ? (customizerState.bottomMat.id + (customizerState.bottomMat.name ? ' - ' + customizerState.bottomMat.name : ''))
            : 'None';
          const frame = customizerState.frame
            ? (customizerState.frame.id + (customizerState.frame.name ? ' - ' + customizerState.frame.name : ''))
            : 'None';
          const liner = customizerState.liner
            ? (customizerState.liner.id + (customizerState.liner.name ? ' - ' + customizerState.liner.name : ''))
            : 'None';

          const instructions = String(customizerState.colorInstruction || '').trim() || 'None';

          const glassText = (glass.width > 0 && glass.height > 0)
            ? (roundSizeInches(glass.width) + ' x ' + roundSizeInches(glass.height) + ' in')
            : '—';
          const overallText = (framed.width > 0 && framed.height > 0)
            ? (Math.round(framed.width) + ' x ' + Math.round(framed.height) + ' in')
            : '—';

          const title =
            root?.closest('.image-customizer-popup')?.querySelector('.customizer-steps-title')?.textContent?.trim() ||
            document.title ||
            'Customized Artwork';

          return [
            'Configuration Summary',
            'Title: ' + title,
            'Image URL: ' + (imageUrl || '—'),
            'Product Url: ' + productUrl,
            'Substrate: ' + substrate,
            'Treatment: ' + treatment,
            'Glazing: ' + getGlazingMaterialLabel(customizer),
            'Image Size: ' + sizeText,
            'Glass Size: ' + glassText,
            'Overall Size: ' + overallText,
            'Top Mat: ' + topMat,
            'Bottom Mat: ' + bottomMat,
            'Frame: ' + frame,
            'Liner: ' + liner,
            'Artwork Color Instructions: ' + instructions
          ].join('\n');
        }

        async function shareCustomizedImage(customizer, channel) {
          const dataUrl = getCustomizedImageDataUrl(customizer);
          if (!dataUrl) {
            alert('No customized canvas available to share yet.');
            return;
          }

          const summaryText = getSummaryShareText(customizer);
          const shareUrl = buildEditCustomizerShareUrl(customizer);
          const pageUrl = shareUrl || window.location.href;
          const shareTitle = 'Customized Artwork';

          // For email sharing we always want a deterministic body (with product + image URLs),
          // not the platform-native file share flow.
          if (channel === 'gmail') {
            let imageUrl = '';
            try {
              imageUrl = await uploadCustomizerImageForShare(customizer);
            } catch (eUpload) {
              imageUrl = '';
            }
            const body = buildShareConfigurationSummary(customizer, pageUrl, imageUrl);
            const gmailUrl =
              'https://mail.google.com/mail/?view=cm&fs=1' +
              '&su=' + encodeURIComponent(shareTitle) +
              '&body=' + encodeURIComponent(body);
            window.open(gmailUrl, '_blank', 'noopener,noreferrer');
            return;
          }

          try {
            const file = dataUrlToFile(dataUrl, 'customized-artwork.png');
            if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
              await navigator.share({
                title: shareTitle,
                text: channel === 'gmail' ? summaryText : shareTitle,
                files: [file]
              });
              return;
            }
          } catch (e) {
            /* Fall back to platform share URLs below. */
          }

          if (channel === 'pinterest') {
            const pinUrl =
              'https://www.pinterest.com/pin/create/button/?url=' + encodeURIComponent(pageUrl) +
              '&media=' + encodeURIComponent(dataUrl) +
              '&description=' + encodeURIComponent(shareTitle);
            window.open(pinUrl, '_blank', 'noopener,noreferrer');
            return;
          }

          const fbUrl = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(pageUrl);
          window.open(fbUrl, '_blank', 'noopener,noreferrer');
        }

        function initStep8(customizer) {
          const panel =
            customizer.querySelector('.step-panel[data-role="summary"]') ||
            customizer.querySelector(`.step-panel[data-step="${getSummaryStep()}"]`);
          if (!panel) return;
          const contentCol = panel.querySelector('.customizer-content-col');
          if (!contentCol) return;

          /* ── Side Mark wiring (lives in the left image column) ── */
          const sideCb    = panel.querySelector('#side-mark-cb');
          const sideInput = panel.querySelector('#side-mark-input');
          if (sideCb && sideInput) {
            sideCb.checked = !!customizerState.sideMark;
            sideInput.value = customizerState.sideMarkText || '';
            sideInput.disabled = !sideCb.checked;
            sideInput.closest('.side-mark-wrap')?.classList.toggle('side-mark-wrap--active', sideCb.checked);

            sideCb.addEventListener('change', () => {
              customizerState.sideMark = sideCb.checked;
              sideInput.disabled = !sideCb.checked;
              sideInput.closest('.side-mark-wrap')?.classList.toggle('side-mark-wrap--active', sideCb.checked);
              if (sideCb.checked) sideInput.focus();
              else customizerState.sideMarkText = '';
              if (!sideCb.checked) sideInput.value = '';
              // Side mark is free-text only — refresh summary cards, never treat as a price change.
              if (getCurrentStep(customizer) === getSummaryStep()) {
                updateSummary(customizer);
              }
            });

            sideInput.addEventListener('input', () => {
              customizerState.sideMarkText = sideInput.value.slice(0, 250);
              if (getCurrentStep(customizer) === getSummaryStep()) {
                updateSummary(customizer);
              }
            });
          }

          updateSummary(customizer);
          updateCurrentSizesDisplay(customizer);

          const approvalCb = contentCol.querySelector('#summary-design-approval-cb');
          const addCartBtn = contentCol.querySelector('.summary-add-cart-btn');

          function syncDesignApprovalUi() {
            syncAddToOrderButtonState(addCartBtn, customizer);
          }

          if (approvalCb && approvalCb.dataset.approvalBound !== '1') {
            approvalCb.dataset.approvalBound = '1';
            approvalCb.checked = !!customizerState.designApproved;
            syncDesignApprovalUi();
            approvalCb.addEventListener('change', () => {
              customizerState.designApproved = approvalCb.checked;
              syncDesignApprovalUi();
            });
          } else if (approvalCb) {
            approvalCb.checked = !!customizerState.designApproved;
            syncDesignApprovalUi();
          }

          contentCol.querySelector('.summary-download-btn')?.addEventListener('click', () => {
            downloadCustomizedImage(customizer);
          });

          const copyLinkBtn = contentCol.querySelector('.summary-copy-link-btn');
          if (copyLinkBtn && copyLinkBtn.dataset.copyLinkBound !== '1') {
            copyLinkBtn.dataset.copyLinkBound = '1';
            if (!copyLinkBtn.dataset.defaultLabel) {
              const lbl = copyLinkBtn.querySelector('.summary-copy-link-btn__label');
              copyLinkBtn.dataset.defaultLabel = lbl ? lbl.textContent.trim() : 'Copy Link';
            }
            copyLinkBtn.addEventListener('click', function () {
              copyEditCustomizerLink(customizer, copyLinkBtn);
            });
          }

          /* Tear Sheet button is wired by image-customizer-patch.js (menu: with / without pricing) */

          /* ── Share trigger (single button → hover/click reveals icons) ── */
          const shareWrap = contentCol.querySelector('.summary-share-actions');
          if (shareWrap && shareWrap.dataset.shareBound !== '1') {
            shareWrap.dataset.shareBound = '1';
            const trigger = shareWrap.querySelector('.summary-share-trigger');
            let closeTimer = null;
            const open  = () => {
              if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
              shareWrap.classList.add('summary-share-actions--open');
              trigger?.setAttribute('aria-expanded', 'true');
            };
            const close = () => {
              if (closeTimer) clearTimeout(closeTimer);
              closeTimer = setTimeout(() => {
                shareWrap.classList.remove('summary-share-actions--open');
                trigger?.setAttribute('aria-expanded', 'false');
              }, 220);
            };
            shareWrap.addEventListener('mouseenter', open);
            shareWrap.addEventListener('mouseleave', close);
            trigger?.addEventListener('focus', open);
            trigger?.addEventListener('click', () => {
              shareWrap.classList.contains('summary-share-actions--open') ? close() : open();
            });

            shareWrap.querySelectorAll('.summary-share-icon').forEach(btn => {
              btn.addEventListener('click', (e) => {
                e.stopPropagation();
                shareCustomizedImage(customizer, btn.dataset.shareChannel || 'facebook');
              });
            });

            document.addEventListener('click', (e) => {
              if (!shareWrap.contains(e.target)) close();
            });
          }

          if (addCartBtn && addCartBtn.dataset.cartBound !== '1') {
            addCartBtn.dataset.cartBound = '1';
            syncAddToOrderButtonState(addCartBtn, customizer);
            addCartBtn.addEventListener('click', async () => {
              if (!isCustomerLoggedIn(customizer)) return;
              if (!customizerState.designApproved) {
                showCustomizerErrorModal('Please approve your custom design before adding to order.');
                approvalCb?.focus();
                return;
              }
              const validationError = getAddToOrderValidationError(customizer);
              if (validationError) {
                showCustomizerErrorModal(validationError);
                return;
              }
              if (addCartBtn.dataset.orderSubmitting === '1') return;
              addCartBtn.dataset.orderSubmitting = '1';
              setAddToOrderButtonLoading(addCartBtn, true);
              try {
                const result = await createCustomizerOrderProduct(customizer);
                window.location.assign(result.cartUrl || '/cart');
              } catch (err) {
                console.error(err);
                showCustomizerErrorModal(err && err.message ? err.message : 'Could not add custom product to order.');
                setAddToOrderButtonLoading(addCartBtn, false);
                addCartBtn.dataset.orderSubmitting = '0';
              }
            });
          }

          // Upload to Shopify button (must run after summary panel exists)
          const uploadBtn = panel.querySelector('#upload-to-shopify-btn');
          const wishlistPanel = panel.querySelector('.upload-image-shopify');
          if (wishlistPanel) wishlistPanel.style.display = 'none';
          if (uploadBtn) uploadBtn.style.display = '';
          if (uploadBtn && customizerState.wishlistAdded) {
            uploadBtn.dataset.wishlistAdded = '1';
            uploadBtn.dataset.wishlistProductId = String(customizerState.wishlistedProductId || '');
            setCustomizerWishlistButtonState(uploadBtn, 'added');
          }

          if (uploadBtn && !uploadBtn.dataset.uploadBound) {
            uploadBtn.dataset.uploadBound = '1';
            uploadBtn.addEventListener('click', async function () {
              if (!isCustomerLoggedIn(customizer)) {
                const loginUrl = getCustomizerDataset(customizer, 'loginUrl') || '/account/login';
                window.location.assign(loginUrl);
                return;
              }
              const validationError = getAddToOrderValidationError(customizer);
              if (validationError) {
                showCustomizerErrorModal(validationError);
                return;
              }
              if (uploadBtn.dataset.wishlistAdded === '1' || uploadBtn.dataset.wishlistSubmitting === '1') return;
              uploadBtn.dataset.wishlistSubmitting = '1';
              setCustomizerWishlistButtonState(uploadBtn, 'loading');
              try {
                const result = await createCustomizerProductAndAddToWishlist(customizer);
                const newId = result && result.refs ? String(result.refs.productId || '') : '';
                uploadBtn.dataset.wishlistProductId = newId;
                uploadBtn.dataset.wishlistAdded = '1';
                customizerState.wishlistAdded = true;
                customizerState.wishlistedProductId = newId;
                setCustomizerWishlistButtonState(uploadBtn, 'added');
              } catch (e) {
                console.error(e);
                showCustomizerErrorModal(e && e.message ? e.message : 'Could not add custom product to wishlist.');
                uploadBtn.dataset.wishlistSubmitting = '0';
                setCustomizerWishlistButtonState(uploadBtn, 'idle');
              }
            });
          }

          // Wishlist Guru: keep button fresh even if widget caches attrs.
          bindWishlistGuruFreshData(customizer);
        }

        /* =========================================================
          TEAR SHEET — handled by image-customizer-patch.js
          (kept here only as a fallback; primary path is the patch's
          generateTearSheet() which downloads a PDF directly.)
        ========================================================= */
        function openTearSheet(customizer, includePricing) {
          if (typeof window !== 'undefined' && typeof window.generateTearSheet === 'function') {
            window.generateTearSheet(!!includePricing);
            return;
          }
          const dataUrl = getCustomizedImageDataUrl(customizer);
          const frame = customizerState.frame || null;
          const treatment = customizerState.treatment || 'None';
          const sub = customizerState.substrate || 'None';
          const sizeText = (customizerState.width && customizerState.height)
            ? formatSizeInches(customizerState.width, customizerState.height)
            : 'N/A';
          const topMat    = customizerState.topMat;
          const bottomMat = customizerState.bottomMat;
          const liner     = customizerState.liner;
          const art       = customizerState.artworkColor;
          const inst      = customizerState.colorInstruction || '';

          const esc = (s) => String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

          const fmt = (entity, fallback) => entity
            ? entity.id + (entity.name ? ' — ' + entity.name : '')
            : (fallback || 'None');

          const productTitle =
            customizer.closest('.image-customizer-popup')
              ?.querySelector('.customizer-steps-title')?.textContent?.trim() ||
            document.title || 'Customized Artwork';

          const html = `<!doctype html>
        <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>Tear Sheet — ${esc(productTitle)}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              font-family: 'DM Sans', system-ui, -apple-system, Arial, sans-serif;
              color: #232323;
              margin: 0;
              padding: 28px 32px;
              background: #fff;
            }
            .ts-header {
              display: flex; justify-content: space-between; align-items: flex-end;
              border-bottom: 2px solid #232323; padding-bottom: 12px; margin-bottom: 24px;
            }
            .ts-title  { font-size: 22px; font-weight: 600; margin: 0; }
            .ts-meta   { font-size: 12px; color: #666; }
            .ts-grid   { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
            .ts-col    { display: flex; flex-direction: column; gap: 16px; }
            .ts-img-wrap {
              border: 1px solid #e0e0e0; padding: 12px; background: #fafafa;
              display: flex; align-items: center; justify-content: center;
              min-height: 280px;
            }
            .ts-img-wrap img { max-width: 100%; max-height: 420px; height: auto; display: block; }
            .ts-img-caption { font-size: 11px; color: #666; text-align: center; margin: 4px 0 0; }
            .ts-card {
              display: flex; gap: 14px; padding: 14px;
              border: 1px solid rgba(70, 69, 67, 0.20); page-break-inside: avoid;
            }
            .ts-card-num {
              flex: 0 0 36px; height: 36px; border-radius: 18px; border: 1px solid #8a8a8a;
              display: flex; align-items: center; justify-content: center;
              font-size: 13px; font-weight: 500; color: #202020;
            }
            .ts-card-body { flex: 1; min-width: 0; }
            .ts-card-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
            .ts-card h3 { font-size: 14px; font-weight: 500; margin: 0 0 4px; color: #232323; }
            .ts-card .sub { font-size: 13px; color: #404040; margin: 0 0 4px; word-break: break-word; }
            .ts-card .desc { font-size: 12px; color: #808080; margin: 0; word-break: break-word; }
            .ts-frame-block {
              border: 1px solid #e0e0e0; padding: 12px; background: #fafafa;
            }
            .ts-frame-block h4 {
              font-size: 12px; font-weight: 600; color: #555; margin: 0 0 8px;
              text-transform: uppercase; letter-spacing: 0.06em;
            }
            .ts-frame-imgs { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
            .ts-frame-imgs figure { margin: 0; text-align: center; }
            .ts-frame-imgs img {
              max-width: 100%; max-height: 180px; height: auto; object-fit: contain;
              border: 1px solid #e8e8e8; background: #fff; padding: 6px;
            }
            .ts-frame-imgs figcaption { font-size: 10px; color: #666; margin-top: 4px; }
            .ts-actions {
              margin-top: 24px; display: flex; gap: 12px; justify-content: flex-end;
              padding-top: 16px; border-top: 1px solid #eee;
            }
            .ts-actions button {
              padding: 10px 22px; border: 1px solid #232323; background: #fff;
              cursor: pointer; font-family: inherit; font-size: 13px;
            }
            .ts-actions button.primary { background: #dfce50; border-color: #dfce50; }
            @media print {
              body { padding: 12px; }
              .ts-actions { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="ts-header">
            <div>
              <h1 class="ts-title">${esc(productTitle)}</h1>
              <div class="ts-meta">Tear Sheet · ${esc(new Date().toLocaleDateString())}</div>
            </div>
            <div class="ts-meta"><strong>Size:</strong> ${esc(sizeText)}</div>
          </div>

          <div class="ts-grid">
            <!-- LEFT: customized artwork preview -->
            <div class="ts-col">
              <div class="ts-img-wrap">
                ${dataUrl
                  ? `<img src="${dataUrl}" alt="Customized artwork preview" />`
                  : `<span style="color:#999;font-size:13px;">No preview available</span>`}
              </div>
              <p class="ts-img-caption">Customized Artwork Preview</p>
            </div>

            <!-- RIGHT: summary + frame images -->
            <div class="ts-col">
              <div class="ts-card">
                <div class="ts-card-num">01</div>
                <div class="ts-card-body">
                  <h3>Substrate &amp; Treatment</h3>
                  <p class="sub">${esc(sub)} · ${esc(sizeText)}</p>
                  <p class="desc">Material Type : ${esc(sub)} with ${esc(treatment)} treatment</p>
                </div>
              </div>

              <div class="ts-card">
                <div class="ts-card-num">02</div>
                <div class="ts-card-body">
                  <h3>Mat</h3>
                  <div class="ts-card-cols">
                    <div>
                      <h4>Top Mat :</h4>
                      <p class="sub">${esc(topMat ? topMat.id : 'None')}</p>
                      <p class="desc">${esc(topMat ? (topMat.name || '') + (topMat.type ? ' (' + topMat.type + ')' : '') : 'No top mat included')}</p>
                    </div>
                    <div>
                      <h4>Bottom Mat</h4>
                      <p class="sub">${esc(bottomMat ? bottomMat.id : 'None')}</p>
                      <p class="desc">${esc(bottomMat ? (bottomMat.name || '') : (treatment === 'Double Mat' ? 'No bottom mat selected' : 'Requires Double Mat treatment'))}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ts-card">
                <div class="ts-card-num">03</div>
                <div class="ts-card-body">
                  <h3>Frame and Liner</h3>
                  <div class="ts-card-cols">
                    <div>
                      <h4>Frame</h4>
                      <p class="sub">${esc(frame ? frame.id : 'None')}</p>
                      <p class="desc">${esc(frame ? (frame.name || '') + (frame.size ? ' · ' + frame.size : '') : 'No frame selected')}</p>
                    </div>
                    <div>
                      <h4>Liner</h4>
                      <p class="sub">${esc(liner ? liner.id : 'None')}</p>
                      <p class="desc">${esc(liner ? (liner.name || '') : 'No liner selected')}</p>
                    </div>
                  </div>
                </div>
              </div>

              ${isOtherStepProduct() ? `
              <div class="ts-card">
                <div class="ts-card-num">04</div>
                <div class="ts-card-body">
                  <h3>Other</h3>
                  <p class="sub">${esc(isMonogramTypographyProduct()
                    ? (customizerState.customTextEnabled ? 'Custom Text' : 'None')
                    : (art ? (art.name || 'Custom') : 'Ready for Display'))}</p>
                  ${isMonogramTypographyProduct()
                    ? (customizerState.customerNotes.trim()
                      ? `<p class="desc">${esc(customizerState.customerNotes)}</p>`
                      : '<p class="desc">No custom text provided</p>')
                    : (inst ? `<p class="desc"><strong>Instructions:</strong> ${esc(inst)}</p>` : '')}
                </div>
              </div>` : ''}

              ${customizerState.sideMark ? `
              <div class="ts-card">
                <div class="ts-card-num">${isOtherStepProduct() ? '05' : '04'}</div>
                <div class="ts-card-body">
                  <h3>Side Mark or Additional Notes</h3>
                  <p class="sub">Enabled</p>
                  <p class="desc">${customizerState.sideMarkText ? esc(customizerState.sideMarkText) : 'No side mark or additional notes provided'}</p>
                </div>
              </div>` : ''}

              ${frame && (frame.img || frame.dim || frame.thumb) ? `
              <div class="ts-frame-block">
                <h4>Selected Frame Detail</h4>
                <div class="ts-frame-imgs">
                  ${frame.img || frame.thumb
                    ? `<figure>
                        <img src="${esc(frame.img || frame.thumb)}" alt="Frame ${esc(frame.id)}" />
                        <figcaption>${esc(frame.id)} — ${esc(frame.name || '')}</figcaption>
                      </figure>`
                    : ''}
                  ${frame.dim
                    ? `<figure>
                        <img src="${esc(frame.dim)}" alt="Frame dimensions ${esc(frame.id)}" />
                        <figcaption>Frame Dimensions</figcaption>
                      </figure>`
                    : ''}
                </div>
              </div>` : ''}
            </div>
          </div>

          <div class="ts-actions">
            <button type="button" onclick="window.close()">Close</button>
            <button type="button" class="primary" onclick="window.print()">Print Tear Sheet</button>
          </div>
        </body>
        </html>`;

          const win = window.open('', '_blank', 'width=900,height=1100');
          if (!win) {
            alert('Please allow pop-ups to view the tear sheet.');
            return;
          }
          win.document.open();
          win.document.write(html);
          win.document.close();
        }

        /* =========================================================
          CUSTOMIZER OPEN LOADER
        ========================================================= */
        function injectCustomizerLoaderStyles() {
          const style = document.createElement('style');
          style.id = 'customizer-loader-styles';
          style.textContent = `
            .customizer-open-loader {
              position: fixed; inset: 0; z-index: 99998;
              display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px;
              background: rgba(255,255,255,0.20);
              backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
              opacity: 0; transition: opacity 0.22s ease; pointer-events: none;
            }
            .customizer-open-loader.loader-visible { opacity: 1; pointer-events: all; }
            .customizer-open-loader__spinner {
              width: 46px; height: 46px;
              border: 3.5px solid rgba(0,0,0,0.10); border-top-color: #dfce50;
              border-radius: 50%; animation: customizer-spin 0.75s linear infinite;
              display:block !important;
            }
            @keyframes customizer-spin { to { transform: rotate(360deg); } }
            .customizer-open-loader__text {
              font-size: 15px; font-weight: 600; color: #333;
              letter-spacing: 0.2px; font-family: system-ui, -apple-system, sans-serif;
            }
            .img-customize-popup:not(.image-customizer-popup--view-in-room) .image-customizer-popup__inner {
              transform: scale(0.92); opacity: 0;
              transition: transform 0.32s cubic-bezier(0.34,1.3,0.64,1), opacity 0.25s ease;
            }
            .img-customize-popup:not(.image-customizer-popup--view-in-room).popup-animate-in .image-customizer-popup__inner {
              transform: scale(1); opacity: 1;
            }
          `;
          document.head.appendChild(style);
        }

        let _loaderEl = null;
        function getOrCreateLoader() {
          if (_loaderEl && document.body.contains(_loaderEl)) return _loaderEl;
          _loaderEl = document.createElement('div');
          _loaderEl.className = 'customizer-open-loader';
          _loaderEl.innerHTML = `
            <div class="customizer-open-loader__spinner"></div>
          `;
          document.body.appendChild(_loaderEl);
          return _loaderEl;
        }

        function showLoaderThenOpenPopup(popupId, opts) {
          opts = opts || {};
          const popup = document.getElementById(popupId);
          if (!popup) return;

          const editPayload = opts.editPayload || parseProductCustomizationFromUrl();

          const isViewInRoom = popup.classList.contains('image-customizer-popup--view-in-room') ||
            String(popupId || '').includes('view-in-room');

          if (isViewInRoom) {
            popup.hidden = false;
            popup.classList.add('popup-animate-in');
            popup.querySelector('.image-customizer-popup__overlay')?.classList.add('active');
            syncCustomizerPopupState();
            return;
          }

          const loader = getOrCreateLoader();
          loader.classList.add('loader-visible');

          setTimeout(() => {
            loader.classList.remove('loader-visible');
            popup.hidden = false;
            popup.classList.remove('popup-animate-in');
            void popup.offsetWidth;
            popup.classList.add('popup-animate-in');
            popup.querySelector('.image-customizer-popup__overlay')?.classList.add('active');
            syncCustomizerPopupState();

            const popupCustomizer = popup.querySelector('.customizer');
            if (popupCustomizer) {
              if (!editPayload) {
                popupCustomizer.dataset.maxStepUnlocked = popupCustomizer.dataset.maxStepUnlocked || '1';
                popupCustomizer.dataset.maxStepVisited = popupCustomizer.dataset.maxStepVisited || '1';
              }
              popupCustomizer.dataset.clearedSteps = '[]';
              bindCustomizerNavState(popupCustomizer);
              refreshCustomizerNav(popupCustomizer, true);

              if (!editPayload) {
                resetUserModifiedSelections(popupCustomizer);
                releaseOriginalSpecLocks(popupCustomizer);
              }
              const popupSpec = readOriginalSpecFromCustomizer(popupCustomizer);
              if (popupSpec && popupSpec.frameId) {
                popupCustomizer.dataset.pendingOriginalFrameId = popupSpec.frameId;
              }

              initArtworkHoverPreview(popupCustomizer);
              initStep1(popupCustomizer);
              initStep2(popupCustomizer);
              popupCustomizer.dataset.customizerInited = '1';
              if (window.__customizerFramesApi?.length && typeof window.__fsBootstrapFrames === 'function') {
                window.__fsBootstrapFrames(window.__customizerFramesApi);
              }
              startOriginalSpecRetryLoop(popupCustomizer);
              refreshStepPillStates(popupCustomizer);
              updateCanvasMatStepAvailability(popupCustomizer);
              captureBaseRetailArtPrice(popupCustomizer);
              refreshFormulaPricingFromProductDefaults(popupCustomizer);
              if (editPayload) {
                bootstrapCustomizerFromEditPayload(popupCustomizer, editPayload);
                goToStep(popupCustomizer, getSummaryStep(), { force: true, skipHistory: true });
              } else {
                updateSummary(popupCustomizer);
              }
              pushCustomizerHistory(popupCustomizer);
            }
          }, 680);
        }

        /* =========================
          MAIN CLICK HANDLER
        ========================== */
        document.addEventListener('click', function (e) {
          if (!(e.target instanceof Element)) return;

          const openBtn = e.target.closest('[data-open-popup]');
          if (openBtn) {
            const popupId = openBtn.getAttribute('data-open-popup');
            showLoaderThenOpenPopup(popupId);
            return;
          }

          const closeBtn = e.target.closest('[data-close-popup]');
          if (closeBtn) {
            const popup = closeBtn.closest('.image-customizer-popup');
            if (popup) closeImageCustomizerPopup(popup);
          }

          const customizer = e.target.closest('.customizer');
          if (customizer) {
            const stepClick = e.target.closest('.step');
            if (stepClick && !stepClick.classList.contains('step--disabled')) {
              const step = parseInt(stepClick.dataset.step, 10);
              if (!isNaN(step) && isStepClickable(customizer, step)) goToStep(customizer, step);
            }

            if (e.target.closest('[data-next-tab]')) {
              const current = getCurrentStep(customizer);
              const panel = current
                ? customizer.querySelector('.step-panel[data-step="' + current + '"]')
                : null;
              const activeTab = panel?.querySelector('.cz-tab.cz-tab--active')?.dataset.czTab || '';
              const isDoubleMat = String(customizerState.treatment || '').trim() === 'Double Mat';
              const skipSubTab =
                (current === 3 && activeTab === 'frame') ||
                (current === 2 && activeTab === 'topmat' && !isDoubleMat);
              const nextTabKey = !skipSubTab ? getNextCzTabKey(panel) : null;
              if (nextTabKey && panel) {
                activateCzTab(panel, nextTabKey);
                updateCustomizerMainPrice(customizer);
                scrollCustomizerToTop(customizer);
                pushCustomizerHistory(customizer);
                return;
              }
              const ordered = getEnabledSteps();
              const idx = current ? ordered.indexOf(current) : -1;
              if (idx >= 0 && idx < ordered.length - 1) {
                const next = ordered[idx + 1];
                unmarkStepCleared(customizer, next);
                goToStep(customizer, next, { force: true });
              }
            }

            if (e.target.closest('[data-prev-tab]')) {
              const current = getCurrentStep(customizer);
              const panel = current
                ? customizer.querySelector('.step-panel[data-step="' + current + '"]')
                : null;
              const prevTabKey = getPrevCzTabKey(panel);
              if (prevTabKey && panel) {
                activateCzTab(panel, prevTabKey);
                updateCustomizerMainPrice(customizer);
                scrollCustomizerToTop(customizer);
                pushCustomizerHistory(customizer);
                return;
              }
              if (current && current > 1) {
                const ordered = getEnabledSteps();
                const idx = ordered.indexOf(current);
                if (idx > 0) goToStep(customizer, ordered[idx - 1], { force: true });
              }
            }

            if (e.target.closest('[data-next-step]')) scrollStepsTrack(customizer, 'next');
            if (e.target.closest('[data-prev-step]')) scrollStepsTrack(customizer, 'prev');
          }

          const cropResetBtn = e.target.closest('.customizer-crop-reset-btn');
          if (cropResetBtn) {
            const customizerEl = cropResetBtn.closest('.customizer');
            if (customizerEl) restoreCropToOriginal(customizerEl);
            return;
          }

          const cropTrigger = e.target.closest('.crop-trigger, .customizer-crop-btn');
          if (cropTrigger) {
            const customizerEl = cropTrigger.closest('.customizer');
            const img = customizerEl?.querySelector('.step-panel[data-step="1"] .editable-image') ||
              cropTrigger.closest('.image-preview-wrapper')?.querySelector('.editable-image');
            if (img && customizerEl) openCropPopup(img, customizerEl);
            return;
          }

          if (e.target.closest('[data-close-crop]')) { closeCropPopup(); return; }

          const rotateBtn = e.target.closest('[data-rotate]');
          if (rotateBtn && cropper) {
            const angle = parseInt(rotateBtn.dataset.rotate);
            if (!isNaN(angle)) cropper.rotate(angle);
          }

          const flipBtn = e.target.closest('[data-flip]');
          if (flipBtn && cropper) {
            if (flipBtn.dataset.flip === 'x') { scaleX *= -1; cropper.scaleX(scaleX); }
            else if (flipBtn.dataset.flip === 'y') { scaleY *= -1; cropper.scaleY(scaleY); }
          }

          if (e.target.closest('[data-reset]') && cropper) {
            if (confirm('Are you sure you want to reset? All customizations including crop will be lost.')) {
              const cropState        = activeCustomizer ? customizerCropState.get(activeCustomizer) : null;
              const restoreW         = cropState?.baseWidth;
              const restoreH         = cropState?.baseHeight;
              resetCropForCustomizer(activeCustomizer);
              const originalSrc = activeImage?.dataset?.originalSrc;
              if (originalSrc) cropper.replace(originalSrc); else cropper.reset();
              scaleX = 1; scaleY = 1;
              if (restoreW > 0 && restoreH > 0 && activeCustomizer) {
                syncArtworkSizeAfterCrop(activeCustomizer, restoreW, restoreH);
              }
              updateCropResetButtonVisibility(activeCustomizer);
              const readout = document.getElementById('cropper-size-readout');
              if (readout) { readout.textContent = ''; readout.hidden = true; }
            }
          }

          if (
            e.target.closest('[data-apply]') && cropper && activeImage &&
            typeof cropper.getData === 'function' && typeof cropper.getCroppedCanvas === 'function'
          ) {
            const cropData         = cropper.getData(true);
            const customizerImages = getCustomizerImages(activeCustomizer);
            const canvas           = cropper.getCroppedCanvas({ fillColor: 'transparent' });
            if (canvas && cropData) {
              const croppedSrc     = canvas.toDataURL();
              const cropDataString = JSON.stringify(cropData);
              const cropBase       = ensureCropBaseDimensions(activeCustomizer) || {};
              const baseWidth      = cropBase.baseWidth || parseFloat(customizerState.width);
              const baseHeight     = cropBase.baseHeight || parseFloat(customizerState.height);
              const newSize        = calculateArtworkSizeFromCrop(cropper, baseWidth, baseHeight);
              if (activeCustomizer) {
                customizerCropState.set(activeCustomizer, {
                  cropData,
                  croppedSrc,
                  baseWidth,
                  baseHeight
                });
              }
              customizerImages.forEach(image => {
                image.src              = croppedSrc;
                image.dataset.cropData = cropDataString;
              });
              if (typeof window.__fsSetCroppedPhoto === 'function') window.__fsSetCroppedPhoto(croppedSrc);
              if (newSize && activeCustomizer) {
                syncArtworkSizeAfterCrop(activeCustomizer, newSize.width, newSize.height);
              }
              if (activeCustomizer && typeof window.markUserModifiedSelections === 'function') {
                window.markUserModifiedSelections(activeCustomizer);
              }
            }
            closeCropPopup();
          }
        });

         function resetStuckAddToOrderButtons() {
          document.querySelectorAll('.summary-add-cart-btn').forEach(function (btn) {
            btn.dataset.orderSubmitting = '0';
            const customizer = btn.closest('.customizer');
            setAddToOrderButtonLoading(btn, false);
            if (customizer) syncAddToOrderButtonState(btn, customizer);
          });
        }

        window.addEventListener('pageshow', function () {
          resetStuckAddToOrderButtons();
        });

        document.addEventListener('visibilitychange', function () {
          if (document.visibilityState === 'visible') {
            resetStuckAddToOrderButtons();
          }
        });

        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape') return;
          const cropPopup = document.getElementById('crop-popup');
          if (cropPopup && !cropPopup.hidden) { closeCropPopup(); return; }
          document.querySelectorAll('.image-customizer-popup:not([hidden])').forEach(function (popup) {
            closeImageCustomizerPopup(popup);
          });
        });

        window.addEventListener('popstate', function (e) {
          if (customizerHistoryClosing) {
            customizerHistoryClosing = false;
            return;
          }
          const state = e.state;
          if (state && state.ipCz) {
            applyCustomizerHistoryState(state);
            return;
          }
          const popup = getArtCustomizerPopup();
          if (popup && !popup.hidden) closeImageCustomizerPopup(popup, { fromHistory: true });
        });

        /* =========================
          STEP NAVIGATION HELPERS
        ========================== */
        function getCurrentStep(customizer) {
          if (!customizer) return null;
          const active = customizer.querySelector('.step-panel.active');
          return active ? parseInt(active.dataset.step) : null;
        }

        function getMaxWizardStep(customizer) {
          const steps = customizer ? Array.from(customizer.querySelectorAll('.step[data-step]')) : [];
          return steps.reduce((max, step) => {
            if (step.classList.contains('step--hidden') || step.getAttribute('aria-hidden') === 'true') return max;
            const n = parseInt(step.dataset.step, 10);
            return isNaN(n) ? max : Math.max(max, n);
          }, 1);
        }

        function scrollStepsTrack(customizer, direction) {
          const stepsTrack = customizer.querySelector('.customizer-steps');
          const stepItem   = stepsTrack?.querySelector('.step');
          if (!stepsTrack || !stepItem) return;
          stepsTrack.scrollBy({ left: direction === 'next' ? (stepItem.offsetWidth + 24) : -(stepItem.offsetWidth + 24), behavior: 'smooth' });
        }

        function setNavArrowVisibility(customizer) {
          const stepsTrack = customizer?.querySelector('.customizer-steps');
          const prev = customizer?.querySelector('.customizer-prev');
          const next = customizer?.querySelector('.customizer-next');
          if (!stepsTrack || !prev || !next) return;
          const maxScroll = Math.max(stepsTrack.scrollWidth - stepsTrack.clientWidth, 0);
          if (maxScroll <= 1) { prev.style.display = 'none'; next.style.display = 'none'; return; }
          prev.style.display = stepsTrack.scrollLeft <= 1              ? 'none' : 'flex';
          next.style.display = stepsTrack.scrollLeft >= maxScroll - 1  ? 'none' : 'flex';
        }

        function bindCustomizerNavState(customizer) {
          if (!customizer || customizer.dataset.navBound === 'true') return;
          const stepsTrack = customizer.querySelector('.customizer-steps');
          if (!stepsTrack) return;
          customizer.dataset.navBound = 'true';
          setNavArrowVisibility(customizer);
          stepsTrack.addEventListener('scroll', () => setNavArrowVisibility(customizer), { passive: true });
          window.addEventListener('resize', () => setNavArrowVisibility(customizer));
        }

        function refreshCustomizerNav(customizer, resetToStart = false) {
          const stepsTrack = customizer?.querySelector('.customizer-steps');
          if (!stepsTrack) return;
          if (resetToStart) stepsTrack.scrollLeft = 0;
          requestAnimationFrame(() => requestAnimationFrame(() => setNavArrowVisibility(customizer)));
        }

        function scrollStepIntoView(customizer, step) {
          customizer?.querySelector(`.step[data-step="${step}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }

        function goToStep(customizer, step, opts) {
          if (!customizer) return;
          const force = !!(opts && opts.force);
          if (!force && !isStepClickable(customizer, step)) return;

          setMaxStepVisited(customizer, step);

          const steps  = customizer.querySelectorAll('.step');
          const panels = customizer.querySelectorAll('.step-panel');
          if (!steps.length || !panels.length) return;

          const prevStep = getCurrentStep(customizer);

          if ((prevStep === 3 || step > 3) && customizerState.frame && typeof window.__fsGetCanvasDataURL === 'function') {
            const canvasUrl = window.__fsGetCanvasDataURL();
            if (canvasUrl) {
              customizer.querySelectorAll('.editable-image').forEach(img => {
                img.src = canvasUrl;
                img.dataset.framedSrc = canvasUrl;
              });
            }
          }

          if (step === 3) {
            customizer.querySelectorAll('.editable-image').forEach(img => {
              if (img.dataset.framedSrc) img.src = img.dataset.framedSrc;
              else if (img.dataset.originalSrc) img.src = img.dataset.originalSrc;
            });
          }

          steps.forEach(s => {
            s.classList.remove('active');
          });
          panels.forEach(p => p.classList.remove('active'));

          customizer.querySelector(`.step[data-step="${step}"]`)?.classList.add('active');
          customizer.querySelector(`.step-panel[data-step="${step}"]`)?.classList.add('active');

          mountSharedCanvasToActiveStep(customizer);

          if (step === 1) {
            requestAnimationFrame(function () {
              copyFsCanvasToPreviewCanvases(customizer);
            });
          }

          scrollStepIntoView(customizer, step);
          setNavArrowVisibility(customizer);
          refreshStepPillStates(customizer);
          updatePanelNavState(customizer);
          scrollCustomizerToTop(customizer);

          if (step === getSummaryStep()) {
            if (typeof invalidateCustomizerPreviewBase64 === 'function') {
              invalidateCustomizerPreviewBase64();
            }
            syncCustomizerSelectionsFromDOM(customizer);
            updateSummary(customizer);
            updateMatStepLabel(customizer);
            updateFrameLinerStepLabel();
            updateSubstrateStepLabel();
          }
          if (step !== 3) updateMatPreview(customizer);
          if (step === 2) updateBottomMatStepAvailability(customizer);
          if (step === 3) updateCanvasLinerAvailability(customizer);
          updateCurrentSizesDisplay(customizer);
          syncNotesFields(customizer);
          updateCustomizerMainPrice(customizer);
          if (!(opts && opts.skipHistory)) pushCustomizerHistory(customizer);

        }

        /* =========================================================
          OUTSIDE DIMENSION TEST INPUTS (below image preview)
        ========================================================= */
        function mountOutsideDimensionTestPanel(customizer) {
          if (!customizer) return;
          const panel = customizer.querySelector('[data-outside-dim-test]');
          if (!panel) return;
          const activePanel = customizer.querySelector('.step-panel.active');
          if (!activePanel) return;
          const imageCol = activePanel.querySelector('.customizer-image-col');
          if (!imageCol) return;
          const anchor = imageCol.querySelector('.fs-stage-host') ||
            imageCol.querySelector('.image-preview-wrapper');
          if (!anchor) return;
          if (anchor.nextElementSibling === panel) return;
          anchor.insertAdjacentElement('afterend', panel);
        }

        function initOutsideDimensionTestInputs(customizer) {
          if (!customizer || customizer.dataset.outsideDimTestBound === '1') return;
          customizer.dataset.outsideDimTestBound = '1';

          const panel = document.createElement('div');
          panel.className = 'outside-dim-test-panel';
          panel.setAttribute('data-outside-dim-test', '');
          panel.innerHTML = `
            <p class="outside-dim-test-panel__label"><strong>Current Outside Dimensions (Test)</strong></p>
            <div class="fs-size-row outside-dim-test-panel__row">
              <div class="fs-size-field">
                <label for="outside-test-w-${customizer.id || 'main'}">WIDTH (IN)</label>
                <input type="text" id="outside-test-w-${customizer.id || 'main'}" data-outside-test-w inputmode="decimal" autocomplete="off" />
              </div>
              <div class="fs-size-field">
                <label for="outside-test-h-${customizer.id || 'main'}">HEIGHT (IN)</label>
                <input type="text" id="outside-test-h-${customizer.id || 'main'}" data-outside-test-h inputmode="decimal" autocomplete="off" />
              </div>
            </div>
          `;
          customizer.appendChild(panel);

          const wInput = panel.querySelector('[data-outside-test-w]');
          const hInput = panel.querySelector('[data-outside-test-h]');

          function readOutsideTestValues() {
            const width = parseFloat(String(wInput.value).trim());
            const height = parseFloat(String(hInput.value).trim());
            if (!isFinite(width) || width <= 0 || !isFinite(height) || height <= 0) return null;
            return { width, height };
          }

          function applyOutsideTestChange(newW, newH) {
            if (!(newW > 0 && newH > 0)) return;
            if (typeof window.__fsApplyTestOutsideDimensions === 'function') {
              window.__fsApplyTestOutsideDimensions(newW, newH);
            }
            document.querySelectorAll('.customizer').forEach(function (c) {
              updateCurrentSizesDisplay(c);
            });
          }

          function commitOutsideTestChange() {
            const dims = readOutsideTestValues();
            if (!dims) return;
            applyOutsideTestChange(dims.width, dims.height);
          }

          wInput.addEventListener('input', commitOutsideTestChange);
          hInput.addEventListener('input', commitOutsideTestChange);
          wInput.addEventListener('change', commitOutsideTestChange);
          hInput.addEventListener('change', commitOutsideTestChange);

          syncOutsideDimTestInputs(customizer, true);
          mountOutsideDimensionTestPanel(customizer);
        }

        /* =========================================================
          SHARED CANVAS MOUNTING
        ========================================================= */
        function mountSharedCanvasToActiveStep(customizer) {
          if (!customizer) return;
          const stage = customizer.querySelector('[data-fs-stage]');
          if (!stage) return;
          const activePanel = customizer.querySelector('.step-panel.active');
          if (!activePanel) return;
          const host = activePanel.querySelector('[data-fs-stage-host]');
          if (!host) return;
          if (!host.contains(stage)) {
            host.appendChild(stage);
          }
          mountOutsideDimensionTestPanel(customizer);
        }

        /* =========================
          CLOSE CROP POPUP
        ========================== */
        function closeCropPopup() {
          const popup = document.getElementById('crop-popup');
          if (!popup) return;
          cropOpenToken++;
          setCropPopupLoading(false);
          const readout = document.getElementById('cropper-size-readout');
          if (readout) { readout.textContent = ''; readout.hidden = true; }
          popup.classList.remove('popup-animate-in');
          popup.hidden = true;
          popup.querySelector('.image-customizer-popup__overlay')?.classList.remove('active');
          if (cropper) { cropper.destroy(); cropper = null; }
          scaleX = 1; scaleY = 1;
          if (!document.querySelector('.image-customizer-popup:not([hidden])')) syncCustomizerPopupState();
        }

        /* =========================
          INJECT STYLES
        ========================== */
        (function injectStyles() {
          const style = document.createElement('style');
          style.textContent = `

            .mat-card--fabric .mat-card-swatch--fabric {
                position: relative;
            width: 70px;
            height: 70px;
            min-height: 70px;
            border-radius: 4px;
            overflow: hidden;
            display: block;
            }
            .mat-card--fabric .mat-card-swatch--fabric img {
              position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block;
            }
            .mat-card-fabric-badge {
              position: absolute; bottom: 2px; right: 2px;
              background: rgba(0,0,0,0.55); color: #fff; font-size: 9px; font-weight: 700;
              padding: 1px 4px; border-radius: 3px; letter-spacing: 0.3px; pointer-events: none;
            }

            .mat-card { position: relative; }
            .mat-card-width-badge {
              position: absolute;
              top: 6px;
              right: 6px;
              background: rgba(255,255,255,0.92);
              border: 1px solid rgba(0,0,0,0.10);
              color: #333;
              font-size: 10px;
              font-weight: 800;
              padding: 2px 6px;
              border-radius: 999px;
              line-height: 1;
              pointer-events: none;
              display: none;
            }

            /* Liner card with moulding-profile thumbnail */
            .mat-card-swatch--liner-thumb {
              width: 70px; height: 70px; min-height: 70px;
              border-radius: 50%;
              overflow: hidden; display: flex; align-items: center; justify-content: center;
              background: #f0f0f0; border: 1px solid rgba(0,0,0,0.10);
            }

            .outside-dim-test-panel {
              margin: 10px 0 0;
              padding: 10px 12px;
              border: 1px dashed #b8940a;
              border-radius: 4px;
              background: #fffef8;
              line-height: normal;
            }
            .outside-dim-test-panel__label {
              margin: 0 0 8px;
              font-size: 12px;
              color: #555;
            }
            .outside-dim-test-panel__row {
              margin: 0;
            }
            .outside-dim-test-panel .fs-size-field label {
              font-size: 10px;
              letter-spacing: 0.04em;
            }
            .outside-dim-test-panel input[data-outside-test-w],
            .outside-dim-test-panel input[data-outside-test-h] {
              width: 100%;
              padding: 6px 8px;
              border: 1px solid #ccc;
              border-radius: 3px;
              font-size: 14px;
              -moz-appearance: textfield;
              appearance: textfield;
            }
            .mat-card-swatch--liner-thumb img {
              width: 100%; height: 100%; object-fit: contain; display: block; 
            }

            .bottom-mat-disabled-msg {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 10px;
            padding: 50px 20px;
            border: 1.5px solid #dfce50;
            margin: 16px 0 40px;
        }
            .mat-disabled-icon { margin-bottom: 4px; }
            .mat-disabled-title {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
        }
            .mat-disabled-icon svg {
          fill: revert-layer;
            stroke: revert-layer;
            width: 24px;
            height: 24px;
        }
            .mat-disabled-body {
            font-size: 14px;
            color: #000;
            line-height: 1.5;
            margin: 0;
        }
            .mat-go-step1-btn {
            margin-top: 20px;
            padding: 9px 20px;
            background: #b05a1a;
            color: #000;
            border: none;
            cursor: pointer;
            transition: background 0.2s;
            background-color: #dfce50;
            border: 1px solid #dfce50;
            font-family: 'DM Sans';
            font-size: 16px;
            padding: 14px 28px;
        }
            .mat-go-step1-btn:hover { opacity: .5; }

            .fs-card { position: relative; }
            .fs-card--discontinued .fs-card-thumb, .fs-card--discontinued  .fs-card-meta {
              opacity: 0.62;
              cursor: not-allowed;
            }
            .fs-card--discontinued.fs-active {
              opacity: 0.85;
            }
            .fs-card-discontinued-label {
             display: inline-block;
            margin-left: 4px;
            background: red;
            color: #fff;
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 0.02em;
            text-transform: capitalize;
            vertical-align: middle;
            position: absolute;
            left: 0;
            bottom: 0;
            padding: 0 10px;
            }
            .fs-card-dim-tooltip {
              display: none; position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
              background: #fff; border: 1px solid #ddd; border-radius: 6px; padding: 4px;
              box-shadow: 0 4px 16px rgba(0,0,0,0.15); z-index: 100; min-width: 140px;
            }
            .fs-card-dim-tooltip img { display: block; max-width: 160px; height: auto; border-radius: 4px; }
            .fs-card:hover .fs-card-dim-tooltip { display: block; }

            .summary-card-subtitle, .summary-card-desc { word-break: break-word; white-space: pre-wrap; }
            .customizer-current-sizes--summary {
              display: flex;
              flex-wrap: wrap;
              justify-content: space-between;
              align-items: baseline;
              gap: 8px 40px;
              margin: 16px 0;
            }
            .customizer-current-sizes--summary .customizer-current-sizes__row {
              margin: 0;
              font-weight: 700;
              font-size: 14px;
              line-height: 1.4;
              color: #111;
            }
          `;
          document.head.appendChild(style);
        })();

        injectCustomizerLoaderStyles();

        /* =========================
          INIT ALL CUSTOMIZERS
        ========================== */
        (async function initAllCustomizers() {
          await Promise.all([
            loadCustomizerMatsFromApi(),
            loadCustomizerFramesFromApi(),
            loadCustomizerLinersFromApi()
          ]);
          document.querySelectorAll('.customizer').forEach(customizer => {
            normalizeWizardSteps(customizer);
            bindCustomizerNavState(customizer);
            bindCzTabs(customizer);
            bindFormulaPricingReviewToggle(customizer);
            bindMatStepPanelClear(customizer);
            bindFrameStepPanelClear(customizer);
            initArtworkHoverPreview(customizer);
            initStep1(customizer);
            initStep2(customizer);
            initMatStep(customizer, 4, 'topMat',    'Top Mat', 2);
            initMatStep(customizer, 5, 'bottomMat', 'Bottom Mat', 2);
            initMatStep(customizer, 6, 'liner',     'Liner', 3);
            updateMatStepLabel();
            updateFrameLinerStepLabel();
            initStep7(customizer);
            initOutsideDimensionTestInputs(customizer);
            mountSharedCanvasToActiveStep(customizer);
            initStep8(customizer);
          });
          document.querySelectorAll('.customizer').forEach(function (customizer) {
            const spec = readOriginalSpecFromCustomizer(customizer);
            if (spec && spec.frameId) {
              customizer.dataset.pendingOriginalFrameId = spec.frameId;
            }
            customizer.dataset.customizerInited = '1';
            if (customizer.dataset.pendingOriginalSpec === '1') {
              delete customizer.dataset.pendingOriginalSpec;
            }
          });
          if (window.__customizerFramesApi?.length && typeof window.__fsBootstrapFrames === 'function') {
            window.__fsBootstrapFrames(window.__customizerFramesApi);
          } else if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
            window.__fsEnforceFrameSubstrateRules();
          }
          document.querySelectorAll('.customizer').forEach(function (customizer) {
            startOriginalSpecRetryLoop(customizer);
            refreshFormulaPricingFromProductDefaults(customizer);
          });

          const editPayload = parseProductCustomizationFromUrl();
          if (editPayload) {
            // Be robust: some pages may not have a `.customizer` visible yet (or there may be multiple).
            // If the URL contains `product-customization`, open the first matching customizer popup.
            const popup =
              document.querySelector('.image-customizer-popup.img-customize-popup[id]') ||
              document.querySelector('.img-customize-popup[id]');
            if (popup && popup.id) {
              setTimeout(function () {
                showLoaderThenOpenPopup(popup.id, { editPayload: editPayload });
              }, 120);
            }
          }
        })();

        /* =========================
          CANVAS → PREVIEW SYNC
        ========================== */
        window.__fsOnDrawDone = function () {
          const fsCanvas = document.getElementById('fs-canvas');
          if (!fsCanvas) return;
          document.querySelectorAll('.customizer').forEach(function (customizer) {
            updateCurrentSizesDisplay(customizer);
            if (copyFsCanvasToPreviewCanvases(customizer)) return;
            try {
              const dataUrl = fsCanvas.toDataURL('image/jpeg', 0.88);
              if (!dataUrl) return;
              customizer.querySelectorAll('.editable-image').forEach(function (img) {
                img.src = dataUrl;
                img.dataset.framedSrc = dataUrl;
                img.hidden = false;
              });
              hidePreviewCanvases(customizer);
            } catch (e) { /* canvas copy + dataURL both unavailable */ }
            // Keep wishlist element attributes in sync with the latest canvas render.
            if (typeof syncWishlistButton === 'function') syncWishlistButton(customizer);
          });
        };

        /** Outside W×H for frame mockup — matches Current Outside Dimensions display. */
        function getFramePreviewOutsideDimensions(customizerRoot) {
          const computed = computeFramedSizeInches(customizerRoot);
          if (computed.width > 0 && computed.height > 0) return computed;
          const artW = parseFloat(customizerState.width) || 0;
          const artH = parseFloat(customizerState.height) || 0;
          return {
            width: artW > 0 ? artW : 20,
            height: artH > 0 ? artH : 20
          };
        }

        /* =========================================================
          FRAME SELECTOR — Step 3 (canvas-based)
          ─────────────────────────────────────────────────────────
          Liner textures are now generated procedurally.
          At IIFE startup, linerImgCache is pre-populated with the
          canvas elements from seedLinerTextures(), so the mitered
          secondary inner-frame renders on the very first draw call
          — no async loading, no solid-colour flash.
        ========================================================= */
        (function () {
          var DEG       = Math.PI / 180;
          var PAGE_SIZE = 16;
          var page      = 1;
          var filtered  = [];
          var selectedId = null;
          var frameSelectGeneration = 0;
          var FRAMES_LOCAL = FRAMES;
          var MAX_PX    = 420;
          var FW        = 48;
          var LW        = 0;

          var fabricImgCache    = {};
          var fabricImgLoading  = {};
          var fabricImgPromises = {};
          var matTileCache      = {};
          var matTileCacheKeys  = [];
          var MAT_TILE_CACHE_MAX = 24;
          var cachedFrameSeamless = null;
          var cachedFrameId       = null;

          /* ── Pre-populate linerImgCache with procedurally generated canvases ──
            Each liner was assigned liner.frameImg = '__liner_<id>' and
            liner._texCanvas = <canvas> by seedLinerTextures() above.
            We populate the cache here so drawLinerBand() hits immediately.    */
          var linerImgCache   = {};
          var linerImgLoading = {};

          (function prewarmLinerCache() {
            LINER_COLORS.forEach(function (liner) {
              if (liner.frameImg && liner._texCanvas && String(liner.frameImg).indexOf('__liner_') === 0) {
                linerImgCache[liner.frameImg] = liner._texCanvas;
              }
            });
          })();

          var canvas = document.getElementById('fs-canvas');
          if (!canvas) return;
          var ctx = canvas.getContext('2d');

          var photoImg = null;
          var imgW = customizerState.width;
          var imgH = customizerState.height;
          var previewOutsideW = 0;
          var previewOutsideH = 0;
          var testOutsideOverride = null;

          function syncPreviewOutsideDims() {
            if (testOutsideOverride && testOutsideOverride.width > 0 && testOutsideOverride.height > 0) {
              previewOutsideW = testOutsideOverride.width;
              previewOutsideH = testOutsideOverride.height;
              return;
            }
            var outside = getFramePreviewOutsideDimensions();
            previewOutsideW = outside.width;
            previewOutsideH = outside.height;
          }

          function applyTestOutsideDimensions(newW, newH) {
            if (!(newW > 0 && newH > 0)) return;
            var prevOutside = (testOutsideOverride && testOutsideOverride.width > 0 && testOutsideOverride.height > 0)
              ? testOutsideOverride
              : getFramePreviewOutsideDimensions();
            var prevArtW = parseFloat(customizerState.width) || parseFloat(imgW) || 0;
            var prevArtH = parseFloat(customizerState.height) || parseFloat(imgH) || 0;
            if (!(prevOutside.width > 0 && prevOutside.height > 0)) return;
            if (!(prevArtW > 0 && prevArtH > 0)) return;

            var ratioW = newW / prevOutside.width;
            var ratioH = newH / prevOutside.height;
            var newArtW = snapArtworkDimension(prevArtW * ratioW);
            var newArtH = snapArtworkDimension(prevArtH * ratioH);

            testOutsideOverride = { width: newW, height: newH };
            previewOutsideW = newW;
            previewOutsideH = newH;
            customizerState.width = newArtW;
            customizerState.height = newArtH;
            imgW = newArtW;
            imgH = newArtH;

            var csW = document.getElementById('cs-width-input');
            var csH = document.getElementById('cs-height-input');
            if (csW) csW.value = newArtW;
            if (csH) csH.value = newArtH;

            syncPreviewMoldingWidths();
            redrawWithCurrentFrame();
          }

          window.__fsApplyTestOutsideDimensions = applyTestOutsideDimensions;
          window.__fsGetTestOutsideOverride = function () { return testOutsideOverride; };
          window.__fsClearTestOutsideOverride = function () {
            if (!testOutsideOverride) return;
            testOutsideOverride = null;
            syncPreviewOutsideDims();
            syncPreviewMoldingWidths();
            redrawWithCurrentFrame();
          };

          function getPreviewOutsideDims() {
            var ow = previewOutsideW;
            var oh = previewOutsideH;
            if (!(ow > 0 && oh > 0)) {
              ow = parseFloat(imgW) > 0 ? parseFloat(imgW) : 20;
              oh = parseFloat(imgH) > 0 ? parseFloat(imgH) : 20;
            }
            return { w: ow, h: oh };
          }

          function getPreviewScaleDim() {
            var outside = getPreviewOutsideDims();
            return canvasPx(outside.w, outside.h);
          }

          /** Re-scale frame/liner px when outside dims change (e.g. mat size). */
          function syncPreviewMoldingWidths() {
            var cur = selectedId ? FRAMES_LOCAL.find(function (x) { return x.id === selectedId; }) : null;
            FW = cur ? calcFW(cur.frameW || 1.2, cur) : 0;
          }

          syncPreviewOutsideDims();

          window.__fsSyncSizeFromStep2 = function(w, h) {
            if (typeof window.__fsClearTestOutsideOverride === 'function') {
              window.__fsClearTestOutsideOverride();
            }
            imgW = parseFloat(w) || imgW;
            imgH = parseFloat(h) || imgH;
            syncPreviewOutsideDims();
            var cur = selectedId ? FRAMES_LOCAL.find(function(x){ return x.id === selectedId; }) : null;
            FW = cur ? calcFW(cur.frameW || 1.2, cur) : 0;
            redrawWithCurrentFrame();
          };

          var fsWidthInput  = document.getElementById('fs-width');
          var fsHeightInput = document.getElementById('fs-height');
          var fsSizeRow     = fsWidthInput ? fsWidthInput.closest('.fs-size-row') : null;
          var fsSizeError   = document.getElementById('fs-size-error');
          if (fsSizeRow)  fsSizeRow.style.display  = 'none';
          if (fsSizeError) fsSizeError.style.display = 'none';

          function calcQ(w, h) {
            var sw = parseFloat(w) > 0 ? parseFloat(w) : 20;
            var sh = parseFloat(h) > 0 ? parseFloat(h) : 20;
            return Math.min(1000 / sw, 1000 / sh);
          }

          function canvasPx(w, h) {
            var q = calcQ(w, h);
            var scale = Math.min(MAX_PX / (w * q), MAX_PX / (h * q));
            return { cw: Math.round(w * q * scale), ch: Math.round(h * q * scale), q: q, scale: scale };
          }

          function previewMoldingWidthInches(frameOrLiner, kind) {
            if (!frameOrLiner) return 1.2;
            if (kind === 'frame') {
              var sw = parseFloat(frameOrLiner.sizeWidthIn);
              if (isFinite(sw) && sw > 0) return sw;
              var fw = parseFloat(frameOrLiner.frameW);
              if (isFinite(fw) && fw > 0) return fw;
              return 1.2;
            }
            var api = frameOrLiner._api;
            if (api) {
              var lw = getLinerWidthInchesFromApiRow(api);
              if (isFinite(lw) && lw > 0) return lw;
            }
            var inches = toInchesFromLegacyWidth(frameOrLiner.frameW);
            return isFinite(inches) && inches > 0 ? inches : 1.0;
          }

          function calcMoldingPreviewPx(inches) {
            var dim = getPreviewScaleDim();
            var inVal = parseFloat(inches);
            if (isNaN(inVal) || inVal <= 0) inVal = 1.2;
            var px = inVal * dim.q * dim.scale;
            return Math.round(Math.min(Math.max(px, 8), 16));
          }

          function calcFW(frameWidthInches, frameDef) {
            var inches = frameDef ? previewMoldingWidthInches(frameDef, 'frame') : parseFloat(frameWidthInches);
            return calcMoldingPreviewPx(inches);
          }

          function calcLW(liner) {
            if (liner) {
              return calcMoldingPreviewPx(previewMoldingWidthInches(liner, 'liner'));
            }
            return Math.round(Math.min(Math.max(FW * 0.35, 8), 16));
          }

          /** Mat reveal width in px — scale with outside preview size (like frame). */
          function calcMatBorderPx(inches) {
            var inVal = parseFloat(inches);
            if (isNaN(inVal) || inVal <= 0) inVal = 1;
            var dim = getPreviewScaleDim();
            var px = inVal * dim.q * dim.scale;
            /* Clamp: keep visible, but don't let mats dominate small previews. */
            return Math.round(Math.min(Math.max(px, 2), Math.min(dim.cw, dim.ch) * 0.25));
          }

          /* ─────────────────────────────────────────────────────────
            drawLinerBand
            Draws the liner as a mitered inner frame strip using the
            procedurally generated canvas texture.  Because the cache
            is pre-warmed above, linerImgCache[liner.frameImg] is
            always populated — the async branch never executes for
            standard liners.
          ───────────────────────────────────────────────────────── */
          function drawLinerBand(liner, lx, ly, lw, lh, lThick) {
            if (!liner || lThick <= 0 || lw <= 0 || lh <= 0) return;

            var cached = liner.frameImg ? linerImgCache[liner.frameImg] : null;

            if (cached) {
              /* Use the mitered frame-strip technique (same as outer frame) */
              _drawFrameStrip(ctx, cached, lx, ly, lw, lh, lThick);
            } else {
              /* Solid-colour fallback (should only be needed if _texCanvas
                was not generated, e.g. canvas API unavailable)            */
              _drawLinerSolid(liner.hex || '#c9a84c', lx, ly, lw, lh, lThick);

              /* Async load path — kept as safety net for remote frameImg URLs */
              if (liner.frameImg && !linerImgLoading[liner.frameImg]) {
                linerImgLoading[liner.frameImg] = true;
                var imgSrc = liner.frameImg;
                loadViaBlob(imgSrc)
                  .then(function(img) {
                    linerImgCache[imgSrc] = img;
                    linerImgLoading[imgSrc] = false;
                    redrawWithCurrentFrame();
                  })
                  .catch(function() {
                    /* Ultimate fallback: store a plain colour canvas */
                    var fb = document.createElement('canvas'); fb.width = 4; fb.height = 20;
                    var fctx = fb.getContext('2d');
                    fctx.fillStyle = liner.hex || '#c9a84c';
                    fctx.fillRect(0, 0, 4, 20);
                    linerImgCache[imgSrc] = fb;
                    linerImgLoading[imgSrc] = false;
                    redrawWithCurrentFrame();
                  });
              }
            }
          }

          var blobCache = {};
          function toWeservProxyUrl(src) {
            try {
              if (!src || typeof src !== 'string') return '';
              if (src.indexOf('data:') === 0) return '';
              var stripped = src.replace(/^https?:\/\//i, '');
              return 'https://images.weserv.nl/?url=' + encodeURIComponent(stripped) + '&il';
            } catch (e) { return ''; }
          }

          function loadViaBlob(src) {
            if (blobCache[src]) return new Promise(function (res, rej) {
              var i = new Image(); i.onload = function () { res(i); }; i.onerror = rej; i.src = blobCache[src];
            });
            function fetchToImg(url) {
              return fetch(url).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
                .then(function (blob) {
                  var obj = URL.createObjectURL(blob);
                  if (url === src) blobCache[src] = obj;
                  return new Promise(function (res, rej) { var i = new Image(); i.onload = function () { res(i); }; i.onerror = rej; i.src = obj; });
                });
            }
            return fetchToImg(src).catch(function () {
              var proxy = toWeservProxyUrl(src);
              if (!proxy) throw new Error('CORS blocked');
              return fetchToImg(proxy);
            });
          }

          function loadViaDataURI(src) {
            return new Promise(function (res, rej) { var i = new Image(); i.onload = function () { res(i); }; i.onerror = rej; i.src = src; });
          }

          function preloadMatImage(src) {
            if (!src) return Promise.resolve(null);
            if (fabricImgCache[src]) return Promise.resolve(fabricImgCache[src]);
            if (fabricImgPromises[src]) return fabricImgPromises[src];
            fabricImgPromises[src] = new Promise(function (resolve) {
              var img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = function () {
                fabricImgCache[src] = img;
                delete fabricImgPromises[src];
                resolve(img);
              };
              img.onerror = function () {
                loadViaBlob(src)
                  .then(function (loaded) {
                    fabricImgCache[src] = loaded;
                    delete fabricImgPromises[src];
                    resolve(loaded);
                  })
                  .catch(function () {
                    delete fabricImgPromises[src];
                    resolve(null);
                  });
              };
              img.src = src;
            });
            return fabricImgPromises[src];
          }

          window.__fsPreloadMatImages = function (mats) {
            (mats || []).forEach(function (m) {
              if (m && m.img) preloadMatImage(m.img);
            });
          };

          function buildMatTileCanvas(cachedImg, w, h) {
            var offscreen = document.createElement('canvas');
            offscreen.width = w;
            offscreen.height = h;
            var oCtx = offscreen.getContext('2d');
            var tileW = cachedImg.naturalWidth || cachedImg.width || 120;
            var tileH = cachedImg.naturalHeight || cachedImg.height || 120;
            var maxTile = Math.round(Math.min(w, h) * 0.55);
            if (tileW > maxTile) {
              var tileScale = maxTile / tileW;
              tileW = Math.round(tileW * tileScale);
              tileH = Math.round(tileH * tileScale);
            }
            for (var ty = 0; ty < h; ty += tileH) {
              for (var tx = 0; tx < w; tx += tileW) {
                oCtx.drawImage(cachedImg, tx, ty, tileW, tileH);
              }
            }
            return offscreen;
          }

          function getMatTileCanvas(matImg, w, h, cachedImg) {
            var key = matImg + '|' + w + '|' + h;
            if (matTileCache[key]) return matTileCache[key];
            var tiled = buildMatTileCanvas(cachedImg, w, h);
            matTileCache[key] = tiled;
            matTileCacheKeys.push(key);
            if (matTileCacheKeys.length > MAT_TILE_CACHE_MAX) {
              delete matTileCache[matTileCacheKeys.shift()];
            }
            return tiled;
          }

          function preloadFrameImage(src) {
            if (!src) return Promise.resolve(null);
            if (fabricImgCache[src]) return Promise.resolve(fabricImgCache[src]);
            if (fabricImgPromises[src]) return fabricImgPromises[src];
            fabricImgPromises[src] = (src.indexOf('data:') === 0 ? loadViaDataURI(src) : loadViaBlob(src))
              .then(function (img) {
                fabricImgCache[src] = img;
                delete fabricImgPromises[src];
                return img;
              })
              .catch(function () {
                delete fabricImgPromises[src];
                return null;
              });
            return fabricImgPromises[src];
          }

          function getCanvasImg(f) {
            var src = f.img || '';
            if (!src) return Promise.reject(new Error('No img'));
            return preloadFrameImage(src).then(function (img) {
              if (img) return img;
              return Promise.reject(new Error('No img'));
            });
          }

          /* ─────────────────────────────────────────────────────────
            SEAMLESS-TILE PREPROCESSOR
            ─────────────────────────────────────────────────────────
            Outer-frame & liner rendering uses createPattern(repeat-x)
            to tile the moulding image across the top/bottom/left/right
            strips. For that to look continuous the image's left edge
            must match its right edge — which is true for purpose-made
            "_piec" profile strips, but NOT for photographed catalog
            images (e.g. 028-800.png), where each tile boundary becomes
            a visible vertical seam.

            We fix this in code by copying the right-edge slice over the
            left-edge slice with a horizontal alpha gradient (1 → 0).
            The right edge of one tile then visually melts into the left
            edge of the next, eliminating the seam without altering the
            middle of the texture (so ornate detail like the bead pattern
            is preserved).
          ───────────────────────────────────────────────────────── */
          var seamlessFrameCache = {};

          function makeSeamlessHorizontal(srcImg) {
            try {
              var w = srcImg.naturalWidth || srcImg.width;
              var h = srcImg.naturalHeight || srcImg.height;
              if (!w || !h) return srcImg;

              var out = document.createElement('canvas');
              out.width  = w;
              out.height = h;
              var octx = out.getContext('2d');
              octx.drawImage(srcImg, 0, 0);

              /* Blend zone = ~12% of width, clamped to a sane range.
                Smaller = sharper detail at seam; larger = softer hide. */
              var blendW = Math.min(
                Math.max(8, Math.round(w * 0.12)),
                Math.max(8, Math.floor(w * 0.45))
              );
              if (blendW < 2) return out;

              /* Lift the right-edge slice onto a temp canvas */
              var temp = document.createElement('canvas');
              temp.width  = blendW;
              temp.height = h;
              var tctx = temp.getContext('2d');
              tctx.drawImage(srcImg, w - blendW, 0, blendW, h, 0, 0, blendW, h);

              /* Apply a left→right alpha mask: opaque at x=0, transparent at x=blendW */
              tctx.globalCompositeOperation = 'destination-in';
              var grad = tctx.createLinearGradient(0, 0, blendW, 0);
              grad.addColorStop(0, 'rgba(0,0,0,1)');
              grad.addColorStop(1, 'rgba(0,0,0,0)');
              tctx.fillStyle = grad;
              tctx.fillRect(0, 0, blendW, h);

              /* Composite onto the left edge of the output canvas */
              octx.drawImage(temp, 0, 0);

              return out;
            } catch (e) {
              return srcImg;
            }
          }

          function getSeamlessFrameImg(img, frameDef) {
            if (!img) return img;
            var key = (frameDef && (frameDef.img || frameDef.id)) || '__default_frame';
            if (seamlessFrameCache[key]) return seamlessFrameCache[key];
            var seamless = makeSeamlessHorizontal(img);
            seamlessFrameCache[key] = seamless;
            return seamless;
          }

          function drawMatRegion(mat, x, y, w, h) {
            if (!mat || w <= 0 || h <= 0) return;
            /* Hex color takes priority over texture image in preview */
            if (mat.hex) {
              ctx.fillStyle = mat.hex;
              ctx.fillRect(x, y, w, h);
              return;
            }
            if (mat.img) {
              var cached = fabricImgCache[mat.img];
              if (cached) {
                var tiled = getMatTileCanvas(mat.img, w, h, cached);
                ctx.drawImage(tiled, x, y, w, h);
                ctx.fillStyle = 'rgba(0,0,0,0.10)';
                ctx.fillRect(x, y, w, h);
                return;
              }
              if (!fabricImgLoading[mat.img]) {
                fabricImgLoading[mat.img] = true;
                preloadMatImage(mat.img).then(function (img) {
                  fabricImgLoading[mat.img] = false;
                  if (img) redrawWithCurrentFrame();
                });
              }
            }
          }

          /* Draw solid colour liner band with bevel shading (pure fallback only) */
          function _drawLinerSolid(hex, lx, ly, lw, lh, lThick) {
            ctx.save();
            ctx.fillStyle = hex;
            ctx.fillRect(lx, ly, lw, lThick);
            ctx.fillRect(lx, ly + lh - lThick, lw, lThick);
            ctx.fillRect(lx, ly + lThick, lThick, lh - lThick * 2);
            ctx.fillRect(lx + lw - lThick, ly + lThick, lThick, lh - lThick * 2);
            ctx.strokeStyle = 'rgba(255,255,255,0.30)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(lx, ly + lh); ctx.lineTo(lx, ly); ctx.lineTo(lx + lw, ly);
            ctx.stroke();
            ctx.strokeStyle = 'rgba(0,0,0,0.18)';
            ctx.beginPath();
            ctx.moveTo(lx + lw, ly); ctx.lineTo(lx + lw, ly + lh); ctx.lineTo(lx, ly + lh);
            ctx.stroke();
            ctx.restore();
          }

          /* ─────────────────────────────────────────────────────────
            _drawFrameStrip — mitered frame band renderer
            Works for both the outer frame (frameImg = photo of
            moulding) and the inner liner (frameImg = procedural
            canvas).  frameImg.height gives strip thickness.
          ───────────────────────────────────────────────────────── */
          function _drawFrameStrip(targetCtx, frameImg, fx, fy, fw, fh, stripW) {
            /* naturalHeight for loaded Image; height for canvas element */
            var srcH = frameImg.naturalHeight || frameImg.height || stripW;
            var sa   = stripW / srcH;
            var ta   = Math.ceil(fw / sa);
            var ua   = Math.ceil(fh / sa);

            var dstFrame = document.createElement('canvas');
            dstFrame.width  = fw;
            dstFrame.height = fh;
            var dfc = dstFrame.getContext('2d');
            var pat = dfc.createPattern(frameImg, 'repeat-x');

            dfc.save(); dfc.scale(sa, sa); dfc.fillStyle = pat; dfc.fillRect(0, 0, ta, srcH); dfc.restore();
            dfc.save(); dfc.scale(sa, sa); dfc.translate(ta, ua); dfc.rotate(180 * DEG); dfc.fillStyle = pat; dfc.fillRect(0, 0, ta, srcH); dfc.restore();
            dfc.save(); dfc.scale(sa, sa); dfc.translate(0, ua); dfc.rotate(-90 * DEG);
            dfc.beginPath(); dfc.moveTo(0, 0); dfc.lineTo(srcH, srcH); dfc.lineTo(ua - srcH, srcH); dfc.lineTo(ua, 0); dfc.closePath(); dfc.clip();
            dfc.fillStyle = pat; dfc.fillRect(0, 0, ua, srcH); dfc.restore();
            dfc.save(); dfc.scale(sa, sa); dfc.translate(ta, 0); dfc.rotate(90 * DEG);
            dfc.beginPath(); dfc.moveTo(0, 0); dfc.lineTo(srcH, srcH); dfc.lineTo(ua - srcH, srcH); dfc.lineTo(ua, 0); dfc.closePath(); dfc.clip();
            dfc.fillStyle = pat; dfc.fillRect(0, 0, ua, srcH); dfc.restore();

            targetCtx.drawImage(dstFrame, fx, fy, fw, fh);
          }

          /* ─────────────────────────────────────────────────────────
            drawScene — main canvas render
            Layer order (back to front):
              1. Top mat
              2. Bottom mat (Double Mat only)
              3. Artwork / photo
              4. Outer frame
              5. Liner band (mitered inner frame strip)
              6. Inner shadow vignette
          ───────────────────────────────────────────────────────── */
          function drawScene(frameImg) {
            syncPreviewOutsideDims();
            syncPreviewMoldingWidths();
            var outside = getPreviewOutsideDims();
            var dim = canvasPx(outside.w, outside.h);
            var totalW = dim.cw;
            var totalH = dim.ch;
            var treatment   = customizerState.treatment;
            var liner       = customizerState.liner || null;

            var showMats    = (treatment === 'Single Mat' || treatment === 'Double Mat');
            var showBottom  = (treatment === 'Double Mat');

            var topMat      = (showMats && customizerState.topMat)      ? customizerState.topMat    : null;
            var bottomMat   = (showBottom && customizerState.bottomMat) ? customizerState.bottomMat : null;

            var topIn = parseFloat(customizerState.topMatSizeInches);
            if (isNaN(topIn) || topIn <= 0) topIn = 1;
            var bottomIn = parseFloat(customizerState.bottomMatSizeInches);
            if (isNaN(bottomIn) || bottomIn <= 0) bottomIn = 1;

            var activeFW = frameImg ? FW : 0;
            LW = liner ? calcLW(liner) : 0;
            var activeLW = LW;

            /* Glass opening fills the frame — inset by moulding, not centered artwork. */
            var aw = Math.max(2, totalW - activeFW * 2);
            var ah = Math.max(2, totalH - activeFW * 2);
            var artOx = activeFW;
            var artOy = activeFW;

            var innerX  = artOx + activeLW;
            var innerY  = artOy + activeLW;
            var innerAw = aw - activeLW * 2;
            var innerAh = ah - activeLW * 2;
            if (innerAw < 2) innerAw = 2;
            if (innerAh < 2) innerAh = 2;

            var topMatPx    = topMat    ? calcMatBorderPx(topIn) : 0;
            var bottomMatPx = bottomMat ? calcMatBorderPx(bottomIn) : 0;

            canvas.width  = totalW;
            canvas.height = totalH;
            ctx.clearRect(0, 0, totalW, totalH);

            /* ── 1. Fill preview background ── */
            ctx.fillStyle = frameImg ? '#f0ede6' : '#ffffff';
            ctx.fillRect(0, 0, totalW, totalH);
            ctx.fillRect(artOx, artOy, aw, ah);

            /* ── 2. Top mat ── */
            if (topMat) drawMatRegion(topMat, innerX, innerY, innerAw, innerAh);

            /* ── 3. Bottom mat ── */
            if (bottomMat) drawMatRegion(bottomMat, innerX + topMatPx, innerY + topMatPx, innerAw - topMatPx * 2, innerAh - topMatPx * 2);

            /* ── 4. Artwork area — size from artwork inches inside glass opening ── */
            var glassIn = computeGlassSizeInches();
            var artWin = parseFloat(imgW) > 0 ? parseFloat(imgW) : glassIn.width;
            var artHin = parseFloat(imgH) > 0 ? parseFloat(imgH) : glassIn.height;
            var openingW = innerAw - (topMatPx + bottomMatPx) * 2;
            var openingH = innerAh - (topMatPx + bottomMatPx) * 2;
            if (openingW < 2) openingW = 2;
            if (openingH < 2) openingH = 2;
            var artW = openingW;
            var artH = openingH;
            if (glassIn.width > 0 && glassIn.height > 0 && artWin > 0 && artHin > 0) {
              artW = Math.max(2, Math.round(aw * (artWin / glassIn.width)));
              artH = Math.max(2, Math.round(ah * (artHin / glassIn.height)));
              if (artW > openingW) artW = openingW;
              if (artH > openingH) artH = openingH;
            }
            var artX = innerX + topMatPx + bottomMatPx + Math.round((openingW - artW) / 2);
            var artY = innerY + topMatPx + bottomMatPx + Math.round((openingH - artH) / 2);

            if (!isRoughDeckleSubstrate()) {
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(artX, artY, artW, artH);
            }

            if (photoImg && artW > 4 && artH > 4) {
              drawArtworkInPreview(
                ctx, photoImg, artX, artY, artW, artH, innerX, innerY, innerAw, innerAh,
                function () { redrawWithCurrentFrame(); }
              );
            } else if (!photoImg) {
              ctx.fillStyle = '#d0c8bc';
              ctx.fillRect(artX, artY, artW, artH);
              ctx.fillStyle = '#a09080';
              ctx.font = '11px sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('Your Artwork', totalW / 2, totalH / 2);
            }

            /* ── 5. Outer frame (uniform strip — all four sides) ── */
            if (frameImg) {
              _drawFrameStrip(ctx, frameImg, 0, 0, totalW, totalH, activeFW);

              /* Inner shadow vignette */
              var sdw = Math.round(activeFW * 0.38);
              var shadowPairs = [
                [ctx.createLinearGradient(0, artOy, 0, artOy + sdw), artOx, artOy, aw, sdw],
                [ctx.createLinearGradient(0, artOy + ah, 0, artOy + ah - sdw), artOx, artOy + ah - sdw, aw, sdw],
                [ctx.createLinearGradient(artOx, 0, artOx + sdw, 0), artOx, artOy, sdw, ah],
                [ctx.createLinearGradient(artOx + aw, 0, artOx + aw - sdw, 0), artOx + aw - sdw, artOy, sdw, ah],
              ];
              shadowPairs.forEach(function (p) {
                p[0].addColorStop(0, 'rgba(0,0,0,0.28)'); p[0].addColorStop(1, 'rgba(0,0,0,0)');
                ctx.fillStyle = p[0]; ctx.fillRect(p[1], p[2], p[3], p[4]);
              });
            }

            /* ── 6. Liner band — inner edge of frame, or outer edge of artwork when no frame ── */
            if (liner && activeLW > 0) {
              drawLinerBand(liner, artOx, artOy, aw, ah, activeLW);
            }

            if (typeof window.__fsOnDrawDone === 'function') window.__fsOnDrawDone();
          }

          function redrawWithCurrentFrame() {
            var cur = selectedId ? FRAMES_LOCAL.find(function (x) { return x.id === selectedId; }) : null;
            if (cur && !isFrameAllowedForCurrentSubstrate(cur)) {
              cur = null;
            }
            if (cur && cachedFrameId === cur.id && cachedFrameSeamless) {
              drawScene(cachedFrameSeamless);
              return;
            }
            if (cur) {
              var expectId = cur.id;
              var generation = frameSelectGeneration;
              getCanvasImg(cur)
                .then(function (fi) {
                  if (generation !== frameSelectGeneration || selectedId !== expectId) return;
                  cachedFrameId = cur.id;
                  cachedFrameSeamless = getSeamlessFrameImg(fi, cur);
                  drawScene(cachedFrameSeamless);
                })
                .catch(function () {
                  if (generation !== frameSelectGeneration || selectedId !== expectId) return;
                  cachedFrameId = null;
                  cachedFrameSeamless = null;
                  drawScene(null);
                });
            } else {
              cachedFrameId = null;
              cachedFrameSeamless = null;
              drawScene(null);
            }
          }

          window.__fsRedrawWithMats    = function () { redrawWithCurrentFrame(); };
          window.__fsGetCanvasDataURL  = function () { try { return canvas.toDataURL('image/png'); } catch (e) { return null; } };

          window.__fsSetCroppedPhoto = function (dataUrl) {
            var isData = dataUrl.indexOf('data:') === 0;
            (isData ? loadViaDataURI(dataUrl) : loadViaBlob(dataUrl))
              .then(function (img) { photoImg = img; redrawWithCurrentFrame(); })
              .catch(function () {
                (isData ? loadViaBlob(dataUrl) : loadViaDataURI(dataUrl))
                  .then(function (img) { photoImg = img; redrawWithCurrentFrame(); })
                  .catch(function () { redrawWithCurrentFrame(); });
              });
          };

          function applyFrameToCanvas(f, fi, generation) {
            if (generation != null && generation !== frameSelectGeneration) return;
            if (!selectedId || !f || selectedId !== f.id) return;
            cachedFrameId = f.id;
            cachedFrameSeamless = getSeamlessFrameImg(fi, f);
            drawScene(cachedFrameSeamless);
            var hint = document.getElementById('fs-hint');
            if (hint) hint.textContent = f.name + ' — ' + f.id;
          }

          function selectFrame(id, opts) {
            opts = opts || {};
            var f = FRAMES_LOCAL.find(function (x) { return x.id === id; });
            if (!f) return;
            if (!opts.allowIncompatibleSubstrate && !isFrameAllowedForCurrentSubstrate(f)) return;

            /* Discontinued (pickFrom:false): keep if already selected / original-spec restore only. */
            if (f.pickFrom === false) {
              var idsMatch = typeof window.__catalogIdsMatch === 'function'
                ? window.__catalogIdsMatch
                : function (a, b) {
                  return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
                };
              var isCurrent = !!(selectedId && idsMatch(selectedId, f.id));
              var isStateCurrent = !!(customizerState.frame && idsMatch(customizerState.frame.id, f.id));
              var applyingOrig = !!document.querySelector('.customizer[data-applying-original-spec="1"]');
              if (!opts.fromOriginalSpec && !opts.allowDiscontinued && !applyingOrig && !isCurrent && !isStateCurrent) {
                return;
              }
            }

            if (!opts.fromOriginalSpec) {
              var applyingOrigSel = !!document.querySelector('.customizer[data-applying-original-spec="1"]');
              if (!applyingOrigSel) {
                document.querySelectorAll('.customizer').forEach(function (c) {
                  if (typeof window.markUserModifiedSelections === 'function') window.markUserModifiedSelections(c);
                });
              }
            }
            selectedId = id;
            var generation = ++frameSelectGeneration;
            document.querySelectorAll('.fs-card').forEach(function (c) { c.classList.toggle('fs-active', c.dataset.fsId === id); });
            FW = calcFW(f.frameW || 1.2, f);
            onFrameSelected(f.id, f.name);

            var spinner = document.getElementById('fs-spinner');
            var hint    = document.getElementById('fs-hint');

            if (cachedFrameId === f.id && cachedFrameSeamless) {
              if (spinner) spinner.style.display = 'none';
              drawScene(cachedFrameSeamless);
              if (hint) hint.textContent = f.name + ' — ' + f.id;
              return;
            }

            var src = f.img || '';
            var cachedImg = src ? fabricImgCache[src] : null;
            if (cachedImg) {
              if (spinner) spinner.style.display = 'none';
              applyFrameToCanvas(f, cachedImg, generation);
              return;
            }

            if (spinner) spinner.style.display = 'block';
            if (hint)    hint.textContent = 'Loading…';
            getCanvasImg(f)
              .then(function (fi) {
                applyFrameToCanvas(f, fi, generation);
              })
              .catch(function (err) {
                if (generation !== frameSelectGeneration || selectedId !== f.id) return;
                console.warn('Frame load failed:', err);
                cachedFrameId = null;
                cachedFrameSeamless = null;
                drawScene(null);
                if (hint) hint.textContent = 'Could not load frame image';
              })
              .finally(function () {
                if (generation === frameSelectGeneration && spinner) spinner.style.display = 'none';
              });
          }

          window.__fsAutoSelectFirst = function () { /* no default frame selection */ };

          MAT_COLORS.forEach(function (m) { if (m && m.img) preloadMatImage(m.img); });

          window.fsResetFrame = function () {
            if (typeof resetFrameFilterDropdowns === 'function') resetFrameFilterDropdowns();
            frameSelectGeneration += 1;
            selectedId = null;
            FW = 0;
            cachedFrameId = null;
            cachedFrameSeamless = null;
            document.querySelectorAll('.fs-card').forEach(function (c) { c.classList.remove('fs-active'); });
            customizerState.frame = null;
            if (customizerState.liner) {
              LW = calcLW(customizerState.liner);
            } else {
              LW = 0;
            }
            drawScene(null);
            var hint = document.getElementById('fs-hint');
            if (hint) hint.textContent = 'Select a frame to preview';
            onFrameSelected(null, null);
            document.querySelectorAll('.customizer').forEach(function (c) {
              updateMatPreview(c);
              if (getCurrentStep(c) === getSummaryStep()) updateSummary(c);
            });
          };

          window.fsReset = window.fsResetFrame;

          window.fsSizeChanged = function () {};


          function repopulateFrameFinishFilter() {
            var colorSel = document.getElementById('fs-f-color');
            if (!colorSel) return;
            while (colorSel.options.length > 1) colorSel.remove(1);
            var finishes = Array.from(new Set(
              getAllowedFramesForCurrentSubstrate().map(function (f) { return getFrameFinishLabel(f); }).filter(Boolean)
            )).sort(function (a, b) { return a.localeCompare(b); });
            finishes.forEach(function (v) {
              var o = document.createElement('option');
              o.value = o.textContent = v;
              colorSel.appendChild(o);
            });
          }

          function repopulateFrameTypeFilter() {
            var typeSel = document.getElementById('fs-f-type');
            if (!typeSel) return;
            while (typeSel.options.length > 1) typeSel.remove(1);
            var types = Array.from(new Set(
              getAllowedFramesForCurrentSubstrate().map(function (f) { return f.type; }).filter(Boolean)
            )).sort(function (a, b) { return a.localeCompare(b); });
            types.forEach(function (v) {
              var o = document.createElement('option');
              o.value = o.textContent = v;
              typeSel.appendChild(o);
            });
          }

          function populateFilters() {
            var colorSel = document.getElementById('fs-f-color');
            if (colorSel) {
              repopulateFrameFinishFilter();
              if (colorSel.dataset.fsFilterBound !== '1') {
                colorSel.dataset.fsFilterBound = '1';
                colorSel.addEventListener('change', window.fsFilter);
              }
            }

            var typeSel = document.getElementById('fs-f-type');
            if (typeSel) {
              repopulateFrameTypeFilter();
              if (typeSel.dataset.fsFilterBound !== '1') {
                typeSel.dataset.fsFilterBound = '1';
                typeSel.addEventListener('change', window.fsFilter);
              }
            }

            var widthSel = document.getElementById('fs-f-width');
            if (widthSel && widthSel.dataset.fsFilterBound !== '1') {
              widthSel.dataset.fsFilterBound = '1';
              widthSel.addEventListener('change', window.fsFilter);
            }
          }

          function isFloaterFrame(f) {
            var t = String((f && f.type) || '').toLowerCase();
            var n = String((f && f.name) || '').toLowerCase();
            return /\bfloat(?:er|ing)?\b/.test(t) || /\bfloat(?:er|ing)?\b/.test(n);
          }

          function isFrameAllowedForCurrentSubstrate(f) {
            var isCanvas = typeof isCanvasSubstrate === 'function' ? !!isCanvasSubstrate() : false;
            var floater = isFloaterFrame(f);
            return isCanvas ? floater : !floater;
          }

          function getAllowedFramesForCurrentSubstrate() {
            return (FRAMES_LOCAL || []).filter(isFrameAllowedForCurrentSubstrate);
          }

          /** Keep every frame visible; pickFrom:false cards are rendered disabled. */
          function applyFramePickFromConstraints(list) {
            return (list || []).filter(Boolean);
          }

          function findFrameDefById(frameId) {
            if (!frameId) return null;
            var match = typeof window.__catalogIdsMatch === 'function'
              ? window.__catalogIdsMatch
              : function (a, b) {
                return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
              };
            return (FRAMES_LOCAL || []).find(function (x) {
              return match(x.id, frameId);
            }) || null;
          }

          function clearIncompatibleFrameSelection() {
            var id = selectedId || (customizerState.frame && customizerState.frame.id) || null;
            if (!id) return false;
            var cur = findFrameDefById(id);
            if (cur && isFrameAllowedForCurrentSubstrate(cur)) return false;
            frameSelectGeneration += 1;
            selectedId = null;
            FW = 0;
            cachedFrameId = null;
            cachedFrameSeamless = null;
            document.querySelectorAll('.fs-card').forEach(function (c) { c.classList.remove('fs-active'); });
            customizerState.frame = null;
            if (customizerState.liner) {
              LW = calcLW(customizerState.liner);
            } else {
              LW = 0;
            }
            drawScene(null);
            var hint = document.getElementById('fs-hint');
            if (hint) hint.textContent = 'Select a frame to preview';
            onFrameSelected(null, null);
            document.querySelectorAll('.customizer').forEach(function (c) {
              updateMatPreview(c);
              if (getCurrentStep(c) === getSummaryStep()) updateSummary(c);
            });
            return true;
          }

          window.__fsEnforceFrameSubstrateRules = function (opts) {
            opts = opts || {};
            try {
              if (!opts.skipReset) {
                clearIncompatibleFrameSelection();
              }
            } catch (e) {}
            populateFilters();
            window.fsFilter();
          };

          window.fsFilter = function () {
            var q  = (document.getElementById('fs-q')?.value || '').toLowerCase();
            var nm = document.getElementById('fs-f-color')?.value || '';
            var tp = document.getElementById('fs-f-type')?.value  || '';
            var fw = document.getElementById('fs-f-width')?.value || '';

            if (selectedId) {
              var selected = findFrameDefById(selectedId);
              if (!selected || !isFrameAllowedForCurrentSubstrate(selected)) {
                clearIncompatibleFrameSelection();
              }
            }

            var base = applyFramePickFromConstraints(getAllowedFramesForCurrentSubstrate());
            filtered = base.filter(function (f) {
              if (nm && getFrameFinishLabel(f) !== nm) return false;
              if (tp && f.type !== tp) return false;
              if (fw) {
                var bucket = faceWidthBucketFromFrameDef(f);
                if (!bucket || bucket !== fw) return false;
              }
              if (q && !f.id.toLowerCase().includes(q) && !f.name.toLowerCase().includes(q)) return false;
              return true;
            });
            /* Search/filter active: still pin discontinued current selection if it matches filters. */
            if (q || nm || tp || fw) {
              filtered = applyFramePickFromConstraints(filtered);
            }
            page = 1; renderGrid();
          };

          window.fsLoadMore = function () { page++; renderGrid(); };

          /* ─────────────────────────────────────────────────────────
            Tooltip behavior: keep open while hovering the trigger
            OR the tooltip itself (small leave delay bridges any gap),
            and wire up arrow / dot slider controls.
          ───────────────────────────────────────────────────────── */
          function bindFsCardTooltip(card) {
            var btn  = card.querySelector('.fs-card-info-btn');
            var tip  = card.querySelector('.fs-card-info-tooltip');
            if (!btn || !tip) return;

            var slides = tip.querySelectorAll('.fs-tt-slide');
            var dots   = tip.querySelectorAll('.fs-tt-dot');
            var idx    = 0;
            var leaveTimer = null;

            function go(i) {
              if (!slides.length) return;
              idx = ((i % slides.length) + slides.length) % slides.length;
              slides.forEach(function (s, j) { s.classList.toggle('fs-tt-slide--active', j === idx); });
              dots.forEach(function (d, j) { d.classList.toggle('fs-tt-dot--active', j === idx); });
            }

            function open()  {
              if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
              tip.classList.add('fs-card-info-tooltip--open');
            }
            function close() {
              if (leaveTimer) clearTimeout(leaveTimer);
              leaveTimer = setTimeout(function () {
                tip.classList.remove('fs-card-info-tooltip--open');
              }, 180);
            }

            btn.addEventListener('mouseenter', open);
            btn.addEventListener('mouseleave', close);
            btn.addEventListener('focus', open);
            btn.addEventListener('blur', close);
            tip.addEventListener('mouseenter', open);
            tip.addEventListener('mouseleave', close);

            tip.addEventListener('click', function (e) {
              var prev = e.target.closest('[data-fs-tt-prev]');
              var next = e.target.closest('[data-fs-tt-next]');
              var dot  = e.target.closest('[data-fs-tt-go]');
              if (prev) { e.stopPropagation(); go(idx - 1); return; }
              if (next) { e.stopPropagation(); go(idx + 1); return; }
              if (dot)  { e.stopPropagation(); go(parseInt(dot.dataset.fsTtGo, 10) || 0); return; }
            });
          }

          var FS_TOOLTIP_MAX_SLIDES = 4;

          function buildFrameTooltipSlides(f) {
            var seen = Object.create(null);
            var out = [];
            function push(src, alt) {
              if (!src || typeof src !== 'string') return;
              var t = src.trim();
              if (!t || seen[t]) return;
              seen[t] = true;
              out.push({ src: t, alt: alt || ('View of ' + (f.name || 'frame')) });
              return out.length >= FS_TOOLTIP_MAX_SLIDES;
            }
            if (Array.isArray(f.gallery) && f.gallery.length) {
              f.gallery.forEach(function (entry, i) {
                if (out.length >= FS_TOOLTIP_MAX_SLIDES) return;
                var src = typeof entry === 'string' ? entry : (entry && entry.src);
                var alt = typeof entry === 'object' && entry && entry.alt ? String(entry.alt) : '';
                push(src, alt || ('Image ' + (i + 1) + ' — ' + (f.name || '')));
              });
              if (out.length) return out;
            }
            var thumbSrc = f.thumb || f.img || '';
            [
              { src: f.dim, alt: 'Dimensions for ' + (f.name || 'frame') },
              { src: f.img, alt: 'Profile for ' + (f.name || 'frame') },
              { src: f.thumb, alt: (f.name || 'Frame') + ' — overview' }
            ].forEach(function (row) {
              if (out.length >= FS_TOOLTIP_MAX_SLIDES) return;
              if (row.src) push(row.src, row.alt);
            });
            if (!out.length && thumbSrc) push(thumbSrc, f.name || 'Frame');
            return out;
          }

          function renderGrid() {
            var grid = document.getElementById('fs-grid');
            if (!grid) return;
            grid.innerHTML = '';

            if (filtered.length === 0) {
              showEmptyState(grid, 'frame');
              var lm = document.getElementById('fs-load-more');
              if (lm) lm.style.display = 'none';
              return;
            }

            filtered.slice(0, page * PAGE_SIZE).forEach(function (f) {
              if (f && f.img) preloadFrameImage(f.img);
              var thumbSrc = f.thumb || f.img || '';
              var isDiscontinued = f.pickFrom === false;
              var card = document.createElement('div');
              card.className = 'fs-card' +
                (selectedId === f.id ? ' fs-active' : '') +
                (isDiscontinued ? ' fs-card--discontinued' : '');
              card.dataset.fsId = f.id;
              card.dataset.pickFrom = isDiscontinued ? '0' : '1';
              if (isDiscontinued) {
                card.setAttribute('aria-disabled', 'true');
                card.title = 'Discontinued — available only as original specification';
              }

              var slidesData = buildFrameTooltipSlides(f);

              var tooltipHtml = '';
              if (slidesData.length) {
                var slidesHtml = slidesData.map(function (s, i) {
                  return '<div class="fs-tt-slide' + (i === 0 ? ' fs-tt-slide--active' : '') + '" data-fs-tt-idx="' + i + '">' +
                    '<img src="' + s.src + '" alt="' + s.alt + '" loading="lazy"/>' +
                  '</div>';
                }).join('');
                var dotsHtml = slidesData.length > 1 ? slidesData.map(function (_, i) {
                  return '<button type="button" class="fs-tt-dot' + (i === 0 ? ' fs-tt-dot--active' : '') + '" data-fs-tt-go="' + i + '" aria-label="Go to image ' + (i + 1) + '"></button>';
                }).join('') : '';
                var arrowsHtml = slidesData.length > 1
                  ? '<button type="button" class="fs-tt-arrow fs-tt-arrow--prev" data-fs-tt-prev aria-label="Previous image">' +
                      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
                    '</button>' +
                    '<button type="button" class="fs-tt-arrow fs-tt-arrow--next" data-fs-tt-next aria-label="Next image">' +
                      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
                    '</button>'
                  : '';
                tooltipHtml =
                  '<div class="fs-card-info-tooltip">' +
                    '<div class="fs-tt-slider">' +
                      '<div class="fs-tt-slides">' + slidesHtml + '</div>' +
                      arrowsHtml +
                      (dotsHtml ? '<div class="fs-tt-dots">' + dotsHtml + '</div>' : '') +
                    '</div>' +
                  '</div>';
              }

              var infoBtnHtml = slidesData.length
                ? '<button type="button" class="fs-card-info-btn" aria-label="Frame dimensions">' +
                    '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.5 15C3.35775 15 0 11.6423 0 7.5C0 3.35775 3.35775 0 7.5 0C11.6423 0 15 3.35775 15 7.5C15 11.6423 11.6423 15 7.5 15ZM7.5 14.1C9.25043 14.1 10.9292 13.4046 12.1669 12.1669C13.4046 10.9292 14.1 9.25043 14.1 7.5C14.1 5.74957 13.4046 4.07084 12.1669 2.8331C10.9292 1.59535 9.25043 0.9 7.5 0.9C5.74957 0.9 4.07084 1.59535 2.8331 2.8331C1.59535 4.07084 0.9 5.74957 0.9 7.5C0.9 9.25043 1.59535 10.9292 2.8331 12.1669C4.07084 13.4046 5.74957 14.1 7.5 14.1ZM7.05 6H7.95V11.25H7.05V6ZM7.5 5.25C7.30109 5.25 7.11032 5.17098 6.96967 5.03033C6.82902 4.88968 6.75 4.69891 6.75 4.5C6.75 4.30109 6.82902 4.11032 6.96967 3.96967C7.11032 3.82902 7.30109 3.75 7.5 3.75C7.69891 3.75 7.88968 3.82902 8.03033 3.96967C8.17098 4.11032 8.25 4.30109 8.25 4.5C8.25 4.69891 8.17098 4.88968 8.03033 5.03033C7.88968 5.17098 7.69891 5.25 7.5 5.25Z" fill="black"/></svg>' +
                  '</button>'
                : '';

              var nameHtml = f.name + (isDiscontinued
                ? ' <span class="fs-card-discontinued-label">Discontinued</span>'
                : '');

              card.innerHTML =
               '<div class="fs-card-thumb">' +
                  (thumbSrc ? '<img src="' + thumbSrc + '" alt="' + f.name + '" loading="lazy"/>' : '') +
                  (isDiscontinued
                    ? '<span class="fs-card-discontinued-label">Discontinued</span>'
                    : '') +
                '</div>' +
                infoBtnHtml +
                tooltipHtml +
                '<div class="fs-card-meta">' +
                  '<span class="fs-card-id">'    + f.id   + '</span>' +
                  '<span class="fs-card-width">' + f.size + '</span>' +
                  '<span class="fs-card-nm">'    + f.name + '</span>' +
                '</div>';

              card.addEventListener('click', function (e) {
                if (e.target.closest('.fs-card-info-btn')) return;
                if (e.target.closest('.fs-card-info-tooltip')) return;
                if (isDiscontinued) return;
                selectFrame(f.id);
              });

              bindFsCardTooltip(card);

              grid.appendChild(card);
            });

            var lm = document.getElementById('fs-load-more');
            if (lm) lm.style.display = filtered.length > page * PAGE_SIZE ? 'flex' : 'none';
          }

          window.__fsBootstrapFrames = function (list) {
            var source = Array.isArray(list) && list.length ? list : FRAMES;
            var pendingFromDom = null;
            var userModified = false;
            document.querySelectorAll('.customizer').forEach(function (c) {
              if (c.dataset.userModifiedSelections === '1') userModified = true;
              if (!pendingFromDom && c.dataset.pendingOriginalFrameId) {
                pendingFromDom = c.dataset.pendingOriginalFrameId;
              }
            });
            var keepFrameId = selectedId || (customizerState.frame && customizerState.frame.id) || null;
            /* Never resurrect the product's original frame after the customer changed substrate. */
            if (!keepFrameId && !userModified) keepFrameId = pendingFromDom;
            FRAMES_LOCAL = source.map(function (f) { return normalizeFrameDef(Object.assign({}, f)); });
            page = 1;
            frameSelectGeneration += 1;
            selectedId = null;
            cachedFrameId = null;
            cachedFrameSeamless = null;
            filtered = FRAMES_LOCAL.slice();
            FRAMES_LOCAL.forEach(function (f) { if (f && f.img) preloadFrameImage(f.img); });
            populateFilters();
            window.fsFilter();
            if (keepFrameId && typeof window.__fsSelectFrameById === 'function') {
              window.__fsSelectFrameById(keepFrameId, { fromOriginalSpec: !userModified });
            }
            if (typeof window.__fsEnforceFrameSubstrateRules === 'function') {
              window.__fsEnforceFrameSubstrateRules();
            }
            syncFrameCardActiveState();
            document.querySelectorAll('.customizer').forEach(function (c) {
              if (c.dataset.userModifiedSelections !== '1' && typeof window.__scheduleApplyOriginalSpecifications === 'function') {
                window.__scheduleApplyOriginalSpecifications(c);
              }
            });
          };

          window.__fsSelectFrameById = function (frameId, opts) {
            opts = opts || {};
            if (!frameId) return false;
            var f = findFrameDefById(frameId);
            if (!f) return false;
            if (!opts.allowIncompatibleSubstrate && !isFrameAllowedForCurrentSubstrate(f)) return false;
            selectFrame(f.id, {
              fromOriginalSpec: opts.fromOriginalSpec !== false,
              allowIncompatibleSubstrate: !!opts.allowIncompatibleSubstrate
            });
            return true;
          };

          /** Re-highlight active frame card after grid re-render (e.g. after bootstrap). */
          function syncFrameCardActiveState() {
            document.querySelectorAll('.fs-card').forEach(function (c) {
              c.classList.toggle('fs-active', selectedId && c.dataset.fsId === selectedId);
            });
          }

          window.__fsGetSelectedFrameId = function () {
            return selectedId || null;
          };

          FW = 0;
          LW = 0;

          function drawInitialScene() {
            var keepId = selectedId || (customizerState.frame && customizerState.frame.id) || null;
            if (keepId && typeof window.__fsSelectFrameById === 'function') {
              if (window.__fsSelectFrameById(keepId)) return;
            }
            selectedId = null;
            FW = 0;
            LW = 0;
            drawScene(null);
          }

          var productImgSrc = (function () {
            var el = document.querySelector('.editable-image');
            if (el && el.dataset.originalSrc && el.src !== el.dataset.originalSrc) return el.src;
            return window.__fsProductImg || '';
          })();

          if (productImgSrc) {
            var isData = productImgSrc.indexOf('data:') === 0;
            (isData ? loadViaDataURI(productImgSrc) : loadViaBlob(productImgSrc))
              .then(function (img) { photoImg = img; drawInitialScene(); })
              .catch(function () { drawInitialScene(); });
          } else {
            drawInitialScene();
          } 

          window.__fsBootstrapFrames(FRAMES);
          document.querySelectorAll('.customizer').forEach(function (c) {
            if (c.dataset.userModifiedSelections !== '1' && typeof window.__scheduleApplyOriginalSpecifications === 'function') {
              window.__scheduleApplyOriginalSpecifications(c);
            }
          });
        })(); 

        /* called by goToStep when entering step 3 */
        function autoSelectFirstFrame() {
          if (typeof window.__fsAutoSelectFirst === 'function') {
            window.__fsAutoSelectFirst();
          } 
        }           